import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from "react";
import ShowMode, { PriceHistoryModal } from "./ShowMode";
import CardScanner from "./CardScanner";
import ReportsPage from "./Reports";
import { QRLabelGenerator, QRScanner } from "./QRTools";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import QuickScanFlow from "./QuickScan";
import { FREE_INVENTORY_LIMIT, resolveTier, canUseFeature } from "./plans";
import CONFIG from "./config";
import { containsBlockedContent, stripBlockedContent } from "./contentFilter";
import Settings from "./Settings";
import HelpPage from "./HelpPage";
import {
  supabase,
  signInWithGoogle, signOut, getSession, onAuthStateChange, markOnboardingTourSeen, seedSampleData,
  getVendor, createVendorAccount, isVendorAlias, clearVendorCache,
  readInventory, readInventoryItem, addInventoryItem, updateInventoryItem, deleteInventoryItem,
  readLots, addLot, updateLot,
  readConsignors, addConsignor, updateConsignor, deleteConsignor,
  readShows, addShow, updateShow,
  readShowItems, addShowItem, removeShowItem,
  readTrades, addTrade, updateTrade,
  subscribeToAll,
  getAllPriceHistory, logListPriceChange, searchCardHistory,
  pkmnpricesLookup, pkmnpricesSearch, computeConditionPrice, computeGradedPrice, refreshSingleCardPrice,
  unifiedCardSearch, unifiedCardLookup, apitcgSearchSealed, apitcgLookupSealed, readLocations, getStaffLocationIds, deriveCategory,
  getAvailableVariants, pkmnpricesFetchPriceData,
  readShoppingLists, addShoppingList, updateShoppingList, deleteShoppingList,
  readBuyers, addBuyer, updateBuyer, deleteBuyer,
  readBuyerWantList, addWantItem, updateWantItem, deleteWantItem, readAllWantListItems, notifyBuyerCardFound,
  addBuyerPurchase, readBuyerPurchases, readAllBuyerPurchases, computeTax, removeDealBoardListingIfListed,
  listOnDealBoard, unlistFromDealBoard, unlistDealBoardById, getDealBoardFeed, getMyDealBoardListings,
  subscribeToDealBoardListings,
  postStandaloneDealBoardListing, uploadCardImage,
  adminRemoveDealBoardListing, acceptDealBoardGuidelines,
  linkDealBoardPlusSubscription,
  getDealBoardPostCap, getDealBoardPostCountThisMonth,
  getUnseenPriceUpdates, markPriceUpdatesSeen, checkNearingCapEmail, logManualPriceEdit,
  getMyFavoriteIds, toggleFavorite, getMyFollowedVendorIds, toggleFollow,
  adjustBuyerStoreCredit,
  sendDealBoardMessage, getDealBoardConnections, getDealBoardThread, markDealBoardThreadRead,
  deleteDealBoardMessage,
  postDealBoardRequest, getDealBoardRequestsFeed, getMyDealBoardRequests, updateDealBoardRequestStatus,
  getDealBoardUnreadCount, respondToDealBoardConnection, blockDealBoardVendor,
  readKioskOrders, readKioskOrderDetail, updateKioskOrderContact,
  setKioskOrderItemFulfilled, removeKioskOrderItem, completeKioskOrder, cancelKioskOrder,
  loadDemoData, DEMO_VENDOR_ID,
  appendPriceHistory,
  getVendorRole, readTeamMembers, addTeamMember, removeTeamMember,
  updateTeamMemberRole, writeAuditLog, readAuditLog,
  readClaims, addClaim, updateClaim, deleteClaim, readClaimHistory, acceptClaimOffer,
  readTradePhotos, readImportBatches, deleteImportBatch, readImportBatchCards,
  checkTrialStatus, startProTrial, declineProTrial, linkPayPalSubscription,
} from "./supabase";
import MarketIntelligence from "./MarketIntelligence";
import { PriceSparkline } from "./PriceRefresh";

// ─── RESPONSIVE HOOK ─────────────────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const CONDITIONS  = ["Mint","Near Mint","Lightly Played","Moderately Played","Heavily Played","Damaged"];
const GENERIC_VARIANT_FALLBACK = ["Normal","Holofoil","Reverse Holo","1st Edition","Unlimited","Shadowless"];
const DEAL_BOARD_GAME_TYPES = ["Lorcana","Magic The Gathering","One-Piece","Other","Pokemon","Sports","Yu-Gi-Oh"];
const DEAL_BOARD_INVENTORY_BACKED_GAMES = ["Pokemon","Lorcana"];

const CUSTOM_ITEM_CATEGORIES = [
  "Accessories","Apparel","Art & Prints","Bags & Totes","Binders & Storage",
  "Books & Comics","Card & Collectible Supplies","Collectibles","Cosplay & Wearables",
  "Dice & Gaming Accessories","Display & Booth Accessories","Handmade Goods",
  "Home & Desk Decor","Jewelry","Keychains & Charms","Pins, Badges & Patches",
  "Plushies","Stickers & Decals","Toys & Figures","Other / Custom",
];
const CUSTOM_ITEM_TYPES = [
  "3D Printed Item","Acrylic Charm","Acrylic Standee","Art Print","Badge","Binder",
  "Button Pin","Card Deck Box","Card Sleeves","Charm","Coaster","Comic Book",
  "Crochet Plush","Custom Card Holder","Custom Slab Holder","Decal","Dice","Dice Set",
  "Display Case","Display Stand","Enamel Pin","Figure","Funko / Vinyl Figure",
  "Keychain","Lanyard","Magnet","Mystery Bag","Mystery Box","One-Touch Holder",
  "Patch","Playmat","Plush","Poster","Print","Slab Case","Slab Stand","Sticker",
  "Sticker Sheet","Storage Box","T-Shirt","Toploader","Tote Bag",
  "Trading Card Binder","Trading Card Display","Tumbler","Wallet","Wristband",
  "Other / Custom",
];
const CUSTOM_ITEM_CONDITIONS = ["New","Like New","Good","Fair","Used","Handmade — Made to Order"];
const LOT_SOURCES = ["Pack Pull","Binder Buy","Bulk Lot"];
const ALL_SOURCES = ["Pack Pull","Binder Buy","Bulk Lot","Individual Buy"];
const CHANNELS    = ["eBay","Facebook","Instagram","Local Show","In-Store","Other"];
const RARITIES = [
  "Common","Uncommon","Rare","Holo Rare","Reverse Holo",
  "Double Rare","Triple Rare","Ultra Rare","Super Rare",
  "Illustration Rare","Special Illustration Rare","Special Art Rare","Art Rare",
  "Hyper Rare","Secret Rare","Shiny Rare","Shiny Holo","Shiny Holo Rare","Shiny Ultra Rare",
  "Full Art","Alternate Art",
  "Mega Attack Rare","Mega Hyper Rare",
  "Promo","Promotional","Classic Collection",
  "Rare Holo V","Rare Holo VMAX","Rare Holo VSTAR","Amazing Rare","Rare Rainbow",
  "Trainer Gallery","Galarian Gallery","Gold Rare",
  "Character Rare","Character Super Rare","Trainer Rare","SAR","UR","K Rare",
];
const GRADES = [
  "PSA 10","PSA 9.5","PSA 9","PSA 8.5","PSA 8","PSA 7.5","PSA 7",
  "PSA 6.5","PSA 6","PSA 5.5","PSA 5","PSA 4.5","PSA 4",
  "PSA 3.5","PSA 3","PSA 2.5","PSA 2","PSA 1.5","PSA 1",
  "BGS Black Label","BGS 10","BGS 9.5","BGS 9","BGS 8.5","BGS 8",
  "BGS 7.5","BGS 7","BGS 6.5","BGS 6","BGS 5.5","BGS 5",
  "BGS 4.5","BGS 4","BGS 3.5","BGS 3","BGS 2.5","BGS 2","BGS 1.5","BGS 1",
  "CGC 10","CGC 9.5","CGC 9","CGC 8.5","CGC 8","CGC 7.5","CGC 7",
  "CGC 6.5","CGC 6","CGC 5.5","CGC 5","CGC 4.5","CGC 4",
  "CGC 3.5","CGC 3","CGC 2.5","CGC 2","CGC 1.5","CGC 1",
  "TAG 10","TAG 9","ACE 10","ACE 9",
];
const ITEM_TYPES  = ["Single Card","Sealed Product"];
const LANGUAGES   = ["English","Japanese","Korean","Chinese","French","German","Spanish","Italian","Portuguese","Other"];
const GENDERS     = ["Male","Female","Non-binary","Prefer not to say"];
const MARKUP      = 1.25;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt       = n   => `$${(+n||0).toFixed(2)}`;
const fmtT      = n   => `$${Math.round(+n||0)}`;

function extractNotesPhoto(notes) {
  if (!notes || !notes.includes("Photo:")) return { cleanText: notes || "", photoUrl: null };
  const match = notes.match(/Photo:\s*(\S+)/);
  const photoUrl = match ? match[1] : null;
  const cleanText = notes.replace(/\s*·?\s*Photo:\s*\S+/, "").trim();
  return { cleanText, photoUrl };
}
const pct       = n   => `${(+n||0).toFixed(1)}%`;
const daysSince = d   => d ? Math.floor((Date.now()-new Date(d).getTime())/86400000) : 0;
function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days/7);
  if (weeks < 5) return `${weeks} week${weeks!==1?"s":""} ago`;
  const months = Math.floor(days/30);
  if (months < 12) return `${months} month${months!==1?"s":""} ago`;
  return `${Math.floor(days/365)}y ago`;
}

const roi = (c,p) => {
  const cost  = parseFloat(c);
  const price = parseFloat(p);
  if (isNaN(price) || price <= 0) return null;
  if (isNaN(cost) || cost < 0) return null;
  if (cost === 0) return Infinity;
  return ((price - cost) / cost) * 100;
};

const localDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
};

const priceAge  = (updatedAt) => {
  if (!updatedAt) return null;
  const mins = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 60000);
  if (mins < 60)  return { label: `${mins}m ago`,        color: "#7EAF5C" };
  if (mins < 720) return { label: `${Math.floor(mins/60)}h ago`, color: "#FF9600" };
  const hrs = Math.floor(mins/60);
  if (hrs  < 48)  return { label: `${hrs}h ago`,          color: "#AE1A21" };
  return { label: `${Math.floor(hrs/24)}d ago`,           color: "#AE1A21" };
};
const minSell   = cost => Math.max(1, Math.ceil(cost * MARKUP));

// ─── STYLES ───────────────────────────────────────────────────────────────────
// ─── THEME ────────────────────────────────────────────────────────────────────
const DARK = {
  bg:        "#081426",
  surface:   "#0D1C34",
  surface2:  "#132846",
  border:    "#1E3A5F",
  borderSoft:"#173052",
  text:      "#F8FAFC",
  textMuted: "#CBD5E1",
  textDim:   "#94A3B8",
  textFaint: "#6B7A94",
  textTiny:  "#4A5872",
  primary:      "#3B82F6",
  primaryHover: "#60A5FA",
  teal:      "#2BBFA4",
  tealHover: "#26A890",
  tealBg:    "rgba(43,191,164,0.14)",
  chip:      "rgba(59,130,246,0.16)",
  chipBorder:"rgba(59,130,246,0.5)",
  chipText:  "#3B82F6",
  chipOff:   "#CBD5E1",
  green:     "#4ADE80",
  greenBg:   "rgba(74,222,128,0.13)",
  red:       "#F87171",
  redBg:     "rgba(248,113,113,0.13)",
  amber:     "#FDBA74",
  amberBg:   "rgba(253,186,116,0.13)",
  pink:      "#f9a8d4",
  pinkBg:    "rgba(249,168,212,0.1)",
  blueBg:    "rgba(59,130,246,0.13)",
  shadowSm:  "0 2px 8px rgba(0,0,0,0.22)",
  shadowMd:  "0 10px 28px rgba(0,0,0,0.28)",
  radius:    { sm:8, md:12, lg:18, xl:24, pill:999 },
};

export const LIGHT = {
  bg:        "#F8FAFD",
  surface:   "#FFFFFF",
  surface2:  "#F1F5F9",
  border:    "#DDE7F5",
  borderSoft:"#EAF0FA",
  text:      "#0F172A",
  textMuted: "#2C4A6F",
  textDim:   "#5E6D8F",
  textFaint: "#8B9BB4",
  textTiny:  "#AAB8CC",
  primary:      "#125DE8",
  primaryHover: "#0C4DAC",
  teal:      "#00C070",
  tealHover: "#00A862",
  tealBg:    "#E8FFF4",
  chip:      "rgba(18,93,232,0.10)",
  chipBorder:"rgba(18,93,232,0.35)",
  chipText:  "#125DE8",
  chipOff:   "#2C4A6F",
  green:     "#16A34A",
  greenBg:   "#E8FFF4",
  red:       "#DC2626",
  redBg:     "#FFF0F0",
  amber:     "#D95B00",
  amberBg:   "#FFF4E8",
  pink:      "#c0387a",
  pinkBg:    "rgba(192,56,122,0.09)",
  blueBg:    "rgba(18,93,232,0.08)",
  shadowSm:  "0 2px 8px rgba(1,19,53,0.06)",
  shadowMd:  "0 8px 24px rgba(1,19,53,0.08)",
  radius:    { sm:8, md:12, lg:18, xl:24, pill:999 },
};

export const makeStyles = (T, isDarkMode=true) => ({
  app:    { fontFamily:"'Sora',sans-serif", background:T.bg, minHeight:"100vh", color:T.text },
  nav:    { background:T.surface,
            borderBottom:`1px solid ${T.border}`,
            padding:"0 16px",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            height:60, position:"sticky", top:0, zIndex:100,
            boxShadow:T.shadowSm },
  logo:   { fontFamily:"'Sora',sans-serif", fontWeight:700, fontSize:18,
            letterSpacing:"-0.3px", display:"flex", alignItems:"center", gap:0 },
  tabs:   { display:"flex", gap:0, background:T.surface,
            borderBottom:`1px solid ${T.border}`,
            overflowX:"auto", WebkitOverflowScrolling:"touch", scrollbarWidth:"none",
            position:"sticky", top:60, zIndex:99 },
  tab:    a => ({ padding:"0 14px", height:38, display:"flex", alignItems:"center",
                  borderBottom:a?`2px solid ${T.primary}`:"2px solid transparent",
                  background:"transparent",
                  color:a?T.primary:T.textMuted,
                  fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:a?600:500,
                  cursor:"pointer", whiteSpace:"nowrap", transition:"color 0.15s",
                  border:"none", borderBottom:a?`2px solid ${T.primary}`:"2px solid transparent",
                  outline:"none" }),
  page:   { padding:"16px 20px", width:"100%", boxSizing:"border-box", maxWidth:1200, margin:"0 auto" },
  card:   { background:T.surface, border:`1px solid ${T.borderSoft}`, borderRadius:T.radius.lg,
            padding:"14px 16px", marginBottom:10, boxShadow:T.shadowSm },
  cardB:  c => ({ background:T.surface, border:`1px solid ${c||T.borderSoft}`, borderRadius:T.radius.lg,
            padding:"14px 16px", marginBottom:10, boxShadow:T.shadowSm }),
  label:  { fontSize:12, fontFamily:"'DM Mono',monospace", color:T.textMuted,
            textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:4, display:"block" },
  input:  { width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius.md,
            color:T.text, padding:"10px 12px", fontSize:14, fontFamily:"'Sora',sans-serif",
            boxSizing:"border-box", outline:"none",
            transition:"border-color 0.15s",
            boxShadow:"none" },
  select: { width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:T.radius.md,
            color:T.text, padding:"10px 12px", fontSize:14, fontFamily:"'Sora',sans-serif",
            boxSizing:"border-box", outline:"none", appearance:"none" },
  btn:    v => ({ padding:v==="sm"?"6px 12px":"11px 18px", borderRadius:T.radius.md,
                  border:v==="ghost"?`1px solid ${T.border}`:"none",
                  background:v==="primary"?T.primary:v==="danger"?T.red:v==="ghost"?"transparent":v==="teal"?T.teal:T.surface2,
                  color:v==="primary"?"#ffffff":v==="danger"?"#fff":v==="ghost"?T.primary:v==="teal"?"#ffffff":T.text,
                  fontFamily:"'Sora',sans-serif", fontSize:v==="sm"?13:14, fontWeight:600,
                  cursor:"pointer", transition:"opacity 0.15s, background 0.15s", whiteSpace:"nowrap" }),
  formWrap: { maxWidth:560, width:"100%" },
  row:    { display:"flex", gap:10, marginBottom:12 },
  col:    { flex:1, minWidth:0 },
  stat:   { background:T.surface, border:`1px solid ${T.borderSoft}`, borderRadius:T.radius.lg,
            padding:"12px 14px", flex:1, boxShadow:T.shadowSm },
  statLbl:{ fontSize:11, fontFamily:"'DM Mono',monospace", color:T.textMuted,
            textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:3 },
  statVal:{ fontSize:20, fontWeight:700, color:T.text, marginTop:2 },
  badge:  c => ({ display:"inline-block", padding:"3px 8px", borderRadius:T.radius.pill,
                  fontSize:10, fontWeight:600, fontFamily:"'DM Mono',monospace",
                  background:c==="green"?T.greenBg:c==="red"?T.redBg:c==="amber"?T.amberBg:c==="blue"?T.blueBg:c==="purple"?T.pinkBg:T.surface2,
                  color:c==="green"?T.green:c==="red"?T.red:c==="amber"?T.amber:c==="blue"?T.primary:c==="purple"?T.pink:T.textDim }),
  h2:     { fontSize:12, fontFamily:"'DM Mono',monospace", color:T.textMuted,
            textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10, marginTop:0 },
  divider:{ borderTop:`1px solid ${T.border}`, margin:"12px 0" },
  overlay:{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:200,
            display:"flex", alignItems:"center", justifyContent:"center", padding:"16px",
            backdropFilter:"blur(4px)" },
  drawer: { background:T.surface, borderRadius:T.radius.xl,
            width:"100%", maxWidth:680, maxHeight:"85vh", overflowY:"auto",
            padding:"20px 20px 32px", border:`1px solid ${T.borderSoft}`,
            boxShadow:T.shadowMd },
  chip:   a => ({ padding:"6px 12px", borderRadius:T.radius.pill, fontSize:12, cursor:"pointer",
                  border:`1px solid ${a?T.chipBorder:T.border}`,
                  background:a?T.chip:"transparent",
                  color:a?T.chipText:T.chipOff, whiteSpace:"nowrap",
                  fontWeight:a?600:400 }),
  infoBox:{ background:T.surface2, border:`1px solid ${T.borderSoft}`, borderRadius:T.radius.md,
            padding:"12px 14px", marginBottom:12 },
});


// ─── IMAGE LIGHTBOX ───────────────────────────────────────────────────────────
function Lightbox({ url, onClose }) {
  const { S, T } = useTheme();
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",zIndex:500,
      display:"flex",alignItems:"center",justifyContent:"center"}}
      onClick={onClose}>
      <img src={url} alt="Card" style={{maxWidth:"90vw",maxHeight:"90vh",
        borderRadius:12,objectFit:"contain"}} />
      <button style={{position:"absolute",top:20,right:20,...S.btn("ghost"),
        padding:"8px 14px",fontSize:18}} onClick={onClose}>✕</button>
    </div>
  );
}

// ─── THEME CONTEXT ───────────────────────────────────────────────────────────
const DEFAULT_RATES = {
  tradeCreditRate: CONFIG.TRADE_CREDIT_RATE,
  cashBuyRate:     CONFIG.CASH_BUY_RATE,
  markupRate:      MARKUP,
  breakevenBuffer: CONFIG.BREAKEVEN_BUFFER,
};
const ThemeContext = createContext({ S: makeStyles(DARK), T: DARK, rates: DEFAULT_RATES });
export const useTheme = () => useContext(ThemeContext);

// ─── PLAN / FEATURE GATING ────────────────────────────────────────────────────
const PlanContext = createContext({ isPaid: true, isAdmin: false, isFree: false, canUse: ()=>true, vendor: null });
export const usePlan = () => useContext(PlanContext);

// ── DEMO CONTEXT ──────────────────────────────────────────────────────────────
const DemoContext = createContext(false);
export const useDemo = () => useContext(DemoContext);
export const IS_DEMO = window.location.pathname === "/demo";

export const DEAL_BOARD_LIVE = true;

export function demoBlockToast(toastFn) {
  toastFn(
    "This is a demo. Create an account to use this feature: vendorstax.com/contact",
    "update"
  );
}

// ── DEMO BANNER ───────────────────────────────────────────────────────────────
function DemoBanner() {
  const { toast } = useNotify();
  return (
    <div style={{
      background:"linear-gradient(90deg,#1a0a4e 0%,#3C66EE 50%,#1a0a4e 100%)",
      padding:"10px 16px",
      display:"flex", alignItems:"center", justifyContent:"space-between",
      flexWrap:"wrap", gap:8, flexShrink:0,
      borderBottom:"1px solid rgba(60,102,238,0.4)",
      zIndex:200,
    }}>
      <div style={{display:"flex", alignItems:"center", gap:8}}>
        <span style={{fontSize:15}}>👀</span>
        <span style={{fontSize:13, fontWeight:600, color:"#fff"}}>Demo Mode</span>
        <span style={{fontSize:12, color:"rgba(255,255,255,0.6)"}}>
          · Read-only · Sample data
        </span>
      </div>
      <a href="https://vendorstax.com/contact"
        target="_blank" rel="noopener noreferrer"
        style={{
          background:"#fff", color:"#3C66EE",
          borderRadius:8, padding:"6px 16px",
          fontSize:12, fontWeight:700, textDecoration:"none", flexShrink:0,
        }}>
        Create Free Account →
      </a>
    </div>
  );
}

function PayPalUpgradeModal({ vendor, onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [cycle, setCycle] = useState("monthly");
  const [phone, setPhone] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [linking, setLinking] = useState(false);
  const buttonsRef = useRef(null);
  const renderedButtons = useRef(null);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const planIds = {
    monthly: process.env.REACT_APP_PAYPAL_PLAN_MONTHLY,
    annual:  process.env.REACT_APP_PAYPAL_PLAN_ANNUAL,
  };

  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) { setSdkReady(true); return; }
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    script.onload = () => setSdkReady(true);
    script.onerror = () => toast("Couldn't load PayPal — please try again", "error");
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [clientId]);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !buttonsRef.current || !phone.trim()) return;
    if (renderedButtons.current) {
      renderedButtons.current.close();
      renderedButtons.current = null;
    }
    buttonsRef.current.innerHTML = "";
    const planId = planIds[cycle];
    if (!planId) return;

    renderedButtons.current = window.paypal.Buttons({
      style: { shape: "pill", color: "blue", layout: "vertical", label: "subscribe" },
      createSubscription: (data, actions) => actions.subscription.create({
        plan_id: planId,
        custom_id: vendor?.id,
      }),
      onApprove: async (data) => {
        setLinking(true);
        try {
          const result = await linkPayPalSubscription(data.subscriptionID, phone.trim());
          toast("You're upgraded to Pro — welcome aboard!", "success");
          onClose(result.plan);
        } catch(e) {
          toast("Your subscription is confirmed! Pro access will unlock automatically within a minute or two.", "success");
          onClose();
        }
      },
      onError: () => {
        toast("Something went wrong with PayPal — please try again", "error");
      },
    });
    renderedButtons.current.render(buttonsRef.current);
  }, [sdkReady, cycle, phone]);

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(null); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div style={S.h2}>Upgrade to Pro</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={()=>onClose(null)}>✕</button>
        </div>

        <div style={{display:"flex", gap:8, marginBottom:20}}>
          {[["monthly","Monthly"],["annual","Annual"]].map(([val,lbl])=>(
            <button key={val} onClick={()=>setCycle(val)}
              style={{flex:1,padding:"12px",borderRadius:10,fontSize:13,fontWeight:600,cursor:"pointer",
                border:`1px solid ${cycle===val?"#3C66EE":T.border}`,
                background:cycle===val?"rgba(60,102,238,0.1)":"transparent",
                color:cycle===val?"#88bdf2":T.textMuted}}>
              {lbl}
            </button>
          ))}
        </div>

        <div style={{marginBottom:16}}>
          <label style={S.label}>Phone Number</label>
          <input style={S.input} type="tel" value={phone}
            onChange={e=>setPhone(e.target.value)} placeholder="(407) 555-0100" />
          <div style={{fontSize:11, color:T.textFaint, marginTop:6, lineHeight:1.5}}>
            We keep this on file so we can reach you directly if something ever needs
            attention on your account, like a vendor-to-vendor issue on Deal Board.
          </div>
        </div>

        {!clientId ? (
          <div style={{...S.infoBox, textAlign:"center", color:T.textMuted, fontSize:13}}>
            PayPal isn't configured yet — check back soon.
          </div>
        ) : linking ? (
          <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:13}}>
            Finishing up…
          </div>
        ) : !phone.trim() ? (
          <div style={{...S.infoBox, textAlign:"center", color:T.textMuted, fontSize:13}}>
            Enter a phone number above to continue
          </div>
        ) : (
          <div ref={buttonsRef} style={{minHeight:150}} />
        )}
      </div>
    </div>
  );
}

function DealBoardPlusUpgradeModal({ vendor, onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [phone, setPhone] = useState("");
  const [sdkReady, setSdkReady] = useState(false);
  const [linking, setLinking] = useState(false);
  const buttonsRef = useRef(null);
  const renderedButtons = useRef(null);

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const planId = process.env.REACT_APP_PAYPAL_PLAN_DEAL_BOARD_PLUS;

  useEffect(() => {
    if (!clientId) return;
    if (window.paypal) { setSdkReady(true); return; }
    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    script.onload = () => setSdkReady(true);
    script.onerror = () => toast("Couldn't load PayPal — please try again", "error");
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, [clientId]);

  useEffect(() => {
    if (!sdkReady || !window.paypal || !buttonsRef.current || !phone.trim() || !planId) return;
    if (renderedButtons.current) {
      renderedButtons.current.close();
      renderedButtons.current = null;
    }
    buttonsRef.current.innerHTML = "";

    renderedButtons.current = window.paypal.Buttons({
      style: { shape: "pill", color: "blue", layout: "vertical", label: "subscribe" },
      createSubscription: (data, actions) => actions.subscription.create({
        plan_id: planId,
        custom_id: vendor?.id,
      }),
      onApprove: async (data) => {
        setLinking(true);
        try {
          const result = await linkDealBoardPlusSubscription(data.subscriptionID, phone.trim());
          toast("You're on Deal Board Plus — happy dealing!", "success");
          onClose(result.plan);
        } catch(e) {
          toast("Your subscription is confirmed! Deal Board Plus will unlock automatically within a minute or two.", "success");
          onClose();
        }
      },
      onError: () => {
        toast("Something went wrong with PayPal — please try again", "error");
      },
    });
    renderedButtons.current.render(buttonsRef.current);
  }, [sdkReady, phone]);

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(null); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={S.h2}>Deal Board Plus — $5/mo</div>
            <div style={{fontSize:12, color:T.textMuted, marginTop:4}}>
              30 Deal Board posts a month, no other VendorStax tools included.
            </div>
          </div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={()=>onClose(null)}>✕</button>
        </div>

        <div style={{marginBottom:16}}>
          <label style={S.label}>Phone Number</label>
          <input style={S.input} type="tel" value={phone}
            onChange={e=>setPhone(e.target.value)} placeholder="(407) 555-0100" />
          <div style={{fontSize:11, color:T.textFaint, marginTop:6, lineHeight:1.5}}>
            We keep this on file so we can reach you directly if something ever needs
            attention on your account, like a vendor-to-vendor issue on Deal Board.
          </div>
        </div>

        {!clientId || !planId ? (
          <div style={{...S.infoBox, textAlign:"center", color:T.textMuted, fontSize:13}}>
            PayPal isn't configured yet — check back soon.
          </div>
        ) : linking ? (
          <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:13}}>
            Finishing up…
          </div>
        ) : !phone.trim() ? (
          <div style={{...S.infoBox, textAlign:"center", color:T.textMuted, fontSize:13}}>
            Enter a phone number above to continue
          </div>
        ) : (
          <div ref={buttonsRef} style={{minHeight:150}} />
        )}
      </div>
    </div>
  );
}

function TrialOfferModal({ show, vendor, onClose }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [busy, setBusy] = useState(false);

  const handleStart = async () => {
    setBusy(true);
    try {
      await startProTrial(vendor.id, show.ID, show.EndDate);
      toast("Pro trial started — enjoy full access through your show, plus 5 extra days!", "success");
      onClose(true);
    } catch(e) {
      toast("Couldn't start trial: " + e.message, "error");
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await declineProTrial(vendor.id);
      onClose(false);
    } catch(e) {
      toast("Something went wrong: " + e.message, "error");
      setBusy(false);
    }
  };

  return (
    <div style={{...S.overlay, zIndex:10002}}>
      <div style={S.drawer}>
        <div style={{fontSize:32, textAlign:"center", marginBottom:12}}>🎉</div>
        <div style={{fontSize:18, fontWeight:700, textAlign:"center", marginBottom:8}}>
          Try VendorStax Pro, Free
        </div>
        <div style={{fontSize:14, color:T.textMuted, textAlign:"center", marginBottom:20, lineHeight:1.6}}>
          <strong>{show.Name}</strong> just went active. Activating it unlocks every Pro
          feature, free, for the rest of this show plus 5 extra days afterward.
        </div>
        <div style={{...S.infoBox, marginBottom:20}}>
          <div style={{fontSize:12, color:T.textMuted, lineHeight:1.9}}>
            ✓ Unlimited inventory<br/>
            ✓ Buyers &amp; Consignors CRM<br/>
            ✓ Team management<br/>
            ✓ Kiosk mode<br/>
            ✓ Card scanning<br/>
            ✓ Advanced reports
          </div>
        </div>
        <button style={{...S.btn("primary"), width:"100%", padding:14, marginBottom:10}}
          onClick={handleStart} disabled={busy}>
          {busy ? "Starting…" : "Start My Free Pro Trial"}
        </button>
        <button style={{...S.btn("ghost"), width:"100%", padding:12}}
          onClick={handleDecline} disabled={busy}>
          No Thanks, Stay on Free
        </button>
        <div style={{fontSize:11, color:T.textFaint, textAlign:"center", marginTop:12}}>
          After the trial, you'll choose to upgrade or move back to Free. No surprise charges.
        </div>
      </div>
    </div>
  );
}

export function PaidFeatureBanner({ feature = "" }) {
  const { T, S } = useTheme();
  const { openUpgradeModal } = useNotify();
  return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      textAlign:"center", padding:"48px 24px", gap:16,
    }}>
      <div style={{fontSize:36}}>🔒</div>
      <div style={{fontSize:18, fontWeight:700, color:T.text}}>Paid Feature</div>
      <div style={{fontSize:14, color:T.textMuted, maxWidth:320, lineHeight:1.6}}>
        {feature ? `${feature} is` : "This is"} available on the Pro plan.
        Upgrade to unlock this and all other Pro features.
      </div>
      <button onClick={openUpgradeModal}
        style={{...S.btn("primary"), marginTop:8}}>
        Upgrade to Pro →
      </button>
    </div>
  );
}

export function PaidFeatureTab({ feature = "" }) {
  const { T, S } = useTheme();
  const { openUpgradeModal } = useNotify();
  return (
    <div style={{
      display:"flex", flexDirection:"column", alignItems:"center",
      textAlign:"center", padding:"32px 20px", gap:12,
    }}>
      <div style={{fontSize:24}}>🔒</div>
      <div style={{fontSize:15, fontWeight:600, color:T.text}}>Paid Feature</div>
      <div style={{fontSize:13, color:T.textMuted, maxWidth:280, lineHeight:1.6}}>
        {feature || "This feature"} is available on the Pro plan.
      </div>
      <button onClick={openUpgradeModal}
        style={{...S.btn("ghost"), fontSize:13}}>
        Upgrade to Pro →
      </button>
    </div>
  );
}

export function PaidBadge() {
  return (
    <span style={{
      fontSize:9, fontWeight:700, letterSpacing:"0.05em",
      color:"#FF9600", background:"rgba(255,150,0,0.12)",
      border:"0.5px solid rgba(255,150,0,0.4)",
      borderRadius:4, padding:"2px 5px", marginLeft:6,
      verticalAlign:"middle",
    }}>PRO</span>
  );
}

// ─── NOTIFY SYSTEM (banners + confirm modal — replaces alert()/window.confirm()) ──
const NOTIFY_COLORS = {
  success: "#7EAF5C",
  error:   "#AE1A21",
  update:  "#005BBA",
  alert:   "#FF9600",
};
const NotifyContext = createContext({
  toast: () => {},
  confirmAction: () => Promise.resolve(false),
  showClaimBanner: () => {},
  openUpgradeModal: () => {},
});
export const useNotify = () => useContext(NotifyContext);

const CLAIM_BANNER_BG = "#f3680c";
const CLAIM_BANNER_FG = "#ffffff";

function NotifyProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const [claimBanners, setClaimBanners] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDealBoardPlusModal, setShowDealBoardPlusModal] = useState(false);

  const toast = useCallback((message, type = "update") => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    const duration = type === "error" ? 6000 : 4000;
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), duration);
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);

  const showClaimBanner = useCallback((message) => {
    const id = Date.now() + Math.random();
    setClaimBanners(b => [...b, { id, message }]);
  }, []);

  const dismissClaimBanner = useCallback((id) => {
    setClaimBanners(b => b.filter(x => x.id !== id));
  }, []);

  const confirmAction = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setConfirmState({
        message,
        confirmLabel: opts.confirmLabel || "Confirm",
        cancelLabel:  opts.cancelLabel  || "Cancel",
        danger:       opts.danger !== false,
        resolve,
      });
    });
  }, []);

  const closeConfirm = (result) => {
    confirmState?.resolve(result);
    setConfirmState(null);
  };

  return (
    <NotifyContext.Provider value={{ toast, confirmAction, showClaimBanner,
      openUpgradeModal: () => setShowUpgradeModal(true),
      _showUpgradeModal: showUpgradeModal,
      _closeUpgradeModal: () => setShowUpgradeModal(false),
      openDealBoardPlusModal: () => setShowDealBoardPlusModal(true),
      _showDealBoardPlusModal: showDealBoardPlusModal,
      _closeDealBoardPlusModal: () => setShowDealBoardPlusModal(false),
    }}>
      {children}

      {claimBanners.length > 0 && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 10001 }}>
          {claimBanners.map(b => (
            <div key={b.id} className="claim-banner" style={{
              width: "100%", boxSizing: "border-box",
              background: CLAIM_BANNER_BG, color: CLAIM_BANNER_FG,
              fontFamily: "'Sora',sans-serif",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
              flexWrap: "wrap",
              padding: "10px 44px 10px 16px",
              position: "relative",
              borderBottom: "0.5px solid rgba(255,255,255,0.25)",
              boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
              animation: "notifySlideDown 0.2s ease",
            }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>🃏</span>
              <span style={{ fontSize: 13, fontWeight: 600, textAlign: "center", lineHeight: 1.4 }}>
                {b.message}
              </span>
              <button
                onClick={() => dismissClaimBanner(b.id)}
                aria-label="Dismiss"
                style={{
                  position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.18)", border: "none", borderRadius: 8,
                  color: CLAIM_BANNER_FG, width: 26, height: 26,
                  fontSize: 16, lineHeight: 1, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{
        position: "fixed", top: claimBanners.length > 0 ? 48 * claimBanners.length : 0, left: 0, right: 0, zIndex: 9999,
        display: "flex", flexDirection: "column", alignItems: "center",
        pointerEvents: "none", transition: "top 0.15s",
      }}>
        {toasts.map(t => (
          <div key={t.id}
            onClick={() => dismissToast(t.id)}
            style={{
              pointerEvents: "all",
              width: "100%", maxWidth: 680,
              margin: "8px 12px 0",
              background: NOTIFY_COLORS[t.type] || NOTIFY_COLORS.update,
              color: "#ffffff",
              fontFamily: "'Sora',sans-serif",
              fontSize: 14, fontWeight: 600,
              padding: "12px 18px",
              borderRadius: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12,
              animation: "notifySlideDown 0.2s ease",
            }}>
            <span style={{display:"flex",alignItems:"center",gap:8,flex:1}}>
              <span style={{fontSize:16,flexShrink:0}}>
                {t.type === "success" ? "✓" : t.type === "error" ? "✕" : t.type === "alert" ? "⚠" : "ℹ"}
              </span>
              {t.message}
            </span>
            <span style={{opacity:0.7,fontSize:18,lineHeight:1,flexShrink:0}}>×</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes notifySlideDown {
          from { transform: translateY(-12px); opacity: 0; }
          to   { transform: translateY(0); opacity: 1; }
        }
        .claim-banner { min-height: 44px; }
        @media (max-width: 520px) {
          .claim-banner { padding: 10px 40px 10px 12px !important; gap: 6px !important; }
          .claim-banner span { font-size: 12px !important; }
        }
      `}</style>

      {confirmState && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 10000,
          background: "rgba(0,0,0,0.55)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}
        onClick={(e) => { if (e.target === e.currentTarget) closeConfirm(false); }}>
          <div style={{
            background: "#1a1f2b",
            borderRadius: 14,
            padding: 24,
            maxWidth: 380, width: "100%",
            boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
          }}>
            <div style={{fontSize:15, color:"#e8f0f8", lineHeight:1.5, marginBottom:20}}>
              {confirmState.message}
            </div>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
              <button
                onClick={() => closeConfirm(false)}
                style={{
                  padding: "10px 18px", borderRadius: 8,
                  background: "transparent", border: "1px solid rgba(255,255,255,0.2)",
                  color: "#c8d8e8", fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 600,
                  cursor: "pointer",
                }}>
                {confirmState.cancelLabel}
              </button>
              <button
                onClick={() => closeConfirm(true)}
                style={{
                  padding: "10px 18px", borderRadius: 8,
                  background: confirmState.danger ? NOTIFY_COLORS.error : NOTIFY_COLORS.update,
                  border: "none",
                  color: "#ffffff", fontFamily: "'Sora',sans-serif", fontSize: 14, fontWeight: 700,
                  cursor: "pointer",
                }}>
                {confirmState.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </NotifyContext.Provider>
  );
}

// ─── LABEL PRINT MODAL ───────────────────────────────────────────────────────
function LabelPrintModal({ show, cards, S, T, onClose }) {
  const { toast } = useNotify();
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [gameFilter, setGameFilter] = useState("all");

  const filteredCards = useMemo(() => cards.filter(c => {
    const price = parseFloat(c.ListPrice) || 0;
    if (priceMin !== "" && price < parseFloat(priceMin)) return false;
    if (priceMax !== "" && price > parseFloat(priceMax)) return false;
    if (gameFilter !== "all" && (c.GameType || "Pokemon") !== gameFilter) return false;
    return true;
  }), [cards, priceMin, priceMax, gameFilter]);

  const [selected, setSelected] = useState(() => new Set(cards.map(c => c.ID)));

  useEffect(() => {
    setSelected(new Set(filteredCards.map(c => c.ID)));
  }, [priceMin, priceMax, gameFilter]);

  const allChecked = selected.size > 0 && filteredCards.every(c => selected.has(c.ID));
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(filteredCards.map(c=>c.ID)));
  const toggle     = id => setSelected(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const exportXLSX = async () => {
    const toExport = cards
      .filter(c => selected.has(c.ID))
      .sort((a,b) => (a.CardName||"").localeCompare(b.CardName||""));

    if (!toExport.length) return;

    const CONDITION_ABBR = {
      "Near Mint":         "NM",
      "Lightly Played":    "LP",
      "Moderately Played": "MP",
      "Heavily Played":    "HP",
      "Damaged":           "DM",
    };

    try {
      const res = await fetch("/export-labels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          showName: show.Name,
          cards: toExport.map(c => ({
            id:        c.ID,
            qrCode:    c.QRCode || c.ID,
            listPrice: parseFloat(c.ListPrice) || 0,
            cardName:  c.CardName || "",
            condition: CONDITION_ABBR[c.Condition] || c.Condition || "",
          })),
        }),
      });

      if (!res.ok) throw new Error(`Server error: ${res.status}`);

      const blob     = await res.blob();
      const url      = URL.createObjectURL(blob);
      const a        = document.createElement("a");
      a.href         = url;
      a.download     = `${show.Name.replace(/[^a-z0-9]/gi,"_")}_labels.xlsx`;
      a.click();
      URL.revokeObjectURL(url);

    } catch(e) {
      toast("Export failed: " + e.message, "error");
    }
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.drawer, maxHeight:"90vh", display:"flex", flexDirection:"column"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:16,flexShrink:0}}>
          <div>
            <div style={{fontSize:16,fontWeight:700}}>🏷 Print Labels</div>
            <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{show.Name}</div>
          </div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>

        <div style={{...S.infoBox, marginBottom:12, flexShrink:0}}>
          <div style={{fontSize:12,color:T.textMuted}}>
            Cards are sorted A–Z. Export XLSX and upload to your <strong>NELKO P21</strong> app to print all labels at once.
          </div>
        </div>

        <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:10, flexShrink:0}}>
          <span style={{fontSize:12, color:T.textMuted, flexShrink:0}}>By Game</span>
          <select style={{...S.select, flex:1, fontSize:12, padding:"7px 8px"}}
            value={gameFilter} onChange={e=>setGameFilter(e.target.value)}>
            <option value="all">All Games</option>
            <option value="Pokemon">Pokémon</option>
            <option value="Lorcana">Lorcana</option>
          </select>
        </div>

        <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:10, flexShrink:0}}>
          <span style={{fontSize:12, color:T.textMuted, flexShrink:0}}>Price</span>
          <div style={{position:"relative", flex:1}}>
            <span style={{position:"absolute", left:8, top:"50%", transform:"translateY(-50%)",
              fontSize:12, color:T.textMuted, pointerEvents:"none"}}>$</span>
            <input style={{...S.input, paddingLeft:20, fontSize:12, padding:"7px 8px 7px 20px"}}
              type="number" min="0" step="0.01" placeholder="Min"
              value={priceMin} onChange={e=>setPriceMin(e.target.value)} />
          </div>
          <span style={{fontSize:12, color:T.textFaint}}>–</span>
          <div style={{position:"relative", flex:1}}>
            <span style={{position:"absolute", left:8, top:"50%", transform:"translateY(-50%)",
              fontSize:12, color:T.textMuted, pointerEvents:"none"}}>$</span>
            <input style={{...S.input, paddingLeft:20, fontSize:12, padding:"7px 8px 7px 20px"}}
              type="number" min="0" step="0.01" placeholder="Max"
              value={priceMax} onChange={e=>setPriceMax(e.target.value)} />
          </div>
          {(priceMin || priceMax) && (
            <button style={{...S.btn("ghost"), fontSize:11, padding:"5px 8px", flexShrink:0}}
              onClick={()=>{ setPriceMin(""); setPriceMax(""); }}>
              ✕
            </button>
          )}
        </div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:10,flexShrink:0}}>
          <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13}}>
            <input type="checkbox" checked={allChecked} onChange={toggleAll}
              style={{width:16,height:16,cursor:"pointer"}} />
            {allChecked ? "Deselect All" : "Select All"} ({selected.size} selected)
          </label>
          <div style={{fontSize:12,color:T.textFaint}}>
            {filteredCards.length}{filteredCards.length !== cards.length ? ` of ${cards.length}` : ""} cards
          </div>
        </div>

        <div style={{flex:1, overflowY:"auto", marginBottom:12}}>
          {filteredCards.length === 0 ? (
            <div style={{textAlign:"center",padding:32,color:T.textMuted}}>
              No cards match this price range
            </div>
          ) : filteredCards.map(c => (
            <label key={c.ID} style={{display:"flex",alignItems:"center",gap:10,
              padding:"8px 4px",borderBottom:`0.5px solid ${T.border}`,
              cursor:"pointer"}}>
              <input type="checkbox" checked={selected.has(c.ID)} onChange={()=>toggle(c.ID)}
                style={{width:16,height:16,cursor:"pointer",flexShrink:0}} />
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {c.CardName}
                </div>
                <div style={{fontSize:11,color:T.textMuted}}>
                  {c.Set}{c.Number?` · #${c.Number}`:""} · {c.Condition}
                </div>
              </div>
              <div style={{fontSize:12,fontFamily:"'DM Mono',monospace",
                color:"#3b82f6",flexShrink:0}}>
                {c.QRCode || "no QR"}
              </div>
              <div style={{fontSize:13,fontWeight:600,color:T.text,
                flexShrink:0,minWidth:44,textAlign:"right"}}>
                ${c.ListPrice||"—"}
              </div>
            </label>
          ))}
        </div>

        {selected.size > 250 && (
          <div style={{...S.infoBox, marginBottom:8, borderColor:"rgba(251,191,36,0.4)", flexShrink:0}}>
            <div style={{fontSize:12, color:"#fbbf24"}}>
              ⚠ Large export ({selected.size} labels) — verify file is under 300kb before uploading to NELKO app
            </div>
          </div>
        )}

        <button style={{...S.btn("primary"),width:"100%",padding:"14px",
          fontSize:15,flexShrink:0}}
          onClick={()=>exportXLSX().catch(e=>toast("Export failed: "+e.message, "error"))}
          disabled={selected.size===0}>
          ⬇ Export {selected.size} Label{selected.size!==1?"s":""} (.xlsx)
        </button>
      </div>
    </div>
  );
}

// ─── TRADE PHOTOS VIEWER ─────────────────────────────────────────────────────
function TradePhotosViewer({ show, onClose, S, T }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useNotify();

  useEffect(() => {
    readTradePhotos(show.ID)
      .then(setPhotos)
      .catch(e => toast("Failed to load trade photos: " + e.message, "error"))
      .finally(() => setLoading(false));
  }, [show.ID]);

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{...S.drawer, maxHeight:"90vh", display:"flex", flexDirection:"column"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
          marginBottom:16,flexShrink:0}}>
          <div>
            <div style={{fontSize:16,fontWeight:700}}>📸 Trade Photos</div>
            <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{show.Name}</div>
          </div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div style={{textAlign:"center", padding:40, color:T.textMuted}}>Loading…</div>
        ) : photos.length === 0 ? (
          <div style={{...S.card, textAlign:"center", padding:32}}>
            <div style={{fontSize:28, marginBottom:8}}>📸</div>
            <div style={{fontSize:13, color:T.textMuted}}>
              No trade photos captured for this show yet.
            </div>
          </div>
        ) : (
          <div style={{
            display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(110px, 1fr))",
            gap:10, overflowY:"auto",
          }}>
            {photos.map((p, i) => (
              <div key={p.id}
                onClick={()=>window.open(p.photo_data, "_blank")}
                style={{cursor:"pointer", textAlign:"center"}}>
                <img src={p.photo_data} alt={`Trade ${i+1}`}
                  style={{width:"100%", aspectRatio:"1", objectFit:"cover",
                    borderRadius:10, border:`1px solid ${T.border}`, marginBottom:6}} />
                <div style={{fontSize:12, fontWeight:600}}>Trade {i+1}</div>
                {(p.cards_out || p.cards_in) && (
                  <div style={{fontSize:10, color:T.textFaint, lineHeight:1.4}}>
                    {p.cards_out && <div>Out: {p.cards_out}</div>}
                    {p.cards_in && <div>In: {p.cards_in}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
// ─── FORBIDDEN PAGE ──────────────────────────────────────────────────────────
// ─── BUY ADVISOR ─────────────────────────────────────────────────────────────
function BuyAdvisor({ S, T, embedded = false }) {
  const [search, setSearch]        = useState("");
  const [cardNumber, setCardNumber]= useState("");
  const [setName, setSetName]      = useState("");
  const [dealerPrice, setDealer]   = useState("");
  const [result, setResult]        = useState(null);
  const [loading, setLoading]      = useState(false);
  const [pctOfList, setPctOfList]  = useState(80);
  const MIN_ROI = 20;

  const lookup = async () => {
    if (!search.trim()) return;
    setLoading(true); setResult(null);
    const data = await searchCardHistory(search.trim(), cardNumber.trim(), setName.trim());
    setResult(data);
    setLoading(false);
  };

  const rec = useMemo(() => {
    if (!result) return null;
    const { priceHistory, soldHistory, ownedCards } = result;
    const dealer = parseFloat(dealerPrice) || 0;
    const manualHistory = priceHistory
      .filter(h => h.source === "manual_update")
      .sort((a,b) => new Date(b.recorded_at) - new Date(a.recorded_at));
    const latestList = manualHistory[0]?.market_value || 0;
    let trend = "unknown", trendPct = null;
    if (manualHistory.length >= 2) {
      const newest = manualHistory[0].market_value;
      const older  = manualHistory[1].market_value;
      trendPct = older > 0 ? ((newest - older) / older * 100) : 0;
      trend = trendPct > 2 ? "rising" : trendPct < -2 ? "falling" : "stable";
    }
    const avgSoldPrice = soldHistory.length > 0
      ? soldHistory.reduce((s,i) => s+(i.sold_price||0),0) / soldHistory.length : 0;
    const avgAcqCost = soldHistory.length > 0
      ? soldHistory.reduce((s,i) => s+(i.acquisition_cost||0),0) / soldHistory.length : 0;
    const avgROI = avgAcqCost > 0 ? ((avgSoldPrice - avgAcqCost) / avgAcqCost * 100) : null;

    const listPrice   = latestList || avgSoldPrice || 0;

    const projROI     = dealer > 0 && listPrice > 0 ? ((listPrice - dealer) / dealer * 100) : null;
    const pctOfMarket = dealer > 0 && listPrice > 0 ? (dealer / listPrice * 100) : null;
    let label = "UNKNOWN", color = T.textMuted, reasons = [];

    if (listPrice > 0 && dealer > 0) {
      const roiOk  = projROI !== null && projROI >= MIN_ROI;
      const pctOk  = pctOfMarket !== null && pctOfMarket <= 85;
      if      (roiOk && pctOk)  { label="BUY";     color="#4ade80"; }
      else if (roiOk && !pctOk) { label="CAUTION"; color="#fbbf24"; }
      else                       { label="PASS";    color="#f87171"; }
      if (projROI !== null) reasons.push(`${projROI.toFixed(0)}% projected ROI (min ${MIN_ROI}%)`);
      if (pctOfMarket !== null && pctOfMarket > 85) reasons.push(`${pctOfMarket.toFixed(0)}% of market — above 85% threshold`);
      if (trendPct !== null) reasons.push(`Price trend: ${trend} ${trendPct>0?"+":""}${trendPct.toFixed(0)}%`);
      if (avgROI !== null) reasons.push(`Avg historical ROI: ${avgROI.toFixed(0)}%`);
    } else if (listPrice > 0 && dealer === 0) {
      label="ENTER PRICE"; color=T.textMuted;
      reasons.push(`Your list: $${Math.round(listPrice)} — enter seller price above for a recommendation`);
      if (trendPct !== null) reasons.push(`Price ${trend} ${trendPct>0?"+":""}${trendPct.toFixed(0)}%`);
      if (avgROI !== null) reasons.push(`Avg historical ROI: ${avgROI.toFixed(0)}%`);
    } else if (!listPrice) {
      label="NO DATA"; reasons.push("No history found. Try adding a set name or card number, or sell a copy of this card to build history.");
    }
    return { label, color, reasons, latestList, trend, trendPct,
             projROI, pctOfMarket, avgSoldPrice, avgROI,
             soldHistory, ownedCards, hasData: listPrice > 0 };
  }, [result, dealerPrice, T]);

  const fmtP = n => n ? `$${Math.round(n)}` : "--";

  return (
    <div style={embedded ? {padding:0} : S.page}>
      {!embedded && <div style={{...S.h2, marginBottom:12}}>Buy Advisor</div>}
      <div style={{...S.card, marginBottom:10}}>
        <div style={{fontSize:12, fontWeight:600, color:"#88bdf2", marginBottom:8}}>Look up a card</div>

        <div style={{marginBottom:8}}>
          <label style={S.label}>Card name *</label>
          <input style={{...S.input}} value={search}
            onChange={e=>setSearch(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&lookup()}
            placeholder="e.g. Charizard ex" />
        </div>

        <div style={{display:"flex", gap:8, marginBottom:10}}>
          <div style={{flex:1}}>
            <label style={S.label}>Card # <span style={{color:T.textFaint}}>(optional)</span></label>
            <input style={S.input} value={cardNumber}
              onChange={e=>setCardNumber(e.target.value)}
              placeholder="e.g. 199/165"
              onKeyDown={e=>e.key==="Enter"&&lookup()} />
          </div>
          <div style={{flex:2}}>
            <label style={S.label}>Set name <span style={{color:T.textFaint}}>(optional)</span></label>
            <input style={S.input} value={setName}
              onChange={e=>setSetName(e.target.value)}
              placeholder="e.g. Obsidian Flames"
              onKeyDown={e=>e.key==="Enter"&&lookup()} />
          </div>
        </div>

        <button style={{...S.btn("primary"), width:"100%", padding:"11px"}}
          onClick={lookup} disabled={loading || !search.trim()}>
          {loading ? "Searching…" : "Search"}
        </button>
      </div>

      {result && (
        <div style={{...S.card, marginBottom:10}}>
          <div style={{display:"flex", gap:8}}>
            <div style={{flex:1}}>
              <label style={S.label}>Seller asking price ($)</label>
              <input style={{...S.input, fontSize:20, fontWeight:700, textAlign:"center",
                color:"#88bdf2", padding:"12px"}}
                type="number" value={dealerPrice}
                onChange={e=>setDealer(e.target.value)}
                placeholder="0" inputMode="decimal" />
            </div>
            <div style={{flex:1}}>
              <label style={S.label}>Quick % of market</label>
              <div style={{display:"flex", gap:4, flexWrap:"wrap"}}>
                {[70,75,80,85,90].map(p=>(
                  <button key={p} style={{...S.chip(pctOfList===p), padding:"6px 8px", fontSize:11}}
                    onClick={()=>{
                      setPctOfList(p);
                      const ref = rec?.latestList || 0;
                      if (ref) setDealer(String(Math.round(ref*p/100)));
                    }}>{p}%</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {result && !rec?.hasData && (
        <div style={{...S.card, textAlign:"center", padding:24, color:T.textMuted}}>
          <div style={{fontSize:28, marginBottom:8}}>&#x1F4EB;</div>
          <div>No data found for "{search}"</div>
          <div style={{fontSize:12, marginTop:4}}>
            Try adding a set name or card number to narrow the search.
          </div>
        </div>
      )}

      {rec?.hasData && (<>
        <div style={{marginBottom:8}}>
          <span style={{fontSize:11, background:"rgba(74,222,128,0.1)", color:"#4ade80",
              border:"0.5px solid rgba(74,222,128,0.3)", borderRadius:20, padding:"3px 10px"}}>
            📊 Based on your history
          </span>
        </div>

        <div style={{...S.card, marginBottom:10,
          background:rec.label==="BUY"?"rgba(74,222,128,0.08)":
                     rec.label==="CAUTION"?"rgba(251,191,36,0.08)":
                     rec.label==="PASS"?"rgba(248,113,113,0.08)":T.surface,
          border:`0.5px solid ${rec.color}55`}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10}}>
            <div style={{fontSize:26, fontWeight:700, color:rec.color}}>
              {rec.label==="BUY"?"✓ BUY":rec.label==="CAUTION"?"⚠ CAUTION":
               rec.label==="PASS"?"✗ PASS":rec.label==="ENTER PRICE"?"💰 Enter Seller Price":"— NO DATA"}
            </div>
            {rec.projROI !== null && (
              <div style={{textAlign:"right"}}>
                <div style={S.statLbl}>Projected ROI</div>
                <div style={{fontSize:20, fontWeight:700,
                  color:rec.projROI>=MIN_ROI?"#4ade80":"#f87171"}}>
                  {rec.projROI.toFixed(0)}%
                </div>
              </div>
            )}
          </div>
          {rec.reasons.map((r,i)=>(
            <div key={i} style={{fontSize:12, color:T.textMuted, marginBottom:3,
              paddingLeft:8, borderLeft:`2px solid ${rec.color}`}}>{r}</div>
          ))}
        </div>

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:10}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Your List Price</div>
            <div style={{...S.statVal, fontSize:16, color:"#3C66EE"}}>
              {fmtP(rec.latestList)}
            </div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Seller Price</div>
            <div style={{...S.statVal, fontSize:16,
              color:rec.pctOfMarket>85?"#f87171":"#88bdf2"}}>
              {dealerPrice?`$${dealerPrice}`:"--"}
            </div>
            {rec.pctOfMarket && <div style={{fontSize:10,color:T.textFaint}}>{rec.pctOfMarket.toFixed(0)}% of market</div>}
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Price Trend</div>
            <div style={{...S.statVal, fontSize:16,
              color:rec.trend==="rising"?"#4ade80":rec.trend==="falling"?"#f87171":"#88bdf2"}}>
              {rec.trend==="rising"?`↑ +${rec.trendPct?.toFixed(0)}%`:
               rec.trend==="falling"?`↓ ${rec.trendPct?.toFixed(0)}%`:
               rec.trend==="stable"?"→ Stable":"—"}
            </div>
          </div>
        </div>

        {rec.soldHistory.length > 0 && (
          <div style={{...S.card, marginBottom:10}}>
            <div style={S.h2}>Your Sales History ({rec.soldHistory.length} sold)</div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:8}}>
              <div>
                <div style={S.statLbl}>Avg Sold Price</div>
                <div style={{fontSize:16, fontWeight:700, color:"#4ade80"}}>{fmtP(rec.avgSoldPrice)}</div>
              </div>
              {rec.avgROI !== null && (
                <div>
                  <div style={S.statLbl}>Avg ROI</div>
                  <div style={{fontSize:16, fontWeight:700,
                    color:rec.avgROI>=MIN_ROI?"#4ade80":"#f87171"}}>
                    {rec.avgROI.toFixed(0)}%
                  </div>
                </div>
              )}
            </div>
            {rec.soldHistory.slice(0,3).map((s,i)=>(
              <div key={i} style={{display:"flex", justifyContent:"space-between",
                padding:"5px 0", borderTop:`0.5px solid ${T.border}`,
                fontSize:12, color:T.textMuted}}>
                <span>{s.set_name||"—"} · {s.condition}</span>
                <span style={{color:"#4ade80", fontWeight:600}}>
                  {fmtP(s.sold_price)}
                  {s.acquisition_cost>0 && (
                    <span style={{color:T.textFaint, fontWeight:400}}>
                      {" "}({(((s.sold_price-s.acquisition_cost)/s.acquisition_cost)*100).toFixed(0)}% ROI)
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {rec.ownedCards.length > 0 && (
          <div style={{...S.infoBox, borderColor:"rgba(251,191,36,0.3)"}}>
            <div style={{fontSize:12, fontWeight:600, color:"#fbbf24", marginBottom:2}}>
              ⚠ You already own {rec.ownedCards.length} cop{rec.ownedCards.length===1?"y":"ies"}
            </div>
            <div style={{fontSize:11, color:T.textMuted}}>Consider whether you need more of this card</div>
          </div>
        )}
      </>)}
    </div>
  );
}

function ForbiddenPage() {
  return (
    <div style={{
      minHeight:"100vh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background:"linear-gradient(150deg,#08112e 0%,#1a2a70 50%,#08112e 100%)",
      padding:24, textAlign:"center", fontFamily:"'Sora',sans-serif",
    }}>
      <img src="/cv-logo_dark.png" alt="VendorStax"
        style={{height:40, objectFit:"contain", marginBottom:32}}
        onError={e=>e.target.style.display="none"} />
      <div style={{background:"rgba(255,255,255,0.05)",
        border:"0.5px solid rgba(203,226,254,0.15)",
        borderRadius:16, padding:"24px 32px",
        maxWidth:320, width:"100%"}}>
        <div style={{fontSize:32, marginBottom:12}}>🔒</div>
        <div style={{fontSize:16, fontWeight:600, color:"#ffffff", marginBottom:8}}>
          Authorized Access Only
        </div>
        <div style={{fontSize:13, color:"rgba(203,226,254,0.6)", lineHeight:1.6}}>
          This is a private vendor management system. Only authorized accounts can access it.
        </div>
      </div>
      <div style={{fontSize:11, color:"rgba(203,226,254,0.25)", marginTop:32}}>
        © 2026 VendorStax · All rights reserved
      </div>
    </div>
  );
}

function PendingApprovalScreen({ user, onSignOut }) {
  return (
    <div style={{
      minHeight:"100vh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background:"linear-gradient(150deg,#08112e 0%,#1a2a70 50%,#08112e 100%)",
      padding:24, textAlign:"center", fontFamily:"'Sora',sans-serif",
    }}>
      <img src="/cv-logo_dark.png" alt="VendorStax"
        style={{height:48, objectFit:"contain", marginBottom:32}}
        onError={e=>e.target.style.display="none"} />
      <div style={{
        background:"rgba(255,255,255,0.05)",
        border:"0.5px solid rgba(203,226,254,0.15)",
        borderRadius:16, padding:"28px 32px",
        maxWidth:340, width:"100%",
      }}>
        <div style={{fontSize:36, marginBottom:12}}>⏳</div>
        <div style={{fontSize:17, fontWeight:700, color:"#ffffff", marginBottom:8}}>
          Your account is pending approval
        </div>
        <div style={{fontSize:13, color:"rgba(203,226,254,0.65)", lineHeight:1.7, marginBottom:20}}>
          You're signed in as <strong style={{color:"#88bdf2"}}>{user?.email}</strong> but your
          VendorStax account hasn't been set up yet.
        </div>
        <div style={{fontSize:13, color:"rgba(203,226,254,0.65)", lineHeight:1.7, marginBottom:24}}>
          Please contact us and we'll get you onboarded:
        </div>
        <a href="https://vendorstax.com/contact"
          target="_blank" rel="noopener noreferrer"
          style={{
            display:"block", background:"#3C66EE", color:"#fff",
            borderRadius:10, padding:"12px 20px", fontSize:14,
            fontWeight:600, textDecoration:"none", marginBottom:12,
          }}>
          Contact VendorStax →
        </a>
        <button onClick={onSignOut}
          style={{
            background:"none", border:"0.5px solid rgba(203,226,254,0.2)",
            color:"rgba(203,226,254,0.5)", borderRadius:10,
            padding:"10px 20px", fontSize:13, cursor:"pointer",
            width:"100%", fontFamily:"'Sora',sans-serif",
          }}>
          Sign out
        </button>
      </div>
      <div style={{fontSize:11, color:"rgba(203,226,254,0.25)", marginTop:28}}>
        © 2026 VendorStax · All rights reserved
      </div>
    </div>
  );
}

function SuspendedAccountScreen({ user, onSignOut }) {
  return (
    <div style={{
      minHeight:"100vh", display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      background:"linear-gradient(150deg,#08112e 0%,#1a2a70 50%,#08112e 100%)",
      padding:24, textAlign:"center", fontFamily:"'Sora',sans-serif",
    }}>
      <img src="/cv-logo_dark.png" alt="VendorStax"
        style={{height:48, objectFit:"contain", marginBottom:32}}
        onError={e=>e.target.style.display="none"} />
      <div style={{
        background:"rgba(255,255,255,0.05)",
        border:"0.5px solid rgba(248,113,113,0.25)",
        borderRadius:16, padding:"28px 32px",
        maxWidth:340, width:"100%",
      }}>
        <div style={{fontSize:36, marginBottom:12}}>🚫</div>
        <div style={{fontSize:17, fontWeight:700, color:"#ffffff", marginBottom:8}}>
          This account has been deactivated
        </div>
        <div style={{fontSize:13, color:"rgba(203,226,254,0.65)", lineHeight:1.7, marginBottom:20}}>
          You're signed in as <strong style={{color:"#88bdf2"}}>{user?.email}</strong>, but access
          to this VendorStax account has been suspended.
        </div>
        <div style={{fontSize:13, color:"rgba(203,226,254,0.65)", lineHeight:1.7, marginBottom:24}}>
          If you believe this is a mistake, please contact us:
        </div>
        <a href="https://vendorstax.com/contact"
          target="_blank" rel="noopener noreferrer"
          style={{
            display:"block", background:"#3C66EE", color:"#fff",
            borderRadius:10, padding:"12px 20px", fontSize:14,
            fontWeight:600, textDecoration:"none", marginBottom:12,
          }}>
          Contact VendorStax →
        </a>
        <button onClick={onSignOut}
          style={{
            background:"none", border:"0.5px solid rgba(203,226,254,0.2)",
            color:"rgba(203,226,254,0.5)", borderRadius:10,
            padding:"10px 20px", fontSize:13, cursor:"pointer",
            width:"100%", fontFamily:"'Sora',sans-serif",
          }}>
          Sign out
        </button>
      </div>
      <div style={{fontSize:11, color:"rgba(203,226,254,0.25)", marginTop:28}}>
        © 2026 VendorStax · All rights reserved
      </div>
    </div>
  );
}


const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

function AccountSetupScreen({ user, onCreated }) {
  const { S, T } = useTheme();
  const googleName = (user?.user_metadata?.full_name || user?.user_metadata?.name || "").trim();
  const [nameParts] = useState(() => {
    const parts = googleName.split(/\s+/);
    return { first: parts[0] || "", last: parts.slice(1).join(" ") || "" };
  });
  const [firstName, setFirstName] = useState(nameParts.first);
  const [lastName, setLastName] = useState(nameParts.last);
  const [businessName, setBusinessName] = useState("");
  const [intent, setIntent] = useState("");
  const [state, setState] = useState("");
  const [trialCode, setTrialCode] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit = firstName.trim() && lastName.trim() && businessName.trim()
    && intent && state && termsAccepted && !saving;

  const handleSave = async () => {
    if (!firstName.trim())    { setErr("Please enter your first name"); return; }
    if (!lastName.trim())     { setErr("Please enter your last name"); return; }
    if (!businessName.trim()) { setErr("Please enter a business name"); return; }
    if (!intent)               { setErr("Let us know what brings you to VendorStax"); return; }
    if (!state)               { setErr("Please select your state"); return; }
    if (!termsAccepted)       { setErr("Please agree to the Terms & Conditions to continue"); return; }
    setSaving(true);
    setErr("");
    try {
      const created = await createVendorAccount({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        businessName: businessName.trim(),
        state,
        termsAccepted,
        referredBy: localStorage.getItem("vs_referred_by") || null,
        trialCode: trialCode.trim() || null,
      });
      localStorage.removeItem("vs_referred_by");
      onCreated(created, intent);
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div style={{...S.app,display:"flex",alignItems:"center",justifyContent:"center",padding:20,minHeight:"100vh"}}>
      <div style={{width:"100%",maxWidth:380,textAlign:"center"}}>
        <img src="/logo-2.png" alt="VendorStax"
          style={{width:100, height:100, objectFit:"contain", marginBottom:24}} />
        <div style={S.card}>
          <div style={{fontSize:17,fontWeight:700,marginBottom:8}}>Welcome to VendorStax! 🎉</div>
          <div style={{fontSize:13,color:S.textMuted,marginBottom:20,lineHeight:1.6}}>
            Just a couple things before you're set up.
          </div>
          {err && <div style={{fontSize:13,color:"#f87171",marginBottom:12}}>{err}</div>}

          <div style={{display:"flex", gap:10, marginBottom:14}}>
            <div style={{flex:1, textAlign:"left"}}>
              <label style={S.label}>First Name</label>
              <input
                style={{...S.input, fontSize:15}}
                placeholder="Brent"
                value={firstName}
                onChange={e=>setFirstName(e.target.value)}
                autoFocus
              />
            </div>
            <div style={{flex:1, textAlign:"left"}}>
              <label style={S.label}>Last Name</label>
              <input
                style={{...S.input, fontSize:15}}
                placeholder="Parker"
                value={lastName}
                onChange={e=>setLastName(e.target.value)}
              />
            </div>
          </div>

          <div style={{textAlign:"left", marginBottom:14}}>
            <label style={S.label}>Business Name</label>
            <input
              style={{...S.input, fontSize:15}}
              placeholder="e.g. Parker Trading Co."
              value={businessName}
              onChange={e=>setBusinessName(e.target.value)}
            />
            <div style={{fontSize:11,color:T.textFaint,marginTop:6}}>
              Shows up on your online shop and in emails to buyers.
            </div>
          </div>

          <div style={{textAlign:"left", marginBottom:16}}>
            <label style={S.label}>What brings you to VendorStax?</label>
            <div style={{display:"flex", flexDirection:"column", gap:8, marginTop:6}}>
              {[
                ["grow", "I want to grow a real vending business", "Full inventory, Show Mode, reporting, and Deal Board included."],
                ["deal", "I mainly want to buy, sell, and trade with other vendors", "Get straight into Deal Board. Turn on full tools anytime."],
              ].map(([val, label, sub]) => (
                <button key={val} type="button" onClick={()=>setIntent(val)}
                  style={{textAlign:"left", padding:"12px 14px", borderRadius:10, cursor:"pointer",
                    border:`1px solid ${intent===val ? "#3C66EE" : T.border}`,
                    background: intent===val ? "rgba(60,102,238,0.08)" : "transparent"}}>
                  <div style={{fontSize:13, fontWeight:600, color: intent===val ? "#3C66EE" : T.text}}>{label}</div>
                  <div style={{fontSize:11, color:T.textFaint, marginTop:3}}>{sub}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{textAlign:"left", marginBottom:16}}>
            <label style={S.label}>State</label>
            <select
              style={S.select}
              value={state}
              onChange={e=>setState(e.target.value)}>
              <option value="">Select a state…</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div style={{textAlign:"left", marginBottom:16}}>
            <label style={S.label}>Trial Code (optional)</label>
            <input
              style={S.input}
              value={trialCode}
              onChange={e=>setTrialCode(e.target.value)}
              placeholder="Got a code from a card show flyer? Enter it here" />
          </div>

          <label style={{display:"flex", alignItems:"flex-start", gap:8, marginBottom:18,
            textAlign:"left", cursor:"pointer", fontSize:12, color:T.textMuted, lineHeight:1.5}}>
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e=>setTermsAccepted(e.target.checked)}
              style={{marginTop:2, flexShrink:0}}
            />
            <span>
              I agree to VendorStax's{" "}
              <a href="https://www.vendorstax.com/terms.html" target="_blank" rel="noopener noreferrer"
                style={{color:"#88bdf2",textDecoration:"underline"}}>Terms & Conditions</a>
              {" "}and{" "}
              <a href="https://www.vendorstax.com/privacy.html" target="_blank" rel="noopener noreferrer"
                style={{color:"#88bdf2",textDecoration:"underline"}}>Privacy Policy</a>
            </span>
          </label>

          <button style={{...S.btn("primary"),width:"100%",padding:"14px 20px",fontSize:15}}
            onClick={handleSave} disabled={!canSubmit}>
            {saving ? "Setting up…" : "Get Started"}
          </button>
        </div>
      </div>
    </div>
  );
}

const TOUR_STOPS = [
  {
    key: "dashboard",
    title: "Dashboard",
    body: "Your at-a-glance view when you log in. It surfaces recent sales, quick stats, and anything that needs your attention right now. Instead of digging through menus to figure out how your day or week is going, this gives you the answer the moment you open the app.",
  },
  {
    key: "inventory",
    title: "Inventory",
    body: "Every card and product you have in stock lives here, searchable and filterable however you need. This is the heart of the app. Keeping it accurate means your pricing, your reports, and everything else downstream actually reflects reality.",
  },
  {
    key: "lots",
    title: "Lots",
    body: "Track the cost basis and profit of bulk purchases, like a binder you bought as a group, an eBay lot, or pack pulls from booster boxes. Cost basis is simply what you actually paid to acquire something, and Lots divides that cost across everything inside it, so as individual cards sell, you can see real profit on each one instead of just a vague sense of whether the whole lot worked out.",
  },
  {
    key: "crm",
    title: "CRM",
    body: "Your customers and buyers, all in one place, with a record of what they've bought from you before. Knowing someone's history means you can reach out when something they'd actually want comes back in stock, instead of relying on memory.",
  },
  {
    key: "shows",
    title: "Shows",
    body: "Set up a card show ahead of time, then switch into Show Mode to buy, sell, and trade in real time while you're there. Built specifically for the pace of an actual show floor, quick entry, fast math, nothing that slows you down mid conversation.",
  },
  {
    key: "dealboard",
    title: "Deal Board",
    body: "Buy, sell, and trade directly with other vendors, or post what you're hunting for in Wanted Items if you can't find it yourself. It's a way to tap into other vendors' inventory and demand instead of only working with what's already in front of you.",
  },
  {
    key: "reports",
    title: "Reports",
    body: "See how your business is actually doing: sales, profit, what's moving and what's sitting. Gut feeling is a starting point, but real numbers are what tell you whether a strategy is actually working or just feels like it is.",
  },
  {
    key: "import",
    title: "Import",
    body: "Already tracking your collection somewhere else, like Collectr or your own spreadsheet? Bring it in here instead of typing everything by hand. This exists specifically to make switching to VendorStax something you can do in minutes, not days.",
  },
  {
    key: "settings",
    title: "Settings",
    body: "Your business info, pricing rules, and everything that controls how VendorStax behaves for your shop specifically. This is where the app gets shaped around how you actually run things, rather than you having to adapt to a one-size-fits-all default.",
  },
  {
    key: "help",
    title: "Help",
    body: "Every article we've written lives here, and you can come back anytime you're stuck. No need to remember this walkthrough perfectly, this is the place that's always there when something doesn't click later.",
  },
];

function SpotlightTour({ vendor, onFinish, ensureNavOpen }) {
  const { S, T } = useTheme();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const current = TOUR_STOPS[step];
  const isLast = step === TOUR_STOPS.length - 1;

  useEffect(() => {
    ensureNavOpen?.();
    const measure = () => {
      const el = document.querySelector(`[data-tour-key="${current.key}"]`);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const t = setTimeout(measure, 60);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [step, current.key, ensureNavOpen]);

  const pad = 6;
  const tooltipTop = rect ? Math.min(rect.bottom + 14, window.innerHeight - 260) : window.innerHeight/2 - 120;
  const tooltipLeft = rect ? Math.min(Math.max(rect.left, 16), window.innerWidth - 340) : window.innerWidth/2 - 160;

  return (
    <div style={{position:"fixed", inset:0, zIndex:9999}}>
      <div style={{
        position:"absolute",
        top: rect ? rect.top - pad : 0,
        left: rect ? rect.left - pad : 0,
        width: rect ? rect.width + pad*2 : 0,
        height: rect ? rect.height + pad*2 : 0,
        borderRadius: 10,
        boxShadow: "0 0 0 9999px rgba(0,0,0,0.72)",
        transition: "top 0.2s, left 0.2s, width 0.2s, height 0.2s",
        pointerEvents: "none",
      }} />
      {!rect && (
        <div style={{position:"absolute", inset:0, background:"rgba(0,0,0,0.72)"}} />
      )}

      <div style={{
        position:"absolute", top: tooltipTop, left: tooltipLeft,
        width:320, ...S.card, padding:20, boxShadow:"0 8px 30px rgba(0,0,0,0.4)",
      }}>
        <div style={{fontSize:15, fontWeight:800, marginBottom:8}}>{current.title}</div>
        <div style={{fontSize:13, color:S.textMuted, lineHeight:1.6, marginBottom:16}}>{current.body}</div>

        <div style={{display:"flex", justifyContent:"center", gap:5, marginBottom:16}}>
          {TOUR_STOPS.map((_, i) => (
            <div key={i} style={{
              width: i === step ? 16 : 5, height: 5, borderRadius: 3,
              background: i === step ? "#3C66EE" : T.border,
              transition: "width 0.15s",
            }} />
          ))}
        </div>

        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
          <button onClick={onFinish}
            style={{background:"none", border:"none", color:S.textFaint, fontSize:12,
              cursor:"pointer", fontFamily:"'Sora',sans-serif", textDecoration:"underline"}}>
            Skip
          </button>
          <div style={{display:"flex", gap:8}}>
            {step > 0 && (
              <button style={{...S.btn("ghost"), padding:"8px 14px", fontSize:13}} onClick={()=>setStep(s=>s-1)}>
                Back
              </button>
            )}
            <button style={{...S.btn("primary"), padding:"8px 16px", fontSize:13}}
              onClick={()=> isLast ? onFinish() : setStep(s=>s+1)}>
              {isLast ? "Done" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const { S, T } = useTheme();
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState("");
  const [mode, setMode]       = useState("signup");
  const [email, setEmail]     = useState("");

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleContinue = async () => {
    if (mode === "signup" && !emailLooksValid) {
      setErr("Please enter a valid email address");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      await signInWithGoogle(mode === "signup" ? email.trim() : undefined);
    } catch(e) {
      setErr("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div style={{...S.app,display:"flex",alignItems:"center",justifyContent:"center",padding:20,minHeight:"100vh"}}>
      <div style={{width:"100%",maxWidth:340,textAlign:"center"}}>
        <img src="/logo-2.png" alt="VendorStax"
          style={{width:120, height:120, objectFit:"contain", marginBottom:32}} />

        <div style={{display:"flex", borderRadius:10, background:T.surface2,
          border:`0.5px solid ${T.border}`, padding:3, marginBottom:20}}>
          <button
            onClick={()=>{ setMode("signup"); setErr(""); }}
            style={{flex:1, padding:"9px 12px", borderRadius:8, border:"none", cursor:"pointer",
              fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:600,
              background: mode==="signup" ? "#3C66EE" : "transparent",
              color: mode==="signup" ? "#fff" : S.textMuted}}>
            Create Account
          </button>
          <button
            onClick={()=>{ setMode("signin"); setErr(""); }}
            style={{flex:1, padding:"9px 12px", borderRadius:8, border:"none", cursor:"pointer",
              fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:600,
              background: mode==="signin" ? "#3C66EE" : "transparent",
              color: mode==="signin" ? "#fff" : S.textMuted}}>
            Sign In
          </button>
        </div>

        <div style={S.card}>
          <div style={{fontSize:16,fontWeight:700,marginBottom:6}}>
            {mode==="signup" ? "Create your VendorStax account" : "Welcome back"}
          </div>
          <div style={{fontSize:13,color:S.textMuted,marginBottom:20,lineHeight:1.6}}>
            {mode==="signup"
              ? "No password to set or remember — just your email and a Google sign-in."
              : "Sign in with the Google account you used to create your VendorStax account."}
          </div>
          {err && <div style={{fontSize:13,color:"#f87171",marginBottom:12}}>{err}</div>}

          {mode==="signup" && (
            <input
              type="email"
              style={{...S.input, marginBottom:12, textAlign:"center", fontSize:15}}
              placeholder="you@example.com"
              value={email}
              onChange={e=>setEmail(e.target.value)}
              onKeyDown={e=>{ if (e.key === "Enter" && emailLooksValid) handleContinue(); }}
              autoFocus
            />
          )}

          <button style={{...S.btn("primary"),width:"100%",padding:"14px 20px",fontSize:15}}
            onClick={handleContinue} disabled={loading || (mode==="signup" && !emailLooksValid)}>
            {loading ? "Please wait…" : mode==="signup" ? "Continue with Google" : "Sign in with Google"}
          </button>
        </div>
        <div style={{fontSize:12,color:S.textFaint,marginTop:16}}>
          {mode==="signup"
            ? "Already have an account? Use the Sign In tab above."
            : "New here? Use the Create Account tab above."}
        </div>
        <div style={{fontSize:11,color:S.textFaint,marginTop:12}}>
          By continuing you agree to our{" "}
          <a href="/vendorstax_privacy_policy.html" target="_blank" rel="noopener noreferrer"
            style={{color:"#88bdf2",textDecoration:"underline"}}>Privacy Policy</a>
          {" "}and{" "}
          <a href="/vendorstax_terms_conditions.html" target="_blank" rel="noopener noreferrer"
            style={{color:"#88bdf2",textDecoration:"underline"}}>Terms & Conditions</a>
        </div>
      </div>
    </div>
  );
}


function LoadingScreen({ message }) {
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:999,
      background:"linear-gradient(150deg,#08112e 0%,#1a2a70 45%,#2e0f65 80%,#08112e 100%)",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      overflow:"hidden",
    }}>
      <div style={{
        position:"absolute", width:280, height:280, borderRadius:"50%",
        background:"rgba(60,102,238,0.35)", filter:"blur(60px)",
        top:"-5%", left:"-10%",
        animation:"cvOrb1 7s ease-in-out infinite alternate",
      }}/>
      <div style={{
        position:"absolute", width:220, height:220, borderRadius:"50%",
        background:"rgba(75,0,144,0.28)", filter:"blur(50px)",
        bottom:"5%", right:"-5%",
        animation:"cvOrb2 9s ease-in-out infinite alternate",
      }}/>
      <div style={{
        position:"absolute", width:150, height:150, borderRadius:"50%",
        background:"rgba(203,226,254,0.15)", filter:"blur(40px)",
        top:"48%", left:"44%",
        animation:"cvOrb3 5s ease-in-out infinite alternate",
      }}/>
      <style>{`
        @keyframes cvOrb1 { from{transform:translate(0,0) scale(1)} to{transform:translate(20px,-28px) scale(1.12)} }
        @keyframes cvOrb2 { from{transform:translate(0,0) scale(1)} to{transform:translate(-18px,22px) scale(1.08)} }
        @keyframes cvOrb3 { from{transform:translate(0,0) scale(1)} to{transform:translate(12px,-16px) scale(1.15)} }
        @keyframes cvBar  { 0%{width:0%;opacity:1} 80%{width:100%;opacity:1} 100%{width:100%;opacity:0} }
      `}</style>
      <div style={{position:"relative", zIndex:2, textAlign:"center"}}>
        <img src="/cv-logo_dark.png" alt="VendorStax"
          style={{
            width:"70%", maxWidth:260, objectFit:"contain",
            marginBottom:24,
          }} />
        <div style={{
          width:100, height:2, background:"rgba(255,255,255,0.1)",
          borderRadius:2, overflow:"hidden", margin:"0 auto",
        }}>
          <div style={{
            height:"100%", borderRadius:2,
            background:"linear-gradient(90deg,#3C66EE,#CBE2FE)",
            animation:"cvBar 1.8s ease-in-out infinite",
          }}/>
        </div>
        {message && (
          <div style={{
            fontSize:11, color:"rgba(203,226,254,0.35)",
            marginTop:16, fontFamily:"'DM Mono',monospace",
            letterSpacing:"0.04em",
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── IMAGE PICKER ─────────────────────────────────────────────────────────────
function ImagePicker({ value, onChange }) {
  const { S, T } = useTheme();
  const [urlInput, setUrlInput] = useState(value || "");

  return (
    <div style={{marginBottom:16}}>
      <label style={S.label}>Card Image URL <span style={{color:T.textFaint}}>(optional)</span></label>

      {value && (
        <div style={{marginBottom:10, position:"relative", display:"inline-block"}}>
          <img src={value} alt="Card" style={{width:80, height:110, objectFit:"cover",
            borderRadius:8, border:`1px solid ${T.border}`, cursor:"pointer"}}
            onClick={()=>window.open(value,"_blank")} />
          <button style={{position:"absolute", top:-6, right:-6, width:20, height:20,
            borderRadius:"50%", background:"#f87171", border:"none", color:"#fff",
            fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center"}}
            onClick={()=>{ onChange(""); setUrlInput(""); }}>✕</button>
        </div>
      )}

      <div style={{display:"flex", gap:8}}>
        <input style={{...S.input, flex:1}} value={urlInput}
          onChange={e=>setUrlInput(e.target.value)}
          placeholder="Paste image URL e.g. https://..." />
        <button style={S.btn("sm")}
          onClick={()=>onChange(urlInput.trim())}>
          Set
        </button>
      </div>
    </div>
  );
}

// ─── LOT DRAWER ───────────────────────────────────────────────────────────────
function LotDrawer({ lot, onClose, onSave }) {
  const { S, T } = useTheme();
  const isEdit = !!lot;
  const [form, setForm] = useState(lot ? {
    Name: lot.Name || "", Source: lot.Source || "Binder Buy",
    Date: lot.Date || localDate(),
    TotalPaid: lot.TotalPaid ?? "", TotalMarketValue: lot.TotalMarketValue ?? "",
    CardCount: lot.CardCount ?? "", PackCount: lot.PackCount ?? "", CardsPerPack: lot.CardsPerPack ?? "",
    Notes: lot.Notes || "",
  } : {
    Name:"", Source:"Binder Buy",
    Date: localDate(),
    TotalPaid:"", TotalMarketValue:"", CardCount:"", PackCount:"", CardsPerPack:"", Notes:"",
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const isPackPull     = form.Source === "Pack Pull";
  const isProportional = !isPackPull;
  const totalPaid      = parseFloat(form.TotalPaid)||0;
  const totalMV        = parseFloat(form.TotalMarketValue)||0;
  const cardsPerPack   = parseInt(form.CardsPerPack)||0;
  const packCount      = parseInt(form.PackCount)||0;
  const cardCount      = isPackPull
    ? (cardsPerPack > 0 && packCount > 0 ? packCount * cardsPerPack : parseInt(form.CardCount)||0)
    : parseInt(form.CardCount)||0;

  const costPerCard    = isPackPull && cardCount>0 && totalPaid ? totalPaid/cardCount : null;
  const minSellPrice   = costPerCard ? minSell(costPerCard) : null;

  const allocationRatio = isProportional && totalMV>0 && totalPaid>0 ? totalPaid/totalMV : null;
  const discountPct     = allocationRatio ? (1-allocationRatio)*100 : null;

  const canSave = form.Name && totalPaid>0 &&
    (isPackPull ? cardCount>0 && cardsPerPack>0 : totalMV>0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave({
      ...form,
      CardCount:        cardCount,
      TotalMarketValue: totalMV,
      CostPerCard:      costPerCard ? +costPerCard.toFixed(4) : 0,
      AllocationRatio:  allocationRatio ? +allocationRatio.toFixed(6) : 0,
      MinSellPrice:     minSellPrice || 0,
      CostMethod:       isPackPull ? "even" : "proportional",
    }, isEdit ? lot.ID : null);
    setSaving(false);
    onClose();
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>{isEdit?"Edit Lot":"Create New Lot"}</div>
          <button style={{width:36,height:36,borderRadius:T.radius.md,border:`1px solid ${T.border}`,
            background:T.surface,color:T.primary,fontSize:18,cursor:"pointer"}} onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:13,color:T.textMuted,marginBottom:18}}>
          {isEdit?"Update the details for this lot.":"Track cost and profit for a batch of cards bought together."}
        </div>

        <div style={{marginBottom:12}}>
          <label style={S.label}>Lot Name *</label>
          <input style={S.input} value={form.Name} onChange={e=>set("Name",e.target.value)}
            placeholder="e.g. Parker Binder June" />
        </div>

        <div style={{marginBottom:16}}>
          <label style={S.label}>Lot Type</label>
          <div style={{display:"flex", border:`1px solid ${T.border}`, borderRadius:T.radius.md,
            overflow:"hidden", background:T.bg}}>
            {LOT_SOURCES.map((s,i)=>(
              <button key={s} onClick={()=>set("Source",s)}
                style={{flex:1, padding:"11px 8px", border:"none", cursor:"pointer",
                  fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:700,
                  borderLeft: i>0 ? `1px solid ${T.border}` : "none",
                  background: form.Source===s ? T.blueBg : "transparent",
                  color: form.Source===s ? T.primary : T.textMuted}}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Date Purchased</label>
            <input style={S.input} type="date" value={form.Date} onChange={e=>set("Date",e.target.value)} />
          </div>
          <div style={S.col}>
            <label style={S.label}>Total Paid ($) *</label>
            <input style={S.input} type="number" value={form.TotalPaid}
              onChange={e=>set("TotalPaid",e.target.value)} placeholder="0.00" />
          </div>
        </div>

        {isPackPull ? (
          <>
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Number of Packs *</label>
                <input style={S.input} type="number" value={form.PackCount}
                  onChange={e=>set("PackCount",e.target.value)} placeholder="e.g. 9" />
              </div>
              <div style={S.col}>
                <label style={S.label}>Cards Per Pack *</label>
                <input style={S.input} type="number" value={form.CardsPerPack}
                  onChange={e=>set("CardsPerPack",e.target.value)} placeholder="e.g. 10" />
                <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>
                  English=10, Chinese=9, Mini=5
                </div>
              </div>
            </div>
            {form.PackCount && form.CardsPerPack && (
              <div style={{fontSize:12,color:T.textMuted,marginBottom:12}}>
                {packCount} packs × {cardsPerPack} cards = <strong>{cardCount} total cards</strong>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Total Market Value of All Cards ($) *</label>
              <input style={S.input} type="number" value={form.TotalMarketValue}
                onChange={e=>set("TotalMarketValue",e.target.value)}
                placeholder="Sum of all card market values e.g. 850.00" />
            </div>
            <div style={{marginBottom:12}}>
              <label style={S.label}>Number of Cards <span style={{color:T.textFaint}}>(optional)</span></label>
              <input style={S.input} type="number" value={form.CardCount}
                onChange={e=>set("CardCount",e.target.value)} placeholder="e.g. 13" />
            </div>
          </>
        )}

        {isPackPull && costPerCard && (
          <div style={{...S.infoBox,marginBottom:12}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div><div style={S.statLbl}>Cost Per Card</div><div style={{fontSize:18,fontWeight:700,color:T.primary}}>{fmt(costPerCard)}</div></div>
              <div><div style={S.statLbl}>Min Sell Price</div><div style={{fontSize:18,fontWeight:700,color:T.green}}>${minSellPrice}</div></div>
              <div><div style={S.statLbl}>Total Cards</div><div style={{fontSize:18,fontWeight:700}}>{cardCount}</div></div>
              <div><div style={S.statLbl}>Break-Even At</div><div style={{fontSize:18,fontWeight:700,color:T.primary}}>{fmt(totalPaid)}</div></div>
            </div>
          </div>
        )}

        {isProportional && totalPaid>0 && totalMV>0 && (
          <div style={{...S.infoBox,marginBottom:12}}>
            <div style={{fontSize:12,color:T.primary,fontWeight:500,marginBottom:10}}>
              Proportional Cost Allocation
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <div><div style={S.statLbl}>Market Value</div><div style={{fontSize:15,fontWeight:700}}>{fmt(totalMV)}</div></div>
              <div><div style={S.statLbl}>You Paid</div><div style={{fontSize:15,fontWeight:700,color:T.green}}>{fmt(totalPaid)}</div></div>
              <div><div style={S.statLbl}>Discount</div><div style={{fontSize:15,fontWeight:700,color:T.green}}>{discountPct?.toFixed(1)}%</div></div>
              <div><div style={S.statLbl}>Allocation</div><div style={{fontSize:15,fontWeight:700}}>{(allocationRatio*100).toFixed(1)}% of MV</div></div>
            </div>
            <div style={{...S.infoBox,marginBottom:0,background:T.bg}}>
              <div style={{fontSize:12,color:T.textMuted,lineHeight:1.8}}>
                <div>Each card cost = its market value × {(allocationRatio*100).toFixed(1)}%</div>
                <div style={{color:T.green}}>$150 card → {fmt(150*allocationRatio)} cost basis</div>
                <div style={{color:T.green}}>$15 card → {fmt(15*allocationRatio)} cost basis</div>
              </div>
            </div>
          </div>
        )}

        {isProportional && totalPaid>0 && !totalMV && (
          <div style={{fontSize:12,color:T.primary,marginBottom:12}}>
            Enter the total market value of all cards to enable proportional cost allocation
          </div>
        )}

        <div style={{marginBottom:20}}>
          <label style={S.label}>Notes <span style={{color:T.textFaint}}>(optional)</span></label>
          <input style={S.input} value={form.Notes} onChange={e=>set("Notes",e.target.value)} placeholder="optional" />
        </div>

        <div style={{display:"flex", justifyContent:"flex-end", gap:12, marginTop:8}}>
          <button style={{...S.btn("ghost"), padding:"12px 24px"}} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={{...S.btn("primary"), padding:"12px 24px"}} onClick={save} disabled={saving||!canSave}>
            {saving?"Saving…":isEdit?"Save Changes":"Create Lot"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ADD CARD DRAWER — catalog search-first flow ──────────────────────────────
function AddCardDrawer({ lots, shows, consignors, vendor, onClose, onSave, onCreateLot, gameType: initialGameType = "Pokemon", activeLocationId = null }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();

  const [gameType, setGameType]     = useState(initialGameType);
  const [activeTab, setActiveTab]   = useState("card");
  const [query, setQuery]           = useState("");
  const [searching, setSearching]   = useState(false);
  const [results, setResults]       = useState([]);
  const [searched, setSearched]     = useState(false);
  const [apiHasMore, setApiHasMore]   = useState(false);
  const [apiSearchPage, setApiSearchPage] = useState(1);
  const [apiLoadingMore, setApiLoadingMore] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected]     = useState(null);
  const [showMore, setShowMore]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [condition, setCondition]   = useState("Near Mint");
  const [variant, setVariant]       = useState("");
  const [variantOptions, setVariantOptions] = useState([]);
  const [source, setSource]         = useState("Individual Buy");
  const [acquisitionCost, setAcquisitionCost] = useState("");
  const [listPrice, setListPrice]   = useState("");
  const [quantity, setQuantity]     = useState(1);
  const [notes, setNotes]           = useState("");
  const [isGrading, setIsGrading]   = useState(false);
  const [gradingCompany, setGradingCompany] = useState("");
  const [grade, setGrade]           = useState("");
  const [gradedPriceInfo, setGradedPriceInfo] = useState(null);
  const [gradedPriceLoading, setGradedPriceLoading] = useState(false);
  const [isConsignment, setIsConsignment]   = useState(false);
  const [consignorId, setConsignorId]       = useState("");
  const [lotId, setLotId]                   = useState("");

  useEffect(() => {
    if (!selected?._priceData) { setVariantOptions([]); return; }
    const opts = getAvailableVariants(selected._priceData);
    setVariantOptions(opts);
    setVariant(opts.length === 1 ? opts[0] : "");
  }, [selected?.tcggoId, !!selected?._priceData]);

  useEffect(() => {
    if (!selected?._priceData) return;
    const recomputed = computeConditionPrice(selected._priceData, condition, variant || undefined);
    if (!recomputed) return;
    setSelected(s => (s ? { ...s, ...recomputed, tcggoId: s.tcggoId, _priceData: s._priceData } : s));
  }, [condition, variant]);

  useEffect(() => {
    if (!isGrading || !gradingCompany || !grade || !selected?.tcggoId) {
      setGradedPriceInfo(null);
      return;
    }
    let cancelled = false;
    setGradedPriceLoading(true);
    computeGradedPrice(selected.tcggoId, gradingCompany, grade).then(result => {
      if (!cancelled) { setGradedPriceInfo(result); setGradedPriceLoading(false); }
    });
    return () => { cancelled = true; };
  }, [isGrading, gradingCompany, grade, selected?.tcggoId]);

  const searchTimer = useRef(null);

  const runSearch = async (q) => {
    if (!q.trim() || q.trim().length < 3) { setResults([]); setSearched(false); setSearchError(null); setApiHasMore(false); return; }
    setSearching(true);
    setApiSearchPage(1);
    if (activeTab === "sealed") {
      const { results: found, error } = await apitcgSearchSealed(gameType, q.trim());
      setResults(found);
      setSearchError(error);
      setSearched(true);
      setSearching(false);
      setApiHasMore(false);
      return;
    }
    const numberMatch = q.trim().match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
    const name = numberMatch ? numberMatch[1].trim() : q.trim();
    const number = numberMatch ? numberMatch[2] : undefined;
    const { results: found, error, pagination } = await unifiedCardSearch(gameType, name, number, 1);
    setResults(found);
    setSearchError(error);
    setSearched(true);
    setSearching(false);
    setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
  };

  const loadMoreResults = async () => {
    if (activeTab === "sealed") return;
    const q = query.trim();
    const numberMatch = q.match(/^(.+)\s+([A-Z]{0,4}\d+(?:\/[A-Z]{0,4}\d+)?)$/i);
    const name = numberMatch ? numberMatch[1].trim() : q;
    const number = numberMatch ? numberMatch[2] : undefined;
    const nextPage = apiSearchPage + 1;
    setApiLoadingMore(true);
    const { results: found, pagination } = await unifiedCardSearch(gameType, name, number, nextPage);
    setResults(prev => [...prev, ...(found || [])]);
    setApiHasMore(!!pagination && pagination.page < pagination.total_pages);
    setApiSearchPage(nextPage);
    setApiLoadingMore(false);
  };

  const onQueryChange = (val) => {
    setQuery(val);
    setSelected(null);
    clearTimeout(searchTimer.current);
    const delay = val.trim().length < 6 ? 900 : 350;
    searchTimer.current = setTimeout(() => runSearch(val), delay);
  };

  const buildCardData = () => {
    const openLots = lots.filter(l => l.Status !== "Closed");
    const lot = openLots.find(l => l.ID === lotId);
    const isSealed = activeTab === "sealed";
    const base = {
      GameType:    gameType,
      Type:        isSealed ? "Sealed Product" : "Single Card",
      Language:    "English",
      Condition:   isSealed ? "New" : condition,
      Variant:     isSealed ? "" : (variant || ""),
      Source:      lot ? (lot.Source || source) : source,
      Rarity:      isSealed ? "" : (selected.rarity || "Common"),
      Quantity:    quantity || 1,
      CardName:    selected.cardName,
      Set:         selected.setName || "",
      Number:      isSealed ? "" : (selected.cardNumber || ""),
      Grade:       (!isSealed && isGrading) ? grade : "",
      AcquisitionCost: acquisitionCost || "",
      ListPrice:       listPrice || "",
      MinSellPrice:    "",
      MarketValueAtAcquisition: selected.price || "",
      AcquisitionDate: localDate(),
      LotID:    lot ? lot.ID   : "",
      LotName:  lot ? lot.Name : "",
      LotItemNumber: "",
      ShowID: "", ShowName: "",
      ImageURL: selected.imageUrl || "",
      Notes: notes || "",
      GradingCompany: isGrading ? gradingCompany : "",
      GradingSentDate: "", GradingReturnDate: "",
      GradingEstimatedValue: "", GradingCost: "",
      IsConsignment: isConsignment,
      ConsignorID:   isConsignment ? consignorId : "",
      MinAcceptPrice: "", TradesAccepted: false,
      LorcanaInkColor:"", LorcanaCharacterVersion:"", LorcanaProperty:"", LorcanaFoilType:"Normal",
      TcggoId:              selected.tcggoId || null,
      MarketPrice:          gradedPriceInfo?.center ?? selected.center ?? selected.price ?? null,
      MarketPriceSource:    gradedPriceInfo?.source ?? selected.source ?? selected.priceSource ?? null,
      MarketPriceUpdatedAt: (gradedPriceInfo?.center ?? selected.center) ? new Date().toISOString() : null,
      MarketPriceData:      (gradedPriceInfo?.center ?? selected.center) ? {
        center: gradedPriceInfo?.center ?? selected.center,
        low:    gradedPriceInfo?.low    ?? selected.low,
        high:   gradedPriceInfo?.high   ?? selected.high,
        pct: selected.pct,
        source: gradedPriceInfo?.source ?? selected.source,
        currency: selected.currency,
        _noConditionPricing: !gradedPriceInfo?.center && !!selected._noConditionPricing,
        updatedAt: new Date().toISOString(), history: [],
      } : null,
    };
    return { ...base, LocationID: activeLocationId, Category: deriveCategory(base) };
  };

  const handleQuickAdd = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await onSave(buildCardData());
      toast(`${selected.cardName} added to inventory`, "success");
      onClose();
    } catch (e) {
      toast("Failed to add card: " + e.message, "error");
    }
    setSaving(false);
  };

  const openLots = lots.filter(l => l.Status !== "Closed");

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget && onClose()}>
      <div style={{...S.drawer, maxHeight:"92vh", display:"flex", flexDirection:"column"}}>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <img
              src={{
                Pokemon:"/pokemon-logo.png", Lorcana:"/lorcana-logo.png", Magic:"/magic-logo.png",
                "One Piece":"/onepiece-logo.png", "Yu-Gi-Oh":"/yugioh-logo.png",
              }[gameType]}
              alt={gameType}
              style={{height:26,objectFit:"contain"}}
              onError={e=>{e.target.style.display="none";}} />
            <div style={{fontSize:16,fontWeight:600}}>Add Card</div>
          </div>
          <button onClick={onClose} style={{background:"none",border:"none",color:T.textMuted,fontSize:20,cursor:"pointer",lineHeight:1}}>×</button>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Choose a Game</label>
          <select style={S.select} value={gameType}
            onChange={e=>{
              setGameType(e.target.value);
              setQuery(""); setResults([]); setSearched(false); setSelected(null);
            }}>
            {[
              { name:"Pokemon",  label:"Pokémon" },
              { name:"Lorcana",  label:"Lorcana" },
              { name:"Magic",    label:"Magic" },
              { name:"One Piece",label:"One Piece" },
              { name:"Yu-Gi-Oh", label:"Yu-Gi-Oh" },
            ].map(({name:g, label})=>(
              <option key={g} value={g}>{label}</option>
            ))}
          </select>
        </div>

        <div style={{display:"flex", gap:6, marginBottom:14}}>
          {[["card","Add Card"],["sealed","Add Sealed"]].map(([tab,label])=>(
            <button key={tab}
              style={{...S.chip(activeTab===tab), flex:1, textAlign:"center", padding:"10px 12px"}}
              onClick={()=>{
                setActiveTab(tab);
                setQuery(""); setResults([]); setSearched(false); setSelected(null);
              }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{flex:1, overflowY:"auto", paddingBottom:4}}>

          {false ? (
            <div style={{...S.infoBox, textAlign:"center", padding:20}}>
              <div style={{fontSize:13, color:T.textMuted, lineHeight:1.6}}>
                Card search isn't available for Lorcana yet — pricing data for Lorcana
                isn't in our catalog source. Use the full manual entry form instead.
              </div>
              <button style={{...S.btn("primary"), marginTop:14}}
                onClick={() => onSave(null, { openFullForm:true, gameType:"Lorcana" })}>
                Open Manual Entry
              </button>
            </div>
          ) : (
          <>
            {showScanner && (
              <CardScanner
                S={S} T={T}
                mode="inventory"
                onClose={() => setShowScanner(false)}
                onSelect={async (r) => {
                  setShowScanner(false);
                  setSelected(r);
                  setQuery(r.cardName || "");
                  if (r.tcggoId && r.center == null) {
                    const priced = await unifiedCardLookup(gameType, r.cardName, r.cardNumber, r.setName, r.tcggoId);
                    if (priced) setSelected(s => (s?.tcggoId === r.tcggoId ? { ...s, ...priced } : s));
                  }
                }}
              />
            )}

            <div style={{marginBottom:8}}>
              <label style={S.label}>Search by: Card Name or Card Number (e.g. 101/084 or 101)</label>
              <div style={{display:"flex", gap:8, alignItems:"center"}}>
                <input
                  style={{...S.input, flex:1}}
                  autoFocus
                  placeholder="e.g. Charizard ex, Mew, Pikachu…"
                  value={query}
                  onChange={e => onQueryChange(e.target.value)}
                />
                <button
                  onClick={() => IS_DEMO ? demoBlockToast(toast) : setShowScanner(true)}
                  title="Scan card with camera"
                  style={{
                    background:"rgba(60,102,238,0.12)",
                    border:"0.5px solid rgba(60,102,238,0.4)",
                    borderRadius:8, color:"#88bdf2",
                    fontSize:20, padding:"8px 12px",
                    cursor:"pointer", flexShrink:0,
                    lineHeight:1,
                  }}>
                  📷
                </button>
              </div>
            </div>
            <div style={{marginBottom:14, textAlign:"right"}}>
              <button
                onClick={() => onSave(null, { openFullForm:true, gameType, prefillName: query, itemType: activeTab==="sealed"?"Sealed Product":"Single Card" })}
                style={{background:"none", border:"none", color:T.textFaint, fontSize:11,
                  fontWeight:500, cursor:"pointer", padding:0, textDecoration:"underline"}}>
                Skip search — add manually
              </button>
            </div>

            {searching && (
              <div style={{textAlign:"center", padding:24, color:T.textMuted, fontSize:13}}>
                Searching…
              </div>
            )}

            {!searching && searched && results.length === 0 && (
              <div style={{...S.infoBox, textAlign:"center", padding:20}}>
                {searchError ? (
                  <>
                    <div style={{fontSize:13, color:"#fbbf24", fontWeight:600, marginBottom:6}}>
                      {searchError === "rate_limited"
                        ? "Card search is temporarily unavailable"
                        : "Couldn't reach card search right now"}
                    </div>
                    <div style={{fontSize:12, color:T.textMuted, lineHeight:1.6}}>
                      {searchError === "rate_limited"
                        ? "We've hit the hourly lookup limit. You can still add this card manually below."
                        : "Something went wrong reaching the card database. You can still add this card manually below."}
                    </div>
                  </>
                ) : (
                  <div style={{fontSize:13, color:T.textMuted}}>
                    No matches for "{query}". Try a shorter or simpler search term, or add it manually below.
                  </div>
                )}
                <button style={{...S.btn("ghost"), marginTop:14}}
                  onClick={() => onSave(null, { openFullForm:true, gameType, prefillName: query, itemType: activeTab==="sealed"?"Sealed Product":"Single Card" })}>
                  ✏️ Add Manually Instead
                </button>
              </div>
            )}

            {!selected && results.length > 0 && (
              <>
              {query.trim().length < 6 && results.length >= 5 && (
                <div style={{...S.infoBox, marginBottom:10, padding:"8px 12px"}}>
                  <div style={{fontSize:11, color:T.textMuted}}>
                    Add more detail, a set name or card number, for faster, more precise results.
                  </div>
                </div>
              )}
              <div style={{
                display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(120px, 1fr))",
                gap:10, marginBottom:14,
              }}>
                {results.map((r, i) => (
                  <button key={r.tcggoId || i}
                    onClick={async () => {
                      setSelected(r);
                      if (r.tcggoId && r.center == null) {
                        const priced = activeTab === "sealed"
                          ? await apitcgLookupSealed(gameType, r.cardName, r.tcggoId)
                          : await unifiedCardLookup(gameType, r.cardName, r.cardNumber, r.setName, r.tcggoId);
                        if (priced) setSelected(s => (s?.tcggoId === r.tcggoId ? { ...s, ...priced } : s));
                      }
                    }}
                    style={{
                      background:T.surface2, border:`0.5px solid ${T.border||"rgba(255,255,255,0.08)"}`,
                      borderRadius:10, padding:8, cursor:"pointer", textAlign:"left",
                      display:"flex", flexDirection:"column", gap:5,
                    }}>
                    <div style={{
                      width:"100%", aspectRatio:"5/7", borderRadius:6, overflow:"hidden",
                      background:T.surface, display:"flex", alignItems:"center", justifyContent:"center",
                    }}>
                      {r.imageUrl
                        ? <img src={r.imageUrl} alt={r.cardName} style={{width:"100%",height:"100%",objectFit:"cover"}} />
                        : <span style={{fontSize:28}}>🃏</span>}
                    </div>
                    <div style={{fontSize:11, fontWeight:600, lineHeight:1.3, color:T.text,
                      display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical", overflow:"hidden"}}>
                      {r.cardName}
                    </div>
                    <div style={{fontSize:9, color:T.textFaint}}>
                      {r.setName}{r.cardNumber ? ` · #${r.cardNumber}` : ""}
                    </div>
                  </button>
                ))}
              </div>
              {apiHasMore && (
                <div style={{textAlign:"center", marginTop:-6, marginBottom:14}}>
                  <span style={{fontSize:13, fontWeight:600, color:"#3C66EE", cursor:"pointer"}}
                    onClick={loadMoreResults}>
                    {apiLoadingMore ? "Loading…" : "More results"}
                  </span>
                </div>
              )}
              </>
            )}

            {selected && (
              <div>
                <button onClick={()=>setSelected(null)}
                  style={{background:"none", border:"none", color:"#3b82f6", fontSize:12,
                    fontWeight:600, cursor:"pointer", marginBottom:10, padding:0}}>
                  ← Back to results
                </button>

                <div style={{
                  width:"100%", maxWidth:280, margin:"0 auto 14px",
                  borderRadius:14, overflow:"hidden",
                  boxShadow:"0 8px 28px rgba(0,0,0,0.35)",
                  background:T.surface2,
                }}>
                  {selected.imageUrl ? (
                    <img src={selected.imageUrl} alt={selected.cardName}
                      style={{width:"100%", display:"block"}} />
                  ) : (
                    <div style={{aspectRatio:"5/7", display:"flex", alignItems:"center", justifyContent:"center", fontSize:48}}>🃏</div>
                  )}
                </div>

                <div style={{textAlign:"center", marginBottom:4}}>
                  <div style={{fontSize:18, fontWeight:700, color:T.text}}>{selected.cardName}</div>
                  <div style={{fontSize:13, color:T.textMuted, marginTop:2}}>
                    {selected.setName}{selected.cardNumber ? ` · #${selected.cardNumber}` : ""}{selected.rarity ? ` · ${selected.rarity}` : ""}
                  </div>
                </div>

                {gradedPriceInfo?.center != null ? (
                  <>
                    <div style={{textAlign:"center", marginBottom:4, fontSize:14, fontWeight:700,
                      color:"#c4b5fd", background:"rgba(139,92,246,0.10)",
                      border:"0.5px solid rgba(139,92,246,0.3)",
                      borderRadius:8, padding:"8px 12px", lineHeight:1.5}}>
                      Slab Price: ${gradedPriceInfo.low?.toFixed(2)} – ${gradedPriceInfo.high?.toFixed(2)}
                      <span style={{fontWeight:400, color:T.textMuted}}> · {gradedPriceInfo.gradingCompany} {gradedPriceInfo.grade}</span>
                    </div>
                    {selected.center != null && (
                      <div style={{textAlign:"center", marginBottom:12, fontSize:11, color:T.textFaint}}>
                        Raw Price: ${selected.low?.toFixed(2)} – ${selected.high?.toFixed(2)} · {selected.condition || condition}
                      </div>
                    )}
                  </>
                ) : gradedPriceLoading ? (
                  <div style={{textAlign:"center", marginBottom:12, fontSize:12, color:T.textFaint}}>
                    Looking up slab pricing…
                  </div>
                ) : isGrading && gradingCompany && grade ? (
                  <div style={{textAlign:"center", marginBottom:12, fontSize:12,
                    color:"#fbbf24", background:"rgba(251,191,36,0.08)",
                    border:"0.5px solid rgba(251,191,36,0.25)",
                    borderRadius:8, padding:"8px 12px", lineHeight:1.5}}>
                    ⚠️ No recent {gradingCompany} {grade} sales found. Raw price shown below, price manually if needed.
                  </div>
                ) : null}

                {!(gradedPriceInfo?.center != null) && (selected.center != null ? (
                  <div style={{textAlign:"center", marginBottom:12, fontSize:13,
                    color:"#88bdf2", background:"rgba(60,102,238,0.08)",
                    border:"0.5px solid rgba(60,102,238,0.25)",
                    borderRadius:8, padding:"8px 12px", lineHeight:1.5}}>
                    {selected._noConditionPricing ? (
                      <>
                        Market Price: ${selected.center?.toFixed(2)}
                        <span style={{color:T.textMuted}}> · Near Mint</span>
                      </>
                    ) : (
                      <>
                        Estimated range: ${selected.low?.toFixed(2)} – ${selected.high?.toFixed(2)}
                        <span style={{color:T.textMuted}}> · {selected.condition || condition}</span>
                      </>
                    )}
                    {selected.source ? <span style={{color:T.textMuted}}> · {selected.source}</span> : null}
                  </div>
                ) : (
                  <div style={{textAlign:"center", marginBottom:12, fontSize:12,
                    color:"#fbbf24", background:"rgba(251,191,36,0.08)",
                    border:"0.5px solid rgba(251,191,36,0.25)",
                    borderRadius:8, padding:"8px 12px", lineHeight:1.5}}>
                    ⚠️ No market price found yet for this card. You can still add it and price it manually.
                  </div>
                ))}

                {vendor?.dynamic_pricing_enabled && (
                  <div style={{textAlign:"center", marginBottom:16, fontSize:12,
                    color:"#86efac", background:"rgba(34,197,94,0.08)",
                    border:"0.5px solid rgba(34,197,94,0.25)",
                    borderRadius:8, padding:"7px 12px", lineHeight:1.5}}>
                    💲 Price Setting is on. You don't need to enter a List Price, it'll be set automatically.
                  </div>
                )}

                <button
                  onClick={handleQuickAdd}
                  disabled={saving}
                  style={{...S.btn("primary"), width:"100%", padding:"15px", fontSize:15, marginBottom:10}}>
                  {saving ? "Adding…" : "Add to Inventory"}
                </button>

                <button
                  onClick={() => setShowMore(m => !m)}
                  style={{
                    width:"100%", background:"none", border:"none",
                    color:T.textMuted, fontSize:13, fontWeight:600,
                    cursor:"pointer", padding:"8px 0", textAlign:"center",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:6,
                  }}>
                  {showMore ? "▲ Hide details" : "▼ More details"}
                  <span style={{fontSize:10, color:T.textFaint, fontWeight:400}}>(optional)</span>
                </button>

                {showMore && (
                  <div style={{marginTop:10, padding:14, background:T.surface2, borderRadius:10}}>

                    <div style={S.row}>
                      <div style={S.col}>
                        <label style={S.label}>Condition</label>
                        <select style={S.select} value={condition} onChange={e=>setCondition(e.target.value)}>
                          {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>Quantity</label>
                        <input style={S.input} type="number" min="1" value={quantity}
                          onChange={e=>setQuantity(parseInt(e.target.value)||1)} />
                      </div>
                    </div>

                    {gameType === "Pokemon" && (
                    <div style={S.row}>
                      <div style={S.col}>
                        <label style={S.label}>Variant</label>
                        <select style={S.select} value={variant} onChange={e=>setVariant(e.target.value)}>
                          <option value="">—</option>
                          {[...new Set([
                            ...(variant ? [variant] : []),
                            ...(variantOptions.length > 1 ? variantOptions : GENERIC_VARIANT_FALLBACK),
                          ])].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                        {variantOptions.length > 1 && (
                          <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
                            This card has multiple real variants on record — pick the one you actually have.
                          </div>
                        )}
                      </div>
                    </div>
                    )}

                    <div style={S.row}>
                      <div style={S.col}>
                        <label style={S.label}>Acquisition Cost</label>
                        <input style={S.input} type="number" step="0.01" placeholder="0.00"
                          value={acquisitionCost} onChange={e=>setAcquisitionCost(e.target.value)} />
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>List Price</label>
                        <input style={S.input} type="number" step="0.01"
                          placeholder={selected.price ? selected.price.toFixed(2) : "0.00"}
                          value={listPrice} onChange={e=>setListPrice(e.target.value)} />
                      </div>
                    </div>

                    <div style={{marginBottom:10}}>
                      <label style={S.label}>Source</label>
                      <select style={S.select} value={source} onChange={e=>setSource(e.target.value)}>
                        {["Individual Buy","Binder Buy","Bulk Lot","Pack Pull"].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </div>

                    {(source==="Binder Buy" || source==="Bulk Lot" || source==="Pack Pull") && openLots.length > 0 && (
                      <div style={{marginBottom:10}}>
                        <label style={S.label}>Assign to Lot (optional)</label>
                        <select style={S.select} value={lotId} onChange={e=>setLotId(e.target.value)}>
                          <option value="">No lot</option>
                          {openLots.filter(l=>l.Source===source||l.Source.includes(source.split(" ")[0]))
                            .map(l => <option key={l.ID} value={l.ID}>{l.Name}</option>)}
                        </select>
                      </div>
                    )}

                    {activeTab !== "sealed" && (
                    <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,cursor:"pointer"}}>
                      <input type="checkbox" checked={isGrading}
                        onChange={e=>setIsGrading(e.target.checked)}
                        style={{width:16,height:16,accentColor:"#3b82f6"}} />
                      <span style={{fontSize:13}}>This card is graded</span>
                    </label>
                    )}
                    {activeTab !== "sealed" && isGrading && (
                      <div style={S.row}>
                        <div style={S.col}>
                          <label style={S.label}>Grading Company</label>
                          <select style={S.select} value={gradingCompany} onChange={e=>setGradingCompany(e.target.value)}>
                            <option value="">Select…</option>
                            {["PSA","BGS","CGC","SGC"].map(g => <option key={g}>{g}</option>)}
                          </select>
                        </div>
                        <div style={S.col}>
                          <label style={S.label}>Grade</label>
                          <input style={S.input} type="text" placeholder="e.g. 9, 10"
                            value={grade} onChange={e=>setGrade(e.target.value)} />
                        </div>
                      </div>
                    )}

                    <label style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,cursor:"pointer"}}>
                      <input type="checkbox" checked={isConsignment}
                        onChange={e=>setIsConsignment(e.target.checked)}
                        style={{width:16,height:16,accentColor:"#3b82f6"}} />
                      <span style={{fontSize:13}}>This is a consignment card</span>
                    </label>
                    {isConsignment && consignors?.length > 0 && (
                      <div style={{marginBottom:10}}>
                        <label style={S.label}>Consignor</label>
                        <select style={S.select} value={consignorId} onChange={e=>setConsignorId(e.target.value)}>
                          <option value="">Select…</option>
                          {consignors.map(c => <option key={c.ID} value={c.ID}>{c.FullName}</option>)}
                        </select>
                      </div>
                    )}

                    <div style={{marginBottom:4}}>
                      <label style={S.label}>Notes</label>
                      <textarea style={{...S.input, minHeight:60, resize:"vertical"}}
                        value={notes} onChange={e=>setNotes(e.target.value)} />
                    </div>

                  </div>
                )}
              </div>
            )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ADD / EDIT ITEM DRAWER ───────────────────────────────────────────────────
function ItemDrawer({ item, lots, shows, inventory, consignors, isPaid, vendor, buyers = [], onClose, onSave, onCreateLot, onDuplicate, isEnterprise = false, locations = [] }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const isEdit = !!item?.ID;
  const [realVariantOptions, setRealVariantOptions] = useState([]);

  useEffect(() => {
    if (!item?.TcggoId) { setRealVariantOptions([]); return; }
    let cancelled = false;
    pkmnpricesFetchPriceData(item.TcggoId).then(priceData => {
      if (!cancelled) setRealVariantOptions(getAvailableVariants(priceData));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [item?.TcggoId]);

  const blank  = {
    GameType:"Pokemon",
    Type:"Single Card", Language:"English", Condition:"Near Mint",
    Source:"Individual Buy", Rarity:"Common", Quantity:1,
    CardName:"", Set:"", Number:"", Grade:"",
    AcquisitionCost:"", ListPrice:"", MinSellPrice:"",
    MarketValueAtAcquisition:"",
    AcquisitionDate: localDate(),
    LotID:"", LotName:"", LotItemNumber:"",
    ShowID:"", ShowName:"",
    ImageURL:"", Notes:"",
    GradingCompany:"", GradingSentDate:"", GradingReturnDate:"",
    GradingEstimatedValue:"", GradingCost:"",
    IsConsignment:false, ConsignorID:"", MinAcceptPrice:"", TradesAccepted:false,
    ConsignPayoutStatus: item?.ConsignPayoutStatus || null,
    IsOnline: item?.IsOnline || false,
    LorcanaInkColor:"", LorcanaCharacterVersion:"", LorcanaProperty:"", LorcanaFoilType:"Normal",
  };
  const { cleanText: initialNotesClean, photoUrl: initialNotesPhoto } = extractNotesPhoto(item?.Notes);
  const initialForm = item?.ID
    ? {...item, Notes: initialNotesClean}
    : item?.CardName
      ? {...blank, ...item, ID: undefined, Notes: initialNotesClean}
      : item?.GameType
        ? {...blank, GameType: item.GameType}
        : blank;
  const [form, setForm] = useState(initialForm);
  const [notesPhoto, setNotesPhoto] = useState(initialNotesPhoto);
  const [saving, setSaving]           = useState(false);
  const [assignBuyerChecked, setAssignBuyerChecked] = useState(!!item?.AssignedBuyerID);
  const [selectedBuyerId, setSelectedBuyerId] = useState(item?.AssignedBuyerID || "");
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [showLotDrawer, setShowLotDrawer] = useState(false);
  const [copySearch, setCopySearch]   = useState("");
  const [copyResults, setCopyResults] = useState([]);
  const [showCopySearch, setShowCopySearch] = useState(!isEdit);
  const [tcgUrl, setTcgUrl]           = useState("");
  const [tcgLoading, setTcgLoading]   = useState(false);
  const [tcgError, setTcgError]       = useState("");
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const fetchFromTCG = async () => {
    if (!tcgUrl.trim()) return;
    setTcgLoading(true);
    setTcgError("");
    try {
      const res  = await fetch(`/tcgplayer-fetch?url=${encodeURIComponent(tcgUrl)}`);
      const data = await res.json();
      if (data.error) { setTcgError(data.error); setTcgLoading(false); return; }
      setForm(f => ({
        ...f,
        CardName:  data.cardName  || f.CardName,
        Set:       data.set       || f.Set,
        Number:    data.number    || f.Number,
        Rarity:    data.rarity    || f.Rarity,
        Condition: data.condition || f.Condition,
        Language:  data.language  || f.Language,
        MarketValueAtAcquisition: data.marketPrice || f.MarketValueAtAcquisition,
        ImageURL:  data.image     || f.ImageURL,
      }));
      setTcgUrl("");
      setShowCopySearch(false);
    } catch(e) {
      setTcgError("Could not fetch card data. Check the URL and try again.");
    }
    setTcgLoading(false);
  };

  const searchExisting = (q) => {
    setCopySearch(q);
    if (!q.trim()) { setCopyResults([]); return; }
    const lower = q.toLowerCase();
    const results = inventory
      .filter(i => i.Status !== "Deleted" &&
        (i.CardName?.toLowerCase().includes(lower) ||
         i.Set?.toLowerCase().includes(lower) ||
         i.Number?.toLowerCase().includes(lower)))
      .slice(0, 6);
    setCopyResults(results);
  };

  const applyFromExisting = (item) => {
    setForm(f => ({
      ...f,
      Type:      item.Type      || "Single Card",
      CardName:  item.CardName  || "",
      Set:       item.Set       || "",
      Number:    item.Number    || "",
      Rarity:    item.Rarity    || "Common",
      Language:  item.Language  || "English",
      Condition: item.Condition || "Near Mint",
      Variant:   item.Variant   || "",
      Grade:     item.Grade     || "",
      Source:    item.Source    || "Individual Buy",
      AcquisitionCost:          "",
      ListPrice:                "",
      MinSellPrice:             "",
      MarketValueAtAcquisition: "",
    }));
    setCopySearch("");
    setCopyResults([]);
    setShowCopySearch(false);
  };

  const isLotSource = LOT_SOURCES.includes(form.Source);
  const openLots    = lots.filter(l=>l.Status==="Open");
  const todayStr = localDate();
  const activeShows = shows.filter(s=>(s.Status==="Upcoming"||s.Status==="Active")&&(!s.EndDate||s.EndDate>=todayStr));

  const handleLotSelect = (lotId) => {
    if (!lotId) { setForm(f=>({...f,LotID:"",LotName:"",AcquisitionCost:"",MinSellPrice:"",ListPrice:""})); return; }
    const lot = lots.find(l=>l.ID===lotId);
    if (!lot) return;

    const lotDate = lot.Date || lot.PurchasedDate || lot.DatePurchased || null;

    if (lot.CostMethod === "proportional" && lot.AllocationRatio) {
      const mv = parseFloat(form.MarketValueAtAcquisition)||0;
      const ac = mv>0 ? +(mv * lot.AllocationRatio).toFixed(2) : 0;
      const ms = ac>0 ? minSell(ac) : 0;
      setForm(f=>({...f,
        LotID:           lot.ID,
        LotName:         lot.Name,
        AcquisitionCost: ac>0 ? ac.toString() : "",
        MinSellPrice:    ms,
        ListPrice:       ms ? ms.toString() : f.ListPrice,
        LotItemNumber:   (lot._itemCount||0)+1,
        ...(lotDate ? { AcquisitionDate: lotDate } : {}),
      }));
    } else {
      setForm(f=>({...f,
        LotID:           lot.ID,
        LotName:         lot.Name,
        AcquisitionCost: lot.CostPerCard ? lot.CostPerCard.toString() : "",
        MinSellPrice:    lot.MinSellPrice || 0,
        ListPrice:       lot.MinSellPrice ? lot.MinSellPrice.toString() : f.ListPrice,
        LotItemNumber:   (lot._itemCount||0)+1,
        ...(lotDate ? { AcquisitionDate: lotDate } : {}),
      }));
    }
  };

  const handleNewLot = async (lotData) => {
    const saved = await onCreateLot(lotData);
    handleLotSelect(saved.ID);
    setShowLotDrawer(false);
  };

  const save = async () => {
    if (!form.CardName?.trim()) {
      toast("Card Name is required.", "alert"); return;
    }
    if (!form.Set) {
      toast("Set is required — please select or enter a set.", "alert"); return;
    }
    if (!form.Condition) {
      toast("Condition is required.", "alert"); return;
    }
    if (form.LotID && form.Source !== "Pack Pull" && form.Type !== "Sealed Product" && !form.MarketValueAtAcquisition) {
      toast("Market Value at Acquisition is required for cards added to a Lot — it's used to split the Lot's cost across cards.", "alert"); return;
    }
    if (!form.ListPrice || parseFloat(form.ListPrice) <= 0) {
      toast("List Price is required.", "alert"); return;
    }
    if (form.IsConsignment && !form.ConsignorID) {
      toast("Please select a Consignor.", "alert"); return;
    }
    const minConsignVal = parseFloat(vendor?.consign_settings?.min_card_value) || 0;
    if (form.IsConsignment && minConsignVal > 0 && (parseFloat(form.ListPrice)||0) < minConsignVal) {
      toast(`Consigned cards must have a List Price of at least $${minConsignVal}.`, "alert"); return;
    }
    setSaving(true);
    const finalNotes = notesPhoto
      ? `${form.Notes?.trim() || ""}${form.Notes?.trim() ? " · " : ""}Photo: ${notesPhoto}`
      : (form.Notes || "");
    await onSave({
      ...form,
      Notes:                    finalNotes,
      AcquisitionCost:          parseFloat(form.AcquisitionCost)||0,
      ListPrice:                parseFloat(form.ListPrice)||0,
      MinSellPrice:             parseFloat(form.MinSellPrice)||0,
      MarketValueAtAcquisition: parseFloat(form.MarketValueAtAcquisition)||0,
      Quantity:                 parseInt(form.Quantity)||1,
      Status:                   form.Status||"In Stock",
      IsConsignment:            !!form.IsConsignment,
      IsOnline:                 !!form.IsOnline,
      ConsignPayoutStatus:      form.IsConsignment ? (form.ConsignPayoutStatus||null) : null,
      ConsignorID:              form.IsConsignment ? (form.ConsignorID||"") : "",
      MinAcceptPrice:           form.IsConsignment ? (parseFloat(form.MinAcceptPrice)||0) : 0,
      TradesAccepted:           form.IsConsignment ? !!form.TradesAccepted : false,
    });
    if (isEdit && item?.ID && item.ListPrice != null) {
      const oldP = parseFloat(item.ListPrice);
      const newP = parseFloat(form.ListPrice);
      if (!isNaN(oldP) && !isNaN(newP) && Math.abs(oldP - newP) > 0.001) {
        logManualPriceEdit(vendor?.id, item.ID, oldP, newP);
      }
    }
    if (isEdit && item?.ID && (form.Variant || "") !== (item?.Variant || "")) {
      refreshSingleCardPrice(item.ID).catch(() => {});
    }
    setSaving(false);
    onClose();
  };

  const roiVal   = roi(form.AcquisitionCost, form.ListPrice);
  const belowMin = form.MinSellPrice>0 && parseFloat(form.ListPrice)<parseFloat(form.MinSellPrice);
  const [drawerWidth, setDrawerWidth] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setDrawerWidth(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const isWideModal = drawerWidth >= 1024;

  return (
    <>
      <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
        <div style={{...S.drawer, maxWidth:1180}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              {form.GameType==="Lorcana" ? (
                <img src="https://cards.disneylorcana.com/assets/logo-br-G3AQ3ZLG.png"
                  alt="Disney Lorcana"
                  style={{height:28,objectFit:"contain",borderRadius:4}}
                  onError={e=>{e.target.style.display="none";}} />
              ) : (
                <img src="https://assets.pokemon.com/assets/cms2/img/misc/gus/buttons/logo-pokemon-79x45.png"
                  alt="Pokémon TCG"
                  style={{height:28,objectFit:"contain"}}
                  onError={e=>{e.target.style.display="none";}} />
              )}
              <div>
                <div style={{fontSize:20,fontWeight:800,color:T.text,letterSpacing:"-0.02em"}}>{isEdit?(form.Type==="Sealed Product"?"Edit Sealed":"Edit Card"):"Add Inventory"}</div>
                <div style={{fontSize:13,color:T.textMuted,marginTop:1}}>Manage pricing, acquisition, grading, and storefront settings</div>
              </div>
            </div>
            <button style={{width:42,height:42,borderRadius:T.radius.md,border:`1px solid ${T.border}`,
              background:T.surface,color:T.primary,fontSize:20,cursor:"pointer",flexShrink:0}}
              onClick={onClose}>✕</button>
          </div>

          {item?.Status === "Sold" && item?.SoldChannel === "Trade" && (
            <div style={{...S.card, marginBottom:14, borderColor:T.primary,
              background:T.blueBg}}>
              <div style={{fontSize:12, fontWeight:700, color:T.primary, marginBottom:8,
                textTransform:"uppercase", letterSpacing:".05em"}}>
                🔄 Trade Gain
              </div>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"baseline"}}>
                <div style={{fontSize:12, color:T.textMuted}}>
                  Traded for {fmt(item.SoldPrice)} · cost {fmt(item.AcquisitionCost)}
                </div>
                <div style={{fontSize:18, fontWeight:700,
                  color: (item.TradeGain||0) > 0 ? T.green : T.textMuted}}>
                  {item.TradeGain > 0 ? `+${fmt(item.TradeGain)}` : "—"}
                </div>
              </div>
            </div>
          )}

          <div style={{display:"grid", gridTemplateColumns: isWideModal ? "1fr 1fr" : "1fr", gap:26}}>
          <div style={{minWidth:0, borderRight: isWideModal ? `1px solid ${T.borderSoft}` : "none",
            paddingRight: isWideModal ? 26 : 0}}>

          <div style={{marginBottom:14}}>
            <label style={S.label}>Game</label>
            <select style={S.select} value={form.GameType}
              onChange={e=>{
                const g = e.target.value;
                set("GameType",g);
                set("Set",""); set("Rarity","Common");
                set("LorcanaInkColor",""); set("LorcanaCharacterVersion",""); set("LorcanaProperty",""); set("LorcanaFoilType","Normal");
              }}>
              {[
                { name:"Pokemon",  label:"Pokémon" },
                { name:"Lorcana",  label:"Lorcana" },
                { name:"Magic",    label:"Magic" },
                { name:"One Piece",label:"One Piece" },
                { name:"Yu-Gi-Oh", label:"Yu-Gi-Oh" },
              ].map(({name:g, label})=>(
                <option key={g} value={g}>{label}</option>
              ))}
            </select>
          </div>

          <div style={{display:"flex",gap:6,marginBottom:16,alignItems:"center"}}>
            {isEdit && (
              <button style={{
                marginLeft:"auto",padding:"5px 12px",borderRadius:20,border:"none",
                background:T.green,color:"#fff",fontFamily:"'Sora',sans-serif",
                fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",
              }}
              onClick={()=>{
                const clone = {
                  ...form,
                  ID: undefined,
                  Status:      "In Stock",
                  SoldPrice:   "", SoldDate:   "", SoldChannel: "",
                  BuyerGender: "", TradeCredit: "",
                  LotID: "", LotName: "", LotItemNumber: "",
                  ShowID: "", ShowName: "", AcquiredShowID: "",
                  AcquisitionDate: localDate(),
                  Quantity: 1,
                };
                onClose();
                setTimeout(()=>onDuplicate(clone), 100);
              }}>
                + Duplicate
              </button>
            )}
          </div>



          {!isEdit && (
            <div style={{marginBottom:16}}>
              {showCopySearch ? (
                <>
                  <label style={S.label}>{form.Type==="Sealed Product"?"Copy details from existing sealed product":"Copy from existing card"} <span style={{color:T.textFaint}}>(optional — pre-fills details)</span></label>
                  <input style={S.input} value={copySearch}
                    onChange={e=>searchExisting(e.target.value)}
                    placeholder="Search by name, set, or card number…" />
                  {copyResults.length > 0 && (
                    <div style={{marginTop:6,border:`1px solid ${T.border}`,borderRadius:8,overflow:"hidden"}}>
                      {copyResults.map(item=>(
                        <div key={item.ID}
                          style={{padding:"10px 12px",background:T.surface2,cursor:"pointer",
                            borderBottom:`1px solid ${T.border}`}}
                          onClick={()=>applyFromExisting(item)}>
                          <div style={{fontSize:13,fontWeight:500,color:T.text}}>{item.CardName}</div>
                          <div style={{fontSize:11,color:T.textMuted}}>
                            {[item.Set,item.Number,item.Rarity,item.Condition].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {copySearch && copyResults.length===0 && (
                    <div style={{fontSize:12,color:T.textFaint,marginTop:6}}>No matches — fill in details below</div>
                  )}
                </>
              ) : (
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  padding:"8px 12px",background:T.surface2,borderRadius:8,border:`1px solid ${T.border}`}}>
                  <div style={{fontSize:12,color:T.textMuted}}>Details copied from: <span style={{color:T.text,fontWeight:500}}>{form.CardName}</span></div>
                  <button style={{...S.btn("ghost"),padding:"4px 10px",fontSize:11}}
                    onClick={()=>setShowCopySearch(true)}>Change</button>
                </div>
              )}
            </div>
          )}

          <ImagePicker value={form.ImageURL} onChange={v=>set("ImageURL",v)} />

          <div style={{marginBottom:12}}>
            <label style={S.label}>{form.Type==="Sealed Product"?"Product Name":"Card Name"} *</label>
            {form.Type==="Sealed Product" && (
              <div style={{marginBottom:8}}>
                <select style={S.select} value={form.Rarity||""}
                  onChange={e=>set("Rarity",e.target.value)}>
                  <option value="">Select product type…</option>
                  {(CONFIG.SEALED_PRODUCT_TYPES[form.GameType] || CONFIG.SEALED_PRODUCT_TYPES.Pokemon).map(t=><option key={t}>{t}</option>)}
                </select>
              </div>
            )}
            <input style={S.input} value={form.CardName}
              onChange={e=>{
                const name = e.target.value;
                set("CardName", name);
                if (!form.ID && !form.ImageURL && name.trim().length > 2) {
                  const match = inventory.find(i =>
                    i.CardName?.toLowerCase() === name.trim().toLowerCase() &&
                    i.ImageURL &&
                    i.Status !== "Deleted"
                  );
                  if (match) set("ImageURL", match.ImageURL);
                }
              }}
              placeholder={form.Type==="Sealed Product"?"e.g. SV Booster Box":"e.g. Charizard VMAX"} />
            {!form.ID && form.ImageURL && form.CardName.trim().length > 2 && (() => {
              const match = inventory.find(i =>
                i.CardName?.toLowerCase() === form.CardName.trim().toLowerCase() &&
                i.ImageURL === form.ImageURL
              );
              if (!match) return null;
              return (
                <div style={{fontSize:11, color:T.textMuted, marginTop:4, display:"flex",
                  alignItems:"center", gap:6}}>
                  <img src={form.ImageURL} alt="" style={{width:20, height:28,
                    objectFit:"cover", borderRadius:3}} />
                  Image auto-filled from existing card
                </div>
              );
            })()}
          </div>

          <div style={S.row}>
            {form.Type!=="Sealed Product" && (
              <div style={S.col}>
                <label style={S.label}>Language</label>
                <select style={S.select} value={form.Language}
                  onChange={e=>{
                    set("Language", e.target.value);
                    set("Set", "");
                    set("Rarity", "Common");
                  }}>
                  {LANGUAGES.map(l=><option key={l}>{l}</option>)}
                </select>
              </div>
            )}
            <div style={{width: form.Type==="Sealed Product" ? "100%" : 80}}>
              <label style={S.label}>Qty</label>
              <input style={S.input} type="number" min="1" value={form.Quantity} onChange={e=>set("Quantity",e.target.value)} />
            </div>
          </div>

          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>Set</label>
              <select style={S.select} value={form.Set==="__new__"?"__new__":form.Set}
                onChange={e => {
                  if (e.target.value === "__new__") {
                    set("Set", "__new__");
                  } else {
                    set("Set", e.target.value);
                  }
                }}>
                <option value="">Select a set...</option>
                {form.GameType === "Lorcana"
                  ? [...CONFIG.LORCANA_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : form.GameType === "Magic"
                  ? [...CONFIG.MAGIC_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : form.GameType === "One Piece"
                  ? [...CONFIG.ONEPIECE_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : form.GameType === "Yu-Gi-Oh"
                  ? [...CONFIG.YUGIOH_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : form.Language === "Chinese"
                  ? [...CONFIG.CHINESE_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : form.Language === "Japanese"
                  ? [...CONFIG.JAPANESE_SETS].sort((a,b)=>a.toLowerCase().localeCompare(b.toLowerCase())).map(s=><option key={s} value={s}>{s}</option>)
                  : [...CONFIG.POKEMON_SETS].sort((a,b)=>a.name.toLowerCase().localeCompare(b.name.toLowerCase())).map(s=><option key={s.name} value={s.name}>{s.name}</option>)
                }
                <option value="__new__">+ Add new set…</option>
              </select>
              {form.Set === "__new__" && (
                <div style={{marginTop:6, display:"flex", gap:6}}>
                  <input
                    style={{...S.input, flex:1}}
                    autoFocus
                    placeholder="New set name e.g. Twilight Force"
                    onKeyDown={e => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        const normalized = e.target.value.trim()
                          .replace(/\s+/g, " ")
                          .replace(/\b\w/g, c => c.toUpperCase());
                        set("Set", normalized);
                      }
                      if (e.key === "Escape") set("Set", "");
                    }}
                  />
                  <button style={{...S.btn("primary"), padding:"8px 12px", fontSize:12, flexShrink:0}}
                    onClick={e => {
                      const input = e.target.closest("div").querySelector("input");
                      if (!input?.value.trim()) return;
                      const normalized = input.value.trim()
                        .replace(/\s+/g, " ")
                        .replace(/\b\w/g, c => c.toUpperCase());
                      set("Set", normalized);
                    }}>
                    Add
                  </button>
                  <button style={{...S.btn("ghost"), padding:"8px 10px", fontSize:12, flexShrink:0}}
                    onClick={()=>set("Set", "")}>
                    ✕
                  </button>
                </div>
              )}
            </div>
            {form.Type==="Single Card" && (
              <div style={{width:95}}>
                <label style={S.label}>Card #</label>
                <input style={S.input} value={form.Number} onChange={e=>set("Number",e.target.value)} placeholder="020/189" />
              </div>
            )}
          </div>
          {form.Type==="Single Card" && form.GameType==="Pokemon" && (
            <div style={{fontSize:11, color:T.textFaint, marginTop:-6, marginBottom:14, lineHeight:1.5}}>
              Enter the exact card name and number as printed — automatic pricing can only match
              cards entered accurately.
            </div>
          )}

          {form.Type==="Single Card" && (
            <>
              <div style={S.row}>
                <div style={S.col}>
                  <label style={S.label}>Condition</label>
                  <select style={S.select} value={form.Condition} onChange={e=>set("Condition",e.target.value)}>
                    {CONDITIONS.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                {form.GameType !== "Lorcana" ? (
                <div style={S.col}>
                  <label style={S.label}>Variant</label>
                  <select style={S.select} value={form.Variant || ""} onChange={e=>set("Variant",e.target.value)}>
                    <option value="">—</option>
                    {[...new Set([
                      ...(form.Variant ? [form.Variant] : []),
                      ...(realVariantOptions.length > 1 ? realVariantOptions : GENERIC_VARIANT_FALLBACK),
                    ])].map(v=><option key={v} value={v}>{v}</option>)}
                  </select>
                  {realVariantOptions.length > 1 && (
                    <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
                      This card has multiple real variants on record — pick the one you actually have.
                    </div>
                  )}
                </div>
                ) : <div style={S.col} />}
              </div>
              <div style={S.row}>
                <div style={S.col}>
                  <label style={S.label}>Rarity</label>
                  <select style={S.select} value={form.Rarity} onChange={e=>set("Rarity",e.target.value)}>
                    {(form.GameType === "Lorcana"
                      ? CONFIG.LORCANA_RARITIES
                      : form.Language === "Japanese"
                      ? CONFIG.RARITIES_JAPANESE
                      : form.Language === "Chinese"
                      ? CONFIG.RARITIES_CHINESE
                      : CONFIG.RARITIES_ENGLISH || RARITIES
                    ).map(v=><option key={v}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div style={{marginBottom:12}}>
                <label style={S.label}>Grade <span style={{color:T.textFaint}}>(optional)</span></label>
                <select style={S.select} value={form.Grade} onChange={e=>set("Grade",e.target.value)}>
                  <option value="">Not graded</option>
                  {GRADES.map(g=><option key={g}>{g}</option>)}
                </select>
              </div>

              {form.GameType === "Lorcana" && (
                <div style={{...S.infoBox, marginBottom:12,
                  borderColor:"rgba(139,92,246,0.4)", background:"rgba(139,92,246,0.05)"}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#a78bfa",marginBottom:10}}>
                    ✨ Lorcana Card Details
                  </div>
                  <div style={{marginBottom:10}}>
                    <label style={S.label}>Ink Color</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {CONFIG.LORCANA_INK_COLORS.map(c=>{
                        const inkColors = {Amber:"#f59e0b",Amethyst:"#8b5cf6",Emerald:"#10b981",Ruby:"#ef4444",Sapphire:"#3b82f6",Steel:"#6b7280"};
                        const active = form.LorcanaInkColor===c;
                        return (
                          <button key={c} style={{
                            padding:"5px 10px",borderRadius:20,fontSize:12,fontWeight:500,
                            border:`0.5px solid ${inkColors[c]||T.border}`,cursor:"pointer",
                            background:active?`${inkColors[c]}22`:"transparent",
                            color:active?inkColors[c]:T.textMuted,
                          }} onClick={()=>set("LorcanaInkColor",c)}>{c}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={{marginBottom:10}}>
                    <label style={S.label}>Foil Type</label>
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      {CONFIG.LORCANA_FOIL_TYPES.map(f=>{
                        const active = (form.LorcanaFoilType||"Normal")===f;
                        const foilColors = {Normal:"#8899bb","Cold Foil":"#3b82f6",Holofoil:"#fbbf24"};
                        return (
                          <button key={f} style={{
                            padding:"6px 12px",borderRadius:20,fontSize:12,fontWeight:600,
                            border:`0.5px solid ${foilColors[f]}`,cursor:"pointer",
                            background:active?`${foilColors[f]}22`:"transparent",
                            color:active?foilColors[f]:T.textMuted,
                          }} onClick={()=>set("LorcanaFoilType",f)}>
                            {f==="Holofoil"?"✨ ":f==="Cold Foil"?"❄️ ":""}{f}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div style={S.row}>
                    <div style={S.col}>
                      <label style={S.label}>Character Version</label>
                      <select style={S.select} value={form.LorcanaCharacterVersion||""}
                        onChange={e=>set("LorcanaCharacterVersion",e.target.value)}>
                        <option value="">Select version…</option>
                        {CONFIG.LORCANA_CHARACTER_VERSIONS.map(v=><option key={v}>{v}</option>)}
                      </select>
                    </div>
                    <div style={S.col}>
                      <label style={S.label}>Property / Franchise</label>
                      <select style={S.select} value={form.LorcanaProperty||""}
                        onChange={e=>set("LorcanaProperty",e.target.value)}>
                        <option value="">Select property…</option>
                        {CONFIG.LORCANA_PROPERTIES.map(p=><option key={p}>{p}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <div style={{...S.infoBox,marginBottom:12,borderColor:T.primary}}>
                <div style={{fontSize:12,fontWeight:700,color:T.pink,marginBottom:8}}>
                  📋 Out for Grading
                </div>
                <div style={{marginBottom:8}}>
                  <label style={S.label}>Grading Company</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {["","PSA","Beckett","CGC","TAG","Other"].map(co=>(
                      <button key={co||"none"} style={{...S.chip(form.GradingCompany===co),
                        padding:"6px 10px",fontSize:12}}
                        onClick={()=>set("GradingCompany",co)}>
                        {co||"Not Submitted"}
                      </button>
                    ))}
                  </div>
                </div>
                {form.GradingCompany && (
                  <>
                    <div style={S.row}>
                      <div style={S.col}>
                        <label style={S.label}>Sent Date</label>
                        <input style={S.input} type="date" value={form.GradingSentDate||""}
                          onChange={e=>set("GradingSentDate",e.target.value)} />
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>Expected Return</label>
                        <input style={S.input} type="date" value={form.GradingReturnDate||""}
                          onChange={e=>set("GradingReturnDate",e.target.value)} />
                      </div>
                    </div>
                    <div style={{marginTop:8}}>
                      <div style={S.row}>
                        <div style={S.col}>
                          <label style={S.label}>Cost of Grading ($) <span style={{color:T.textFaint}}>(optional)</span></label>
                          <input style={S.input} type="number" value={form.GradingCost||""}
                            onChange={e=>set("GradingCost",e.target.value)}
                            placeholder="e.g. 25" />
                        </div>
                        <div style={S.col}>
                          <label style={S.label}>Estimated Value After Grading ($)</label>
                          <input style={S.input} type="number" value={form.GradingEstimatedValue||""}
                            onChange={e=>set("GradingEstimatedValue",e.target.value)}
                            placeholder="e.g. 250" />
                        </div>
                      </div>
                      {form.GradingEstimatedValue && form.AcquisitionCost && (
                        <div style={{fontSize:11,color:T.green,marginTop:4,fontFamily:"'DM Mono',monospace"}}>
                          Est. ROI: +{(((parseFloat(form.GradingEstimatedValue)-(parseFloat(form.AcquisitionCost)+(parseFloat(form.GradingCost)||0)))/(parseFloat(form.AcquisitionCost)+(parseFloat(form.GradingCost)||0)))*100).toFixed(0)}%
                          · Net: {fmt(parseFloat(form.GradingEstimatedValue)-(parseFloat(form.AcquisitionCost)+(parseFloat(form.GradingCost)||0)))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          </div>
          <div style={{minWidth:0}}>

          <div style={{marginBottom:12}}>
            <label style={S.label}>Acquisition Source</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {ALL_SOURCES.map(s=>(
                <button key={s} style={S.chip(form.Source===s)}
                  onClick={()=>set("Source",s)}>{s}</button>
              ))}
              {activeShows.length > 0 && (
                <button style={S.chip(!!form.ShowID)}
                  onClick={()=>{
                    if (form.ShowID) {
                      setForm(f=>({...f, ShowID:"", ShowName:""}));
                    } else {
                      const first = activeShows[0];
                      setForm(f=>({...f, ShowID:first.ID, ShowName:first.Name}));
                    }
                  }}>
                  🎪 Card Show
                </button>
              )}
            </div>
            {form.ShowID && activeShows.length > 1 && (
              <select style={{...S.select, marginTop:8}} value={form.ShowID} onChange={e=>{
                const show = shows.find(s=>s.ID===e.target.value);
                setForm(f=>({...f, ShowID:e.target.value, ShowName:show?.Name||""}));
              }}>
                {activeShows.map(s=><option key={s.ID} value={s.ID}>{s.Name}</option>)}
              </select>
            )}
          </div>

          <div style={{marginBottom:12}}>
            <label style={S.label}>Acquisition Date</label>
            <input style={S.input} type="date" value={form.AcquisitionDate} onChange={e=>set("AcquisitionDate",e.target.value)} />
          </div>

          {isEdit && isEnterprise && locations.length > 0 && (
            <div style={{marginBottom:12}}>
              <label style={S.label}>Location</label>
              <select style={S.select} value={form.LocationID || ""} onChange={e=>set("LocationID", e.target.value)}>
                {locations.map(l => (
                  <option key={l.ID} value={l.ID}>{l.StoreName} · #{l.StoreNumber}</option>
                ))}
              </select>
            </div>
          )}

          {(
            <div style={{...S.infoBox,marginBottom:12}}>
              <div style={{fontSize:12,color:T.primary,fontWeight:500,marginBottom:10}}>Assign to Lot</div>
              <select style={S.select} value={form.LotID} onChange={e=>handleLotSelect(e.target.value)}>
                <option value="">No lot / standalone</option>
                {openLots.map(l=>{
                  const isP = l.CostMethod==="proportional" && l.AllocationRatio>0;
                  const hasEven = l.CostPerCard>0;
                  const desc = isP
                    ? `${(l.AllocationRatio*100).toFixed(1)}% of MV`
                    : hasEven
                    ? `${fmt(l.CostPerCard)}/card`
                    : "enter MV to calc cost";
                  return (
                    <option key={l.ID} value={l.ID}>
                      {l.Name} — {desc}
                    </option>
                  );
                })}
              </select>
              <button style={{...S.btn("ghost"),marginTop:8,fontSize:12,padding:"6px 12px"}} onClick={()=>setShowLotDrawer(true)}>
                + Create New Lot
              </button>
              {form.LotName && (
                <div style={{marginTop:8}}>
                  <div style={{fontSize:12,color:T.green}}>Lot: {form.LotName} · Item #{form.LotItemNumber}</div>
                  {(() => {
                    const lot = lots.find(l=>l.ID===form.LotID);
                    if (lot?.CostMethod==="proportional" && lot.AllocationRatio>0) {
                      return <div style={{fontSize:11,color:T.green,marginTop:2}}>
                        Enter market value below → cost auto-calculates at {(lot.AllocationRatio*100).toFixed(1)}% of MV
                      </div>;
                    } else if (lot && (!lot.CostMethod || lot.CostMethod==="even") && !lot.CostPerCard) {
                      return <div style={{fontSize:11,color:T.primary,marginTop:2}}>
                        Open this lot and tap Edit to set Total Market Value
                      </div>;
                    }
                    return null;
                  })()}
                </div>
              )}
            </div>
          )}

          {(() => {
            const isAutoCalc =
              (form.LotID && lots.find(l=>l.ID===form.LotID)?.CostMethod==="proportional");
            return (
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>
                Acquisition Cost ($)
                {isAutoCalc && <span style={{color:T.green,marginLeft:4}}>auto</span>}
              </label>
              <input style={{...S.input, opacity:isAutoCalc?0.7:1}}
                type="number" min="0" step="0.01" value={form.AcquisitionCost}
                readOnly={isAutoCalc}
                onChange={e=>{
                  if (isAutoCalc) return;
                  const c=e.target.value;
                  const ms=c?minSell(parseFloat(c)):"";
                  setForm(f=>({...f,AcquisitionCost:c,MinSellPrice:ms,ListPrice:f.ListPrice||ms}));
                }} placeholder={isAutoCalc?"Auto-calculated":"0.00"} />
            </div>
            <div style={S.col}>
              <label style={S.label}>List Price ($)</label>
              {item?.Status === "Reserved" ? (
                <>
                  <div style={{...S.input, color:T.textMuted, display:"flex", alignItems:"center", gap:6}}>
                    🔒 ${parseFloat(item.ListPrice || 0).toFixed(2)}
                  </div>
                  <div style={{fontSize:11, color:T.textFaint, marginTop:6}}>
                    Reserved for {item.ShowName || "a show"} — adjust price from Show Mode.
                  </div>
                </>
              ) : (
                <input style={{...S.input,borderColor:belowMin?T.red:undefined}}
                  type="number" min="0" step="0.01" value={form.ListPrice}
                  onChange={e=>{
                    const lp = e.target.value;
                    const newMin = lp && parseFloat(lp) > 0
                      ? Math.round(parseFloat(lp) * 0.9)
                      : form.MinSellPrice;
                    setForm(f=>({...f, ListPrice:lp, MinSellPrice:newMin}));
                  }} placeholder="0.00" />
              )}
            </div>
          </div>
            );
          })()}

          {form.MinSellPrice>0 && (
            <div style={{fontSize:12,color:T.primary,marginBottom:10}}>
              Min sell: ${form.MinSellPrice} (25% markup)
              {belowMin && <span style={{color:T.red}}> ⚠ Below minimum!</span>}
            </div>
          )}

          {form.AcquisitionCost && form.ListPrice && roiVal !== null && (
            <div style={{fontSize:12,color:roiVal===Infinity||roiVal>=0?T.green:T.red,marginBottom:12}}>
              ROI: {roiVal===Infinity ? "∞ (free card, all profit)" : pct(roiVal)} · Break-even: {fmt(parseFloat(form.AcquisitionCost)*1.15)}
            </div>
          )}

          <div style={{marginBottom:12}}>
            <label style={S.label}>
              Market Value at Acquisition ($){form.LotID ? " *" : ""}
            </label>
            {!form.LotID && (
              <div style={{fontSize:11, color:T.textFaint, marginBottom:4}}>
                Optional for individual buys. Required for cards added to a Lot.
              </div>
            )}
            <input style={S.input} type="number" min="0" step="0.01" value={form.MarketValueAtAcquisition||""}
              onChange={e=>{
                const mv = parseFloat(e.target.value)||0;

                if (form.LotID) {
                  const lot = lots.find(l=>l.ID===form.LotID);
                  if (lot?.CostMethod==="proportional" && lot.AllocationRatio && mv>0) {
                    const ac = +(mv * lot.AllocationRatio).toFixed(2);
                    const ms = minSell(ac);
                    setForm(f=>({...f,
                      MarketValueAtAcquisition: e.target.value,
                      AcquisitionCost: ac.toString(),
                      MinSellPrice: ms,
                      ListPrice: ms.toString(),
                    }));
                    return;
                  }
                }

                const ac = parseFloat(form.AcquisitionCost)||0;
                const ms = ac>0 ? minSell(ac) : 0;
                setForm(f=>({...f,
                  MarketValueAtAcquisition: e.target.value,
                  MinSellPrice: ms,
                }));
              }}
              placeholder="What it was selling for when you bought it" />
            {form.MarketValueAtAcquisition && form.AcquisitionCost && (()=>{
              const mv=parseFloat(form.MarketValueAtAcquisition);
              const ac=parseFloat(form.AcquisitionCost);
              if(!mv||!ac) return null;
              const diff=((mv-ac)/mv)*100;
              const label=diff>0?`Bought ${diff.toFixed(1)}% below market`:diff<0?`Paid ${Math.abs(diff).toFixed(1)}% above market`:"Bought at market price";
              const color=diff>0?T.green:diff<0?T.red:T.textFaint;
              return <div style={{fontSize:12,color,marginTop:4}}>{label}</div>;
            })()}
          </div>

          {/* Online ─────────────────────────────────────────────────────── */}
          <div style={{marginBottom:12, padding:12, borderRadius:10,
            display:"flex", justifyContent:"space-between", alignItems:"center",
            border:`0.5px solid ${form.IsOnline?T.chipBorder:T.border}`,
            background:form.IsOnline?T.blueBg:"transparent"}}>
            <span style={S.label}>List Online</span>
            <button type="button" onClick={()=>set("IsOnline", !form.IsOnline)}
              style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
                background: form.IsOnline ? "#4ade80" : T.surface, position:"relative",
                border:`0.5px solid ${form.IsOnline ? "#4ade80" : T.border}`}}>
              <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
                position:"absolute", top:2, left: form.IsOnline ? 21 : 3, transition:"left 0.15s"}} />
            </button>
          </div>

          {/* Consignment ───────────────────────────────────────────────── */}
          {isPaid && (
          <div style={{marginBottom:20, padding:12, borderRadius:10,
            border:`0.5px solid ${form.IsConsignment?T.chipBorder:T.border}`,
            background:form.IsConsignment?T.blueBg:"transparent"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
              marginBottom:form.IsConsignment?12:0}}>
              <span style={S.label}>Consignment Item</span>
              <button type="button" onClick={()=>set("IsConsignment", !form.IsConsignment)}
                style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
                  background: form.IsConsignment ? "#4ade80" : T.surface, position:"relative",
                  border:`0.5px solid ${form.IsConsignment ? "#4ade80" : T.border}`}}>
                <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
                  position:"absolute", top:2, left: form.IsConsignment ? 21 : 3, transition:"left 0.15s"}} />
              </button>
            </div>

            {form.IsConsignment && (<>
              {(() => {
                const minVal = parseFloat(vendor?.consign_settings?.min_card_value) || 0;
                const listP  = parseFloat(form.ListPrice) || 0;
                if (minVal > 0 && listP < minVal && listP > 0) {
                  return (
                    <div style={{fontSize:11, color:T.red, marginBottom:10,
                      padding:"6px 8px", background:T.redBg,
                      borderRadius:6, border:`0.5px solid ${T.red}`}}>
                      List price is below your minimum consignment value of ${minVal}.
                    </div>
                  );
                }
                return null;
              })()}

              <div style={{marginBottom:12}}>
                <label style={S.label}>Consignor *</label>
                <select style={S.select} value={form.ConsignorID||""}
                  onChange={e=>set("ConsignorID", e.target.value)}>
                  <option value="">Select consignor…</option>
                  {(consignors||[]).map(c=>(
                    <option key={c.ID} value={c.ID}>{c.FullName}</option>
                  ))}
                </select>
                {!consignors?.length && (
                  <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
                    No consignors yet — add one from the CRM tab.
                  </div>
                )}
              </div>

              {(() => {
                const rate   = parseFloat(vendor?.consign_settings?.commission_rate) || 0;
                const listP  = parseFloat(form.ListPrice) || 0;
                if (rate > 0 && listP > 0) {
                  const profit   = (listP * rate / 100);
                  const consignorPayout = listP - profit;
                  return (
                    <div style={{
                      background:T.surface2, border:`0.5px solid ${T.border}`,
                      borderRadius:8, padding:"10px 12px", marginBottom:12}}>
                      <div style={{fontSize:11, color:T.textMuted, marginBottom:6,
                        textTransform:"uppercase", letterSpacing:"0.05em"}}>
                        Commission Preview ({rate}%)
                      </div>
                      <div style={{display:"flex", justifyContent:"space-between", marginBottom:4}}>
                        <span style={{fontSize:13, color:T.textMuted}}>Your commission</span>
                        <span style={{fontSize:15, fontWeight:700, color:T.green}}>
                          ${profit.toFixed(2)}
                        </span>
                      </div>
                      <div style={{display:"flex", justifyContent:"space-between"}}>
                        <span style={{fontSize:13, color:T.textMuted}}>Consignor receives</span>
                        <span style={{fontSize:14, fontWeight:600, color:T.text}}>
                          ${consignorPayout.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              <div style={S.row}>
                <div style={S.col}>
                  <label style={S.label}>Min Accept Price ($)</label>
                  <input style={S.input} type="number" value={form.MinAcceptPrice||""}
                    onChange={e=>set("MinAcceptPrice", e.target.value)}
                    placeholder="Lowest price consignor will accept" />
                </div>
                <div style={S.col}>
                  <label style={S.label}>Trades Accepted?</label>
                  <select style={S.select} value={form.TradesAccepted ? "yes" : "no"}
                    onChange={e=>set("TradesAccepted", e.target.value==="yes")}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>
              </div>

              {(form.Status === "Sold" || form.ConsignPayoutStatus) && (
                <div style={{marginTop:12}}>
                  <label style={S.label}>Payout Status</label>
                  <select style={{...S.select,
                    borderColor: form.ConsignPayoutStatus==="paid_out" ? T.green
                      : form.ConsignPayoutStatus==="sold_awaiting_payout" ? T.primary
                      : form.ConsignPayoutStatus==="returned_unsold" ? T.border : T.border}}
                    value={form.ConsignPayoutStatus||""}
                    onChange={e=>set("ConsignPayoutStatus", e.target.value||null)}>
                    <option value="">— Not set —</option>
                    <option value="sold_awaiting_payout">💰 Sold — Awaiting Payout</option>
                    <option value="paid_out">✅ Paid Out</option>
                    <option value="returned_unsold">📦 Returned to Owner Unsold</option>
                  </select>
                </div>
              )}
            </>)}
          </div>
          )}

          <div style={{marginBottom:20}}>
            <label style={S.label}>Notes <span style={{color:T.textFaint}}>(optional)</span></label>
            {notesPhoto && (
              <div style={{position:"relative", marginBottom:8, display:"inline-block"}}>
                <img src={notesPhoto} alt="Transaction"
                  style={{width:120, height:120, objectFit:"cover", borderRadius:8,
                    border:`1px solid ${T.border}`, cursor:"pointer", display:"block"}}
                  onClick={()=>window.open(notesPhoto,"_blank")} />
                <button
                  onClick={()=>setNotesPhoto(null)}
                  title="Remove photo"
                  style={{position:"absolute", top:-8, right:-8, width:24, height:24,
                    borderRadius:"50%", border:"none", background:T.red, color:"#fff",
                    fontSize:13, cursor:"pointer", lineHeight:1}}>
                  ✕
                </button>
              </div>
            )}
            <input style={S.input} value={form.Notes||""} onChange={e=>set("Notes",e.target.value)} placeholder="Any notes..." />
          </div>

          {isPaid && isEdit && buyers.length > 0 && (
            <div style={{marginBottom:20}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
                <span style={S.label}>Buyers Request</span>
                <button type="button"
                  onClick={async()=>{
                    const checked = !assignBuyerChecked;
                    setAssignBuyerChecked(checked);
                    if (!checked && item?.ID) {
                      setSelectedBuyerId("");
                      set("AssignedBuyerID", "");
                      set("BuyerNotifiedAt", "");
                      try { await onSave(item.ID, { AssignedBuyerID: "", BuyerNotifiedAt: "" }); }
                      catch(e) { toast("Failed to clear assignment: " + e.message, "error"); }
                    }
                  }}
                  style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
                    background: assignBuyerChecked ? "#4ade80" : T.surface, position:"relative",
                    border:`0.5px solid ${assignBuyerChecked ? "#4ade80" : T.border}`}}>
                  <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
                    position:"absolute", top:2, left: assignBuyerChecked ? 21 : 3, transition:"left 0.15s"}} />
                </button>
              </div>

              {assignBuyerChecked && (
                <select style={S.select} value={selectedBuyerId}
                  onChange={e=>{
                    const buyerId = e.target.value;
                    setSelectedBuyerId(buyerId);
                    if (buyerId) setShowEmailConfirm(true);
                  }}>
                  <option value="">Select a buyer...</option>
                  {buyers.map(b=>(
                    <option key={b.ID} value={b.ID}>
                      {b.FullName}{(b.Phone||b.Email) ? ` (${[b.Phone,b.Email].filter(Boolean).join(", ")})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          </div>
          </div>

          <div style={{display:"flex", justifyContent:"flex-end", gap:12, marginTop:22,
            paddingTop:18, borderTop:`1px solid ${T.borderSoft}`}}>
            <button style={{...S.btn("ghost"), padding:"12px 24px"}} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button style={{...S.btn("primary"), padding:"12px 24px"}} onClick={save}
              disabled={saving}>
              {saving?"Saving…":isEdit?"Save Changes":"Add to Inventory"}
            </button>
          </div>
        </div>
      </div>

      {showEmailConfirm && (() => {
        const buyer = buyers.find(b => b.ID === selectedBuyerId);
        if (!buyer) return null;
        const finishAssignment = async (sendEmail) => {
          setNotifying(true);
          try {
            if (sendEmail) {
              await notifyBuyerCardFound({
                buyerEmail: buyer.Email,
                buyerName: buyer.FullName,
                cardName: form.CardName || "your requested card",
                businessName: vendor?.business_name || "",
              });
            }
            const notifiedAt = sendEmail ? new Date().toISOString() : "";
            set("AssignedBuyerID", buyer.ID);
            set("BuyerNotifiedAt", notifiedAt);
            if (item?.ID) {
              await onSave(item.ID, { AssignedBuyerID: buyer.ID, BuyerNotifiedAt: notifiedAt });
            }
            toast(sendEmail ? `Email sent to ${buyer.FullName}` : `Assigned to ${buyer.FullName}`, "success");
            setShowEmailConfirm(false);
          } catch(e) {
            toast("Failed: " + e.message, "error");
          }
          setNotifying(false);
        };
        return (
          <div style={{...S.overlay, zIndex:10003}}>
            <div style={S.drawer}>
              <div style={S.h2}>Notify {buyer.FullName}?</div>
              <div style={{fontSize:13, color:T.textMuted, marginBottom:20, lineHeight:1.6}}>
                Send an email letting {buyer.FullName} know you've acquired their requested card
                {form.CardName ? ` (${form.CardName})` : ""}, and to contact you at{" "}
                <strong>{vendor?.email || "your email"}</strong> for pickup instructions?
              </div>
              {!buyer.Email && (
                <div style={{...S.infoBox, marginBottom:16, borderColor:T.red}}>
                  <div style={{fontSize:12, color:T.red}}>
                    This buyer has no email on file. An email can't be sent, but you can still assign this card to them.
                  </div>
                </div>
              )}
              <button style={{...S.btn("primary"), width:"100%", padding:14, marginBottom:10}}
                disabled={notifying || !buyer.Email}
                onClick={()=>finishAssignment(true)}>
                {notifying ? "Sending…" : "Yes, Send Email"}
              </button>
              <button style={{...S.btn("ghost"), width:"100%", padding:12}}
                disabled={notifying}
                onClick={()=>finishAssignment(false)}>
                Do Not Send Email
              </button>
            </div>
          </div>
        );
      })()}
      {showLotDrawer && <LotDrawer onClose={()=>setShowLotDrawer(false)} onSave={handleNewLot} />}
    </>
  );
}

// ─── SELL DRAWER ──────────────────────────────────────────────────────────────
function SellDrawer({ item, shows, vendor, onClose, onSave }) {
  const { S, T, rates } = useTheme();
  const [mode, setMode]           = useState("sell");

  const tradeRate    = rates?.tradeCreditRate ?? 0.80;
  const autoCredit   = item.ListPrice ? Math.round(parseFloat(item.ListPrice) * tradeRate) : "";

  const [form, setForm] = useState({
    SoldPrice:   item.ListPrice||"",
    SoldChannel: "eBay",
    BuyerGender: "Male",
    SoldDate:    localDate(),
    TradeCredit: autoCredit,
    ShowID:      "",
    ShowName:    "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const completedShows = (shows||[]).filter(s =>
    s.Status === "Completed" || s.Status === "Archived" || s.Status === "Active"
  ).sort((a,b) => (b.StartDate||"").localeCompare(a.StartDate||""));

  const handleSetMode = (m) => {
    setMode(m);
    if (m === "trade" && !form.TradeCredit && item.ListPrice) {
      set("TradeCredit", Math.round(parseFloat(item.ListPrice) * tradeRate).toString());
    }
  };

  const profit   = (parseFloat(form.SoldPrice)||0)-(item.AcquisitionCost||0);
  const belowMin = item.MinSellPrice>0 && parseFloat(form.SoldPrice)<item.MinSellPrice;

  const save = async () => {
    setSaving(true);
    const tradeGain = mode === "trade"
      ? Math.max(0, (item.MarketValueAtAcquisition || item.ListPrice || 0) - (item.AcquisitionCost || 0))
      : 0;
    await onSave(item.ID, {
      Status:      "Sold",
      SoldPrice:   mode==="trade"?0:parseFloat(form.SoldPrice)||0,
      SoldChannel: mode==="trade"?"Trade":form.SoldChannel,
      BuyerGender: form.BuyerGender,
      SoldDate:    form.SoldDate,
      TradeCredit: mode==="trade"?parseFloat(form.TradeCredit)||0:0,
      TradeGain:   tradeGain || null,
      ShowID:      form.SoldChannel==="Local Show" ? form.ShowID  : (item.ShowID||""),
      ShowName:    form.SoldChannel==="Local Show" ? form.ShowName: (item.ShowName||""),
    });
    setSaving(false);
    onClose();
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{fontSize:16,fontWeight:600}}>Mark as Sold / Traded</div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:14,color:"#aaa",marginBottom:16}}>
          {item.CardName} · {item.Condition}
          {item.LotName && <span style={{color:"#666",fontSize:12}}> · {item.LotName}</span>}
          {item.ShowName && <span style={{color:"var(--cv-pink,#c0387a)",fontSize:12}}> · {item.ShowName}</span>}
        </div>

        <div style={{display:"flex",gap:6,marginBottom:16}}>
          {["sell","trade"].map(m=>(
            <button key={m} style={S.chip(mode===m)} onClick={()=>handleSetMode(m)}>
              {m==="sell"?"Cash Sale":"Trade Out"}
            </button>
          ))}
        </div>

        {mode==="sell" ? (
          <>
            {item.MinSellPrice>0 && <div style={{fontSize:12,color:"#88bdf2",marginBottom:10}}>Min sell: ${item.MinSellPrice}</div>}
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Sold For ($)</label>
                <input style={{...S.input,borderColor:belowMin?"#f87171":undefined}}
                  type="number" value={form.SoldPrice} onChange={e=>set("SoldPrice",e.target.value)} placeholder="0.00" />
                {belowMin && <div style={{fontSize:11,color:"#f87171",marginTop:3}}>Below min sell price!</div>}
              </div>
              <div style={S.col}>
                <label style={S.label}>Date</label>
                <input style={S.input} type="date" value={form.SoldDate} onChange={e=>set("SoldDate",e.target.value)} />
              </div>
            </div>
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Channel</label>
                <select style={S.select} value={form.SoldChannel}
                  onChange={e=>{
                    set("SoldChannel", e.target.value);
                    if (e.target.value !== "Local Show") {
                      set("ShowID",""); set("ShowName","");
                    }
                  }}>
                  {CHANNELS.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div style={S.col}>
                <label style={S.label}>Buyer Gender</label>
                <select style={S.select} value={form.BuyerGender} onChange={e=>set("BuyerGender",e.target.value)}>
                  {GENDERS.map(g=><option key={g}>{g}</option>)}
                </select>
              </div>
            </div>

            {form.SoldChannel === "Local Show" && (
              <div style={{marginBottom:12}}>
                <label style={S.label}>Which Show?</label>
                {completedShows.length > 0 ? (
                  <select style={S.select} value={form.ShowID}
                    onChange={e=>{
                      const show = completedShows.find(s=>s.ID===e.target.value);
                      set("ShowID", e.target.value);
                      set("ShowName", show?.Name||"");
                      if (show?.EndDate || show?.StartDate) {
                        set("SoldDate", show.EndDate||show.StartDate);
                      }
                    }}>
                    <option value="">Select a show...</option>
                    {completedShows.map(s=>(
                      <option key={s.ID} value={s.ID}>
                        {s.Name}{s.StartDate ? ` · ${s.StartDate}` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div style={{fontSize:12,color:T.textMuted,padding:"8px 0"}}>
                    No completed shows yet. Complete a show first to link sold cards.
                  </div>
                )}
                {form.ShowName && (
                  <div style={{fontSize:11,color:"var(--cv-pink,#c0387a)",marginTop:4}}>
                    Linked to: {form.ShowName}
                  </div>
                )}
              </div>
            )}
            {form.SoldPrice && (
              <div style={{...S.infoBox,marginBottom:12}}>
                <div style={{fontSize:11,color:T.textMuted,textTransform:"uppercase",
                  letterSpacing:"0.05em",fontFamily:"'DM Mono',monospace",marginBottom:8}}>
                  Negotiation breakdown
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
                  <div>
                    <div style={{fontSize:9,color:T.textFaint,marginBottom:1}}>Acquired For</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#f87171",
                      fontFamily:"'DM Mono',monospace"}}>
                      {item.AcquisitionCost ? fmt(item.AcquisitionCost) : "N/A"}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.textFaint,marginBottom:1}}>Mkt at Buy</div>
                    <div style={{fontSize:13,fontWeight:700,color:"#88bdf2",
                      fontFamily:"'DM Mono',monospace"}}>
                      {item.MarketValueAtAcquisition ? fmt(item.MarketValueAtAcquisition) : "N/A"}
                    </div>
                  </div>
                </div>
                <div style={{borderTop:`0.5px solid ${T.border}`,paddingTop:8,
                  display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                  <div>
                    <div style={{fontSize:9,color:T.textFaint,marginBottom:1}}>Profit / Loss</div>
                    <div style={{fontSize:15,fontWeight:700,
                      color:profit>=0?"#4ade80":"#f87171",
                      fontFamily:"'DM Mono',monospace"}}>
                      {item.AcquisitionCost
                        ? (profit>=0?"+":"")+fmt(profit)
                        : "N/A"}
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:9,color:T.textFaint,marginBottom:1}}>ROI</div>
                    <div style={{fontSize:15,fontWeight:700,
                      color:profit>=0?"#4ade80":"#f87171",
                      fontFamily:"'DM Mono',monospace"}}>
                      {(() => {
                        const r = roi(item.AcquisitionCost, form.SoldPrice);
                        if (r === null) return "N/A";
                        if (r === Infinity) return "∞";
                        return (r>=0?"+":"")+pct(r);
                      })()}
                    </div>
                  </div>
                </div>
                {belowMin && (
                  <div style={{marginTop:8,fontSize:11,color:"#f87171",fontWeight:600}}>
                    ⚠ Below minimum sell price of ${item.MinSellPrice}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{...S.infoBox,marginBottom:12}}>
              <div style={{fontSize:12,color:"#3b82f6",fontWeight:500,marginBottom:4}}>
                Trade Out — credit at {Math.round(tradeRate*100)}% of list price
              </div>
              <div style={{fontSize:11,color:T.textMuted}}>
                Based on your trade credit rate in Settings. Adjust below if needed.
              </div>
            </div>
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Trade Credit Value ($)</label>
                <input style={S.input} type="number" value={form.TradeCredit}
                  onChange={e=>set("TradeCredit",e.target.value)} placeholder="0.00" />
                {item.ListPrice && (
                  <button style={{...S.btn("ghost"),marginTop:6,fontSize:11,padding:"4px 8px"}}
                    onClick={()=>set("TradeCredit", Math.round(parseFloat(item.ListPrice)*tradeRate).toString())}>
                    Reset to {Math.round(tradeRate*100)}% of list (${Math.round(parseFloat(item.ListPrice||0)*tradeRate)})
                  </button>
                )}
              </div>
              <div style={S.col}>
                <label style={S.label}>Date</label>
                <input style={S.input} type="date" value={form.SoldDate} onChange={e=>set("SoldDate",e.target.value)} />
              </div>
            </div>
          </>
        )}

        <button style={{...S.btn("primary"),width:"100%"}} onClick={save} disabled={saving}>
          {saving?"Saving…":`Confirm ${mode==="sell"?"Sale":"Trade"}`}
        </button>
      </div>
    </div>
  );
}

// ─── BULK EDIT DRAWER ─────────────────────────────────────────────────────────
function BulkEditDrawer({ inventory, lots, isStaff, onClose, onBulkSave }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [filters, setFilters] = useState({ Status:"", Online:"", Source:"", Condition:"", Set:"", LotID:"", dateFrom:"", dateTo:"" });
  const [adj, setAdj]         = useState({ type:"pct", value:"" });
  const [field, setField]     = useState("ListPrice");
  const [onlineVal, setOnlineVal] = useState(null);
  const [saving, setSaving]   = useState(false);
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const filtered = useMemo(()=>inventory.filter(item=>{
    if (item.Status==="Sold"||item.Status==="Deleted") return false;
    if (filters.Status) {
      const statuses = filters.Status.split(",");
      if (!statuses.includes(item.Status)) return false;
    }
    if (filters.Source    && item.Source!==filters.Source)          return false;
    if (filters.Online === "true"  && !item.IsOnline) return false;
    if (filters.Online === "false" &&  item.IsOnline) return false;
    if (filters.Condition && item.Condition!==filters.Condition)    return false;
    if (filters.LotID     && item.LotID!==filters.LotID)            return false;
    if (filters.Set       && !item.Set?.toLowerCase().includes(filters.Set.toLowerCase())) return false;
    if (filters.dateFrom  && item.AcquisitionDate<filters.dateFrom) return false;
    if (filters.dateTo    && item.AcquisitionDate>filters.dateTo)   return false;
    return true;
  }),[inventory,filters]);

  const applicable = useMemo(() => {
    if (field !== "ListPrice") return filtered;
    return filtered.filter(item => item.Status !== "Reserved");
  }, [filtered, field]);
  const skippedForShow = filtered.length - applicable.length;

  const preview = cur => {
    if (adj.type==="round_nearest") return Math.round(cur);
    const v=parseFloat(adj.value); if(!v) return cur;
    if(adj.type==="pct")       return +(cur*(1+v/100)).toFixed(2);
    if(adj.type==="fixed_add") return +(cur+v).toFixed(2);
    if(adj.type==="fixed_set") return +v.toFixed(2);
    return cur;
  };

  const apply = async () => {
    setSaving(true);
    if (field === "IsOnline") {
      for (const item of filtered) {
        await onBulkSave(item.ID, { IsOnline: onlineVal });
      }
    } else {
      for (const item of applicable) {
        await onBulkSave(item.ID, { [field]: preview(parseFloat(item[field])||0) });
      }
      if (skippedForShow > 0) {
        toast(
          `Updated ${applicable.length} card${applicable.length!==1?"s":""} · skipped ${skippedForShow} reserved for a show — adjust ${skippedForShow!==1?"those":"that one"} from Show Mode instead`,
          "alert"
        );
      }
    }
    setSaving(false);
    onClose();
  };

  return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:16,fontWeight:600}}>Bulk Edit</div>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={onClose}>✕</button>
        </div>

        <div style={S.h2}>Filters</div>

        <div style={{marginBottom:12}}>
          <label style={S.label}>Status</label>
          <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            {[["","All"],["In Stock","In Stock"],["Reserved","In Show"],["In Stock,Reserved","In Stock + In Show"]].map(([val,label])=>(
              <button key={val}
                style={{...S.chip(filters.Status===val), fontSize:12, padding:"6px 10px"}}
                onClick={()=>setF("Status", val)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{marginBottom:12}}>
          <label style={S.label}>Online Listing</label>
          <div style={{display:"flex", gap:6, flexWrap:"wrap"}}>
            {[["","All"],["true","Online Now"],["false","Not Online"]].map(([val,label])=>(
              <button key={val}
                style={{...S.chip(filters.Online===val), fontSize:12, padding:"6px 10px"}}
                onClick={()=>setF("Online", val)}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Source</label>
            <select style={S.select} value={filters.Source} onChange={e=>setF("Source",e.target.value)}>
              <option value="">All</option>{ALL_SOURCES.map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
          <div style={S.col}>
            <label style={S.label}>Condition</label>
            <select style={S.select} value={filters.Condition} onChange={e=>setF("Condition",e.target.value)}>
              <option value="">All</option>{CONDITIONS.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Lot</label>
          <select style={S.select} value={filters.LotID} onChange={e=>setF("LotID",e.target.value)}>
            <option value="">All lots</option>{lots.map(l=><option key={l.ID} value={l.ID}>{l.Name}</option>)}
          </select>
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Set contains</label>
          <input style={S.input} value={filters.Set} onChange={e=>setF("Set",e.target.value)} placeholder="e.g. Evolving Skies" />
        </div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Acquired from</label>
            <input style={S.input} type="date" value={filters.dateFrom} onChange={e=>setF("dateFrom",e.target.value)} />
          </div>
          <div style={S.col}>
            <label style={S.label}>Acquired to</label>
            <input style={S.input} type="date" value={filters.dateTo} onChange={e=>setF("dateTo",e.target.value)} />
          </div>
        </div>

        <div style={S.divider}/>
        <div style={S.h2}>Adjustment</div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Apply to</label>
          <select style={S.select} value={field} onChange={e=>setField(e.target.value)}>
            <option value="ListPrice">List Price</option>
            <option value="AcquisitionCost">Acquisition Cost</option>
            <option value="IsOnline">Online Listing</option>
          </select>
        </div>

        {field === "IsOnline" ? (
          <div style={{marginBottom:12}}>
            <label style={S.label}>Set all filtered cards to</label>
            <div style={{display:"flex", gap:8}}>
              <button
                style={{...S.chip(onlineVal===true), flex:1, textAlign:"center", padding:"10px 0", fontSize:13}}
                onClick={()=>setOnlineVal(true)}>
                🌐 List Online
              </button>
              <button
                style={{...S.chip(onlineVal===false), flex:1, textAlign:"center", padding:"10px 0", fontSize:13}}
                onClick={()=>setOnlineVal(false)}>
                Remove from Online
              </button>
            </div>
          </div>
        ) : (
          <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Type</label>
            <select style={S.select} value={adj.type} onChange={e=>setAdj(a=>({...a,type:e.target.value}))}>
              <option value="pct">% Change</option>
              <option value="fixed_add">Add Fixed ($)</option>
              <option value="fixed_set">Set to Price</option>
              <option value="round_nearest">Rounded to nearest...</option>
            </select>
          </div>
          <div style={S.col}>
            {adj.type === "round_nearest" ? (
              <>
                <label style={S.label}>Rounds to</label>
                <div style={{...S.input, display:"flex", alignItems:"center", color:T.textMuted}}>
                  Nearest whole dollar
                </div>
              </>
            ) : (
              <>
                <label style={S.label}>Value</label>
                <input style={S.input} type="number" value={adj.value}
                  onChange={e=>setAdj(a=>({...a,value:e.target.value}))}
                  placeholder={adj.type==="pct"?"e.g. 10":"0.00"} />
              </>
            )}
          </div>
          </div>
        )}

        <div style={{...S.infoBox,marginBottom:16}}>
          <div style={{fontSize:12,color:"#3b82f6",fontWeight:500,marginBottom:6}}>
            {field === "IsOnline" ? filtered.length : applicable.length} item{(field==="IsOnline"?filtered.length:applicable.length)!==1?"s":""} selected
          </div>
          {field === "IsOnline" ? (
            <div style={{fontSize:12,color:"#666"}}>
              Will {onlineVal ? "list" : "remove"} {filtered.length} card{filtered.length!==1?"s":""} {onlineVal ? "on" : "from"} your online storefront
            </div>
          ) : (
            applicable.slice(0,3).map(item=>(
              <div key={item.ID} style={{fontSize:12,color:"#666",marginBottom:3}}>
                {item.CardName} · {fmt(parseFloat(item[field])||0)} → <span style={{color:"#4ade80"}}>{fmt(preview(parseFloat(item[field])||0))}</span>
              </div>
            ))
          )}
          {field !== "IsOnline" && applicable.length>3 && <div style={{fontSize:11,color:"#444"}}>+{applicable.length-3} more</div>}
          {field === "ListPrice" && skippedForShow > 0 && (
            <div style={{fontSize:11,color:"#fbbf24",marginTop:8,paddingTop:8,borderTop:`0.5px solid ${T.border}`}}>
              ⚠ {skippedForShow} card{skippedForShow!==1?"s":""} reserved for a show will be skipped — adjust {skippedForShow!==1?"those":"that one"} from Show Mode.
            </div>
          )}
        </div>

        <button style={{...S.btn("primary"),width:"100%"}} onClick={apply}
          disabled={saving||(field==="IsOnline"?filtered.length===0:applicable.length===0)||(field==="IsOnline"?onlineVal===null:(adj.type!=="round_nearest"&&!adj.value))}>
          {saving?"Updating…":`Apply to ${field==="IsOnline"?filtered.length:applicable.length} Items`}
        </button>
      </div>
    </div>
  );
}

// ─── INVENTORY CARD ───────────────────────────────────────────────────────────
const InventoryCard = React.memo(function InventoryCard({ item, activeShow, alreadyInShow, itemPriceHistory, onEdit, onSell, onDelete, onRestock, onAddToShow, onUpdateItem, showLocation, locationOptions = [], onAddLocationOption, dynamicPricingEnabled, isPaid = true }) {
  const { S, T } = useTheme();
  const { toast } = useNotify();
  const [lightbox, setLightbox]       = useState(false);
  const [expanded, setExpanded]       = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyInfo, setHistoryInfo] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationInput, setLocationInput]     = useState(item.Location || "");

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleGetPrice = async (e) => {
    e.stopPropagation();
    if (fetchingPrice) return;

    const missing = [];
    if (!item.CardName?.trim())  missing.push("Card Name");
    if (!item.Set?.trim())       missing.push("Set");
    if (!item.Number?.trim())    missing.push("Card Number");

    if (missing.length > 0) {
      const fields = missing.join(", ");
      toast(
        `Can't fetch price — missing: ${fields}. Edit the card to add ${missing.length === 1 ? "this field" : "these fields"} first.`,
        "alert"
      );
      return;
    }

    setFetchingPrice(true);
    try {
      const range = await refreshSingleCardPrice(item.ID);
      if (range?.center != null) {
        toast(`${item.CardName}: $${range.low.toFixed(0)} – $${range.high.toFixed(0)} (${range.source})`, "success");
      } else {
        toast(
          `No price found for ${item.CardName}. Check that the card name, set, and card number are accurate — small discrepancies can prevent a match.`,
          "alert"
        );
      }
    } catch(e) {
      toast("Price lookup failed: " + e.message, "error");
    }
    setFetchingPrice(false);
  };
  const [addedToShow, setAddedToShow] = useState(false);
  const handleViewHistory = async (e) => {
    e.stopPropagation();
    if (historyLoading) return;
    if (historyInfo) { setShowHistoryModal(true); return; }
    setHistoryLoading(true);
    try {
      const priced = await unifiedCardLookup(
        item.GameType, item.CardName, item.Number, item.Set, item.TcggoId,
        item.GradingCompany, item.Grade, item.Condition
      );
      if (priced?._priceData || priced?._rawRows) {
        setHistoryInfo(priced);
        setShowHistoryModal(true);
      } else {
        toast("No price history found for this card.", "alert");
      }
    } catch(e) {
      toast("Couldn't load price history: " + e.message, "error");
    }
    setHistoryLoading(false);
  };
  const days    = daysSince(item.AcquisitionDate);
  const roiVal  = roi(item.AcquisitionCost, item.ListPrice);
  const isStale = days>=30 && item.Status!=="Sold";
  const isSold  = item.Status==="Sold";
  const isReserved = item.Status==="Reserved" || alreadyInShow || addedToShow;
  const isGrading  = !!item.GradingSentDate && !item.GradingReturnDate;
  const belowMin = !isSold && !isReserved && !isGrading && item.MinSellPrice>0 && item.ListPrice<item.MinSellPrice;

  return (
    <>
      <div style={{...S.card, borderColor: isStale?T.amber:isSold?T.green:isReserved?T.primary:belowMin?T.red:T.borderSoft,
        boxShadow:T.shadowSm, marginBottom:10}}>
        <div style={{display:"flex",gap:14,marginBottom:expanded?12:0,cursor:"pointer",alignItems:"center"}}
          onClick={()=>handleExpand()}>
          {item.ImageURL ? (
            <img src={item.ImageURL} alt={item.CardName} loading="lazy"
              style={{width:78,height:104,objectFit:"cover",borderRadius:10,
                border:`1px solid ${T.border}`,boxShadow:T.shadowSm,cursor:"pointer",flexShrink:0}}
              onClick={e=>{e.stopPropagation();setLightbox(true);}} />
          ) : (
            <div style={{width:78,height:104,borderRadius:10,border:`1px dashed ${T.border}`,
              background:T.surface2,
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:22,color:T.textFaint,flexShrink:0}}>⬡</div>
          )}

          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
              <div style={{flex:1,marginRight:8}}>
                <div style={{fontWeight:800,fontSize:18,marginBottom:5,color:T.text,letterSpacing:"-0.02em"}}>{item.CardName}</div>
                <div style={{fontSize:14,color:T.textMuted,fontWeight:600}}>
                  {[item.Set,item.Number,item.Rarity].filter(Boolean).join(" · ")}
                </div>
                {(item.Language && item.Language!=="English") && (
                  <div style={{fontSize:12,color:T.textFaint,marginTop:1}}>{item.Language}</div>
                )}
                {item.LotName && <div style={{fontSize:11,color:T.primary,marginTop:1}}>{item.LotName}{item.LotItemNumber?` · #${item.LotItemNumber}`:""}</div>}
                {item.ShowName && <div style={{fontSize:11,color:T.pink,marginTop:1}}>{item.ShowName}</div>}

                {item.GameType==="Lorcana" && (
                  <div style={{display:"inline-block",fontSize:10,fontWeight:700,color:T.pink,
                    background:T.pinkBg,border:`0.5px solid ${T.pink}`,
                    borderRadius:6,padding:"2px 6px",marginTop:6}}>
                    ✨ Lorcana{item.LorcanaFoilType && item.LorcanaFoilType!=="Normal" ? ` · ${item.LorcanaFoilType}` : ""}
                  </div>
                )}
                {item.IsConsignment && (
                  <div style={{display:"inline-block",fontSize:10,fontWeight:700,color:T.primary,
                    background:T.blueBg,border:`0.5px solid ${T.primary}`,
                    borderRadius:6,padding:"2px 6px",marginTop:6}}>
                    💎 CONSIGNMENT
                  </div>
                )}
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                {isSold
                  ? <span style={S.badge("green")}>Sold {fmt(item.SoldPrice)}</span>
                  : isGrading
                  ? <span style={{...S.badge("purple"),background:T.pinkBg,
                      color:T.pink,borderColor:T.primary}}>
                      📋 {item.GradingCompany}
                    </span>
                  : isReserved
                  ? <span style={S.badge("purple")}>In Show</span>
                  : (
                    <>
                      <div style={{fontSize:23,fontWeight:900,color:T.primary,letterSpacing:"-0.03em"}}>{fmt(item.ListPrice)}</div>
                      {item.MinSellPrice > 0 && (
                        <div style={{fontSize:13,color:T.textMuted,fontWeight:600,marginTop:2}}>min {fmt(item.MinSellPrice)}</div>
                      )}
                    </>
                  )}
                {!isSold && !isReserved && item.MinSellPrice>0 && (
                  <div style={{fontSize:10,color:belowMin?"#f87171":"#555",marginTop:2}}>min ${item.MinSellPrice}</div>
                )}
              </div>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {item.Condition && <span style={S.badge()}>{item.Condition}</span>}
              {isStale && <span style={S.badge("amber")}>{days}d sitting</span>}
              {!isSold && !isReserved && roiVal!==null && (
                <span style={S.badge(roiVal===Infinity||roiVal>=0?"green":"red")}>
                  {roiVal===Infinity ? "∞ ROI" : `${roiVal>=0?"+":""}${pct(roiVal)} ROI`}
                </span>
              )}
              {belowMin && <span style={S.badge("red")}>↓ MIN</span>}
            </div>
          </div>
        </div>

        {expanded && (
          <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12}} onClick={e=>e.stopPropagation()}>

            {item.CardName && item.GameType === "Pokemon" && (
              <div style={{fontSize:12, fontWeight:700, color:T.primary, marginBottom:10, cursor:"pointer"}}
                onClick={handleViewHistory}>
                {historyLoading ? "Loading…" : "View History"}
              </div>
            )}
            {showHistoryModal && historyInfo && (
              <PriceHistoryModal
                marketPriceInfo={historyInfo}
                condition={item.Condition}
                cardName={item.CardName}
                isPaid={isPaid}
                onClose={()=>setShowHistoryModal(false)}
                S={S} T={T} />
            )}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:10}}>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Acquired For</div>
                <div style={{fontSize:14,fontWeight:700,color:"#f87171",fontFamily:"'DM Mono',monospace"}}>
                  {item.AcquisitionCost ? fmt(item.AcquisitionCost) : "N/A"}
                </div>
              </div>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Mkt at Buy</div>
                <div style={{fontSize:14,fontWeight:700,color:"#88bdf2",fontFamily:"'DM Mono',monospace"}}>
                  {item.MarketValueAtAcquisition ? fmt(item.MarketValueAtAcquisition) : "N/A"}
                </div>
              </div>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>List Price</div>
                <div style={{fontSize:14,fontWeight:700,color:"#3b82f6",fontFamily:"'DM Mono',monospace"}}>
                  {item.ListPrice ? fmt(item.ListPrice) : "N/A"}
                </div>
                {dynamicPricingEnabled && (
                  <label style={{display:"flex", alignItems:"center", gap:5, marginTop:5, cursor:"pointer"}}
                    onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!item.PriceOverride}
                      onChange={e => onUpdateItem(item.ID, { PriceOverride: e.target.checked })}
                      style={{margin:0, width:12, height:12}}
                    />
                    <span style={{fontSize:9, color:T.textFaint, lineHeight:1.3}}>
                      Price Override
                    </span>
                  </label>
                )}
              </div>
              {(() => {
                const md = item.MarketPriceData;
                const hasRange = md && md.low != null && md.high != null;
                return (
                  <div style={{background:"rgba(251,191,36,0.07)",
                    border:"0.5px solid rgba(251,191,36,0.25)",
                    borderRadius:6, padding:"7px 8px"}}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3}}>
                      <div style={{fontSize:9, color:T.textFaint, textTransform:"uppercase",
                        letterSpacing:"0.05em"}}>{md?._noConditionPricing ? "Market Price" : "Market Range"}</div>
                      <button onClick={handleGetPrice} disabled={fetchingPrice}
                        style={{background:"none", border:"none", cursor:fetchingPrice?"default":"pointer",
                          fontSize:11, color:"#fbbf24", padding:0, opacity:fetchingPrice?0.5:1}}>
                        {fetchingPrice ? "…" : "🔄"}
                      </button>
                    </div>
                    {hasRange ? (
                      <>
                        <div style={{fontSize:11, color:"#fbbf24", lineHeight:1.5, fontWeight:600}}>
                          {md._noConditionPricing ? fmt(md.center) : <>{fmt(md.low)} – {fmt(md.high)}</>}
                        </div>
                        <div style={{fontSize:9, color:T.textFaint, marginTop:2}}>
                          {md.source || "Estimated"}
                          {md._noConditionPricing
                            ? " · Near Mint"
                            : item.GradingCompany && item.Grade
                            ? ` · ${item.GradingCompany} ${item.Grade}`
                            : item.Condition ? ` · ${item.Condition}` : ""}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{fontSize:11, color:"#fbbf24", lineHeight:1.5}}>
                          {item.MarketPriceUpdatedAt ? "No price range available" : "Not fetched yet"}
                        </div>
                        <div style={{fontSize:9, color:T.textFaint, marginTop:2}}>
                          {item.MarketPriceUpdatedAt
                            ? "We fetched, but couldn't find a usable range for this card"
                            : "Tap 🔄 to fetch, or wait for tonight's refresh"}
                        </div>
                      </>
                    )}
                    {item.MarketPriceUpdatedAt && (
                      <div style={{fontSize:9, color:T.textFaint, marginTop:2}}>
                        Last fetched {timeAgo(item.MarketPriceUpdatedAt)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:10}}>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Days Sitting</div>
                <div style={{fontSize:13,fontWeight:700,color:isStale?"#fbbf24":T.text}}>{isSold?"—":days}</div>
              </div>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>ROI</div>
                <div style={{fontSize:13,fontWeight:700,color:roiVal===Infinity||roiVal>=0?"#4ade80":"#f87171"}}>
                  {roiVal===Infinity ? "∞" : roiVal!==null ? `${roiVal>=0?"+":""}${pct(roiVal)}` : "N/A"}
                </div>
              </div>
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px"}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Source</div>
                <div style={{fontSize:11,fontWeight:600,color:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {item.Source||"—"}
                </div>
              </div>
            </div>

            {item.LotName && (
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px",marginBottom:10}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:1}}>Associated Lot</div>
                <div style={{fontSize:12,fontWeight:600,color:"#88bdf2"}}>
                  {item.LotName}{item.LotItemNumber?` · Item #${item.LotItemNumber}`:""}
                </div>
              </div>
            )}

            {item.MarketValueAtAcquisition>0 && !isSold && (()=>{
              const mv=parseFloat(item.MarketValueAtAcquisition);
              const ac=item.AcquisitionCost;
              const diff=mv>0?((mv-ac)/mv)*100:0;
              const label=diff>2?`↓${diff.toFixed(0)}% below mkt at buy`:diff<-2?`↑${Math.abs(diff).toFixed(0)}% above mkt at buy`:"Bought at market";
              const color=diff>2?"#4ade80":diff<-2?"#f87171":"#888";
              const history=itemPriceHistory;
              return (
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                  <div style={{fontSize:11,color,fontFamily:"'DM Mono',monospace"}}>{label}</div>
                  {history&&history.length>=2&&<PriceSparkline history={history} currentPrice={mv}/>}
                </div>
              );
            })()}

            {item.Notes && (
              <div style={{fontSize:11,color:T.textMuted,marginBottom:10,fontStyle:"italic",
                borderLeft:`2px solid ${T.border}`,paddingLeft:8}}>
                {item.Notes}
              </div>
            )}

            {showLocation && (
              <div style={{background:T.surface2,borderRadius:6,padding:"6px 8px",marginBottom:10}}>
                <div style={{fontSize:9,color:T.textFaint,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:3}}>Location</div>
                {editingLocation ? (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {locationOptions.length > 0 && (
                      <select value="" onChange={e=>{
                          if (!e.target.value) return;
                          setLocationInput(e.target.value);
                          onUpdateItem(item.ID, {Location: e.target.value});
                          setEditingLocation(false);
                        }}
                        style={{...S.input, padding:"6px 8px", fontSize:12}}>
                        <option value="">Choose existing…</option>
                        {locationOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}
                    <div style={{display:"flex",gap:6}}>
                      <input value={locationInput} onChange={e=>setLocationInput(e.target.value)}
                        placeholder="e.g. Display Case 2, Binder 14"
                        style={{...S.input, flex:1, padding:"6px 8px", fontSize:12}}
                        onKeyDown={e=>{
                          if (e.key === "Enter" && locationInput.trim()) {
                            const val = locationInput.trim();
                            onUpdateItem(item.ID, {Location: val});
                            if (!locationOptions.includes(val) && onAddLocationOption) onAddLocationOption(val);
                            setEditingLocation(false);
                          }
                        }} />
                      <button style={{...S.btn("sm"), padding:"6px 10px", fontSize:11}}
                        onClick={()=>{
                          const val = locationInput.trim();
                          if (val) {
                            onUpdateItem(item.ID, {Location: val});
                            if (!locationOptions.includes(val) && onAddLocationOption) onAddLocationOption(val);
                          }
                          setEditingLocation(false);
                        }}>
                        Save
                      </button>
                      <button style={{...S.btn("ghost"), padding:"6px 10px", fontSize:11}}
                        onClick={()=>{ setLocationInput(item.Location||""); setEditingLocation(false); }}>
                        ✕
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}
                    onClick={()=>setEditingLocation(true)}>
                    <span style={{fontSize:12,fontWeight:600,color:item.Location?T.text:T.textFaint}}>
                      {item.Location || "Not set — tap to add"}
                    </span>
                    <span style={{fontSize:11,color:"#3C66EE"}}>Edit</span>
                  </div>
                )}
              </div>
            )}

            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {!isSold && !isGrading && (
                <button
                  style={{...S.btn("sm"), flex:1, padding:"8px",
                    background: item.IsOnline ? "rgba(60,102,238,0.15)" : "transparent",
                    color: item.IsOnline ? "#88bdf2" : T.textFaint,
                    borderColor: item.IsOnline ? "rgba(60,102,238,0.4)" : T.border}}
                  title={item.IsOnline ? "Remove from online shop" : "List on online shop"}
                  onClick={async(e)=>{
                    e.stopPropagation();
                    await onUpdateItem(item.ID, { IsOnline: !item.IsOnline });
                  }}>
                  🌐
                </button>
              )}
              {!isSold && !isReserved && (
                <>
                  <button style={{...S.btn("sm"),flex:1,padding:"8px"}} onClick={()=>onEdit(item)}>✏ Edit</button>
                  <button style={{...S.btn("sm"),flex:1,padding:"8px",background:"#0f2e1a",color:"#4ade80"}} onClick={()=>onSell(item)}>💰 Sell</button>
                  {!isGrading && activeShow && onAddToShow && (
                    <button
                      style={{...S.btn("sm"),flex:1,padding:"8px",
                        background:alreadyInShow||addedToShow?"#1a2e1a":"#0f1e2e",
                        color:alreadyInShow||addedToShow?"#4ade80":"#88bdf2",
                        borderColor:alreadyInShow||addedToShow?"#166534":"#1e3a5f"}}
                      disabled={alreadyInShow||addedToShow}
                      onClick={async()=>{await onAddToShow(activeShow.ID,item.ID);setAddedToShow(true);}}>
                      {alreadyInShow||addedToShow?"✓ In Show":"+ Show"}
                    </button>
                  )}
                  {(() => {
                    const cardUrl = `${window.location.origin}${window.location.pathname}?s=${item.QRCode||item.ID}`;
                    return (
                      <button
                        style={{...S.btn("sm"),flex:1,padding:"8px",
                          background:"rgba(60,102,238,0.1)",color:"#3C66EE",
                          borderColor:"rgba(60,102,238,0.3)"}}
                        onClick={e=>{
                          e.stopPropagation();
                          navigator.clipboard.writeText(cardUrl).then(()=>{
                            e.target.textContent="✓ Copied!";
                            setTimeout(()=>{ if(e.target) e.target.textContent="🏷 Label URL"; },1500);
                          });
                        }}>
                        🏷 Label URL
                      </button>
                    );
                  })()}
                  {isGrading && (
                    <div style={{fontSize:10,color:"var(--cv-pink,#c0387a)",padding:"6px 0",flex:2}}>
                      📋 {item.GradingCompany} · Sent {item.GradingSentDate}
                      {item.GradingReturnDate && ` · Return ${item.GradingReturnDate}`}
                    </div>
                  )}
                  <button style={{...S.btn("sm"),padding:"8px 12px",background:"#2e0f0f",color:"#f87171"}} onClick={()=>onDelete(item.ID)}>✕</button>
                </>
              )}
              {isSold && (
                <button style={{...S.btn("sm"),width:"100%",background:"#0f2e1a",color:"#4ade80"}} onClick={()=>onRestock(item)}>Restock</button>
              )}
            </div>
          </div>
        )}
      </div>
      {lightbox && <Lightbox url={item.ImageURL} onClose={()=>setLightbox(false)} />}
    </>
  );
});

// ─── CARD SHOWS PAGE ──────────────────────────────────────────────────────────
function computePreShowIssues(cards) {
  const STALE_MS = 7 * 24 * 60 * 60 * 1000;
  const highValue    = cards.filter(c => (parseFloat(c.ListPrice) || 0) > 100);
  const staleRef     = cards.filter(c => {
    if (!c.MarketPriceUpdatedAt) return true;
    return (Date.now() - new Date(c.MarketPriceUpdatedAt).getTime()) > STALE_MS;
  });
  const noListPrice  = cards.filter(c => !(parseFloat(c.ListPrice) > 0));
  const belowCost    = cards.filter(c =>
    (parseFloat(c.ListPrice) || 0) > 0 &&
    (parseFloat(c.AcquisitionCost) || 0) > 0 &&
    parseFloat(c.ListPrice) < parseFloat(c.AcquisitionCost)
  );
  const priceChanged = cards
    .filter(c => (parseFloat(c.ListPrice) || 0) > 0 && (parseFloat(c.MarketPrice) || 0) > 0)
    .map(c => {
      const list = parseFloat(c.ListPrice);
      const mkt  = parseFloat(c.MarketPrice);
      const pctDiff = (mkt - list) / list;
      return { ...c, _list: list, _mkt: mkt, _pctDiff: pctDiff };
    })
    .filter(c => Math.abs(c._pctDiff) >= 0.02)
    .sort((a,b) => Math.abs(b._pctDiff) - Math.abs(a._pctDiff));
  const totalIssues = highValue.length + staleRef.length + noListPrice.length + belowCost.length + priceChanged.length;
  return { highValue, staleRef, noListPrice, belowCost, priceChanged, totalIssues };
}

function PreShowSummaryModal({ show, cards, onClose }) {
  const { S, T } = useTheme();
  const [expanded, setExpanded] = useState(null);
  const { highValue, staleRef, noListPrice, belowCost, priceChanged, totalIssues } = useMemo(
    () => computePreShowIssues(cards), [cards]
  );

  const CATEGORIES = [
    { key: "priceChanged", label: "Listing price vs. current market",       list: priceChanged,
      note: c => fmt(c._list) },
    { key: "highValue",   label: "High-value cards (over $100)",         list: highValue,
      note: c => fmt(c.ListPrice) },
    { key: "staleRef",    label: "Reference price not updated in 7+ days", list: staleRef,
      note: c => c.MarketPriceUpdatedAt ? new Date(c.MarketPriceUpdatedAt).toLocaleDateString() : "Never checked" },
    { key: "noListPrice", label: "No List Price set",                     list: noListPrice,
      note: () => "No price" },
    { key: "belowCost",   label: "List Price is below cost",              list: belowCost,
      note: c => `${fmt(c.ListPrice)} < cost ${fmt(c.AcquisitionCost)}` },
  ];

  return (
    <div style={S.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.drawer}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4}}>
          <div style={S.h2}>Pre-Show Summary</div>
          <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:12, color:T.textMuted, marginBottom:16}}>
          {show.Name} · {cards.length} card{cards.length !== 1 ? "s" : ""} reserved for this show
        </div>

        {totalIssues === 0 ? (
          <div style={{textAlign:"center", padding:"28px 16px"}}>
            <div style={{fontSize:32, marginBottom:10}}>✅</div>
            <div style={{fontSize:15, fontWeight:700, color:"#4ade80"}}>
              Cards have passed pre-show check!
            </div>
          </div>
        ) : (
          <div>
            <div style={{fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:12}}>
              ⚠ Needs Review
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:8, marginBottom:8}}>
              {CATEGORIES.filter(c => c.list.length > 0).map(cat => (
                <div key={cat.key} style={{...S.card, padding:"10px 12px"}}>
                  <div
                    style={{display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer"}}
                    onClick={() => setExpanded(e => e === cat.key ? null : cat.key)}>
                    <div style={{fontSize:13, fontWeight:600}}>
                      {cat.label} <span style={{color:T.textMuted, fontWeight:400}}>({cat.list.length})</span>
                    </div>
                    <span style={{fontSize:11, color:"#88bdf2"}}>
                      {expanded === cat.key ? "Hide" : "View cards"}
                    </span>
                  </div>
                  {expanded === cat.key && cat.key === "priceChanged" && (
                    <div style={{marginTop:10, paddingTop:10, borderTop:`0.5px solid ${T.border}`}}>
                      <div style={{display:"grid", gridTemplateColumns:"1fr 70px 70px 30px", gap:6,
                        fontSize:10, color:T.textFaint, textTransform:"uppercase", letterSpacing:"0.03em",
                        marginBottom:6, paddingBottom:4, borderBottom:`0.5px solid ${T.border}`}}>
                        <span>Card</span>
                        <span style={{textAlign:"right"}}>Listing</span>
                        <span style={{textAlign:"right"}}>Current</span>
                        <span style={{textAlign:"center"}}>Chg</span>
                      </div>
                      <div style={{display:"flex", flexDirection:"column", gap:6}}>
                        {cat.list.map(c => (
                          <div key={c.ID} style={{display:"grid", gridTemplateColumns:"1fr 70px 70px 30px", gap:6,
                            fontSize:12, alignItems:"center"}}>
                            <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                              {c.CardName}
                            </span>
                            <span style={{textAlign:"right", fontFamily:"'DM Mono',monospace"}}>{fmt(c._list)}</span>
                            <span style={{textAlign:"right", fontFamily:"'DM Mono',monospace"}}>{fmt(c._mkt)}</span>
                            <span style={{textAlign:"center", fontSize:14,
                              color: c._pctDiff > 0 ? "#4ade80" : "#f87171"}}>
                              {c._pctDiff > 0 ? "▲" : "▼"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {expanded === cat.key && cat.key !== "priceChanged" && (
                    <div style={{marginTop:10, paddingTop:10, borderTop:`0.5px solid ${T.border}`,
                      display:"flex", flexDirection:"column", gap:6}}>
                      {cat.list.map(c => (
                        <div key={c.ID} style={{display:"flex", justifyContent:"space-between",
                          fontSize:12, gap:8}}>
                          <span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                            {c.CardName}
                          </span>
                          <span style={{color:T.textMuted, flexShrink:0, fontFamily:"'DM Mono',monospace"}}>
                            {cat.note(c)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ShowsPageInner({ shows, inventory, showItems, trades, onAddShow, onUpdateShow, onAddShowItem, onRemoveShowItem, onUpdateItem, onOpenPreShow }) {
  const { S, T } = useTheme();
  const { toast, confirmAction, openUpgradeModal } = useNotify();
  const { isPaid } = usePlan();
  const [view, setView]         = useState("list");
  const [activeShow, setActive] = useState(null);
  const [form, setForm]         = useState({ Name:"", Location:"", StartDate:"", EndDate:"", TableCost:"", Marketing:"", Travel:"", Food:"", Notes:"" });
  const [editingShow, setEditingShow]   = useState(false);
  const [showEditForm, setShowEditForm] = useState({Name:"",Location:"",StartDate:"",EndDate:""});
  const [saving, setSaving]     = useState(false);
  const [carryProgress, setCarryProgress] = useState(null);
  const [search, setSearch]     = useState("");

  const runCarryForward = async (cards, fromShowId, toShow, afterDone) => {
    const total = cards.length;
    const startTime = Date.now();
    setCarryProgress({ done: 0, total, startTime });
    for (let i = 0; i < cards.length; i++) {
      const card = cards[i];
      await onRemoveShowItem(fromShowId, card.ID);
      await onAddShowItem(toShow.ID, card.ID);
      await onUpdateItem(card.ID, { ShowID: toShow.ID, ShowName: toShow.Name, Status: "Reserved" });
      setCarryProgress({ done: i + 1, total, startTime });
    }
    setCarryProgress(null);
    await afterDone();
  };
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showSort, setShowSort] = useState("name_asc");
  const [showInvSearch, setShowInvSearch] = useState("");
  const [showLabelPrint, setShowLabelPrint] = useState(false);
  const [showTradePhotos, setShowTradePhotos] = useState(false);

  const today = new Date();
  const [calYear,  setCalYear]  = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());

  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const saveShow = async () => {
    if (!form.Name||!form.StartDate) return;
    setSaving(true);
    await onAddShow(form);
    setSaving(false);
    setView("list");
    setForm({ Name:"", Location:"", StartDate:"", EndDate:"", TableCost:"", Marketing:"", Travel:"", Food:"", Notes:"" });
  };

  const saveEditShow = async () => {
    if (!showEditForm.Name||!showEditForm.StartDate) return;
    setSaving(true);
    await onUpdateShow(activeShow.ID, showEditForm);
    setActive(s => ({...s, ...showEditForm}));
    setSaving(false);
    setEditingShow(false);
  };

  const showCardIds = useMemo(()=>
    showItems.filter(si=>si.ShowID===activeShow?.ID).map(si=>si.InventoryID),
  [showItems, activeShow]);

  const showCards = useMemo(()=>
    inventory.filter(i=>{
      if (!showCardIds.includes(i.ID)) return false;
      if (showInvSearch && !i.CardName?.toLowerCase().includes(showInvSearch.toLowerCase()) &&
                           !i.Set?.toLowerCase().includes(showInvSearch.toLowerCase())) return false;
      const price = parseFloat(i.ListPrice) || 0;
      if (priceMin !== "" && price < parseFloat(priceMin)) return false;
      if (priceMax !== "" && price > parseFloat(priceMax)) return false;
      return true;
    }),
  [inventory, showCardIds, showInvSearch, priceMin, priceMax]);

  const available = useMemo(()=>
    inventory.filter(i=>{
      if (i.Status!=="In Stock" || showCardIds.includes(i.ID)) return false;
      if (search && !i.CardName?.toLowerCase().includes(search.toLowerCase()) &&
                    !i.Set?.toLowerCase().includes(search.toLowerCase())) return false;
      if (priceMin && i.ListPrice < parseFloat(priceMin)) return false;
      if (priceMax && i.ListPrice > parseFloat(priceMax)) return false;
      return true;
    }),
  [inventory, showCardIds, search, priceMin, priceMax]);

  const showStats = (show) => {
    const junctionIds = new Set(showItems.filter(si=>si.ShowID===show.ID).map(si=>si.InventoryID));
    const brought   = inventory.filter(i=>i.ShowID===show.ID || junctionIds.has(i.ID));

    const soldForShow = inventory.filter(i => (i.ShowID===show.ID || junctionIds.has(i.ID)) && i.Status==="Sold");

    const bought    = inventory.filter(i=>i.AcquiredShowID===show.ID && i.Source!=="Customer Trade");
    const tradeIns  = inventory.filter(i=>i.AcquiredShowID===show.ID && i.Source==="Customer Trade");

    const sold      = soldForShow.filter(i=>i.SoldChannel!=="Trade");
    const traded    = soldForShow.filter(i=>i.SoldChannel==="Trade");
    const remaining = brought.filter(i=>i.Status!=="Sold");
    const revenue   = sold.reduce((s,i)=>s+(i.SoldPrice||0),0);
    const cost      = brought.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
    const totalValue= brought.reduce((s,i)=>s+(parseFloat(i.ListPrice)||0),0);
    const tradeGains= traded.reduce((s,i)=>s+(i.TradeGain||0),0);
    const boughtCost= bought.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
    const operatingCost = (show.TableCost||0) + (show.Marketing||0) + (show.Travel||0) + (show.Food||0);
    const netProfit = revenue - sold.reduce((s,i)=>s+(i.AcquisitionCost||0),0) + tradeGains - operatingCost;
    return { brought, bought, tradeIns, sold, traded, remaining, revenue, cost,
             totalValue, tradeGains, boughtCost, operatingCost, netProfit,
             profit: revenue - sold.reduce((s,i)=>s+(i.AcquisitionCost||0),0) + tradeGains };
  };

  const statusColor = s => s.Status==="Active"?"#4ade80":s.Status==="Completed"?"#60a5fa":s.Status==="Upcoming"?"#60a5fa":"#888";

  if (view==="new") return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setView("list")}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>New Card Show</div>
          <button style={{width:36,height:36,borderRadius:T.radius.md,border:`1px solid ${T.border}`,
            background:T.surface,color:T.primary,fontSize:18,cursor:"pointer"}}
            onClick={()=>setView("list")}>✕</button>
        </div>

        <div style={{marginBottom:12}}>
          <label style={S.label}>Show Name *</label>
          <input style={S.input} value={form.Name} onChange={e=>set("Name",e.target.value)} placeholder="e.g. Orlando Card Show June" />
        </div>
        <div style={{marginBottom:12}}>
          <label style={S.label}>Location</label>
          <input style={S.input} value={form.Location} onChange={e=>set("Location",e.target.value)} placeholder="e.g. Orange County Convention Center" />
        </div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Start Date *</label>
            <input style={S.input} type="date" value={form.StartDate} onChange={e=>set("StartDate",e.target.value)} />
          </div>
          <div style={S.col}>
            <label style={S.label}>End Date</label>
            <input style={S.input} type="date" value={form.EndDate} onChange={e=>set("EndDate",e.target.value)} />
          </div>
        </div>
        <div style={S.divider}/>
        <div style={{fontSize:13,color:T.text,marginBottom:12,fontWeight:800}}>Show Costs</div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Table Cost ($) *</label>
            <input style={S.input} type="number" value={form.TableCost} onChange={e=>set("TableCost",e.target.value)} placeholder="0.00" />
          </div>
          <div style={S.col}>
            <label style={S.label}>Marketing ($)</label>
            <input style={S.input} type="number" value={form.Marketing} onChange={e=>set("Marketing",e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Travel ($)</label>
            <input style={S.input} type="number" value={form.Travel} onChange={e=>set("Travel",e.target.value)} placeholder="0.00" />
          </div>
          <div style={S.col}>
            <label style={S.label}>Food ($)</label>
            <input style={S.input} type="number" value={form.Food} onChange={e=>set("Food",e.target.value)} placeholder="0.00" />
          </div>
        </div>
        <div style={{marginBottom:20}}>
          <label style={S.label}>Notes (optional)</label>
          <input style={S.input} value={form.Notes} onChange={e=>set("Notes",e.target.value)} placeholder="optional" />
        </div>
        <div style={{display:"flex", justifyContent:"flex-end", gap:12}}>
          <button style={{...S.btn("ghost"), padding:"12px 24px"}} onClick={()=>setView("list")} disabled={saving}>Cancel</button>
          <button style={{...S.btn("primary"), padding:"12px 24px"}} onClick={saveShow} disabled={saving||!form.Name||!form.StartDate}>
            {saving?"Creating…":"Create Show"}
          </button>
        </div>
      </div>
    </div>
  );

  if (showLabelPrint && activeShow) {
    const showCardIds = showItems.filter(si=>si.ShowID===activeShow.ID).map(si=>si.InventoryID);
    const printableCards = inventory
      .filter(i => showCardIds.includes(i.ID) && i.Status !== "Deleted")
      .sort((a,b) => (a.CardName||"").localeCompare(b.CardName||""));

    return (
      <LabelPrintModal
        show={activeShow}
        cards={printableCards}
        S={S} T={T}
        onClose={()=>setShowLabelPrint(false)}
      />
    );
  }

  if (showTradePhotos && activeShow) return (
    <TradePhotosViewer show={activeShow} onClose={()=>setShowTradePhotos(false)} S={S} T={T} />
  );

  if (editingShow && activeShow) return (
    <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setEditingShow(false)}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Edit Show</div>
          <button style={{width:36,height:36,borderRadius:T.radius.md,border:`1px solid ${T.border}`,
            background:T.surface,color:T.primary,fontSize:18,cursor:"pointer"}}
            onClick={()=>setEditingShow(false)}>✕</button>
        </div>
        {[["Name","Show Name *","text"],["Location","Location","text"],["StartDate","Start Date *","date"],["EndDate","End Date","date"]].map(([k,lbl,type])=>(
          <div key={k} style={{marginBottom:12}}>
            <label style={S.label}>{lbl}</label>
            <input style={S.input} type={type} value={showEditForm[k]||""}
              onChange={e=>setShowEditForm(f=>({...f,[k]:e.target.value}))} />
          </div>
        ))}
        <div style={S.divider}/>
        <div style={{fontSize:13,color:T.text,marginBottom:12,fontWeight:800}}>Show Costs</div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Table Cost ($)</label>
            <input style={S.input} type="number" value={showEditForm.TableCost||""}
              onChange={e=>setShowEditForm(f=>({...f,TableCost:e.target.value}))} placeholder="0.00"/>
          </div>
          <div style={S.col}>
            <label style={S.label}>Marketing ($)</label>
            <input style={S.input} type="number" value={showEditForm.Marketing||""}
              onChange={e=>setShowEditForm(f=>({...f,Marketing:e.target.value}))} placeholder="0.00"/>
          </div>
        </div>
        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Travel ($)</label>
            <input style={S.input} type="number" value={showEditForm.Travel||""}
              onChange={e=>setShowEditForm(f=>({...f,Travel:e.target.value}))} placeholder="0.00"/>
          </div>
          <div style={S.col}>
            <label style={S.label}>Food ($)</label>
            <input style={S.input} type="number" value={showEditForm.Food||""}
              onChange={e=>setShowEditForm(f=>({...f,Food:e.target.value}))} placeholder="0.00"/>
          </div>
        </div>
        <div style={{marginBottom:20}}>
          <label style={S.label}>Notes</label>
          <input style={S.input} value={showEditForm.Notes||""}
            onChange={e=>setShowEditForm(f=>({...f,Notes:e.target.value}))} placeholder="optional"/>
        </div>
        <div style={{display:"flex", justifyContent:"flex-end", gap:12}}>
          <button style={{...S.btn("ghost"), padding:"12px 24px"}} onClick={()=>setEditingShow(false)} disabled={saving}>Cancel</button>
          <button style={{...S.btn("primary"), padding:"12px 24px"}} onClick={saveEditShow} disabled={saving||!showEditForm.Name||!showEditForm.StartDate}>
            {saving?"Saving…":"Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );

  if (view==="detail" && activeShow) {
    const stats = showStats(activeShow);
    return (
      <div style={S.page}>
        <button style={{...S.btn("ghost"),padding:"6px 10px",marginBottom:12}} onClick={()=>setView("list")}>← Back</button>

        <div style={{display:"flex",alignItems:"flex-start",gap:14,marginBottom:16}}>
          <div style={{width:56,height:56,borderRadius:"50%",flexShrink:0,
            background:T.blueBg, color:T.primary,
            display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>
            🏪
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:22,fontWeight:800,color:T.text,letterSpacing:"-0.02em"}}>{activeShow.Name}</div>
            {activeShow.Location && (
              <div style={{fontSize:14,color:T.textMuted,marginTop:4}}>📍 {activeShow.Location}</div>
            )}
            <div style={{fontSize:13,color:T.textFaint,marginTop:2}}>
              📅 {activeShow.StartDate}{activeShow.EndDate&&` → ${activeShow.EndDate}`}
            </div>
          </div>
        </div>

        <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:16}}>
          <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:12}} onClick={()=>{
            setShowEditForm({
              Name:activeShow.Name||"", Location:activeShow.Location||"",
              StartDate:activeShow.StartDate||"", EndDate:activeShow.EndDate||"",
              TableCost:activeShow.TableCost||"", Marketing:activeShow.Marketing||"",
              Travel:activeShow.Travel||"", Food:activeShow.Food||"",
              Notes:activeShow.Notes||""
            });
            setEditingShow(true);
          }}>✏ Edit</button>
          <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:12}}
            onClick={()=>setShowLabelPrint(true)}>
            🏷 Print Labels
          </button>
          <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:12}}
            onClick={()=>setShowTradePhotos(true)}>
            📸 Trade Photos
          </button>
          {isPaid && stats.brought.length > 0 && (
            <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:12}}
              onClick={()=>onOpenPreShow(activeShow)}>
              🔍 Pre-Show Check
            </button>
          )}
          <span style={{...S.badge(), color:statusColor(activeShow), border:`1px solid ${statusColor(activeShow)}`, background:"transparent"}}>
            {activeShow.Status}
          </span>
        </div>

        {activeShow.Status === "Active" && activeShow.EndDate && activeShow.EndDate < localDate() && (() => {
          const daysAgo = Math.round((Date.now() - new Date(activeShow.EndDate).getTime()) / 86400000);
          const nextShow = [...shows]
            .filter(s => s.ID !== activeShow.ID && (s.Status === "Upcoming" || s.Status === "Active"))
            .sort((a,b) => (a.StartDate||"").localeCompare(b.StartDate||""))[0];

          return (
            <div style={{background:"rgba(251,191,36,0.1)",border:"1px solid rgba(251,191,36,0.4)",
              borderRadius:12,padding:"10px 14px",marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:10,marginBottom:10}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:"#fbbf24"}}>
                    ⚠ This show ended {daysAgo} day{daysAgo!==1?"s":""} ago
                  </div>
                  <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                    {stats.remaining.length} unsold card{stats.remaining.length!==1?"s":""} still attached to this show.
                    What would you like to do with them?
                  </div>
                </div>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                <button style={{...S.btn("primary"),padding:"7px 12px",fontSize:12,whiteSpace:"nowrap"}}
                  onClick={async()=>{
                    if (!await confirmAction(
                      `Return ${stats.remaining.length} unsold card${stats.remaining.length!==1?"s":""} to inventory and close this show?`,
                      {confirmLabel:"Return to Inventory"}
                    )) return;
                    await onUpdateShow(activeShow.ID,{Status:"Completed"});
                    for (const card of stats.remaining) {
                      await onUpdateItem(card.ID,{ShowID:"",ShowName:"",Status:"In Stock"});
                    }
                    setView("list");
                    toast(`${stats.remaining.length} card${stats.remaining.length!==1?"s":""} returned to inventory`, "success");
                  }}>
                  📦 Return to Inventory
                </button>
                {nextShow ? (
                  <button style={{...S.btn("ghost"),padding:"7px 12px",fontSize:12,whiteSpace:"nowrap",
                    borderColor:"#3C66EE",color:"#3C66EE"}}
                    onClick={async()=>{
                      if (!await confirmAction(
                        `Carry ${stats.remaining.length} unsold card${stats.remaining.length!==1?"s":""} forward to "${nextShow.Name}" and close this show?`,
                        {confirmLabel:"Carry Forward"}
                      )) return;
                      await onUpdateShow(activeShow.ID,{Status:"Completed"});
                      await runCarryForward(stats.remaining, activeShow.ID, nextShow, async () => {
                        setView("list");
                        toast(`${stats.remaining.length} card${stats.remaining.length!==1?"s":""} carried forward to ${nextShow.Name}`, "success");
                      });
                    }}>
                    🎪 Carry Forward to "{nextShow.Name}"
                  </button>
                ) : (
                  <div style={{fontSize:11,color:T.textFaint,alignSelf:"center"}}>
                    No upcoming show yet — create one to enable carry-forward.
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
          {[
            { icon:"📦", iconColor:T.primary, iconBg:T.blueBg, label:"Total Inventory", value:stats.brought.length, valueColor:T.text },
            { icon:"🛒", iconColor:T.green, iconBg:T.greenBg, label:"# of Sold Items", value:stats.sold.length, valueColor:T.green },
            { icon:"📦", iconColor:T.primary, iconBg:T.blueBg, label:"Remaining", value:stats.remaining.length, valueColor:T.primary },
            { icon:"🏷", iconColor:T.primary, iconBg:T.blueBg, label:"Total Inventory Value", value:fmt(stats.totalValue), valueColor:T.primary },
            { icon:"📈", iconColor:T.green, iconBg:T.greenBg, label:"Revenue", value:fmt(stats.revenue), valueColor:T.green },
            { icon:"💵", iconColor:stats.profit>=0?T.green:T.red, iconBg:stats.profit>=0?T.greenBg:T.redBg, label:"Profit", value:fmt(stats.profit), valueColor:stats.profit>=0?T.green:T.red },
          ].map((t,i)=>(
            <div key={i} style={{...S.stat, display:"flex", flexDirection:"column", gap:8}}>
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                  background:t.iconBg,color:t.iconColor,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{t.icon}</div>
                <div style={S.statLbl}>{t.label}</div>
              </div>
              <div style={{...S.statVal, color:t.valueColor}}>{t.value}</div>
            </div>
          ))}
          {stats.traded.length > 0 && <>
            <div style={S.stat}><div style={S.statLbl}>Traded Out</div><div style={{...S.statVal,color:T.pink}}>{stats.traded.length}</div></div>
            <div style={S.stat}><div style={S.statLbl}>Trade Gains</div><div style={{...S.statVal,color:T.pink}}>{fmt(stats.tradeGains)}</div></div>
            <div style={S.stat}>
              <div style={S.statLbl}>Total Gain</div>
              <div style={{...S.statVal,color:T.green}}>{fmt(stats.profit + stats.tradeGains)}</div>
            </div>
          </>}
          {stats.operatingCost > 0 && (
            <div style={{...S.stat, display:"flex", flexDirection:"column", gap:8}}>
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                  background:T.redBg,color:T.red,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>🧮</div>
                <div style={S.statLbl}>Operating Costs</div>
              </div>
              <div style={{...S.statVal,color:T.red}}>{fmt(stats.operatingCost)}</div>
              <div style={{fontSize:9,color:T.textFaint}}>Table · Mktg · Travel · Food</div>
            </div>
          )}
          {stats.operatingCost > 0 && (
            <div style={{...S.stat, display:"flex", flexDirection:"column", gap:8}}>
              <div style={{display:"flex", alignItems:"center", gap:10}}>
                <div style={{width:36,height:36,borderRadius:"50%",flexShrink:0,
                  background:stats.netProfit>=0?T.greenBg:T.redBg,color:stats.netProfit>=0?T.green:T.red,
                  display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>{stats.netProfit>=0?"📈":"📉"}</div>
                <div style={S.statLbl}>Net Profit</div>
              </div>
              <div style={{...S.statVal,color:stats.netProfit>=0?T.green:T.red}}>{fmt(stats.netProfit)}</div>
              <div style={{fontSize:9,color:T.textFaint}}>After operating costs</div>
            </div>
          )}
          {stats.bought && stats.bought.length > 0 && (
            <div style={S.stat}><div style={S.statLbl}># of Items Purchased</div><div style={{...S.statVal,color:T.amber}}>{stats.bought.length}</div></div>
          )}
        </div>

        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {activeShow.Status==="Upcoming" && (
            <button style={S.btn("primary")} onClick={async ()=>{
              const alreadyActive = shows.find(s => s.ID !== activeShow.ID && s.Status === "Active");
              if (alreadyActive) {
                toast(`"${alreadyActive.Name}" is already active. Close or complete it before activating another show.`, "alert");
                return;
              }
              await onUpdateShow(activeShow.ID,{Status:"Active"});
              toast("Show marked as Active", "update");
            }}>Mark Active</button>
          )}
          {activeShow.Status==="Active" && (
            <button style={S.btn("primary")} onClick={async ()=>{
              for (const card of stats.remaining) {
                if (card.Status==="Reserved") await onUpdateItem(card.ID,{Status:"In Stock",ShowID:"",ShowName:""});
              }
              await onUpdateShow(activeShow.ID,{Status:"Completed"});
              setView("list");
              toast("Show closed and marked Completed", "success");
            }}>Close Show & Return Unsold</button>
          )}
          {!isPaid && (
            <button style={{...S.btn("ghost"),borderColor:"#4ade80",color:"#4ade80"}}
              onClick={openUpgradeModal}>
              ⭐ Upgrade to Pro
            </button>
          )}
          {activeShow.Status==="Active" && stats.remaining.length>0 && (() => {
            const nextShow = [...shows]
              .filter(s => s.ID !== activeShow.ID && (s.Status === "Upcoming" || s.Status === "Active"))
              .sort((a,b) => (a.StartDate||"").localeCompare(b.StartDate||""))[0];
            if (!nextShow) return null;
            return (
              <button style={{...S.btn("ghost"),borderColor:"#3C66EE",color:"#3C66EE"}}
                onClick={async ()=>{
                  if (!await confirmAction(
                    `Carry ${stats.remaining.length} unsold card${stats.remaining.length!==1?"s":""} forward to "${nextShow.Name}" and close this show?`,
                    {confirmLabel:"Carry Forward"}
                  )) return;
                  for (const card of stats.remaining) {
                    await onRemoveShowItem(activeShow.ID, card.ID);
                    await onAddShowItem(nextShow.ID, card.ID);
                    await onUpdateItem(card.ID,{ShowID:nextShow.ID, ShowName:nextShow.Name, Status:"Reserved"});
                  }
                  await onUpdateShow(activeShow.ID,{Status:"Completed"});
                  setView("list");
                  toast(`${stats.remaining.length} card${stats.remaining.length!==1?"s":""} carried forward to ${nextShow.Name}`, "success");
                }}>
                🎪 Close & Carry Forward to "{nextShow.Name}"
              </button>
            );
          })()}
          {activeShow.Status==="Completed" && (() => {
            const nextShow = [...shows]
              .filter(s => s.ID !== activeShow.ID && (s.Status === "Upcoming" || s.Status === "Active"))
              .sort((a,b) => (a.StartDate||"").localeCompare(b.StartDate||""))[0];
            return (
              <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
                {nextShow && stats.remaining.length > 0 && (
                  <button style={{...S.btn("ghost"),borderColor:"#3C66EE",color:"#3C66EE"}}
                    onClick={async()=>{
                      if (!await confirmAction(
                        `Carry ${stats.remaining.length} unsold card${stats.remaining.length!==1?"s":""} forward to "${nextShow.Name}" and archive this show?`,
                        {confirmLabel:"Carry Forward"}
                      )) return;
                      await runCarryForward(stats.remaining, activeShow.ID, nextShow, async () => {
                        await onUpdateShow(activeShow.ID,{Status:"Archived"});
                        setView("list");
                        toast(`${stats.remaining.length} card${stats.remaining.length!==1?"s":""} carried forward to ${nextShow.Name}`, "success");
                      });
                    }}>
                    🎪 Carry Forward to "{nextShow.Name}"
                  </button>
                )}
                <button style={{...S.btn("ghost"),borderColor:"#f87171",color:"#f87171"}}
                  onClick={async ()=>{
                    if (!await confirmAction("Return any remaining reserved cards to inventory and archive this show?", {confirmLabel:"Archive Show"})) return;
                    const ids = showItems.filter(si=>si.ShowID===activeShow.ID).map(si=>si.InventoryID);
                    const allReserved = inventory.filter(i=>
                      i.Status==="Reserved" && (ids.includes(i.ID) || i.ShowID===activeShow.ID)
                    );
                    await Promise.all(allReserved.map(c=>onUpdateItem(c.ID,{Status:"In Stock",ShowID:"",ShowName:""})));
                    await Promise.all(ids.map(invId=>onRemoveShowItem(activeShow.ID, invId)));
                    await onUpdateShow(activeShow.ID,{Status:"Archived"});
                    setView("list");
                    toast("Show archived", "success");
                  }}>Archive Show</button>
              </div>
            );
          })()}
          {activeShow.Status!=="Completed" && (
            <button style={S.btn("danger")} onClick={async ()=>{
              if (!await confirmAction("Delete this show and return all cards to In Stock?", {confirmLabel:"Delete Show"})) return;
              const ids = showItems.filter(si=>si.ShowID===activeShow.ID).map(si=>si.InventoryID);
              const reserved = inventory.filter(i=>ids.includes(i.ID)&&i.Status==="Reserved");
              await Promise.all(reserved.map(c=>onUpdateItem(c.ID,{Status:"In Stock",ShowID:"",ShowName:""})));
              await Promise.all(ids.map(invId=>onRemoveShowItem(activeShow.ID, invId)));
              await onUpdateShow(activeShow.ID,{Status:"Deleted"});
              setView("list");
              toast("Show deleted", "success");
            }}>Delete Show</button>
          )}
        </div>

        <div style={S.divider}/>

        {(activeShow.Status==="Completed"||activeShow.Status==="Archived") && (
          <ShowPerformancePanel stats={stats} show={activeShow} S={S} T={T} />
        )}

        {/* ── SHOW INVENTORY ───────────────────────────────────── */}
        {(<>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={S.h2}>Show Inventory ({showCards.length} cards)</div>
          <select
            style={{...S.select, fontSize:12, padding:"5px 10px", width:"auto"}}
            value={showSort}
            onChange={e=>setShowSort(e.target.value)}>
            <option value="name_asc">Name A→Z</option>
            <option value="name_desc">Name Z→A</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="cost_desc">Cost: High to Low</option>
            <option value="cost_asc">Cost: Low to High</option>
            <option value="game">Game Type</option>
          </select>
        </div>

        <div style={{position:"relative", marginBottom:10}}>
          <span style={{position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
            fontSize:13, color:T.textMuted, pointerEvents:"none"}}>🔍</span>
          <input
            style={{...S.input, paddingLeft:32}}
            placeholder="Search cards in this show..."
            value={showInvSearch}
            onChange={e=>setShowInvSearch(e.target.value)}
          />
          {showInvSearch && (
            <button
              onClick={()=>setShowInvSearch("")}
              style={{position:"absolute", right:10, top:"50%", transform:"translateY(-50%)",
                background:"none", border:"none", color:T.textFaint, cursor:"pointer", fontSize:13}}>
              ✕
            </button>
          )}
        </div>

        <div style={{display:"flex", gap:8, alignItems:"center", marginBottom:12}}>
          <span style={{fontSize:12, color:T.textMuted, flexShrink:0}}>Price range</span>
          <div style={{position:"relative", flex:1}}>
            <span style={{position:"absolute", left:10, top:"50%", transform:"translateY(-50%)",
              fontSize:12, color:T.textMuted, pointerEvents:"none"}}>$</span>
            <input
              style={{...S.input, paddingLeft:22, fontSize:12}}
              type="number" min="0" step="0.01"
              placeholder="Min"
              value={priceMin}
              onChange={e=>setPriceMin(e.target.value)}
            />
          </div>
          <span style={{fontSize:12, color:T.textFaint, flexShrink:0}}>–</span>
          <div style={{position:"relative", flex:1}}>
            <span style={{position:"absolute", left:10, top:"50%", transform:"translateY(-50%)",
              fontSize:12, color:T.textMuted, pointerEvents:"none"}}>$</span>
            <input
              style={{...S.input, paddingLeft:22, fontSize:12}}
              type="number" min="0" step="0.01"
              placeholder="Max"
              value={priceMax}
              onChange={e=>setPriceMax(e.target.value)}
            />
          </div>
          {(priceMin || priceMax) && (
            <button
              style={{...S.btn("ghost"), fontSize:11, padding:"5px 8px", flexShrink:0}}
              onClick={()=>{ setPriceMin(""); setPriceMax(""); }}>
              ✕ Clear
            </button>
          )}
        </div>
        {[...showCards]
          .sort((a,b)=>{
            if (showSort==="name_asc")   return (a.CardName||"").toLowerCase().localeCompare((b.CardName||"").toLowerCase());
            if (showSort==="name_desc")  return (b.CardName||"").toLowerCase().localeCompare((a.CardName||"").toLowerCase());
            if (showSort==="price_desc") return (parseFloat(b.ListPrice)||0) - (parseFloat(a.ListPrice)||0);
            if (showSort==="price_asc")  return (parseFloat(a.ListPrice)||0) - (parseFloat(b.ListPrice)||0);
            if (showSort==="cost_desc")  return (parseFloat(b.AcquisitionCost)||0) - (parseFloat(a.AcquisitionCost)||0);
            if (showSort==="cost_asc")   return (parseFloat(a.AcquisitionCost)||0) - (parseFloat(b.AcquisitionCost)||0);
            if (showSort==="game")       return (a.GameType||"").localeCompare(b.GameType||"") || (a.CardName||"").localeCompare(b.CardName||"");
            return 0;
          })
          .map(item=>(
          <div key={item.ID} style={S.cardB(item.Status==="Sold"?"#0a1f0a":undefined)}>
            <div style={{display:"flex",gap:12,alignItems:"center"}}>
              {item.ImageURL ? (
                <img src={item.ImageURL} alt={item.CardName}
                  style={{width:52,height:72,objectFit:"cover",borderRadius:6,
                    border:`0.5px solid ${T.border}`,flexShrink:0,cursor:"pointer"}}
                  onClick={()=>window.open(item.ImageURL,"_blank")} />
              ) : (
                <div style={{width:52,height:72,borderRadius:6,
                  border:`1px dashed ${T.border}`,flexShrink:0,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:18,color:T.textFaint}}>⬡</div>
              )}
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
                  <div style={{flex:1,marginRight:8}}>
                    <div style={{fontWeight:600,fontSize:15}}>{item.CardName}</div>
                    <div style={{fontSize:12,color:T.textMuted}}>
                      {[item.Set,item.Condition,item.Rarity&&item.Rarity!=="Common"&&item.Rarity,
                        item.Language!=="English"&&item.Language].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{textAlign:"right",flexShrink:0}}>
                    {item.Status==="Sold"
                      ? <span style={S.badge("green")}>Sold {fmt(item.SoldPrice)}</span>
                      : <div style={{fontSize:17,fontWeight:700,color:"#3b82f6"}}>{fmt(item.ListPrice)}</div>}
                  </div>
                </div>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:T.textFaint}}>
                    Cost: {fmt(item.AcquisitionCost)}
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button
                      style={{...S.btn("sm"),background:"rgba(60,102,238,0.1)",color:"#3C66EE",
                        borderColor:"rgba(60,102,238,0.3)"}}
                      onClick={e=>{
                        const cardUrl = `https://app.vendorstax.com?s=${item.QRCode||item.ID}`;
                        navigator.clipboard.writeText(cardUrl).then(()=>{
                          e.target.textContent="✓ Copied!";
                          setTimeout(()=>{ if(e.target) e.target.textContent="🏷 Label"; },1200);
                        });
                      }}>
                      🏷 Label
                    </button>
                    {activeShow.Status!=="Completed" && item.Status!=="Sold" && (
                      <button style={{...S.btn("sm"),background:"#2e0f0f",color:"#f87171"}}
                        onClick={async ()=>{
                          await onRemoveShowItem(activeShow.ID, item.ID);
                          await onUpdateItem(item.ID, {Status:"In Stock", ShowID:"", ShowName:""});
                        }}>Remove</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {(activeShow.Status==="Upcoming"||activeShow.Status==="Active") && (
          <>
            <div style={S.divider}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8,flexWrap:"wrap",gap:8}}>
              <div style={S.h2}>Add Cards to Show <span style={{fontWeight:400,color:T.textMuted}}>(search by name or set)</span></div>
              {available.length>=10 && (
                <button
                  style={{...S.btn("sm"),background:"#0f2e1a",color:"#4ade80",flexShrink:0}}
                  onClick={async ()=>{
                    const count = available.length;
                    const ok = await confirmAction(
                      `Adding ${count} card${count===1?"":"s"} to this show.`,
                      {confirmLabel:"Confirm"}
                    );
                    if (!ok) return;
                    try {
                      for (const item of available) {
                        await onAddShowItem(activeShow.ID, item.ID);
                        await onUpdateItem(item.ID, {Status:"Reserved", ShowID:activeShow.ID, ShowName:activeShow.Name});
                      }
                      toast(`${count} card${count===1?"":"s"} added to ${activeShow.Name}`, "success");
                    } catch(e) {
                      toast("Bulk add failed: " + e.message, "error");
                    }
                  }}>
                  📥 Bulk Add
                </button>
              )}
            </div>
            <input style={{...S.input,marginBottom:8}} value={search}
              onChange={e=>setSearch(e.target.value)} placeholder="Search inventory to add cards..." />
            <div style={{display:"flex",gap:8,marginBottom:10,alignItems:"center"}}>
              <input style={{...S.input,fontSize:13}} type="number" value={priceMin}
                onChange={e=>setPriceMin(e.target.value)} placeholder="Min $" />
              <div style={{color:T.textMuted,fontSize:13,flexShrink:0}}>—</div>
              <input style={{...S.input,fontSize:13}} type="number" value={priceMax}
                onChange={e=>setPriceMax(e.target.value)} placeholder="Max $" />
            </div>
            {available.slice(0,20).map(item=>(
              <div key={item.ID} style={{...S.card,marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  {item.ImageURL ? (
                    <img src={item.ImageURL} alt={item.CardName}
                      style={{width:36,height:50,objectFit:"cover",borderRadius:4,
                        border:`0.5px solid ${T.border}`,flexShrink:0}} />
                  ) : (
                    <div style={{width:36,height:50,borderRadius:4,
                      border:`0.5px dashed ${T.border}`,flexShrink:0,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:14,color:T.textFaint}}>⬡</div>
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:500}}>{item.CardName}</div>
                    <div style={{fontSize:12,color:T.textMuted}}>
                      {[item.Set,item.Condition,item.Variant,item.Rarity&&item.Rarity!=="Common"&&item.Rarity].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{fontSize:12,color:"#3b82f6",fontWeight:500}}>{fmt(item.ListPrice)}</div>
                  </div>
                  <button style={{...S.btn("sm"),background:"#0f2e1a",color:"#4ade80",flexShrink:0}}
                    onClick={async ()=>{
                      await onAddShowItem(activeShow.ID, item.ID);
                      await onUpdateItem(item.ID, {Status:"Reserved", ShowID:activeShow.ID, ShowName:activeShow.Name});
                    }}>+ Add</button>
                </div>
              </div>
            ))}
            {search && available.length===0 && (
              <div style={{fontSize:13,color:T.textFaint,textAlign:"center",padding:"12px 0"}}>No available cards match</div>
            )}
          </>
        )}

        {(() => {
          const showTrades = (trades||[]).filter(t =>
            t.Notes && t.Notes.includes(activeShow.Name)
          );
          if (showTrades.length === 0) return null;
          const totalGain = showTrades.reduce((s,t)=>s+(t.TotalCreditValue||0),0);
          return (
            <div style={{marginTop:16}}>
              <div style={S.divider}/>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                <div style={S.h2}>Trades at this Show ({showTrades.length})</div>
                <span style={{fontSize:12,color:"var(--cv-pink,#c0387a)",fontWeight:600}}>{fmt(totalGain)} total credit</span>
              </div>
              {showTrades.map(t=>(
                <div key={t.ID} style={{...S.card,marginBottom:8,borderColor:"rgba(60,102,238,0.15)"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12,fontWeight:600,marginBottom:2}}>
                        {t.Date}
                      </div>
                      {t.CardsOut && (
                        <div style={{fontSize:11,color:T.textMuted,marginBottom:2}}>
                          <span style={{color:"#f87171"}}>Out:</span> {t.CardsOut}
                        </div>
                      )}
                      {t.CardsIn && (
                        <div style={{fontSize:11,color:T.textMuted,marginBottom:2}}>
                          <span style={{color:"#4ade80"}}>In:</span> {t.CardsIn}
                        </div>
                      )}
                      {t.Notes && (
                        <div style={{fontSize:10,color:T.textFaint,marginTop:4,fontStyle:"italic"}}>
                          {t.Notes.replace(activeShow.Name,"").replace(/^ · /,"").slice(0,80)}
                        </div>
                      )}
                    </div>
                    <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                      <div style={{fontSize:10,color:T.textMuted}}>Credit</div>
                      <div style={{fontSize:15,fontWeight:700,color:"var(--cv-pink,#c0387a)"}}>
                        {fmt(t.TotalCreditValue)}
                      </div>
                      <div style={{fontSize:10,color:T.textFaint}}>
                        {Math.round((t.CreditRate||0)*100)}% rate
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {stats.bought && stats.bought.length > 0 && (
          <div style={{marginTop:16}}>
            <div style={S.divider}/>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={S.h2}>Bought at Show ({stats.bought.length})</div>
              <span style={{fontSize:12,color:"#f87171",fontWeight:600}}>
                {fmt(stats.boughtCost)} spent
              </span>
            </div>
            {stats.bought.map(item=>(
              <div key={item.ID} style={{...S.card,marginBottom:6}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:500,overflow:"hidden",
                      textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{item.CardName}</div>
                    <div style={{fontSize:11,color:T.textMuted}}>
                      {[item.Set,item.Condition,item.Variant,item.Number?`#${item.Number}`:null].filter(Boolean).join(" · ")}
                    </div>
                    {item.Notes?.includes("Photo:") && (() => {
                      const photoUrl = extractNotesPhoto(item.Notes).photoUrl;
                      return photoUrl ? (
                        <img src={photoUrl} alt="Purchase"
                          style={{marginTop:6,width:"100%",maxHeight:120,
                            objectFit:"cover",borderRadius:6,
                            border:`1px solid ${T.border}`,cursor:"pointer"}}
                          onClick={()=>window.open(photoUrl,"_blank")} />
                      ) : null;
                    })()}
                  </div>
                  <div style={{textAlign:"right",flexShrink:0,marginLeft:12}}>
                    <div style={{fontSize:14,fontWeight:700,color:"#f87171"}}>
                      {fmt(item.AcquisitionCost)}
                    </div>
                    <div style={{fontSize:10,color:T.textMuted}}>{item.AcquisitionDate}</div>
                    <div style={{fontSize:10,color:item.Status==="Sold"?"#4ade80":T.textFaint}}>
                      {item.Status}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>)}
      </div>
    );
  }

  // ── CALENDAR VIEW ──────────────────────────────────────────────────────────
  if (view === "calendar") {
    const MONTHS = ["January","February","March","April","May","June",
                    "July","August","September","October","November","December"];
    const DAYS   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

    const minDate = new Date(today.getFullYear(), today.getMonth(), 1);
    const maxDate = new Date(today.getFullYear(), today.getMonth() + 12, 0);

    const prevMonth = () => {
      if (calYear === minDate.getFullYear() && calMonth === minDate.getMonth()) return;
      if (calMonth === 0) { setCalMonth(11); setCalYear(y => y - 1); }
      else setCalMonth(m => m - 1);
    };
    const nextMonth = () => {
      const next = new Date(calYear, calMonth + 1, 1);
      if (next > maxDate) return;
      if (calMonth === 11) { setCalMonth(0); setCalYear(y => y + 1); }
      else setCalMonth(m => m + 1);
    };

    const firstDay = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);

    const showsForDay = (day) => {
      if (!day) return [];
      const dateStr = `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
      return shows.filter(s => {
        if (s.Status === "Deleted") return false;
        const start = s.StartDate || "";
        const end   = s.EndDate   || s.StartDate || "";
        return dateStr >= start && dateStr <= end;
      });
    };

    const todayStr = today.toISOString().split("T")[0];
    const isToday = (day) => day &&
      `${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}` === todayStr;

    const statusDot = (show) =>
      show.Status === "Active"    ? "#4ade80" :
      show.Status === "Completed" ? "#60a5fa" : "#fbbf24";

    return (
      <div style={S.page}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={{display:"flex",gap:6}}>
            <button style={{...S.btn("ghost"),padding:"6px 12px",fontSize:12}}
              onClick={()=>setView("list")}>☰ List</button>
            <button style={{...S.btn("primary"),padding:"6px 12px",fontSize:12}}>
              📅 Calendar</button>
          </div>
          <button style={S.btn("primary")} onClick={()=>setView("new")}>+ New Show</button>
        </div>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
          <button style={{...S.btn("ghost"),padding:"6px 12px",fontSize:18}}
            onClick={prevMonth}>‹</button>
          <div style={{fontSize:16,fontWeight:700}}>
            {MONTHS[calMonth]} {calYear}
          </div>
          <button style={{...S.btn("ghost"),padding:"6px 12px",fontSize:18}}
            onClick={nextMonth}>›</button>
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:4}}>
          {DAYS.map(d => (
            <div key={d} style={{textAlign:"center",fontSize:10,fontWeight:600,
              color:T.textMuted,padding:"4px 0",
              fontFamily:"'DM Mono',monospace",letterSpacing:"0.05em"}}>
              {d}
            </div>
          ))}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
          {cells.map((day, i) => {
            const dayShows = showsForDay(day);
            const isT = isToday(day);
            return (
              <div key={i} style={{
                minHeight:56,
                borderRadius:6,
                background: isT ? "rgba(59,130,246,0.15)" : day ? T.surface2 : "transparent",
                border: isT ? `1px solid #3b82f6` : `1px solid ${day ? T.border : "transparent"}`,
                padding:"4px 5px",
                cursor: dayShows.length > 0 ? "pointer" : "default",
                opacity: day ? 1 : 0,
              }}
              onClick={() => {
                if (dayShows.length === 1) { setActive(dayShows[0]); setView("detail"); }
              }}>
                {day && (
                  <>
                    <div style={{fontSize:11,fontWeight:isT?700:400,
                      color:isT?"#3b82f6":T.textMuted,marginBottom:3}}>
                      {day}
                    </div>
                    {dayShows.slice(0,2).map(show => (
                      <div key={show.ID}
                        onClick={e=>{e.stopPropagation();setActive(show);setView("detail");}}
                        style={{
                          fontSize:9, fontWeight:600, lineHeight:1.3,
                          background: statusDot(show) + "22",
                          border: `1px solid ${statusDot(show)}55`,
                          borderLeft: `3px solid ${statusDot(show)}`,
                          borderRadius:3, padding:"2px 4px",
                          marginBottom:2, cursor:"pointer",
                          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                          color: T.text,
                        }}>
                        {show.Name}
                      </div>
                    ))}
                    {dayShows.length > 2 && (
                      <div style={{fontSize:9,color:T.textFaint}}>+{dayShows.length-2} more</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div style={{display:"flex",gap:14,marginTop:14,flexWrap:"wrap"}}>
          {[["Upcoming","#fbbf24"],["Active","#4ade80"],["Completed","#60a5fa"]].map(([label,color])=>(
            <div key={label} style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:T.textMuted}}>
              <div style={{width:10,height:10,borderRadius:2,background:color+"44",
                border:`2px solid ${color}`,borderLeft:`3px solid ${color}`}}/>
              {label}
            </div>
          ))}
        </div>

        {(() => {
          const upcoming = shows
            .filter(s => s.Status !== "Deleted" && s.StartDate &&
              new Date(s.StartDate) >= new Date(calYear, calMonth, 1) &&
              new Date(s.StartDate) <= new Date(calYear, calMonth + 1, 0))
            .sort((a,b) => a.StartDate.localeCompare(b.StartDate));
          if (upcoming.length === 0) return null;
          return (
            <div style={{marginTop:20}}>
              <div style={{fontSize:12,fontWeight:600,color:T.textMuted,
                marginBottom:8,textTransform:"uppercase",letterSpacing:"0.05em",
                fontFamily:"'DM Mono',monospace"}}>
                This month
              </div>
              {upcoming.map(show => {
                const stats = showStats(show);
                return (
                  <div key={show.ID} style={{...S.card,cursor:"pointer",marginBottom:8}}
                    onClick={()=>{setActive(show);setView("detail");}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <div>
                        <div style={{fontWeight:600,fontSize:14}}>{show.Name}</div>
                        <div style={{fontSize:11,color:T.textMuted,marginTop:2}}>
                          {show.Location && `${show.Location} · `}
                          {show.StartDate}{show.EndDate && ` → ${show.EndDate}`}
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <span style={{fontSize:10,fontWeight:700,padding:"3px 8px",borderRadius:10,
                          color:statusColor(show),border:`1px solid ${statusColor(show)}`,
                          background:"transparent"}}>
                          {show.Status}
                        </span>
                        <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>
                          {stats.brought.length} cards
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <>
    <div style={S.page}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          <div style={{fontSize:12,color:"#444",fontFamily:"'DM Mono',monospace"}}>{shows.filter(s=>s.Status!=="Deleted"&&s.Status!=="Archived").length} shows</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px",fontSize:12}}
            onClick={()=>setView("calendar")}>📅 Calendar</button>
        </div>
        <button style={S.btn("primary")} onClick={()=>setView("new")}>+ New Show</button>
      </div>

      {shows.length===0 && (
        <div style={{textAlign:"center",padding:"40px 0",color:"#333",fontSize:13}}>
          No shows yet. Create one to start tracking your card show inventory.
        </div>
      )}

      {shows.filter(s=>s.Status!=="Deleted"&&s.Status!=="Archived"&&s.Status!=="Completed").slice().reverse().map(show=>{
        const stats = showStats(show);
        return (
          <div key={show.ID} style={{...S.card,cursor:"pointer"}} onClick={()=>{setActive(show);setView("detail");}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{fontWeight:600,fontSize:15}}>{show.Name}</div>
                <div style={{fontSize:12,color:"#555",marginTop:2}}>
                  {show.Location&&`${show.Location} · `}{show.StartDate}{show.EndDate&&` → ${show.EndDate}`}
                </div>
              </div>
              <span style={{...S.badge(),color:statusColor(show),border:`1px solid ${statusColor(show)}`,background:"transparent"}}>
                {show.Status}
              </span>
            </div>
            <div style={{display:"flex",gap:16,fontSize:12,color:"#666"}}>
              <span>{stats.brought.length} cards</span>
              <span style={{color:"#4ade80"}}>{stats.sold.length} sold</span>
              <span style={{color:"#3b82f6"}}>{fmt(stats.revenue)} revenue</span>
              <span style={{color:stats.profit>=0?"#4ade80":"#f87171"}}>{fmt(stats.profit)} P&L</span>
            </div>
          </div>
        );
      })}

      {shows.filter(s=>s.Status==="Completed"||s.Status==="Archived").length > 0 && (
        <div style={{marginTop:16}}>
          <div style={{fontSize:11,color:T.textFaint,fontFamily:"'DM Mono',monospace",
            textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>
            Completed Shows ({shows.filter(s=>s.Status==="Completed"||s.Status==="Archived").length})
          </div>
          {shows.filter(s=>s.Status==="Completed"||s.Status==="Archived").slice().reverse().map(show=>{
            const stats = showStats(show);
            return (
              <div key={show.ID} style={{...S.card,cursor:"pointer",opacity:0.7,
                background:T.surface2,borderColor:T.border}}
                onClick={()=>{setActive(show);setView("detail");}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                  <div>
                    <div style={{fontWeight:600,fontSize:15,color:T.textMuted}}>{show.Name}</div>
                    <div style={{fontSize:12,color:T.textFaint,marginTop:2}}>
                      {show.Location&&`${show.Location} · `}{show.StartDate}{show.EndDate&&` → ${show.EndDate}`}
                    </div>
                  </div>
                  <span style={{...S.badge(),color:T.textFaint,border:`1px solid ${T.border}`,background:"transparent"}}>
                    Completed
                  </span>
                </div>
                <div style={{display:"flex",gap:16,fontSize:12,color:T.textFaint}}>
                  <span>{stats.brought.length} cards</span>
                  <span>{stats.sold.length} sold</span>
                  <span>{fmt(stats.revenue)} revenue</span>
                  <span style={{color:stats.profit>=0?T.green:T.red}}>{fmt(stats.profit)} P&L</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>

    {/* ── Carry Forward Progress Modal ─────────────────────────────────────── */}
    {carryProgress && (() => {
      const { done, total, startTime } = carryProgress;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      const elapsed = (Date.now() - startTime) / 1000;
      const rate = done > 0 ? elapsed / done : 0;
      const remaining = Math.max(0, Math.round(rate * (total - done)));
      const timeStr = remaining > 60
        ? `~${Math.round(remaining/60)} min remaining`
        : remaining > 0
        ? `~${remaining}s remaining`
        : "Almost done…";
      return (
        <div style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.75)",
          zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center",
          padding:24,
        }}>
          <div style={{
            background:T.surface, border:`1px solid ${T.border}`,
            borderRadius:16, padding:28, maxWidth:380, width:"100%",
            textAlign:"center", boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
          }}>
            <div style={{fontSize:36, marginBottom:12}}>🎪</div>
            <div style={{fontSize:18, fontWeight:700, marginBottom:6}}>
              Moving cards to next show…
            </div>
            <div style={{fontSize:13, color:T.textMuted, marginBottom:20, lineHeight:1.6}}>
              Please don't close or refresh this screen.<br/>
              Your cards are being transferred one by one.
            </div>

            <div style={{background:T.surface2, borderRadius:100, height:10, marginBottom:10, overflow:"hidden"}}>
              <div style={{
                height:"100%", borderRadius:100,
                background:"linear-gradient(90deg,#3C66EE,#4ade80)",
                width:`${pct}%`,
                transition:"width 0.3s ease",
              }}/>
            </div>

            <div style={{display:"flex", justifyContent:"space-between", fontSize:12, color:T.textMuted, marginBottom:8}}>
              <span>{done} of {total} cards</span>
              <span style={{fontWeight:600, color:T.text}}>{pct}%</span>
            </div>

            <div style={{fontSize:12, color:T.textFaint}}>{done > 0 ? timeStr : "Starting…"}</div>

            <div style={{
              marginTop:16, padding:"8px 12px",
              background:"rgba(248,113,113,0.08)", border:"0.5px solid rgba(248,113,113,0.3)",
              borderRadius:8, fontSize:12, color:"#f87171",
            }}>
              ⚠️ Do not close this tab until complete
            </div>
          </div>
        </div>
      );
    })()}
    </>
  );
}

function ShowsPage(props) {
  const { shows, inventory, showItems } = props;
  const { isPaid } = usePlan();
  const [preShowShow, setPreShowShow] = useState(null);
  const [preShowDismissed, setPreShowDismissed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("preShowDismissed") || "[]")); }
    catch { return new Set(); }
  });

  useEffect(() => {
    if (!isPaid) return;
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const dueShow = shows.find(s =>
      s.Status === "Upcoming" && s.StartDate === tomorrow && !preShowDismissed.has(s.ID)
    );
    if (dueShow) setPreShowShow(dueShow);
  }, [shows, isPaid]);

  const dismissPreShow = (showId) => {
    setPreShowShow(null);
    setPreShowDismissed(d => {
      const next = new Set(d).add(showId);
      try { sessionStorage.setItem("preShowDismissed", JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const preShowCards = (show) => {
    if (!show) return [];
    const ids = showItems.filter(si => si.ShowID === show.ID).map(si => si.InventoryID);
    return inventory.filter(i => ids.includes(i.ID));
  };

  return (
    <>
      <ShowsPageInner {...props} onOpenPreShow={setPreShowShow} />
      {preShowShow && (
        <PreShowSummaryModal
          show={preShowShow}
          cards={preShowCards(preShowShow)}
          onClose={() => dismissPreShow(preShowShow.ID)}
        />
      )}
    </>
  );
}

function ShowPerformancePanel({ stats, show, S, T }) {
  const fmt = n => `$${(+n||0).toFixed(2)}`;
  const pct = n => `${(+n||0).toFixed(1)}%`;

  const buysAtShow    = (stats.bought||[]);
  const tradeIns      = (stats.tradeIns||[]);
  const unrealizedGain= buysAtShow.reduce((s,i)=>s+(parseFloat(i.ListPrice)||0),0)
                       + tradeIns.reduce((s,i)=>s+(parseFloat(i.ListPrice)||0),0);
  const buysAtShowCost= buysAtShow.reduce((s,i)=>s+(i.AcquisitionCost||0),0);
  const tradeInCost   = tradeIns.reduce((s,i)=>s+(i.AcquisitionCost||0),0);

  const sellThru = stats.brought.length > 0
    ? (stats.sold.length / stats.brought.length) * 100 : 0;
  const avgSale  = stats.sold.length > 0
    ? stats.revenue / stats.sold.length : 0;

  return (
    <div style={{marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:700,color:"#88bdf2",marginBottom:10}}>📊 Show Performance</div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:8}}>
        <div style={S.stat}>
          <div style={S.statLbl}>Revenue</div>
          <div style={{...S.statVal,color:"#4ade80",fontSize:16}}>{fmt(stats.revenue)}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Net Profit</div>
          <div style={{...S.statVal,color:stats.netProfit>=0?"#4ade80":"#f87171",fontSize:16}}>{fmt(stats.netProfit)}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Show Costs</div>
          <div style={{...S.statVal,color:"#f87171",fontSize:16}}>{fmt(stats.operatingCost)}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Sold / Total</div>
          <div style={{...S.statVal,fontSize:14}}>{stats.sold.length} / {stats.brought.length}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Sell-Through</div>
          <div style={{...S.statVal,color:"#60a5fa",fontSize:14}}>{pct(sellThru)}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Avg Sale</div>
          <div style={{...S.statVal,fontSize:14}}>{fmt(avgSale)}</div>
        </div>
      </div>

      {(buysAtShow.length > 0 || tradeIns.length > 0) && (
        <div style={{...S.infoBox,marginBottom:8,borderColor:"rgba(139,92,246,0.35)",
          background:"rgba(139,92,246,0.06)"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#a78bfa",marginBottom:6}}>
            📦 Unrealized Inventory Gain
          </div>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:8,lineHeight:1.5}}>
            Cards acquired at this show not yet sold — potential future value.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            {buysAtShow.length > 0 && (<>
              <div>
                <div style={S.statLbl}>Purchased ({buysAtShow.length} cards)</div>
                <div style={{fontSize:14,fontWeight:700,color:"#a78bfa"}}>
                  {fmt(buysAtShow.reduce((s,i)=>s+(parseFloat(i.ListPrice)||0),0))}
                </div>
                <div style={{fontSize:10,color:T.textFaint}}>
                  Cost: {fmt(buysAtShowCost)}
                </div>
              </div>
            </>)}
            {tradeIns.length > 0 && (<>
              <div>
                <div style={S.statLbl}>Trade-Ins ({tradeIns.length} cards)</div>
                <div style={{fontSize:14,fontWeight:700,color:"#a78bfa"}}>
                  {fmt(tradeIns.reduce((s,i)=>s+(parseFloat(i.ListPrice)||0),0))}
                </div>
                <div style={{fontSize:10,color:T.textFaint}}>
                  Credit given: {fmt(tradeInCost)}
                </div>
              </div>
            </>)}
          </div>
          <div style={{marginTop:8,paddingTop:8,borderTop:`0.5px solid rgba(139,92,246,0.25)`,
            display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontSize:12,color:T.textMuted}}>Total unrealized list value</span>
            <span style={{fontSize:14,fontWeight:700,color:"#a78bfa"}}>{fmt(unrealizedGain)}</span>
          </div>
        </div>
      )}

      {stats.operatingCost > 0 && (
        <div style={{fontSize:11,color:T.textFaint,marginBottom:8}}>
          Costs: Table {fmt(show.TableCost||0)}
          {show.Marketing>0?` · Marketing ${fmt(show.Marketing)}`:""}
          {show.Travel>0?` · Travel ${fmt(show.Travel)}`:""}
          {show.Food>0?` · Food ${fmt(show.Food)}`:""}
        </div>
      )}
      <div style={S.divider}/>
    </div>
  );
}

// ─── INVENTORY LIST ───────────────────────────────────────────────────────────
function InventoryList({ inventory, lots, shows, showItems, priceHistory, onEdit, onSell, onDelete, onBulkEdit, onRestock, onAddToShow, onUpdateItem, showLocation, locationOptions, onAddLocationOption, dynamicPricingEnabled, isPaid = true, isEnterprise = false, locations = [], activeLocationId = null }) {
  const { S, T } = useTheme();
  const [showDeleteAll, setShowDeleteAll]         = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletingAll, setDeletingAll]             = useState(false);
  const [deleteProgress, setDeleteProgress]       = useState(0);
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [gameFilter, setGameFilter]     = useState("all");
  const [typeFilter, setTypeFilter]     = useState("all");
  const [storeLocationFilter, setStoreLocationFilter] = useState(activeLocationId || "all");
  const prevActiveLocationId = useRef(activeLocationId);
  useEffect(() => {
    if (activeLocationId !== prevActiveLocationId.current) {
      setStoreLocationFilter(activeLocationId || "all");
      prevActiveLocationId.current = activeLocationId;
    }
  }, [activeLocationId]);
  const [lotFilter, setLotFilter]       = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [sort, setSort]                 = useState("newest");
  const [priceMin, setPriceMin]         = useState("");
  const [priceMax, setPriceMax]         = useState("");
  const PAGE_SIZE = 50;
  const [page, setPage]                 = useState(1);

  const prevSearch = useRef(search);
  const prevStatus = useRef(statusFilter);
  const prevGame   = useRef(gameFilter);
  const prevType   = useRef(typeFilter);
  const prevStoreLocation = useRef(storeLocationFilter);
  useEffect(() => {
    if (prevSearch.current !== search || prevStatus.current !== statusFilter
        || prevGame.current !== gameFilter || prevType.current !== typeFilter
        || prevStoreLocation.current !== storeLocationFilter) {
      setPage(1);
      prevSearch.current = search;
      prevStatus.current = statusFilter;
      prevGame.current   = gameFilter;
      prevType.current   = typeFilter;
      prevStoreLocation.current = storeLocationFilter;
    }
  }, [search, statusFilter, gameFilter, typeFilter, storeLocationFilter]);

  const activeShow = useMemo(() =>
    shows?.find(s => s.Status === "Active") ?? null,
  [shows]);

  const showCardSet = useMemo(() => {
    if (!activeShow || !showItems) return new Set();
    return new Set(
      showItems.filter(si => si.ShowID === activeShow.ID).map(si => si.InventoryID)
    );
  }, [activeShow, showItems]);

  const handleEdit    = useCallback((item) => onEdit(item),    [onEdit]);
  const handleSell    = useCallback((item) => onSell(item),    [onSell]);
  const handleDelete  = useCallback((id)   => onDelete(id),    [onDelete]);
  const handleRestock = useCallback((item) => onRestock(item), [onRestock]);
  const handleAddToShow = useCallback((showId, invId) => onAddToShow(showId, invId), [onAddToShow]);

  const filtered = useMemo(()=>{
    let items = inventory.filter(item=>{
      if (item.Status==="Deleted") return false;
      if (statusFilter==="Active"    && item.Status!=="In Stock" && item.Status!=="Reserved") return false;
      if (statusFilter==="In Stock"  && item.Status!=="In Stock")   return false;
      if (statusFilter==="Reserved"  && item.Status!=="Reserved")   return false;
      if (statusFilter==="Sold"      && item.Status!=="Sold")       return false;
      if (statusFilter==="Non-Movers"&& (item.Status==="Sold"||daysSince(item.AcquisitionDate)<30)) return false;
      if (statusFilter==="No Image"  && item.ImageURL) return false;
      if (statusFilter==="No Image"  && item.Status==="Sold") return false;
      if (statusFilter==="Online"      && !item.IsOnline) return false;
      if (gameFilter!=="all" && (item.GameType||"Pokemon")!==gameFilter) return false;
      if (typeFilter==="card"   && item.Type==="Sealed Product") return false;
      if (typeFilter==="sealed" && item.Type!=="Sealed Product") return false;
      if (storeLocationFilter!=="all" && item.LocationID!==storeLocationFilter) return false;
      if (lotFilter && item.LotID!==lotFilter) return false;
      if (languageFilter) {
        const lang = item.Language || "English";
        if (lang.toLowerCase() !== languageFilter.toLowerCase()) return false;
      }
      if (search) {
        const q = search.toLowerCase().trim();
        const matchesItem =
          item.CardName?.toLowerCase().includes(q) ||
          item.Set?.toLowerCase().includes(q) ||
          item.Number?.toLowerCase().includes(q) ||
          item.Rarity?.toLowerCase().includes(q) ||
          item.Condition?.toLowerCase().includes(q) ||
          item.Grade?.toLowerCase().includes(q) ||
          item.LotName?.toLowerCase().includes(q) ||
          item.Notes?.toLowerCase().includes(q) ||
          `${item.CardName || ""} ${item.Number || ""}`.toLowerCase().includes(q) ||
          `${item.Number || ""} ${item.CardName || ""}`.toLowerCase().includes(q) ||
          (() => {
            const num = (item.Number || "").split("/")[0].trim();
            return `${item.CardName || ""} ${num}`.toLowerCase().includes(q) ||
                   `${num} ${item.CardName || ""}`.toLowerCase().includes(q);
          })();
        if (!matchesItem) return false;
      }
      const price = parseFloat(item.ListPrice) || 0;
      if (priceMin !== "" && price < parseFloat(priceMin)) return false;
      if (priceMax !== "" && price > parseFloat(priceMax)) return false;
      return true;
    });
    if (sort==="newest")     items=items.sort((a,b)=>(b.CreatedAt||"").localeCompare(a.CreatedAt||""));
    if (sort==="oldest")     items=items.sort((a,b)=>(a.AcquisitionDate||"").localeCompare(b.AcquisitionDate||""));
    if (sort==="az")         items=items.sort((a,b)=>(a.CardName||"").toLowerCase().localeCompare((b.CardName||"").toLowerCase()));
    if (sort==="za")         items=items.sort((a,b)=>(b.CardName||"").toLowerCase().localeCompare((a.CardName||"").toLowerCase()));
    if (sort==="price_high") items=items.sort((a,b)=>b.ListPrice-a.ListPrice);
    if (sort==="price_low")  items=items.sort((a,b)=>a.ListPrice-b.ListPrice);
    return items;
  },[inventory,search,statusFilter,gameFilter,typeFilter,storeLocationFilter,lotFilter,languageFilter,sort,priceMin,priceMax]);

  return (
    <div style={S.page}>
      <div style={{...S.card, padding:20, marginBottom:14}}>
        <div style={{position:"relative", marginBottom:16}}>
          <span style={{position:"absolute", left:16, top:"50%", transform:"translateY(-50%)",
            fontSize:16, color:T.textMuted, pointerEvents:"none"}}>🔍</span>
          <input style={{...S.input, height:52, paddingLeft:42, fontSize:15}} value={search}
            onChange={e=>setSearch(e.target.value)} placeholder="Search by name, #, set, condition, grade…"/>
        </div>

        <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:6,scrollbarWidth:"none",marginBottom:10}}>
          {["Active","In Stock","Reserved","Sold","Non-Movers","No Image","Consignment","Online"].map(s=>(
            <button key={s} style={S.chip(statusFilter===s)} onClick={()=>setStatusFilter(s)}>{s==="Active"?"In Stock + Reserved":s==="Consignment"?"💎 Consignment":s==="Online"?"🌐 Online":s}</button>
          ))}
        </div>

        <div style={{display:"flex", gap:8, marginBottom:14}}>
          <select style={{...S.select, fontSize:13, flex:1}}
            value={gameFilter}
            onChange={e=>setGameFilter(e.target.value)}>
            <option value="all">Filter by game…</option>
            <option value="Pokemon">Pokémon</option>
            <option value="Lorcana">Lorcana</option>
            <option value="Magic">Magic</option>
            <option value="One Piece">One Piece</option>
            <option value="Yu-Gi-Oh">Yu-Gi-Oh</option>
          </select>
          <select style={{...S.select, fontSize:13, flex:1}}
            value={typeFilter}
            onChange={e=>setTypeFilter(e.target.value)}>
            <option value="all">Filter by type…</option>
            <option value="card">Card</option>
            <option value="sealed">Sealed</option>
          </select>
          {isEnterprise && locations.length > 1 && (
            <select style={{...S.select, fontSize:13, flex:1}}
              value={storeLocationFilter}
              onChange={e=>setStoreLocationFilter(e.target.value)}>
              <option value="all">All Locations</option>
              {locations.map(l => (
                <option key={l.ID} value={l.ID}>{l.StoreName} · #{l.StoreNumber}</option>
              ))}
            </select>
          )}
        </div>

        <div style={{display:"grid", gridTemplateColumns: lots.length>0 ? "1fr 1fr 1fr auto 1fr" : "1fr 1fr auto 1fr",
          gap:12, alignItems:"center", marginBottom:16}}>
          {lots.length>0 && (
            <select style={{...S.select,fontSize:13,height:46}} value={lotFilter} onChange={e=>setLotFilter(e.target.value)}>
              <option value="">All lots</option>
              {lots.map(l=><option key={l.ID} value={l.ID}>{l.Name}</option>)}
            </select>
          )}
          <select style={{...S.select,fontSize:13,height:46}} value={languageFilter}
            onChange={e=>setLanguageFilter(e.target.value)}>
            <option value="">All languages</option>
            {LANGUAGES.map(l=><option key={l} value={l}>{l}</option>)}
          </select>
          <div style={{position:"relative"}}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",
              fontSize:13,color:T.textMuted,pointerEvents:"none"}}>$</span>
            <input style={{...S.input, height:46, paddingLeft:22, fontSize:13}}
              type="number" min="0" step="0.01" placeholder="Min"
              value={priceMin} onChange={e=>{setPriceMin(e.target.value);setPage(1);}} />
          </div>
          <span style={{fontSize:13, color:T.textMuted, fontWeight:800, textAlign:"center"}}>–</span>
          <div style={{position:"relative"}}>
            <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",
              fontSize:13,color:T.textMuted,pointerEvents:"none"}}>$</span>
            <input style={{...S.input, height:46, paddingLeft:22, fontSize:13}}
              type="number" min="0" step="0.01" placeholder="Max"
              value={priceMax} onChange={e=>{setPriceMax(e.target.value);setPage(1);}} />
          </div>
        </div>
        {(priceMin || priceMax) && (
          <div style={{marginTop:-8, marginBottom:12}}>
            <button style={{...S.btn("ghost"), fontSize:11, padding:"5px 8px"}}
              onClick={()=>{ setPriceMin(""); setPriceMax(""); }}>
              ✕ Clear price filter
            </button>
          </div>
        )}

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{fontSize:15,color:T.textMuted,fontWeight:700}}>
            {filtered.length} item{filtered.length!==1?"s":""}{search.trim() ? " (all shown)" : ""}
          </div>
          <div style={{display:"flex",gap:8}}>
            <select style={{...S.select,width:"auto",fontSize:13,padding:"8px 12px",height:40}}
              value={sort} onChange={e=>setSort(e.target.value)}>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="az">A → Z</option>
              <option value="za">Z → A</option>
              <option value="price_high">Price ↓</option>
              <option value="price_low">Price ↑</option>
            </select>
            {onBulkEdit && <button style={{...S.btn("sm"),background:T.chip,color:T.chipText}} onClick={onBulkEdit}>Bulk Edit</button>}
            <button style={{...S.btn("sm"),background:T.red,color:"#fff"}}
              onClick={()=>setShowDeleteAll(true)}>Delete All</button>
          </div>
        </div>
      </div>

      {showDeleteAll && (
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setShowDeleteAll(false)}>
          <div style={{...S.drawer,textAlign:"center",padding:"32px 24px"}}>
            <div style={{fontSize:36,marginBottom:12}}>⚠️</div>
            <div style={{fontSize:18,fontWeight:700,marginBottom:8,color:"#dc2626"}}>
              Delete All Inventory?
            </div>
            <div style={{fontSize:14,color:T.textMuted,marginBottom:8,lineHeight:1.6}}>
              This will permanently delete <strong style={{color:T.text}}>{inventory.length} cards</strong>.
            </div>
            <div style={{fontSize:13,color:"#f87171",marginBottom:20,lineHeight:1.6}}>
              This cannot be undone. All inventory data will be permanently removed.
            </div>
            <div style={{...S.infoBox,marginBottom:20,textAlign:"left"}}>
              <div style={{fontSize:12,color:T.textMuted,marginBottom:6,fontWeight:600}}>
                Type <strong>DELETE</strong> to confirm:
              </div>
              <input style={{...S.input,textAlign:"center",letterSpacing:4,fontSize:15,fontWeight:700}}
                value={deleteConfirmText}
                onChange={e=>setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="DELETE" />
            </div>
            {deletingAll && (
              <div style={{marginBottom:16}}>
                <div style={{height:6,borderRadius:3,background:T.border,overflow:"hidden",marginBottom:6}}>
                  <div style={{height:"100%",borderRadius:3,background:"#dc2626",
                    width:`${deleteProgress}%`,transition:"width 0.2s"}} />
                </div>
                <div style={{fontSize:12,color:T.textMuted}}>Deleting… {deleteProgress}%</div>
              </div>
            )}
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btn("ghost"),flex:1}}
                onClick={()=>{setShowDeleteAll(false);setDeleteConfirmText("");}}>
                Cancel
              </button>
              <button style={{flex:2,padding:"12px",borderRadius:8,border:"none",
                background:deleteConfirmText==="DELETE"?"#dc2626":"#888",
                color:"#fff",fontFamily:"'Sora',sans-serif",fontSize:14,
                fontWeight:700,cursor:deleteConfirmText==="DELETE"?"pointer":"not-allowed",
                transition:"background 0.2s"}}
                disabled={deleteConfirmText!=="DELETE"||deletingAll}
                onClick={async()=>{
                  if(deleteConfirmText!=="DELETE") return;
                  setDeletingAll(true);
                  let done=0;
                  for (const item of inventory) {
                    await onDelete(item.ID);
                    done++;
                    setDeleteProgress(Math.round((done/inventory.length)*100));
                  }
                  setDeletingAll(false);
                  setShowDeleteAll(false);
                  setDeleteConfirmText("");
                  setDeleteProgress(0);
                }}>
                {deletingAll?`Deleting… ${deleteProgress}%`:"Yes, Delete Everything"}
              </button>
            </div>
          </div>
        </div>
      )}

      {filtered.length===0 && <div style={{textAlign:"center",padding:"40px 0",color:"#333",fontSize:13}}>No items match</div>}
      <div style={{
        display:"grid",
        gridTemplateColumns:"repeat(auto-fill, minmax(min(100%, 480px), 1fr))",
        gap:8,
      }}>
        {(search.trim() ? filtered : filtered.slice(0, page * PAGE_SIZE)).map(item=>(
          <InventoryCard key={item.ID} item={item}
            activeShow={activeShow}
            alreadyInShow={showCardSet.has(item.ID)}
            itemPriceHistory={priceHistory?.[item.ID]}
            onEdit={handleEdit} onSell={handleSell} onDelete={handleDelete}
            onRestock={handleRestock} onAddToShow={handleAddToShow}
            onUpdateItem={onUpdateItem}
            showLocation={showLocation}
            locationOptions={locationOptions}
            onAddLocationOption={onAddLocationOption}
            dynamicPricingEnabled={dynamicPricingEnabled}
            isPaid={isPaid}/>
        ))}
      </div>
      {!search.trim() && filtered.length > page * PAGE_SIZE && (
        <div style={{textAlign:"center",padding:"16px 0"}}>
          <button style={{...S.btn("ghost"),padding:"10px 24px",fontSize:13}}
            onClick={()=>setPage(p=>p+1)}>
            Load more ({filtered.length - page * PAGE_SIZE} remaining)
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CONVERT LOT TO PROPORTIONAL ─────────────────────────────────────────────
function ConvertToProportional({ lot, onSave }) {
  const { S, T } = useTheme();
  const [open, setOpen]   = useState(false);
  const [totalMV, setTotalMV] = useState("");
  const [saving, setSaving]   = useState(false);

  const tv         = parseFloat(totalMV) || 0;
  const ratio      = tv > 0 ? lot.TotalPaid / tv : 0;
  const discountPct = tv > 0 ? ((tv - lot.TotalPaid) / tv) * 100 : 0;

  const save = async () => {
    if (!tv) return;
    setSaving(true);
    await onSave({
      TotalMarketValue: tv,
      AllocationRatio:  +ratio.toFixed(6),
      CostMethod:       "proportional",
    });
    setSaving(false);
    setOpen(false);
  };

  if (!open) return (
    <button style={{...S.btn("ghost"), marginBottom:16, fontSize:12}}
      onClick={()=>setOpen(true)}>
      Convert to Proportional Cost Allocation
    </button>
  );

  return (
    <div style={{...S.infoBox, marginBottom:16}}>
      <div style={{fontSize:13, fontWeight:600, marginBottom:12}}>
        Convert to Proportional Allocation
      </div>
      <div style={{fontSize:12, color:T.textMuted, marginBottom:12, lineHeight:1.6}}>
        Enter the total market value of all cards in this lot.
        Each card's cost basis will be recalculated as:
        card market value × allocation ratio.
      </div>

      <div style={{marginBottom:12}}>
        <label style={S.label}>Total Market Value of All Cards ($)</label>
        <input style={S.input} type="number" value={totalMV}
          onChange={e=>setTotalMV(e.target.value)}
          placeholder={`e.g. ${(lot.TotalPaid * 1.3).toFixed(0)}`} />
      </div>

      {tv > 0 && (
        <div style={{...S.infoBox, marginBottom:12}}>
          <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
            <div>
              <div style={S.statLbl}>You Paid</div>
              <div style={{fontSize:15, fontWeight:700, color:"#f87171"}}>{fmt(lot.TotalPaid)}</div>
            </div>
            <div>
              <div style={S.statLbl}>Market Value</div>
              <div style={{fontSize:15, fontWeight:700}}>{fmt(tv)}</div>
            </div>
            <div>
              <div style={S.statLbl}>Discount</div>
              <div style={{fontSize:15, fontWeight:700, color:"#4ade80"}}>{discountPct.toFixed(1)}%</div>
            </div>
            <div>
              <div style={S.statLbl}>Allocation Ratio</div>
              <div style={{fontSize:15, fontWeight:700}}>{(ratio*100).toFixed(1)}%</div>
            </div>
          </div>
          <div style={{marginTop:10, fontSize:12, color:T.textMuted, lineHeight:1.8}}>
            <div>Example: $150 card → <span style={{color:"#4ade80"}}>{fmt(150*ratio)} cost basis</span></div>
            <div>Example: $15 card → <span style={{color:"#4ade80"}}>{fmt(15*ratio)} cost basis</span></div>
          </div>
        </div>
      )}

      <div style={{display:"flex", gap:8}}>
        <button style={{...S.btn("primary"), flex:1}}
          onClick={save} disabled={saving||!tv}>
          {saving ? "Saving…" : "Convert Lot"}
        </button>
        <button style={S.btn("ghost")} onClick={()=>setOpen(false)}>Cancel</button>
      </div>

      <div style={{fontSize:11, color:T.textMuted, marginTop:10}}>
        Note: This updates the lot settings only. To update individual card cost
        bases, edit each card and re-enter the market value — the new ratio will
        apply automatically.
      </div>
    </div>
  );
}

// ─── SHOPPING LIST PAGE ───────────────────────────────────────────────────────
function ShoppingListPage({ onAddItem, S, T }) {
  const { toast, confirmAction } = useNotify();
  const [lists, setLists]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeList, setActiveList]     = useState(null);
  const [showNewList, setShowNewList]   = useState(false);
  const [newListName, setNewListName]   = useState("");
  const [editListName, setEditListName] = useState(false);
  const [editNameVal, setEditNameVal]   = useState("");
  const [cardName, setCardName]         = useState("");
  const [cardPrice, setCardPrice]       = useState("");
  const [cardSet, setCardSet]           = useState("");
  const [cardImage, setCardImage]       = useState("");
  const [cardNotes, setCardNotes]       = useState("");
  const [saving, setSaving]             = useState(false);
  const [addingToInventory, setAddingToInventory] = useState(false);
  const [swipeId, setSwipeId]           = useState(null);
  const [editingItem, setEditingItem]   = useState(null);
  const touchStart                      = useRef(null);

  useEffect(()=>{
    readShoppingLists().then(data => {
      setLists(data);
      setLoading(false);
    }).catch(()=>setLoading(false));
  }, []);

  const persist = async (id, name, items) => {
    await updateShoppingList(id, name, items);
  };

  const createList = async () => {
    if (!newListName.trim()) return;
    setSaving(true);
    const created = await addShoppingList(newListName.trim());
    setLists(l=>[...l, created]);
    setActiveList(created);
    setNewListName(""); setShowNewList(false); setSaving(false);
  };

  const deleteList = async (id) => {
    if (!await confirmAction("Delete this list?", {confirmLabel:"Delete List"})) return;
    await deleteShoppingList(id);
    setLists(l=>l.filter(x=>x.id!==id));
    setActiveList(null);
    toast("List deleted", "success");
  };

  const renameList = async (id, name) => {
    if (!name.trim()) return;
    const list = lists.find(l=>l.id===id);
    if (!list) return;
    await persist(id, name.trim(), list.items);
    setLists(l=>l.map(x=>x.id===id?{...x,name:name.trim()}:x));
    setActiveList(a=>({...a,name:name.trim()}));
    setEditListName(false);
  };

  const addCard = async () => {
    if (!cardName.trim() || !activeList) return;
    const item = { id: Date.now(), name:cardName.trim(),
      price:parseFloat(cardPrice)||0, set:cardSet.trim(),
      imageUrl:cardImage.trim(), notes:cardNotes.trim(),
      done:false, addedAt:new Date().toISOString() };
    const newItems = [...activeList.items, item];
    await persist(activeList.id, activeList.name, newItems);
    setLists(l=>l.map(x=>x.id===activeList.id?{...x,items:newItems}:x));
    setActiveList(a=>({...a,items:newItems}));
    setCardName(""); setCardPrice(""); setCardSet("");
    setCardImage(""); setCardNotes("");
  };

  const toggleItem = async (itemId) => {
    if (!activeList) return;
    const newItems = activeList.items.map(i=>i.id===itemId?{...i,done:!i.done}:i);
    await persist(activeList.id, activeList.name, newItems);
    setLists(l=>l.map(x=>x.id===activeList.id?{...x,items:newItems}:x));
    setActiveList(a=>({...a,items:newItems}));
    setSwipeId(null);
  };

  const deleteItem = async (itemId) => {
    if (!activeList) return;
    const newItems = activeList.items.filter(i=>i.id!==itemId);
    await persist(activeList.id, activeList.name, newItems);
    setLists(l=>l.map(x=>x.id===activeList.id?{...x,items:newItems}:x));
    setActiveList(a=>({...a,items:newItems}));
    setSwipeId(null);
  };

  const saveEditItem = async (itemId, updates) => {
    if (!activeList) return;
    const newItems = activeList.items.map(i=>i.id===itemId?{...i,...updates}:i);
    await persist(activeList.id, activeList.name, newItems);
    setLists(l=>l.map(x=>x.id===activeList.id?{...x,items:newItems}:x));
    setActiveList(a=>({...a,items:newItems}));
    setEditingItem(null);
  };

  const addCompletedToInventory = async () => {
    if (!activeList) return;
    const done = activeList.items.filter(i=>i.done);
    if (done.length===0) return;
    setAddingToInventory(true);
    for (const item of done) {
      await onAddItem({
        Type:"Single Card", CardName:item.name, Set:item.set||"",
        Number:"", Rarity:"Common", Language:"English",
        Condition:"Near Mint", Grade:"", Quantity:1,
        Source:"Individual Buy",
        AcquisitionCost:item.price||0, ListPrice:"",
        MinSellPrice: item.price>0 ? Math.ceil(item.price*1.25) : 0,
        MarketValueAtAcquisition:0,
        AcquisitionDate:localDate(),
        Status:"In Stock", LotID:"", LotName:"", LotItemNumber:"",
        ShowID:"", ShowName:"", ImageURL: item.imageUrl||"",
        Notes:`From shopping list: ${activeList.name}${item.notes?` · ${item.notes}`:""}`,
      });
    }
    const newItems = activeList.items.filter(i=>!i.done);
    await persist(activeList.id, activeList.name, newItems);
    setLists(l=>l.map(x=>x.id===activeList.id?{...x,items:newItems}:x));
    setActiveList(a=>({...a,items:newItems}));
    setAddingToInventory(false);
  };

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (activeList) {
    const pending   = activeList.items.filter(i=>!i.done);
    const done      = activeList.items.filter(i=>i.done);
    const doneTotal = done.reduce((s,i)=>s+(i.price||0),0);

    return (
      <div>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}}
            onClick={()=>{setActiveList(null);setEditListName(false);}}>← Back</button>
          <div style={{flex:1}} onClick={()=>{setEditListName(true);setEditNameVal(activeList.name);}}>
            {editListName ? (
              <input style={S.input} autoFocus value={editNameVal}
                onChange={e=>setEditNameVal(e.target.value)}
                onBlur={()=>renameList(activeList.id,editNameVal)}
                onKeyDown={e=>e.key==="Enter"&&renameList(activeList.id,editNameVal)} />
            ) : (
              <div style={{fontSize:16,fontWeight:600}}>{activeList.name} <span style={{fontSize:12,color:T.textFaint}}>✏</span></div>
            )}
          </div>
          <button style={{...S.btn("ghost"),padding:"6px 10px",color:T.red}}
            onClick={()=>deleteList(activeList.id)}>🗑</button>
        </div>

        <div style={{...S.card,marginBottom:14}}>
          <div style={{fontSize:12,fontWeight:600,color:T.primary,marginBottom:8}}>Add to list</div>
          <input style={{...S.input,marginBottom:6}} value={cardName}
            onChange={e=>setCardName(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addCard()}
            placeholder="Card name *" />
          <div style={{display:"flex",gap:6,marginBottom:6}}>
            <input style={{...S.input,flex:2}} value={cardSet}
              onChange={e=>setCardSet(e.target.value)} placeholder="Set (optional)" />
            <input style={{...S.input,flex:1,textAlign:"right"}} type="number"
              value={cardPrice} onChange={e=>setCardPrice(e.target.value)}
              placeholder="$ target" />
          </div>
          <input style={{...S.input,marginBottom:6}} value={cardImage}
            onChange={e=>setCardImage(e.target.value)}
            placeholder="TCGPlayer image URL (optional)" />
          <input style={{...S.input,marginBottom:8}} value={cardNotes}
            onChange={e=>setCardNotes(e.target.value)}
            placeholder="Notes (optional — grade, variant, etc.)" />
          <button style={{...S.btn("primary"),width:"100%"}}
            onClick={addCard} disabled={!cardName.trim()}>+ Add Card</button>
        </div>

        {pending.length > 0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:600,color:T.textMuted,
              textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
              Want ({pending.length})
            </div>
            {pending.map(item=>(
              <div key={item.id} style={{marginBottom:6}}>

                {editingItem?.id===item.id ? (
                  <div style={{...S.card,background:T.surface2,borderColor:T.primary}}>
                    <div style={{fontSize:12,fontWeight:600,color:T.primary,marginBottom:8}}>
                      Edit card
                    </div>
                    <input style={{...S.input,marginBottom:6}}
                      value={editingItem.name}
                      onChange={e=>setEditingItem(ei=>({...ei,name:e.target.value}))}
                      placeholder="Card name *" />
                    <div style={{display:"flex",gap:6,marginBottom:6}}>
                      <input style={{...S.input,flex:2}}
                        value={editingItem.set||""}
                        onChange={e=>setEditingItem(ei=>({...ei,set:e.target.value}))}
                        placeholder="Set" />
                      <input style={{...S.input,flex:1,textAlign:"right"}} type="number"
                        value={editingItem.price||""}
                        onChange={e=>setEditingItem(ei=>({...ei,price:parseFloat(e.target.value)||0}))}
                        placeholder="$ target" />
                    </div>
                    <input style={{...S.input,marginBottom:6}}
                      value={editingItem.imageUrl||""}
                      onChange={e=>setEditingItem(ei=>({...ei,imageUrl:e.target.value}))}
                      placeholder="TCGPlayer image URL (optional)" />
                    <input style={{...S.input,marginBottom:8}}
                      value={editingItem.notes||""}
                      onChange={e=>setEditingItem(ei=>({...ei,notes:e.target.value}))}
                      placeholder="Notes (optional)" />
                    <div style={{display:"flex",gap:6}}>
                      <button style={{...S.btn("ghost"),flex:1}}
                        onClick={()=>setEditingItem(null)}>Cancel</button>
                      <button style={{...S.btn("ghost"),flex:1,color:T.red,borderColor:T.red}}
                        onClick={()=>{deleteItem(item.id);setEditingItem(null);}}>Delete</button>
                      <button style={{...S.btn("primary"),flex:2}}
                        onClick={()=>saveEditItem(item.id,{
                          name:editingItem.name, set:editingItem.set,
                          price:editingItem.price, imageUrl:editingItem.imageUrl,
                          notes:editingItem.notes
                        })} disabled={!editingItem.name?.trim()}>
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{position:"relative",overflow:"hidden",borderRadius:10}}>
                    {swipeId===item.id && (
                      <div style={{position:"absolute",right:0,top:0,bottom:0,width:80,
                        background:T.red,display:"flex",alignItems:"center",
                        justifyContent:"center",cursor:"pointer",borderRadius:"0 10px 10px 0",
                        fontSize:12,fontWeight:700,color:"#fff"}}
                        onClick={()=>deleteItem(item.id)}>Delete</div>
                    )}
                    <div style={{...S.card,marginBottom:0,background:T.surface2,
                      transform:swipeId===item.id?"translateX(-80px)":"translateX(0)",
                      transition:"transform 0.2s"}}
                      onTouchStart={e=>{touchStart.current=e.touches[0].clientX;}}
                      onTouchEnd={e=>{
                        if(touchStart.current===null)return;
                        const diff=touchStart.current-e.changedTouches[0].clientX;
                        touchStart.current=null;
                        if(diff>50)setSwipeId(item.id);
                        else if(diff<-20)setSwipeId(null);
                      }}>
                      <div style={{display:"flex",alignItems:"center",gap:10}}>
                        <div
                          style={{width:24,height:24,borderRadius:"50%",flexShrink:0,
                            border:`2px solid ${T.border}`,display:"flex",alignItems:"center",
                            justifyContent:"center",fontSize:14,color:T.textFaint,cursor:"pointer"}}
                          onClick={e=>{e.stopPropagation();toggleItem(item.id);}}>
                          ○
                        </div>
                        {item.imageUrl && (
                          <img src={item.imageUrl} alt={item.name} loading="lazy"
                            style={{width:40,height:56,objectFit:"cover",borderRadius:5,
                              flexShrink:0,border:`1px solid ${T.border}`,cursor:"pointer"}}
                            onClick={e=>{e.stopPropagation();window.open(item.imageUrl,"_blank");}} />
                        )}
                        <div style={{flex:1,minWidth:0,cursor:"pointer"}}
                          onClick={()=>swipeId===item.id?setSwipeId(null):setEditingItem({...item})}>
                          <div style={{fontSize:13,fontWeight:500}}>{item.name}</div>
                          {item.set&&<div style={{fontSize:11,color:T.textMuted}}>{item.set}</div>}
                          {item.notes&&<div style={{fontSize:11,color:T.amber,marginTop:2,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                            {item.notes}
                          </div>}
                          <div style={{fontSize:10,color:T.textFaint,marginTop:2}}>Tap to edit</div>
                        </div>
                        {item.price>0&&<div style={{fontSize:13,fontWeight:600,
                          color:T.primary,flexShrink:0}}>{fmt(item.price)}</div>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {done.length > 0 && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:600,color:T.green,
              textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
              Got it ✓ ({done.length}) · {fmt(doneTotal)}
            </div>
            {done.map(item=>(
              <div key={item.id} style={{...S.card,marginBottom:6,
                background:T.greenBg,borderColor:T.green}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div
                    style={{width:24,height:24,borderRadius:"50%",flexShrink:0,
                      background:T.green,display:"flex",alignItems:"center",
                      justifyContent:"center",color:T.green,fontSize:14,cursor:"pointer"}}
                    onClick={()=>toggleItem(item.id)}>
                    ✓
                  </div>
                  {item.imageUrl && (
                    <img src={item.imageUrl} alt={item.name} loading="lazy"
                      style={{width:40,height:56,objectFit:"cover",borderRadius:5,
                        flexShrink:0,opacity:0.5,border:`1px solid ${T.border}`}} />
                  )}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,textDecoration:"line-through",color:T.textMuted}}>
                      {item.name}
                    </div>
                    {item.set&&<div style={{fontSize:11,color:T.textFaint}}>{item.set}</div>}
                    {item.notes&&<div style={{fontSize:11,color:T.textFaint,marginTop:2,
                      overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {item.notes}
                    </div>}
                  </div>
                  {item.price>0&&<div style={{fontSize:13,color:T.textMuted,
                    flexShrink:0}}>{fmt(item.price)}</div>}
                </div>
              </div>
            ))}
            <button style={{...S.btn("primary"),width:"100%",padding:"12px",
              background:T.green,marginTop:8}}
              onClick={addCompletedToInventory} disabled={addingToInventory}>
              {addingToInventory
                ? "Adding…"
                : `📦 Add ${done.length} card${done.length!==1?"s":""} to Inventory · ${fmt(doneTotal)}`}
            </button>
          </div>
        )}

        {activeList.items.length===0&&(
          <div style={{textAlign:"center",padding:"32px 0",color:T.textFaint}}>
            <div style={{fontSize:28,marginBottom:8}}>🛒</div>
            <div style={{fontSize:13}}>Add cards you're looking for</div>
          </div>
        )}
      </div>
    );
  }

  // ── LIST OF LISTS ─────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{textAlign:"center",padding:"32px 0",color:T.textFaint,fontSize:13}}>
      Loading lists…
    </div>
  );

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:12,color:T.textFaint}}>{lists.length} list{lists.length!==1?"s":""}</div>
        <button style={S.btn("primary")} onClick={()=>setShowNewList(true)}>+ New List</button>
      </div>

      {showNewList && (
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setShowNewList(false)}>
          <div style={{...S.drawer, maxWidth:440}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
              <div style={{fontSize:18,fontWeight:800,color:T.text}}>Create New Shopping List</div>
              <button style={{width:32,height:32,borderRadius:T.radius.md,border:"none",
                background:"transparent",color:T.textMuted,fontSize:18,cursor:"pointer"}}
                onClick={()=>setShowNewList(false)}>✕</button>
            </div>
            <div style={{fontSize:13,color:T.textMuted,marginBottom:16}}>
              Add a new list to track cards you are looking for.
            </div>
            <input style={{...S.input,marginBottom:16}} autoFocus value={newListName}
              onChange={e=>setNewListName(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&createList()}
              placeholder="List name (e.g. Regionals, LGS Run)" />
            <div style={{display:"flex",gap:10}}>
              <button style={{...S.btn("ghost"),flex:1,padding:"12px 0"}}
                onClick={()=>{setShowNewList(false);setNewListName("");}}>Cancel</button>
              <button style={{...S.btn("primary"),flex:1,padding:"12px 0"}}
                onClick={createList} disabled={saving||!newListName.trim()}>
                {saving?"Creating…":"Create List"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lists.length===0&&!showNewList&&(
        <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint}}>
          <div style={{fontSize:32,marginBottom:8}}>🛒</div>
          <div style={{fontSize:14,marginBottom:4}}>No shopping lists yet</div>
          <div style={{fontSize:12}}>Create a list before your next show or shop run</div>
        </div>
      )}

      {lists.map(list=>{
        const done  = list.items.filter(i=>i.done).length;
        const total = list.items.length;
        const spent = list.items.filter(i=>i.done).reduce((s,i)=>s+(i.price||0),0);
        const pct   = total>0?(done/total)*100:0;
        return (
          <div key={list.id} style={{...S.card,cursor:"pointer",marginBottom:10}}
            onClick={()=>setActiveList(list)}>
            <div style={{display:"flex",justifyContent:"space-between",
              alignItems:"flex-start",marginBottom:8}}>
              <div>
                <div style={{fontSize:15,fontWeight:600}}>{list.name}</div>
                <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>
                  {done}/{total} found · {fmt(spent)} spent
                </div>
              </div>
              <span style={S.badge(done===total&&total>0?"green":"")}>
                {done===total&&total>0?"Complete":`${total-done} remaining`}
              </span>
            </div>
            {total>0&&(
              <div style={{height:5,borderRadius:3,background:T.border,overflow:"hidden"}}>
                <div style={{height:"100%",borderRadius:3,background:T.green,
                  width:`${pct}%`,transition:"width 0.3s"}} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── LOTS PAGE ────────────────────────────────────────────────────────────────
function LotsPage({ lots, inventory, onNewLot, onUpdateLot, onUpdateItem, onAddItem }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();
  const [showDrawer, setShowDrawer]     = useState(false);
  const [activeLot, setActiveLot]       = useState(null);
  const [editingLot, setEditingLot]     = useState(false);
  const [subView, setSubView]           = useState("lots");

  const lotStats = useMemo(()=>lots.filter(l=>l.Status!=="Deleted").map(lot=>{
    const items   = inventory.filter(i=>i.LotID===lot.ID&&i.Status!=="Deleted");
    const sold    = items.filter(i=>i.Status==="Sold");
    const inStock = items.filter(i=>i.Status==="In Stock"||i.Status==="Reserved");
    const revenue = sold.reduce((s,i)=>s+(i.SoldPrice||0),0);
    const profit  = revenue-lot.TotalPaid;
    const pctRecov= lot.TotalPaid>0?Math.min(100,(revenue/lot.TotalPaid)*100):0;
    return {...lot,items,sold,inStock,revenue,profit,pctRecov,remaining:lot.TotalPaid-revenue};
  }),[lots,inventory]);

  if (activeLot) {
    const lot = lotStats.find(l=>l.ID===activeLot.ID);
    if (!lot) { setActiveLot(null); return null; }
    return (
      <div style={S.page}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setActiveLot(null)}>← Back</button>
          <div style={{flex:1}}>
            <div style={{fontSize:18,fontWeight:800,color:T.text}}>{lot.Name}</div>
            <div style={{fontSize:12,color:T.textMuted}}>{lot.Source} · {lot.Date} · {lot.CardCount} cards</div>
          </div>
          <button style={S.btn("sm")} onClick={()=>setEditingLot(true)}>Edit</button>
        </div>

        {editingLot && (
          <LotDrawer lot={lot} onClose={()=>setEditingLot(false)} onSave={async (formData, lotId) => {
            await onUpdateLot(lotId, formData);
            setActiveLot(prev=>({...prev, ...formData}));
            setEditingLot(false);
          }} />
        )}

        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:12}}>
          <div style={S.stat}><div style={S.statLbl}>Spent</div><div style={{...S.statVal,color:T.red}}>{fmt(lot.TotalPaid)}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Revenue</div><div style={{...S.statVal,color:T.green}}>{fmt(lot.revenue)}</div></div>
          <div style={S.stat}><div style={S.statLbl}>P&L</div><div style={{...S.statVal,color:lot.profit>=0?T.green:T.red}}>{lot.profit>=0?"+":""}{fmt(lot.profit)}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Added</div><div style={S.statVal}>{lot.items.length}</div></div>
          <div style={S.stat}><div style={S.statLbl}>Sold</div><div style={{...S.statVal,color:T.green}}>{lot.sold.length}</div></div>
          <div style={S.stat}><div style={S.statLbl}>In Stock</div><div style={{...S.statVal,color:T.primary}}>{lot.inStock.length}</div></div>
        </div>

        <div style={{...S.infoBox,marginBottom:12}}>
          <div style={{display:"flex",gap:20,fontSize:12,flexWrap:"wrap"}}>
            <div><span style={{color:T.textMuted}}>Cost/card: </span><span style={{fontWeight:600}}>{fmt(lot.CostPerCard)}</span></div>
            <div><span style={{color:T.textMuted}}>Min sell: </span><span style={{fontWeight:600,color:T.green}}>${lot.MinSellPrice}</span></div>
            <div><span style={{color:T.textMuted}}>Break-even: </span><span style={{fontWeight:600}}>{fmt(lot.TotalPaid)}</span></div>
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMuted,marginBottom:4}}>
            <span>Break-even progress</span>
            <span style={{color:lot.pctRecov>=100?T.green:T.primary}}>{lot.pctRecov.toFixed(0)}%</span>
          </div>
          <div style={{height:8,borderRadius:4,background:T.border,overflow:"hidden"}}>
            <div style={{height:"100%",borderRadius:4,width:`${lot.pctRecov}%`,
              background:lot.pctRecov>=100?T.green:T.primary,transition:"width 0.3s"}} />
          </div>
          {lot.remaining>0 && <div style={{fontSize:11,color:T.textMuted,marginTop:4}}>{fmt(lot.remaining)} still needed to break even</div>}
        </div>

        {lot.CostMethod !== "proportional" && lot.Source !== "Pack Pull" && (
          <ConvertToProportional lot={lot} onSave={async (updates) => {
            await onUpdateLot(lot.ID, updates);
            setActiveLot(prev => ({...prev, ...updates}));
          }} />
        )}

        {lot.CostMethod === "proportional" && (
          <div style={{...S.infoBox, marginBottom:16}}>
            <div style={{fontSize:12, color:T.green, fontWeight:500}}>
              ✓ Proportional allocation active — {((lot.AllocationRatio||0)*100).toFixed(1)}% of each card's market value
            </div>
            <div style={{fontSize:11, color:T.textMuted, marginTop:2}}>
              Total market value: {fmt(lot.TotalMarketValue)} · Paid: {fmt(lot.TotalPaid)}
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap"}}>
          {lot.Status==="Open" && lot.profit>0 && (
            <button style={S.btn("primary")}
              onClick={()=>{onUpdateLot(lot.ID,{Status:"Closed"});setActiveLot(null);}}>
              Mark Lot Closed
            </button>
          )}
          <button style={S.btn("danger")}
            onClick={async ()=>{
              if (!await confirmAction("Delete this lot? Cards will remain in inventory but will no longer be linked to this lot.", {confirmLabel:"Delete Lot"})) return;
              const lotCards = inventory.filter(i => i.LotID === lot.ID);
              for (const card of lotCards) {
                await onUpdateItem(card.ID, { LotID:"", LotName:"", LotItemNumber:"" });
              }
              onUpdateLot(lot.ID, { Status:"Deleted" });
              setActiveLot(null);
              toast("Lot deleted", "success");
            }}>
            Delete Lot
          </button>
        </div>

        <div style={S.h2}>Cards in this lot ({lot.items.length})</div>
        {lot.items.length===0 && (
          <div style={{fontSize:13,color:T.textFaint,textAlign:"center",padding:"20px 0"}}>
            No cards added to this lot yet. Add inventory and assign it to this lot.
          </div>
        )}
        {lot.items.map(item=>(
          <div key={item.ID} style={S.cardB(item.Status==="Sold"?T.greenBg:undefined)}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:14,fontWeight:500}}>{item.CardName}</div>
                <div style={{fontSize:12,color:T.textMuted}}>
                  {[item.Set,item.Number,item.Rarity&&item.Rarity!=="Common"&&item.Rarity,item.Condition].filter(Boolean).join(" · ")}
                  {item.LotItemNumber?` · #${item.LotItemNumber}`:""}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0,marginLeft:10}}>
                {item.Status==="Sold"
                  ? <><div style={S.badge("green")}>Sold {fmt(item.SoldPrice)}</div>
                      <div style={{fontSize:11,color:T.green,marginTop:2}}>+{fmt((item.SoldPrice||0)-(item.AcquisitionCost||0))}</div></>
                  : <><div style={{fontSize:14,fontWeight:600,color:T.primary}}>{fmt(item.ListPrice)}</div>
                      <div style={{fontSize:11,color:T.textMuted}}>cost {fmt(item.AcquisitionCost)}</div></>}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={{display:"flex",gap:6,marginBottom:14, padding:4,
        background:T.surface2, borderRadius:T.radius.pill, border:`1px solid ${T.border}`}}>
        <button style={{flex:1,textAlign:"center",padding:"9px 0", borderRadius:T.radius.pill,
          border:"none", cursor:"pointer", fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:700,
          background: subView==="lots" ? T.blueBg : "transparent",
          color: subView==="lots" ? T.primary : T.textMuted}}
          onClick={()=>setSubView("lots")}>📦 Lots</button>
        <button style={{flex:1,textAlign:"center",padding:"9px 0", borderRadius:T.radius.pill,
          border:"none", cursor:"pointer", fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:700,
          background: subView==="shopping" ? T.blueBg : "transparent",
          color: subView==="shopping" ? T.primary : T.textMuted}}
          onClick={()=>setSubView("shopping")}>🛒 Shopping Lists</button>
      </div>

      {subView==="shopping" && <ShoppingListPage onAddItem={onAddItem} S={S} T={T} />}

      {subView==="lots" && (<>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:12,color:T.textFaint,fontFamily:"'DM Mono',monospace"}}>{lots.filter(l=>l.Status!=="Deleted").length} lots</div>
        <button style={S.btn("primary")} onClick={()=>setShowDrawer(true)}>+ New Lot</button>
      </div>

      {lots.length===0 && <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint,fontSize:13}}>No lots yet.</div>}

      {lotStats.map(lot=>(
        <div key={lot.ID} style={{...S.card,cursor:"pointer"}} onClick={()=>setActiveLot(lot)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
            <div>
              <div style={{fontWeight:600,fontSize:15}}>{lot.Name}</div>
              <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{lot.Source} · {lot.Date} · {lot.CardCount} cards</div>
            </div>
            <span style={S.badge(lot.profit>0?"green":lot.pctRecov>=50?"amber":"")}>
              {lot.profit>0?"Profitable":lot.pctRecov>=100?"Break-Even":"Open"}
            </span>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8,marginBottom:10}}>
            <div style={S.infoBox}><div style={S.statLbl}>Spent</div><div style={{fontSize:14,fontWeight:600,color:T.red}}>{fmt(lot.TotalPaid)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>Revenue</div><div style={{fontSize:14,fontWeight:600,color:T.green}}>{fmt(lot.revenue)}</div></div>
            <div style={S.infoBox}><div style={S.statLbl}>P&L</div><div style={{fontSize:14,fontWeight:600,color:lot.profit>=0?T.green:T.red}}>{lot.profit>=0?"+":""}{fmt(lot.profit)}</div></div>
          </div>

          <div style={{height:6,borderRadius:3,background:T.border,overflow:"hidden",marginBottom:6}}>
            <div style={{height:"100%",borderRadius:3,width:`${lot.pctRecov}%`,
              background:lot.pctRecov>=100?T.green:T.primary,transition:"width 0.3s"}} />
          </div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textMuted}}>
            <span>{lot.pctRecov.toFixed(0)}% to break-even · {lot.items.length} cards · {lot.sold.length} sold</span>
            <span style={{color:T.primary}}>Tap to open →</span>
          </div>
        </div>
      ))}

      {showDrawer && <LotDrawer onClose={()=>setShowDrawer(false)} onSave={async(d)=>{await onNewLot(d);setShowDrawer(false);}} />}
      </>)}
    </div>
  );
}

// ─── BUYERS LIST CRM ──────────────────────────────────────────────────────────
// ─── CRM PAGE ─────────────────────────────────────────────────────────────────
function fmtClaimTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

function CRMPage({ buyers, consignors, inventory, vendor, shows, userEmail, onAddBuyer, onUpdateBuyer, onDeleteBuyer,
  onAddConsignor, onUpdateConsignor, onDeleteConsignor, onUpdateItem,
  canUseBuyers, canUseConsignors, isEnterprise = false, S, T }) {
  const { toast, confirmAction } = useNotify();
  const [crmTab, setCrmTab] = useState("claims");
  const [claims, setClaims]   = useState([]);
  const [claimsLoading, setClaimsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [cardFilter, setCardFilter]     = useState("all");
  const [drawer, setDrawer]             = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(new Set());
  const [historyData, setHistoryData]   = useState({});
  const [acceptingIds, setAcceptingIds] = useState(new Set());
  const [storeOrders, setStoreOrders] = useState([]);
  const [storeOrdersLoading, setStoreOrdersLoading] = useState(false);

  const loadStoreOrders = () => {
    if (!vendor?.id || !vendor?.store_mode_enabled) return;
    setStoreOrdersLoading(true);
    readKioskOrders(vendor.id).then(setStoreOrders).catch(()=>{}).finally(()=>setStoreOrdersLoading(false));
  };
  useEffect(loadStoreOrders, [vendor?.id, vendor?.store_mode_enabled]);
  const pendingStoreCount = storeOrders.filter(o => o.Status === "pending").length;

  const loadClaims = () => {
    if (!vendor?.id) return;
    setClaimsLoading(true);
    readClaims(vendor.id)
      .then(data => setClaims(data || []))
      .catch(e => toast("Failed to load claims: " + e.message, "error"))
      .finally(() => setClaimsLoading(false));
  };

  useEffect(() => {
    if (crmTab === "claims") loadClaims();
  }, [crmTab, vendor?.id]);

  // ── Ranking: group claims on the same card, order by submit time ──────────
  const rankedClaims = useMemo(() => {
    const groups = {};
    claims.forEach(c => {
      const key = c.inventory_id || `req:${(c.card_name || "").replace("[REQUEST] ", "").trim().toLowerCase()}`;
      (groups[key] ||= []).push(c);
    });
    Object.values(groups).forEach(group => {
      group.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      group.forEach((c, i) => { c._rank = i + 1; c._groupSize = group.length; });
    });
    return claims;
  }, [claims]);

  const cardOptions = useMemo(() => {
    const seen = new Map();
    claims.forEach(c => {
      const key = c.inventory_id || `req:${(c.card_name || "").replace("[REQUEST] ", "").trim().toLowerCase()}`;
      if (!seen.has(key)) seen.set(key, (c.card_name || "").replace("[REQUEST] ", ""));
    });
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [claims]);

  const filteredClaims = useMemo(() => {
    return rankedClaims
      .filter(c => statusFilter === "all" || c.status === statusFilter)
      .filter(c => {
        if (cardFilter === "all") return true;
        const key = c.inventory_id || `req:${(c.card_name || "").replace("[REQUEST] ", "").trim().toLowerCase()}`;
        return key === cardFilter;
      })
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [rankedClaims, statusFilter, cardFilter]);

  const statusColor = s => s === "pending" ? "#fbbf24" : s === "contacted" ? "#60a5fa"
    : s === "accepted" ? "#4ade80" : s === "declined" ? "#f87171"
    : s === "sold" ? "#4ade80" : "#888";

  const rankLabel = n => n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`;
  const rankColor = n => n === 1 ? "#4ade80" : n === 2 ? "#60a5fa" : n === 3 ? "#fbbf24" : T.textMuted;

  const handleStatusChange = async (claim, status) => {
    try {
      const updated = await updateClaim(claim.id, vendor.id, userEmail, { status }, claim);
      setClaims(cs => cs.map(x => x.id === claim.id ? { ...x, ...updated } : x));
    } catch (e) {
      toast("Failed to update claim: " + e.message, "error");
    }
  };

  const handleDelete = async (claim) => {
    const ok = await confirmAction(
      `Delete this claim from ${claim.customer_name}? This can't be undone.`,
      { confirmLabel: "Delete", danger: true }
    );
    if (!ok) return;
    try {
      await deleteClaim(claim.id, vendor.id, userEmail, claim);
      setClaims(cs => cs.filter(x => x.id !== claim.id));
      toast("Claim deleted", "success");
    } catch (e) {
      toast("Failed to delete claim: " + e.message, "error");
    }
  };

  const handleAcceptOffer = async (claim) => {
    setAcceptingIds(s => new Set(s).add(claim.id));
    try {
      await acceptClaimOffer(claim.id, vendor.id);
      setClaims(cs => cs.map(x => x.id === claim.id
        ? { ...x, status: "accepted", accepted_at: new Date().toISOString() }
        : x));
      toast(`Buyer notified — offer accepted for ${claim.card_name?.replace("[REQUEST] ", "")}`, "success");
    } catch (e) {
      toast("Failed to notify buyer: " + e.message, "error");
    } finally {
      setAcceptingIds(s => { const n = new Set(s); n.delete(claim.id); return n; });
    }
  };

  const toggleHistory = async (claim) => {
    const isOpen = expandedHistory.has(claim.id);
    if (isOpen) {
      setExpandedHistory(s => { const n = new Set(s); n.delete(claim.id); return n; });
      return;
    }
    setExpandedHistory(s => new Set(s).add(claim.id));
    if (!historyData[claim.id]) {
      try {
        const events = await readClaimHistory(vendor.id, claim.id);
        setHistoryData(h => ({ ...h, [claim.id]: events }));
      } catch (e) {
        setHistoryData(h => ({ ...h, [claim.id]: [] }));
      }
    }
  };

  const handleSaveClaim = async (form) => {
    if (drawer.mode === "edit") {
      const updated = await updateClaim(drawer.claim.id, vendor.id, userEmail, form, drawer.claim);
      setClaims(cs => cs.map(x => x.id === drawer.claim.id ? { ...x, ...updated } : x));
      toast("Claim updated", "success");
    } else {
      const created = await addClaim(vendor.id, userEmail, form);
      setClaims(cs => [created, ...cs]);
      toast("Claim added", "success");
    }
    setDrawer(null);
  };

  const pendingCount = claims.filter(c => c.status === "pending").length;

  const CRM_TABS = [
    { id:"claims",     label:"Shop Claims" },
    { id:"buyers",     label:"Buyers"      },
    { id:"consignors", label:"Consignors"  },
    ...(vendor?.store_mode_enabled ? [{ id:"storeorders", label:"Store Requests" }] : []),
  ];

  const STATUS_OPTIONS = ["pending", "contacted", "accepted", "declined", "sold", "expired"];

  return (
    <div style={S.page}>
      <div style={{fontSize:22, fontWeight:800, color:T.text, letterSpacing:"-0.02em"}}>CRM</div>
      <div style={{fontSize:13, color:T.textMuted, marginBottom:16}}>Manage buyers, consignors, and claims in one place.</div>

      <div style={{display:"flex", gap:4, marginBottom:20, padding:4,
        background:T.surface2, borderRadius:T.radius.md, border:`1px solid ${T.border}`}}>
        {CRM_TABS.map(t => (
          <button key={t.id}
            style={{flex:1, padding:"9px 0", borderRadius:T.radius.sm, cursor:"pointer",
              border:"none", fontFamily:"'Sora',sans-serif", fontSize:13, fontWeight:700,
              background: crmTab===t.id ? T.surface : "transparent",
              color: crmTab===t.id ? T.primary : T.textMuted,
              boxShadow: crmTab===t.id ? T.shadowSm : "none"}}
            onClick={()=>setCrmTab(t.id)}>
            {t.label}
            {t.id==="claims" && pendingCount > 0 && (
              <span style={{marginLeft:6, background:T.red, color:"#fff",
                borderRadius:T.radius.pill, padding:"1px 6px", fontSize:10, fontWeight:700}}>
                {pendingCount}
              </span>
            )}
            {t.id==="storeorders" && pendingStoreCount > 0 && (
              <span style={{marginLeft:6, background:T.red, color:"#fff",
                borderRadius:T.radius.pill, padding:"1px 6px", fontSize:10, fontWeight:700}}>
                {pendingStoreCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {crmTab === "claims" && (
        <div>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, marginBottom:12, flexWrap:"wrap"}}>
            <div style={{fontSize:12, color:T.textMuted, lineHeight:1.6}}>
              Claims submitted from your online shop at{" "}
              <span style={{color:"#88bdf2", fontFamily:"'DM Mono',monospace", fontSize:11}}>
                app.vendorstax.com/shop.html?s={IS_DEMO ? "vendor-demo" : (vendor?.kiosk_slug || vendor?.business_name?.toLowerCase().replace(/\s+/g,"-") || "your-store")}
              </span>
            </div>
            <button style={S.btn("primary")} onClick={()=>setDrawer({ mode: "new" })}>
              + New Claim
            </button>
          </div>

          {claims.length > 0 && (
            <div style={{marginBottom:16}}>
              <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:8}}>
                <button style={{...S.chip(statusFilter==="all"), fontSize:11, padding:"4px 10px"}}
                  onClick={()=>setStatusFilter("all")}>
                  All ({claims.length})
                </button>
                {STATUS_OPTIONS.map(s => {
                  const count = claims.filter(c=>c.status===s).length;
                  if (count === 0) return null;
                  return (
                    <button key={s}
                      style={{...S.chip(statusFilter===s), fontSize:11, padding:"4px 10px", textTransform:"capitalize"}}
                      onClick={()=>setStatusFilter(s)}>
                      {s} ({count})
                    </button>
                  );
                })}
              </div>
              {cardOptions.length > 1 && (
                <select
                  value={cardFilter}
                  onChange={e=>setCardFilter(e.target.value)}
                  style={{...S.select, maxWidth:320}}>
                  <option value="all">Filter by card — all cards</option>
                  {cardOptions.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {claimsLoading ? (
            <div style={{textAlign:"center", padding:40, color:T.textMuted}}>Loading…</div>
          ) : claims.length === 0 ? (
            <div style={{...S.card, textAlign:"center", padding:40}}>
              <div style={{fontSize:32, marginBottom:10}}>📬</div>
              <div style={{fontSize:15, fontWeight:600, marginBottom:6}}>No claims yet</div>
              <div style={{fontSize:13, color:T.textMuted}}>
                When customers claim cards from your shop, they'll appear here.
              </div>
            </div>
          ) : filteredClaims.length === 0 ? (
            <div style={{...S.card, textAlign:"center", padding:32}}>
              <div style={{fontSize:13, color:T.textMuted}}>No claims match this filter.</div>
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:10}}>
              {filteredClaims.map(claim => (
                <div key={claim.id} style={{...S.card, padding:"12px 14px"}}>
                  <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:8}}>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:2, flexWrap:"wrap"}}>
                        <div style={{fontSize:14, fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                          {claim.card_name?.startsWith("[REQUEST]")
                            ? `🔍 ${claim.card_name.replace("[REQUEST] ","")}`
                            : `🃏 ${claim.card_name}`}
                        </div>
                        {claim._groupSize > 1 && (
                          <span style={{
                            fontSize:10, fontWeight:700, borderRadius:6, padding:"2px 7px",
                            background:`${rankColor(claim._rank)}20`, color:rankColor(claim._rank),
                            border:`0.5px solid ${rankColor(claim._rank)}55`, flexShrink:0,
                          }}>
                            {rankLabel(claim._rank)} of {claim._groupSize}
                          </span>
                        )}
                      </div>
                      <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
                        {claim.list_price && (
                          <div style={{fontSize:13, color:"#4ade80", fontFamily:"'DM Mono',monospace"}}>
                            List ${parseFloat(claim.list_price).toFixed(2)}
                          </div>
                        )}
                        {claim.offer_price != null && (
                          <div style={{fontSize:13, color:"#f3680c", fontWeight:700, fontFamily:"'DM Mono',monospace"}}>
                            Offer ${parseFloat(claim.offer_price).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div style={{
                      fontSize:11, fontWeight:700, borderRadius:6, padding:"3px 8px",
                      background:`${statusColor(claim.status)}20`,
                      color:statusColor(claim.status), flexShrink:0,
                      border:`0.5px solid ${statusColor(claim.status)}55`,
                      textTransform:"capitalize",
                    }}>
                      {claim.status}
                    </div>
                  </div>
                  <div style={{display:"flex", gap:16, marginBottom:10, flexWrap:"wrap"}}>
                    <div>
                      <div style={{fontSize:10, color:T.textFaint, textTransform:"uppercase", letterSpacing:".05em", marginBottom:2}}>Customer</div>
                      <div style={{fontSize:13, fontWeight:600}}>{claim.customer_name}</div>
                    </div>
                    <div>
                      <div style={{fontSize:10, color:T.textFaint, textTransform:"uppercase", letterSpacing:".05em", marginBottom:2}}>Contact</div>
                      <div style={{fontSize:13, color:"#88bdf2"}}>{claim.customer_contact}</div>
                    </div>
                    <div>
                      <div style={{fontSize:10, color:T.textFaint, textTransform:"uppercase", letterSpacing:".05em", marginBottom:2}}>Requested</div>
                      <div style={{fontSize:12, color:T.textMuted}}>{fmtClaimTime(claim.created_at)}</div>
                    </div>
                    {claim.accepted_at && (
                      <div>
                        <div style={{fontSize:10, color:T.textFaint, textTransform:"uppercase", letterSpacing:".05em", marginBottom:2}}>Accepted</div>
                        <div style={{fontSize:12, color:"#4ade80"}}>{fmtClaimTime(claim.accepted_at)}</div>
                      </div>
                    )}
                  </div>
                  {claim.notes && (
                    <div style={{fontSize:12, color:T.textMuted, marginBottom:10, fontStyle:"italic"}}>
                      "{claim.notes}"
                    </div>
                  )}
                  <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:8}}>
                    {STATUS_OPTIONS.map(s => (
                      <button key={s}
                        style={{...S.chip(claim.status===s), fontSize:11, padding:"4px 10px", textTransform:"capitalize"}}
                        onClick={()=>handleStatusChange(claim, s)}>
                        {s}
                      </button>
                    ))}
                  </div>
                  <div style={{display:"flex", gap:8, flexWrap:"wrap", alignItems:"center"}}>
                    {["pending","contacted"].includes(claim.status) && (
                      <button
                        style={{...S.btn("sm"), background:"#f3680c", color:"#fff"}}
                        disabled={acceptingIds.has(claim.id)}
                        onClick={()=>handleAcceptOffer(claim)}>
                        {acceptingIds.has(claim.id) ? "Notifying…" : "✓ Accept & Notify Buyer"}
                      </button>
                    )}
                    <button style={S.btn("ghost")} onClick={()=>setDrawer({ mode: "edit", claim })}>
                      Edit
                    </button>
                    <button style={{...S.btn("ghost"), color:"#f87171", borderColor:"#f8717155"}}
                      onClick={()=>handleDelete(claim)}>
                      Delete
                    </button>
                    <button
                      style={{background:"none", border:"none", color:T.textMuted, fontSize:12,
                        cursor:"pointer", fontFamily:"'Sora',sans-serif", textDecoration:"underline"}}
                      onClick={()=>toggleHistory(claim)}>
                      {expandedHistory.has(claim.id) ? "Hide history" : "View history"}
                    </button>
                  </div>
                  {expandedHistory.has(claim.id) && (
                    <div style={{marginTop:10, paddingTop:10, borderTop:`0.5px solid ${T.border}`}}>
                      {!historyData[claim.id] ? (
                        <div style={{fontSize:12, color:T.textMuted}}>Loading…</div>
                      ) : historyData[claim.id].length === 0 ? (
                        <div style={{fontSize:12, color:T.textMuted}}>No history recorded.</div>
                      ) : (
                        historyData[claim.id].map(ev => (
                          <div key={ev.id} style={{fontSize:12, color:T.textMuted, marginBottom:4}}>
                            <span style={{fontFamily:"'DM Mono',monospace", color:T.textFaint}}>{fmtClaimTime(ev.created_at)}</span>
                            {" — "}{ev.action}
                            {ev.old_value && ev.new_value ? ` (${ev.old_value} → ${ev.new_value})` : ev.new_value ? `: ${ev.new_value}` : ""}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {drawer && (
            <ClaimFormDrawer
              mode={drawer.mode}
              claim={drawer.claim}
              inventory={inventory}
              S={S} T={T}
              onClose={()=>setDrawer(null)}
              onSave={handleSaveClaim}
            />
          )}
        </div>
      )}

      {crmTab === "buyers" && (canUseBuyers
        ? <BuyersPage buyers={buyers} vendor={vendor} shows={shows} onAdd={onAddBuyer} onUpdate={onUpdateBuyer} onDelete={onDeleteBuyer} isEnterprise={isEnterprise} />
        : <PaidFeatureBanner feature="Buyers List" />
      )}

      {crmTab === "consignors" && (canUseConsignors
        ? <ConsignorsPage consignors={consignors} inventory={inventory}
            onAdd={onAddConsignor} onUpdate={onUpdateConsignor} onDelete={onDeleteConsignor}
            onUpdateItem={onUpdateItem} />
        : <PaidFeatureBanner feature="Consignors" />
      )}

      {crmTab === "storeorders" && (
        <StoreRequestsTab vendor={vendor} onUpdateItem={onUpdateItem} onAddBuyer={onAddBuyer}
          orders={storeOrders} loading={storeOrdersLoading} onReload={loadStoreOrders} S={S} T={T} />
      )}
    </div>
  );
}

// ─── SHOP CLAIM CREATE/EDIT DRAWER ────────────────────────────────────────────
function ClaimFormDrawer({ mode, claim, inventory, S, T, onClose, onSave }) {
  const [form, setForm] = useState({
    cardName:        claim?.card_name?.replace("[REQUEST] ", "") || "",
    inventoryId:     claim?.inventory_id || "",
    listPrice:       claim?.list_price ?? "",
    offerPrice:      claim?.offer_price ?? "",
    customerName:    claim?.customer_name || "",
    customerContact: claim?.customer_contact || "",
    notes:           claim?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canSave = form.cardName.trim() && form.customerName.trim() && form.customerContact.trim();

  const handleInventoryPick = (id) => {
    set("inventoryId", id);
    const item = inventory.find(i => i.ID === id);
    if (item) {
      set("cardName", item.CardName);
      set("listPrice", item.ListPrice ?? "");
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
      <div style={S.drawer}>
        <div style={S.h2}>{mode === "edit" ? "Edit Claim" : "New Claim"}</div>
        <div style={S.formWrap}>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Link to inventory card (optional)</label>
            <select style={S.select} value={form.inventoryId} onChange={e=>handleInventoryPick(e.target.value)}>
              <option value="">— Not linked / custom entry —</option>
              {inventory.filter(i=>i.Status==="In Stock").map(i => (
                <option key={i.ID} value={i.ID}>{i.CardName} — {i.SetName || ""}</option>
              ))}
            </select>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Card Name *</label>
            <input style={S.input} value={form.cardName} onChange={e=>set("cardName", e.target.value)} />
          </div>
          <div style={S.row}>
            <div style={S.col}>
              <label style={S.label}>List Price</label>
              <input style={S.input} type="number" min="0" step="0.01" value={form.listPrice}
                onChange={e=>set("listPrice", e.target.value)} />
            </div>
            <div style={S.col}>
              <label style={S.label}>Offer Price</label>
              <input style={S.input} type="number" min="0" step="0.01" value={form.offerPrice}
                onChange={e=>set("offerPrice", e.target.value)} />
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Customer Name *</label>
            <input style={S.input} value={form.customerName} onChange={e=>set("customerName", e.target.value)} />
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Email or Phone *</label>
            <input style={S.input} value={form.customerContact} onChange={e=>set("customerContact", e.target.value)} />
          </div>
          <div style={{marginBottom:16}}>
            <label style={S.label}>Notes</label>
            <textarea style={{...S.input, minHeight:70, resize:"vertical"}} value={form.notes}
              onChange={e=>set("notes", e.target.value)} />
          </div>
          <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
            <button style={S.btn("ghost")} onClick={onClose}>Cancel</button>
            <button style={S.btn("primary")} disabled={!canSave || saving} onClick={save}>
              {saving ? "Saving…" : mode === "edit" ? "Save Changes" : "Create Claim"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BuyersPage({ buyers, onAdd, onUpdate, onDelete, vendor, shows = [], isEnterprise = false }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();
  const [view, setView]               = useState("list");
  const [activeBuyer, setActive]      = useState(null);
  const [wantList, setWantList]       = useState([]);
  const [purchases, setPurchases]     = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [search, setSearch]           = useState("");
  const [saving, setSaving]           = useState(false);
  const [allPurchases, setAllPurchases] = useState([]);

  useEffect(() => {
    if (!vendor?.id) return;
    readAllBuyerPurchases(vendor.id).then(setAllPurchases).catch(()=>{});
  }, [vendor?.id]);

  const buyerStats = useMemo(() => {
    const map = {};
    allPurchases.forEach(p => {
      const s = map[p.BuyerID] || (map[p.BuyerID] = { count: 0, total: 0, lastActive: null });
      s.count += 1;
      s.total += p.SoldPrice || 0;
      if (!s.lastActive || new Date(p.PurchasedAt) > new Date(s.lastActive)) s.lastActive = p.PurchasedAt;
    });
    return map;
  }, [allPurchases]);

  const AVATAR_COLORS = ["#125DE8","#7C3AED","#00C070","#FB6C06","#DC2626"];
  const avatarColor = name => AVATAR_COLORS[[...(name||"")].reduce((s,c)=>s+c.charCodeAt(0),0) % AVATAR_COLORS.length];

  const monthAgoStats = new Date(); monthAgoStats.setDate(1); monthAgoStats.setHours(0,0,0,0);
  const overallStats = useMemo(() => ({
    totalBuyers: buyers.length,
    newThisMonth: buyers.filter(b => new Date(b.CreatedAt) >= monthAgoStats).length,
    totalPurchases: allPurchases.length,
    totalSpent: allPurchases.reduce((s,p)=>s+(p.SoldPrice||0),0),
  }), [buyers, allPurchases]);

  const [mainTab, setMainTab]         = useState("buyers");
  const [allRequests, setAllRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [reqShowFilter, setReqShowFilter] = useState("");
  const [reqDateFilter, setReqDateFilter] = useState("");

  useEffect(() => {
    if (mainTab === "requests" && vendor?.id) {
      setLoadingRequests(true);
      readAllWantListItems(vendor.id)
        .then(setAllRequests)
        .catch(e => toast("Failed to load requests: " + e.message, "error"))
        .finally(() => setLoadingRequests(false));
    }
  }, [mainTab, vendor?.id]);

  const filteredRequests = useMemo(() => {
    return allRequests.filter(r => {
      if (reqShowFilter && r.ShowID !== reqShowFilter) return false;
      if (reqDateFilter && (r.CreatedAt || "").slice(0,10) !== reqDateFilter) return false;
      return true;
    });
  }, [allRequests, reqShowFilter, reqDateFilter]);

  const blankForm = { FullName:"", Phone:"", Email:"", Notes:"", Address:"", City:"", State:"", Zip:"" };
  const [form, setForm]               = useState(blankForm);
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const blankWant = { CardName:"", SetName:"", CardNumber:"", MaxPrice:"", Notes:"" };
  const [wantForm, setWantForm]       = useState(blankWant);
  const [addingWant, setAddingWant]   = useState(false);
  const [showAddWant, setShowAddWant] = useState(false);
  const setW = (k,v) => setWantForm(f=>({...f,[k]:v}));

  const filtered = useMemo(() => {
    if (!search.trim()) return buyers;
    const q = search.toLowerCase();
    return buyers.filter(b =>
      b.FullName.toLowerCase().includes(q) ||
      (b.Phone||"").includes(q) ||
      (b.Email||"").toLowerCase().includes(q)
    );
  }, [buyers, search]);

  const openDetail = async (buyer) => {
    setActive(buyer);
    setForm({ FullName: buyer.FullName, Phone: buyer.Phone||"", Email: buyer.Email||"", Notes: buyer.Notes||"" });
    setView("detail");
    setShowAddWant(false);
    setWantForm(blankWant);
    setLoadingDetail(true);
    const [wl, p] = await Promise.all([
      readBuyerWantList(buyer.ID),
      readBuyerPurchases(buyer.ID),
    ]);
    setWantList(wl);
    setPurchases(p);
    setLoadingDetail(false);
  };

  const handleAdd = async () => {
    if (!form.FullName.trim()) return;
    setSaving(true);
    try {
      const created = await onAdd(form);
      toast(`${created.FullName} added to Buyers List`, "success");
      setForm(blankForm);
      setView("list");
    } catch(e) { toast("Failed to add buyer: " + e.message, "error"); }
    setSaving(false);
  };

  const handleUpdate = async () => {
    if (!form.FullName.trim() || !activeBuyer) return;
    setSaving(true);
    try {
      const updated = await onUpdate(activeBuyer.ID, form);
      setActive(updated);
      toast("Buyer updated", "update");
    } catch(e) { toast("Failed to update: " + e.message, "error"); }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!activeBuyer) return;
    if (!await confirmAction(`Remove ${activeBuyer.FullName} from your Buyers List?`, { confirmLabel:"Remove Buyer" })) return;
    try {
      await onDelete(activeBuyer.ID);
      toast("Buyer removed", "success");
      setView("list"); setActive(null);
    } catch(e) { toast("Failed to remove buyer: " + e.message, "error"); }
  };

  // ── Store Credit (Enterprise) ──────────────────────────────────────────────
  const [creditAdjustment, setCreditAdjustment] = useState(null);
  const [savingCredit, setSavingCredit] = useState(false);

  const handleCreditAdjustment = async () => {
    if (!activeBuyer || !creditAdjustment?.amount) return;
    const amt = parseFloat(creditAdjustment.amount);
    if (isNaN(amt) || amt <= 0) { toast("Enter a valid amount", "error"); return; }
    setSavingCredit(true);
    try {
      const newBalance = await adjustBuyerStoreCredit(activeBuyer.ID, amt * creditAdjustment.sign, creditAdjustment.reason);
      setActive(b => ({ ...b, StoreCreditBalance: newBalance }));
      setCreditAdjustment(null);
      toast(creditAdjustment.sign > 0 ? "Credit added" : "Credit redeemed", "success");
    } catch (e) {
      toast("Couldn't update store credit: " + e.message, "error");
    } finally {
      setSavingCredit(false);
    }
  };

  const handleAddWant = async () => {
    if (!wantForm.CardName.trim() || !activeBuyer) return;
    setAddingWant(true);
    try {
      const item = await addWantItem(activeBuyer.ID, wantForm);
      setWantList(w => [item, ...w]);
      setWantForm(blankWant);
      setShowAddWant(false);
      toast("Added to want list", "success");
    } catch(e) { toast("Failed to add: " + e.message, "error"); }
    setAddingWant(false);
  };

  const handleMarkFound = async (item) => {
    try {
      const updated = await updateWantItem(item.ID, { status:"found" });
      setWantList(w => w.map(x => x.ID === item.ID ? updated : x));
      toast(`${item.CardName} marked as found`, "success");
    } catch(e) { toast("Failed: " + e.message, "error"); }
  };

  const handleRemoveWant = async (item) => {
    if (!await confirmAction(`Remove "${item.CardName}" from ${activeBuyer.FullName}'s want list?`, { confirmLabel:"Remove" })) return;
    try {
      await deleteWantItem(item.ID);
      setWantList(w => w.filter(x => x.ID !== item.ID));
    } catch(e) { toast("Failed: " + e.message, "error"); }
  };

  const sourceLabel = s => s === "kiosk_request" ? "🖥 Kiosk" : s === "show_mode" ? "🎪 Show" : "✏️ Manual";

  // ── NEW BUYER FORM ─────────────────────────────────────────────────────────
  if (view === "new") return (
    <div style={S.page}>
      <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:16}}>
        <button style={{...S.btn("ghost"), padding:"6px 10px"}}
          onClick={()=>{ setForm(blankForm); setView("list"); }}>← Back</button>
        <div style={{fontSize:16, fontWeight:600}}>New Buyer</div>
      </div>
      <div style={S.formWrap}>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Full Name *</label>
          <input style={S.input} placeholder="e.g. Jane Smith"
            value={form.FullName} onChange={e=>setF("FullName",e.target.value)} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Phone</label>
          <input style={S.input} type="tel" placeholder="(555) 555-5555"
            value={form.Phone} onChange={e=>setF("Phone",e.target.value)} />
        </div>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Email</label>
          <input style={S.input} type="email" placeholder="email@example.com"
            value={form.Email} onChange={e=>setF("Email",e.target.value)} />
        </div>
        {isEnterprise && (
          <>
            <div style={{marginBottom:14}}>
              <label style={S.label}>Address <span style={{color:T.textFaint}}>(optional)</span></label>
              <input style={S.input} placeholder="Street address"
                value={form.Address} onChange={e=>setF("Address",e.target.value)} />
            </div>
            <div style={{display:"flex", gap:8, marginBottom:14}}>
              <div style={{flex:2}}>
                <label style={S.label}>City</label>
                <input style={S.input} value={form.City} onChange={e=>setF("City",e.target.value)} />
              </div>
              <div style={{flex:1}}>
                <label style={S.label}>State</label>
                <input style={S.input} value={form.State} onChange={e=>setF("State",e.target.value)} />
              </div>
              <div style={{flex:1}}>
                <label style={S.label}>Zip</label>
                <input style={S.input} value={form.Zip} onChange={e=>setF("Zip",e.target.value)} />
              </div>
            </div>
          </>
        )}
        <div style={{marginBottom:20}}>
          <label style={S.label}>Notes <span style={{color:T.textFaint}}>(optional)</span></label>
          <textarea style={{...S.input, minHeight:64, resize:"vertical"}}
            placeholder="Preferences, interests, details…"
            value={form.Notes} onChange={e=>setF("Notes",e.target.value)} />
        </div>
        <button style={{...S.btn("primary"), width:"100%"}}
          onClick={handleAdd} disabled={saving || !form.FullName.trim()}>
          {saving ? "Adding…" : "Add to Buyers List"}
        </button>
      </div>
    </div>
  );

  // ── BUYER DETAIL ───────────────────────────────────────────────────────────
  if (view === "detail" && activeBuyer) {
    const hunting = wantList.filter(w => w.Status === "hunting");
    const found   = wantList.filter(w => w.Status === "found");

    return (
      <div style={S.page}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:16}}>
          <button style={{...S.btn("ghost"), padding:"6px 10px"}}
            onClick={()=>{ setView("list"); setActive(null); }}>← Buyers List</button>
          <div style={{flex:1, fontSize:16, fontWeight:600}}>{activeBuyer.FullName}</div>
          <span style={{fontSize:11, color:T.textFaint}}>{sourceLabel(activeBuyer.Source)}</span>
        </div>

        <div style={{...S.card, marginBottom:12}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12}}>
            <div style={S.h2}>Contact Info</div>
            <button onClick={handleDelete}
              style={{background:"none", border:"none", color:"#f87171",
                fontSize:12, fontWeight:600, cursor:"pointer", padding:0}}>
              Remove Buyer
            </button>
          </div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Full Name</label>
            <input style={S.input} value={form.FullName}
              onChange={e=>setF("FullName",e.target.value)} />
          </div>
          <div style={{display:"flex", gap:10, marginBottom:10}}>
            <div style={{flex:1}}>
              <label style={S.label}>Phone</label>
              <input style={S.input} type="tel" value={form.Phone}
                onChange={e=>setF("Phone",e.target.value)} />
            </div>
            <div style={{flex:1}}>
              <label style={S.label}>Email</label>
              <input style={S.input} type="email" value={form.Email}
                onChange={e=>setF("Email",e.target.value)} />
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={S.label}>Notes</label>
            <textarea style={{...S.input, minHeight:52, resize:"vertical"}}
              value={form.Notes} onChange={e=>setF("Notes",e.target.value)} />
          </div>
          {isEnterprise && (
            <>
              <div style={{marginBottom:10}}>
                <label style={S.label}>Address <span style={{color:T.textFaint}}>(optional)</span></label>
                <input style={S.input} placeholder="Street address"
                  value={form.Address} onChange={e=>setF("Address",e.target.value)} />
              </div>
              <div style={{display:"flex", gap:8, marginBottom:12}}>
                <div style={{flex:2}}>
                  <label style={S.label}>City</label>
                  <input style={S.input} value={form.City} onChange={e=>setF("City",e.target.value)} />
                </div>
                <div style={{flex:1}}>
                  <label style={S.label}>State</label>
                  <input style={S.input} value={form.State} onChange={e=>setF("State",e.target.value)} />
                </div>
                <div style={{flex:1}}>
                  <label style={S.label}>Zip</label>
                  <input style={S.input} value={form.Zip} onChange={e=>setF("Zip",e.target.value)} />
                </div>
              </div>
            </>
          )}
          <button style={{...S.btn("primary"), width:"100%"}}
            onClick={handleUpdate} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>

        {isEnterprise && (
          <div style={{...S.card, marginBottom:12}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4}}>
              <div style={S.h2}>Store Credit</div>
              <div style={{fontSize:20, fontWeight:700, color: (activeBuyer.StoreCreditBalance||0) > 0 ? "#4ade80" : T.textMuted}}>
                {fmt(activeBuyer.StoreCreditBalance||0)}
              </div>
            </div>
            <div style={{display:"flex", gap:8, marginTop:8}}>
              <button style={{...S.btn("ghost"), flex:1, fontSize:12}}
                onClick={()=>setCreditAdjustment({sign:1})}>+ Add Credit</button>
              <button style={{...S.btn("ghost"), flex:1, fontSize:12}}
                onClick={()=>setCreditAdjustment({sign:-1})}>− Redeem Credit</button>
            </div>
            {creditAdjustment && (
              <div style={{marginTop:10, padding:10, borderRadius:8, background:T.surface2}}>
                <div style={{display:"flex", gap:8, marginBottom:8}}>
                  <input style={{...S.input, width:100}} type="number" min="0" step="0.01"
                    placeholder="Amount" autoFocus
                    value={creditAdjustment.amount||""}
                    onChange={e=>setCreditAdjustment(c=>({...c, amount:e.target.value}))} />
                  <input style={{...S.input, flex:1}} placeholder="Reason (optional)"
                    value={creditAdjustment.reason||""}
                    onChange={e=>setCreditAdjustment(c=>({...c, reason:e.target.value}))} />
                </div>
                <div style={{display:"flex", gap:8}}>
                  <button style={{...S.btn("primary"), flex:1, fontSize:12}}
                    onClick={handleCreditAdjustment} disabled={savingCredit || !creditAdjustment.amount}>
                    {savingCredit ? "Saving…" : (creditAdjustment.sign>0 ? "Add" : "Redeem")}
                  </button>
                  <button style={{...S.btn("ghost"), fontSize:12}} onClick={()=>setCreditAdjustment(null)}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:12}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Want List</div>
            <div style={S.statVal}>{hunting.length}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Purchases</div>
            <div style={S.statVal}>{purchases.length}</div>
          </div>
        </div>

        <div style={{...S.card, marginBottom:12}}>
          <div style={{display:"flex", justifyContent:"space-between",
            alignItems:"center", marginBottom:12}}>
            <div style={S.h2}>🎯 Want List</div>
            <button style={{...S.btn("ghost"), padding:"5px 10px"}}
              onClick={()=>setShowAddWant(w=>!w)}>
              {showAddWant ? "Cancel" : "+ Add Card"}
            </button>
          </div>

          {showAddWant && (
            <div style={{...S.infoBox, marginBottom:12}}>
              <div style={{marginBottom:10}}>
                <label style={S.label}>Card Name *</label>
                <input style={S.input} placeholder="e.g. Charizard ex"
                  value={wantForm.CardName} onChange={e=>setW("CardName",e.target.value)} />
              </div>
              <div style={{display:"flex", gap:10, marginBottom:10}}>
                <div style={{flex:1}}>
                  <label style={S.label}>Set (optional)</label>
                  <input style={S.input} placeholder="e.g. Obsidian Flames"
                    value={wantForm.SetName} onChange={e=>setW("SetName",e.target.value)} />
                </div>
                <div style={{flex:1}}>
                  <label style={S.label}>Max Price</label>
                  <input style={S.input} type="number" step="0.01" placeholder="0.00"
                    value={wantForm.MaxPrice} onChange={e=>setW("MaxPrice",e.target.value)} />
                </div>
              </div>
              <div style={{marginBottom:10}}>
                <label style={S.label}>Notes</label>
                <input style={S.input} placeholder="Condition preference, etc."
                  value={wantForm.Notes} onChange={e=>setW("Notes",e.target.value)} />
              </div>
              <button style={{...S.btn("primary"), width:"100%"}}
                onClick={handleAddWant} disabled={addingWant || !wantForm.CardName.trim()}>
                {addingWant ? "Adding…" : "Add to Want List"}
              </button>
            </div>
          )}

          {loadingDetail ? (
            <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:12}}>Loading…</div>
          ) : wantList.length === 0 ? (
            <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:12}}>
              No cards on the want list yet.
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:6}}>
              {hunting.map(item => (
                <div key={item.ID} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"10px 12px", background:T.surface, borderRadius:10,
                  border:`0.5px solid rgba(251,191,36,0.2)`,
                }}>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:600}}>{item.CardName}</div>
                    <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>
                      {[item.SetName, item.MaxPrice ? `Max $${item.MaxPrice}` : null, item.Notes]
                        .filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{display:"flex", gap:6, marginLeft:10, flexShrink:0}}>
                    <button onClick={()=>handleMarkFound(item)} style={{
                      background:"rgba(126,175,92,0.12)", border:"0.5px solid rgba(126,175,92,0.35)",
                      color:"#7EAF5C", borderRadius:6, fontSize:11, fontWeight:600,
                      padding:"5px 10px", cursor:"pointer",
                    }}>Found ✓</button>
                    <button onClick={()=>handleRemoveWant(item)} style={{
                      background:"none", border:"none", color:T.textFaint,
                      fontSize:18, cursor:"pointer", lineHeight:1, padding:"0 4px",
                    }}>×</button>
                  </div>
                </div>
              ))}
              {found.length > 0 && (<>
                <div style={S.h2}>Found</div>
                {found.map(item => (
                  <div key={item.ID} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"8px 12px", background:T.surface, borderRadius:10, opacity:0.55,
                  }}>
                    <div style={{fontSize:12, textDecoration:"line-through", color:T.textMuted}}>
                      {item.CardName}
                    </div>
                    <span style={{fontSize:10, color:"#7EAF5C", fontWeight:700}}>✓ Found</span>
                  </div>
                ))}
              </>)}
            </div>
          )}
        </div>

        <div style={S.card}>
          <div style={{...S.h2, marginBottom:12}}>🛍 Purchase History</div>
          {loadingDetail ? (
            <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:12}}>Loading…</div>
          ) : purchases.length === 0 ? (
            <div style={{textAlign:"center", padding:20, color:T.textMuted, fontSize:12}}>
              No purchases recorded yet.{"\n"}Attach this buyer when completing a sale in Show Mode.
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:6}}>
              {purchases.map(p => (
                <div key={p.ID} style={{
                  display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"10px 12px", background:T.surface, borderRadius:10,
                }}>
                  <div>
                    <div style={{fontSize:13, fontWeight:600}}>{p.CardName || "Card"}</div>
                    <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>
                      {new Date(p.PurchasedAt).toLocaleDateString()}
                      {p.ShowID ? " · Show" : ""}
                    </div>
                  </div>
                  {p.SoldPrice != null && (
                    <div style={{fontSize:14, fontWeight:700, color:"#4ade80",
                      fontFamily:"'DM Mono',monospace"}}>
                      ${p.SoldPrice.toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={{display:"flex", justifyContent:"space-between",
        alignItems:"center", marginBottom:16}}>
        <div style={{fontSize:18, fontWeight:800, color:T.text}}>👥 Buyers</div>
        {mainTab==="buyers" && (
          <button style={{...S.btn("primary"), background:T.primary}}
            onClick={()=>{ setForm(blankForm); setView("new"); }}>
            + Add Buyer
          </button>
        )}
      </div>

      <div style={{display:"flex", gap:8, marginBottom:16}}>
        {[["buyers","Buyers"],["requests","Requests"]].map(([key,label])=>(
          <button key={key} onClick={()=>setMainTab(key)}
            style={{flex:1, padding:"9px 0", borderRadius:T.radius.md, cursor:"pointer",
              fontSize:13, fontWeight:600,
              border:`1px solid ${mainTab===key?T.primary:T.border}`,
              background:mainTab===key?T.blueBg:"transparent",
              color:mainTab===key?T.primary:T.textMuted}}>
            {label}
          </button>
        ))}
      </div>

      {mainTab==="buyers" && (<>
      <div style={{fontSize:13, color:T.textMuted, marginBottom:12}}>
        {buyers.length} buyer{buyers.length!==1?"s":""} · Track frequent customers and their want lists
      </div>

      <input style={{...S.input, marginBottom:14}}
        placeholder="Search by name, phone, email…"
        value={search} onChange={e=>setSearch(e.target.value)} />

      {filtered.length === 0 ? (
        <div style={{textAlign:"center", padding:40, color:T.textMuted}}>
          <div style={{fontSize:32, marginBottom:8}}>👥</div>
          <div style={{fontSize:14, marginBottom:4}}>
            {search ? "No buyers match that search." : "No buyers yet"}
          </div>
          <div style={{fontSize:12, color:T.textFaint}}>
            {search ? "Try a different search term." : "Add your first buyer to start tracking want lists and purchase history."}
          </div>
        </div>
      ) : (
        <div style={{...S.card, padding:0}}>
          {filtered.map((b,i) => {
            const stats = buyerStats[b.ID] || { count:0, total:0, lastActive:null };
            return (
            <div key={b.ID} style={{cursor:"pointer",
                borderTop: i>0 ? `1px solid ${T.borderSoft}` : "none",
                padding:"14px 16px"}}
              onClick={()=>openDetail(b)}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:12}}>
                <div style={{display:"flex", alignItems:"center", gap:12, minWidth:0, flex:1}}>
                  <div style={{width:40, height:40, borderRadius:"50%", flexShrink:0,
                    background:avatarColor(b.FullName), color:"#fff",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontWeight:700, fontSize:15}}>
                    {(b.FullName||"?")[0]?.toUpperCase()}
                  </div>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:700, fontSize:15, color:T.text}}>{b.FullName}</div>
                    <div style={{fontSize:12, color:T.textMuted, marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {[b.Phone, b.Email].filter(Boolean).join(" · ") || "No contact info"}
                    </div>
                  </div>
                </div>

                {stats.lastActive && (
                  <div style={{fontSize:12, color:T.textFaint, flexShrink:0, textAlign:"right"}}>
                    <div>Last active</div>
                    <div style={{color:T.primary, fontWeight:600}}>{timeAgo(stats.lastActive)}</div>
                  </div>
                )}
                <div style={{fontSize:12, color:T.textFaint, flexShrink:0, textAlign:"right"}}>
                  <div>Joined</div>
                  <div style={{color:T.textMuted, fontWeight:600}}>{new Date(b.CreatedAt).toLocaleDateString()}</div>
                </div>
                <div style={{textAlign:"center", flexShrink:0}}>
                  <div style={{fontSize:11, color:T.textFaint}}>Purchases</div>
                  <div style={{fontSize:15, fontWeight:700, color:T.primary}}>{stats.count}</div>
                </div>
                <div style={{textAlign:"right", flexShrink:0, minWidth:80}}>
                  <div style={{fontSize:11, color:T.textFaint}}>Total Spent</div>
                  <div style={{fontSize:15, fontWeight:700, color:T.green}}>{fmt(stats.total)}</div>
                </div>
                <span style={{color:T.textFaint, fontSize:18, flexShrink:0}}>›</span>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {filtered.length > 0 && (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(120px, 1fr))", gap:10, marginTop:14}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Total Buyers</div>
            <div style={S.statVal}>{overallStats.totalBuyers}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>New This Month</div>
            <div style={S.statVal}>{overallStats.newThisMonth}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Total Purchases</div>
            <div style={S.statVal}>{overallStats.totalPurchases}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Total Spent</div>
            <div style={{...S.statVal, color:T.green}}>{fmt(overallStats.totalSpent)}</div>
          </div>
        </div>
      )}
      </>)}

      {mainTab==="requests" && (<>
        <div style={{display:"flex", gap:8, marginBottom:14}}>
          <select style={{...S.select, flex:1}}
            value={reqShowFilter} onChange={e=>setReqShowFilter(e.target.value)}>
            <option value="">All shows</option>
            {shows.map(s=><option key={s.ID} value={s.ID}>{s.Name}</option>)}
          </select>
          <input type="date" style={{...S.input, flex:1}}
            value={reqDateFilter} onChange={e=>setReqDateFilter(e.target.value)} />
          {(reqShowFilter || reqDateFilter) && (
            <button style={S.btn("ghost")} onClick={()=>{setReqShowFilter("");setReqDateFilter("");}}>
              Clear
            </button>
          )}
        </div>

        <div style={{fontSize:12, color:T.textMuted, marginBottom:12}}>
          Card requests submitted through your Kiosk during a show, automatically added here as Buyer want-list entries.
        </div>

        {loadingRequests ? (
          <div style={{textAlign:"center", padding:32, color:T.textMuted}}>Loading requests…</div>
        ) : filteredRequests.length === 0 ? (
          <div style={{textAlign:"center", padding:40, color:T.textMuted}}>
            <div style={{fontSize:32, marginBottom:8}}>📋</div>
            <div style={{fontSize:14}}>No requests {reqShowFilter||reqDateFilter ? "match these filters" : "yet"}</div>
            <div style={{fontSize:12, marginTop:4, color:T.textFaint}}>
              Customer requests from the Kiosk will appear here automatically
            </div>
          </div>
        ) : (
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {filteredRequests.map(r => (
              <div key={r.ID} style={{...S.card, marginBottom:0}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6}}>
                  <div style={{fontWeight:600, fontSize:14}}>{r.CardName}</div>
                  <div style={{fontSize:10, color:T.textFaint}}>
                    {r.CreatedAt ? new Date(r.CreatedAt).toLocaleDateString() : ""}
                  </div>
                </div>
                {r.Notes && <div style={{fontSize:12, color:T.textMuted, marginBottom:4}}>{r.Notes}</div>}
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <div style={{fontSize:12, color:T.textFaint}}>
                    {r.BuyerName} · {[r.BuyerPhone, r.BuyerEmail].filter(Boolean).join(" · ")}
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:8}}>
                    {r.ShowName && <span style={S.badge()}>{r.ShowName}</span>}
                    <select
                      style={{fontSize:11, background:T.surface2, border:`0.5px solid ${T.border}`,
                        borderRadius:6, color:T.text, padding:"3px 8px", cursor:"pointer"}}
                      value={r.Status||"hunting"}
                      onChange={async(e)=>{
                        try {
                          await updateWantItem(r.ID, { status: e.target.value });
                          setAllRequests(reqs => reqs.map(x => x.ID===r.ID ? {...x, Status:e.target.value} : x));
                        } catch(err) { toast("Failed to update: " + err.message, "error"); }
                      }}>
                      <option value="hunting">Hunting</option>
                      <option value="sourced">Sourced</option>
                      <option value="contacted">Contacted</option>
                      <option value="fulfilled">Fulfilled</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </>)}
    </div>
  );
}

// ─── ADMIN PAGE ───────────────────────────────────────────────────────────────
const PLAN_FEATURES = [
  { key:"add_card_api",      label:"Add Card — API Search",         default:true  },
  { key:"consignors",        label:"Consignors",                    default:true  },
  { key:"show_trade",        label:"Show Mode — Trade Tab",         default:true  },
  { key:"kiosk",             label:"Kiosk Mode",                    default:true  },
  { key:"buyers_list",       label:"Buyers List CRM",               default:true  },
  { key:"market_pricing",    label:"Market Pricing",                default:true  },
  { key:"batch_refresh",     label:"Batch Price Refresh",           default:true  },
  { key:"reports_advanced",  label:"Advanced Reports",              default:true  },
  { key:"import_collectr",   label:"Collectr Import",               default:true  },
  { key:"qr_labels",         label:"QR Labels",                     default:true  },
  { key:"card_scanning",     label:"Card Scanning (future)",        default:true  },
  { key:"aliases",           label:"Shared Email Access (max 3)",   default:true  },
  { key:"settings_edit",     label:"Edit Settings",                 default:true  },
];

function AdminPage({ onVendorCreated }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();

  const blank = {
    firstName:"", lastName:"", businessName:"", email:"",
    phone:"", plan:"free", aliases:["","",""],
    features: Object.fromEntries(PLAN_FEATURES.map(f=>[f.key, f.default])),
  };
  const [form, setForm]     = useState(blank);
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView]     = useState("list");

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  useEffect(()=>{
    supabase.from("vendors").select("id,email,name,business_name,plan,role,created_at,status")
      .order("created_at",{ascending:false})
      .then(({data})=>{ setVendors(data||[]); setLoading(false); })
      .catch(()=>setLoading(false));
  },[]);

  useEffect(()=>{
    const isPaid = form.plan !== "free";
    setForm(f=>({
      ...f,
      features: Object.fromEntries(PLAN_FEATURES.map(f=>[f.key, isPaid ? f.default : false]))
    }));
  },[form.plan]);

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.email.trim()) {
      toast("First name and email are required", "alert"); return;
    }
    setSaving(true);
    try {
      const aliases = form.aliases.filter(a=>a.trim());
      const { error } = await supabase.from("vendors").insert({
        email:         form.email.trim().toLowerCase(),
        name:          `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        business_name: form.businessName.trim() || null,
        plan:          form.plan,
        role:          "vendor",
        aliases:       aliases.length ? aliases : null,
        status:        "active",
      });
      if (error) throw error;
      toast(`${form.firstName} added successfully`, "success");
      setForm(blank);
      setView("list");
      const {data} = await supabase.from("vendors")
        .select("id,email,name,business_name,plan,role,created_at,status")
        .order("created_at",{ascending:false});
      setVendors(data||[]);
      if (onVendorCreated) onVendorCreated();
    } catch(e) { toast("Failed to create vendor: " + e.message, "error"); }
    setSaving(false);
  };

  const handleToggleStatus = async (v) => {
    const newStatus = v.status === "active" ? "suspended" : "active";
    if (!await confirmAction(
      `${newStatus === "suspended" ? "Suspend" : "Reactivate"} ${v.name || v.email}?`,
      { confirmLabel: newStatus === "suspended" ? "Suspend" : "Reactivate" }
    )) return;
    await supabase.from("vendors").update({status:newStatus}).eq("id",v.id);
    setVendors(vs=>vs.map(x=>x.id===v.id?{...x,status:newStatus}:x));
    toast(`${v.name || v.email} ${newStatus}`, "update");
  };

  const handleDeleteVendor = async (v) => {
    const ok = await confirmAction(
      `Permanently delete ${v.name || v.email}'s account? This cannot be undone. ` +
      `If they have existing inventory, shows, or other data, deletion will fail — suspend them instead in that case.`,
      { confirmLabel: "Delete Permanently", danger: true }
    );
    if (!ok) return;
    try {
      const { data, error } = await supabase.from("vendors").delete().eq("id", v.id).select();
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error("No rows were deleted — check permissions");
      }
      setVendors(vs => vs.filter(x => x.id !== v.id));
      toast(`${v.name || v.email} deleted`, "success");
    } catch (e) {
      toast(
        `Couldn't delete: ${e.message}. If this account has existing inventory, shows, or other data, suspend it instead.`,
        "error"
      );
    }
  };

  // ── List view ──────────────────────────────────────────────────────────────
  if (view === "list") return (
    <div style={S.page}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:700}}>🛡 Admin — Vendors</div>
        <button style={S.btn("primary")} onClick={()=>setView("new")}>+ New Vendor</button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:32,color:T.textMuted}}>Loading…</div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {vendors.map(v=>(
            <div key={v.id} style={{...S.card}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div>
                  <div style={{fontWeight:600,fontSize:14}}>{v.name || "—"}</div>
                  <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>{v.email}</div>
                  {v.business_name && <div style={{fontSize:12,color:T.textFaint}}>{v.business_name}</div>}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
                  <span style={{
                    fontSize:10,fontWeight:700,letterSpacing:"0.04em",
                    padding:"2px 8px",borderRadius:10,
                    background: v.plan==="free" ? "rgba(255,255,255,0.08)" : "rgba(60,102,238,0.15)",
                    color: v.plan==="free" ? T.textFaint : "#88bdf2",
                    border: `0.5px solid ${v.plan==="free" ? T.border : "rgba(60,102,238,0.4)"}`,
                  }}>
                    {v.plan?.toUpperCase() || "FREE"}
                  </span>
                  {v.role === "admin" && (
                    <span style={{fontSize:10,fontWeight:700,color:"#FF9600",
                      background:"rgba(255,150,0,0.12)",border:"0.5px solid rgba(255,150,0,0.4)",
                      borderRadius:10,padding:"2px 8px"}}>ADMIN</span>
                  )}
                  {v.role !== "admin" && (
                    <>
                      <button
                        onClick={()=>handleToggleStatus(v)}
                        style={{...S.btn(v.status==="active"?"ghost":"primary"),
                          fontSize:11,padding:"4px 10px"}}>
                        {v.status==="active" ? "Suspend" : "Reactivate"}
                      </button>
                      <button
                        onClick={()=>handleDeleteVendor(v)}
                        style={{...S.btn("ghost"), color:"#f87171", borderColor:"#f8717155",
                          fontSize:11,padding:"4px 10px"}}>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ── New vendor form ────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <button style={{...S.btn("ghost"),padding:"6px 10px"}}
          onClick={()=>setView("list")}>← Back</button>
        <div style={{fontSize:16,fontWeight:600}}>New Vendor</div>
      </div>
      <div style={S.formWrap}>

        <div style={{...S.card,marginBottom:12}}>
          <div style={S.h2}>Contact Info</div>
          <div style={{display:"flex",gap:10,marginTop:10,marginBottom:10}}>
            <div style={{flex:1}}>
              <label style={S.label}>First Name *</label>
              <input style={S.input} value={form.firstName}
                onChange={e=>setF("firstName",e.target.value)} placeholder="Jane" />
            </div>
            <div style={{flex:1}}>
              <label style={S.label}>Last Name</label>
              <input style={S.input} value={form.lastName}
                onChange={e=>setF("lastName",e.target.value)} placeholder="Smith" />
            </div>
          </div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Business Name</label>
            <input style={S.input} value={form.businessName}
              onChange={e=>setF("businessName",e.target.value)}
              placeholder="e.g. Smith Cards & Collectibles" />
          </div>
          <div style={{marginBottom:10}}>
            <label style={S.label}>Email Address *</label>
            <input style={S.input} type="email" value={form.email}
              onChange={e=>setF("email",e.target.value)} placeholder="vendor@email.com" />
          </div>
          <div style={{marginBottom:0}}>
            <label style={S.label}>Contact Phone</label>
            <input style={S.input} type="tel" value={form.phone}
              onChange={e=>setF("phone",e.target.value)} placeholder="(555) 555-5555" />
          </div>
        </div>

        <div style={{...S.card,marginBottom:12}}>
          <div style={S.h2}>Shared Email Access</div>
          <div style={{fontSize:12,color:T.textMuted,marginTop:4,marginBottom:10}}>
            Up to 3 additional email addresses that can access this vendor's data.
            Available on paid plans only.
          </div>
          {[0,1,2].map(i=>(
            <div key={i} style={{marginBottom:8}}>
              <label style={S.label}>Shared Email {i+1}</label>
              <input style={{...S.input, opacity:form.plan==="free"?0.4:1}}
                type="email" disabled={form.plan==="free"}
                value={form.aliases[i]}
                onChange={e=>{
                  const a=[...form.aliases]; a[i]=e.target.value;
                  setF("aliases",a);
                }}
                placeholder={form.plan==="free" ? "Available on paid plans" : "email@example.com"} />
            </div>
          ))}
        </div>

        <div style={{...S.card,marginBottom:12}}>
          <div style={S.h2}>Plan Type</div>
          <div style={{display:"flex",gap:8,marginTop:10}}>
            {["free","monthly","annual"].map(p=>(
              <button key={p}
                onClick={()=>setF("plan",p)}
                style={{
                  ...S.btn(form.plan===p?"primary":"ghost"),
                  flex:1, textTransform:"capitalize",
                }}>
                {p}
              </button>
            ))}
          </div>
          {form.plan==="free" && (
            <div style={{fontSize:11,color:T.textMuted,marginTop:8}}>
              Free plan: max {FREE_INVENTORY_LIMIT} cards, limited features
            </div>
          )}
        </div>

        <div style={{...S.card,marginBottom:12}}>
          <div style={S.h2}>Feature Access</div>
          <div style={{fontSize:12,color:T.textMuted,marginTop:4,marginBottom:12}}>
            Features checked below will be available to this vendor.
            Changing the plan above auto-updates these defaults.
          </div>
          {PLAN_FEATURES.map(f=>(
            <label key={f.key} style={{
              display:"flex",alignItems:"center",gap:10,
              padding:"8px 0",
              borderBottom:`0.5px solid ${T.border}`,
              cursor:"pointer",
            }}>
              <input type="checkbox"
                checked={!!form.features[f.key]}
                onChange={e=>{
                  setForm(frm=>({...frm,features:{...frm.features,[f.key]:e.target.checked}}));
                }}
                style={{width:16,height:16,accentColor:"#3C66EE",flexShrink:0}} />
              <span style={{fontSize:13}}>{f.label}</span>
              {form.plan==="free" && f.default && (
                <span style={{marginLeft:"auto"}}><PaidBadge /></span>
              )}
            </label>
          ))}
        </div>

        <button style={{...S.btn("primary"),width:"100%",padding:"14px"}}
          onClick={handleSubmit} disabled={saving||!form.firstName.trim()||!form.email.trim()}>
          {saving ? "Creating…" : "Create Vendor Account"}
        </button>

      </div>
    </div>
  );
}

// ─── CONSIGNORS PAGE ─────────────────────────────────────────────────────────
function ConsignorsPage({ consignors, inventory, onAdd, onUpdate, onDelete, onUpdateItem }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();
  const [view, setView]           = useState("list");
  const [activeConsignor, setActiveConsignor] = useState(null);
  const [form, setForm]           = useState({ FullName:"", Email:"", Phone:"", Notes:"" });
  const [editForm, setEditForm]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const consignorCards = useMemo(() => {
    const map = {};
    (inventory||[]).forEach(item => {
      if (item.IsConsignment && item.ConsignorID && item.Status !== "Deleted") {
        if (!map[item.ConsignorID]) map[item.ConsignorID] = [];
        map[item.ConsignorID].push(item);
      }
    });
    return map;
  }, [inventory]);

  const saveNew = async () => {
    if (!form.FullName.trim()) return;
    setSaving(true);
    await onAdd(form);
    setSaving(false);
    setForm({ FullName:"", Email:"", Phone:"", Notes:"" });
    setView("list");
  };

  // ── NEW CONSIGNOR FORM ─────────────────────────────────────────────────────
  if (view === "new") return (
    <div style={S.page}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
        <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setView("list")}>← Back</button>
        <div style={{fontSize:16,fontWeight:600}}>New Consignor</div>
      </div>

      <div style={S.formWrap}>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Full Name *</label>
          <input style={S.input} value={form.FullName} onChange={e=>set("FullName",e.target.value)}
            placeholder="e.g. Jane Smith" />
        </div>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Email</label>
          <input style={S.input} type="email" value={form.Email} onChange={e=>set("Email",e.target.value)}
            placeholder="jane@example.com" />
        </div>
        <div style={{marginBottom:14}}>
          <label style={S.label}>Phone</label>
          <input style={S.input} type="tel" value={form.Phone} onChange={e=>set("Phone",e.target.value)}
            placeholder="(555) 555-5555" />
        </div>
        <div style={{marginBottom:20}}>
          <label style={S.label}>Notes <span style={{color:T.textFaint}}>(optional)</span></label>
          <input style={S.input} value={form.Notes} onChange={e=>set("Notes",e.target.value)}
            placeholder="Any notes about this consignor..." />
        </div>
        <button style={{...S.btn("primary"),width:"100%"}} onClick={saveNew}
          disabled={saving||!form.FullName.trim()}>
          {saving?"Saving…":"Add Consignor"}
        </button>
      </div>
    </div>
  );

  // ── CONSIGNOR DETAIL ────────────────────────────────────────────────────────
  if (view === "detail" && activeConsignor) {
    const consignor = consignors.find(c=>c.ID===activeConsignor.ID) || activeConsignor;
    const cards = consignorCards[consignor.ID] || [];
    const totalValue = cards.reduce((s,c)=>s+(parseFloat(c.ListPrice)||0),0);

    return (
      <div style={S.page}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
          <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>{setView("list");setActiveConsignor(null);setEditForm(null);}}>← Back</button>
          <div style={{flex:1}}>
            {editForm ? (
              <input style={{...S.input,fontSize:16,fontWeight:600}} value={editForm.FullName}
                onChange={e=>setEditForm(f=>({...f,FullName:e.target.value}))} />
            ) : (
              <div style={{fontSize:16,fontWeight:600}}>{consignor.FullName}</div>
            )}
          </div>
          {editForm ? (
            <div style={{display:"flex",gap:6}}>
              <button style={S.btn("primary")} onClick={async()=>{
                await onUpdate(consignor.ID, editForm);
                setEditForm(null);
              }}>Save</button>
              <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setEditForm(null)}>✕</button>
            </div>
          ) : (
            <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:12}}
              onClick={()=>setEditForm({FullName:consignor.FullName,Email:consignor.Email||"",Phone:consignor.Phone||"",Notes:consignor.Notes||""})}>
              ✏ Edit
            </button>
          )}
        </div>

        <div style={{...S.card, marginBottom:12}}>
          {editForm ? (
            <>
              <div style={{marginBottom:10}}>
                <label style={S.label}>Email</label>
                <input style={S.input} type="email" value={editForm.Email}
                  onChange={e=>setEditForm(f=>({...f,Email:e.target.value}))} placeholder="jane@example.com" />
              </div>
              <div style={{marginBottom:10}}>
                <label style={S.label}>Phone</label>
                <input style={S.input} type="tel" value={editForm.Phone}
                  onChange={e=>setEditForm(f=>({...f,Phone:e.target.value}))} placeholder="(555) 555-5555" />
              </div>
              <div>
                <label style={S.label}>Notes</label>
                <input style={S.input} value={editForm.Notes}
                  onChange={e=>setEditForm(f=>({...f,Notes:e.target.value}))} placeholder="Any notes..." />
              </div>
            </>
          ) : (
            <>
              <div style={{display:"flex",gap:16,flexWrap:"wrap",marginBottom:consignor.Notes?8:0}}>
                <div style={{fontSize:13,color:T.textMuted}}>✉️ {consignor.Email || "—"}</div>
                <div style={{fontSize:13,color:T.textMuted}}>📞 {consignor.Phone || "—"}</div>
              </div>
              {consignor.Notes && <div style={{fontSize:12,color:T.textFaint}}>{consignor.Notes}</div>}
            </>
          )}
        </div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:12}}>
          <div style={S.stat}>
            <div style={S.statLbl}>Cards Consigned</div>
            <div style={S.statVal}>{cards.length}</div>
          </div>
          <div style={S.stat}>
            <div style={S.statLbl}>Total List Value</div>
            <div style={{...S.statVal,color:"#4ade80"}}>{fmt(totalValue)}</div>
          </div>
        </div>

        <div style={S.h2}>Consigned Cards & Requirements</div>
        {cards.length === 0 ? (
          <div style={{textAlign:"center",padding:32,color:T.textMuted}}>
            <div style={{fontSize:28,marginBottom:8}}>💎</div>
            <div style={{fontSize:14}}>No cards consigned yet</div>
            <div style={{fontSize:12,marginTop:4,color:T.textFaint}}>
              Mark a card as consignment in Inventory and assign it to this consignor.
            </div>
          </div>
        ) : cards.map(card => (
          <div key={card.ID} style={{...S.card, marginBottom:8}}>
            <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:8}}>
              {card.ImageURL ? (
                <img src={card.ImageURL} alt={card.CardName}
                  style={{width:44,height:60,objectFit:"cover",borderRadius:6,
                    border:`0.5px solid ${T.border}`,flexShrink:0}} />
              ) : (
                <div style={{width:44,height:60,borderRadius:6,border:`1px dashed ${T.border}`,
                  flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:16,color:T.textFaint}}>⬡</div>
              )}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,fontSize:14}}>{card.CardName}</div>
                <div style={{fontSize:11,color:T.textMuted}}>
                  {[card.Set,card.Number,card.Condition,card.Variant,card.Grade&&`${card.GradingCompany} ${card.Grade}`].filter(Boolean).join(" · ")}
                </div>
                <div style={{fontSize:11,color:T.textFaint,marginTop:2}}>
                  Status: {card.Status}
                </div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <div style={{fontSize:16,fontWeight:700,color:"#3b82f6"}}>{fmt(card.ListPrice)}</div>
              </div>
            </div>

            <div style={{display:"flex",gap:8,paddingTop:8,borderTop:`0.5px solid ${T.border}`}}>
              <div style={{flex:1}}>
                <label style={{...S.label,fontSize:10}}>Min Accept Price</label>
                <input style={{...S.input,fontSize:13,padding:"6px 10px"}} type="number"
                  defaultValue={card.MinAcceptPrice||""}
                  onBlur={e=>onUpdateItem(card.ID,{MinAcceptPrice:parseFloat(e.target.value)||0})}
                  placeholder="No minimum set" />
              </div>
              <div style={{flex:1}}>
                <label style={{...S.label,fontSize:10}}>Trades Accepted</label>
                <select style={{...S.select,fontSize:13,padding:"6px 10px"}}
                  value={card.TradesAccepted?"yes":"no"}
                  onChange={e=>onUpdateItem(card.ID,{TradesAccepted:e.target.value==="yes"})}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        {cards.length === 0 && (
          <button style={{...S.btn("danger"),width:"100%",marginTop:8}}
            onClick={async()=>{
              if (!await confirmAction(`Remove ${consignor.FullName} as a consignor?`, {confirmLabel:"Remove Consignor"})) return;
              await onDelete(consignor.ID);
              setView("list");
              setActiveConsignor(null);
              toast("Consignor removed", "success");
            }}>
            Remove Consignor
          </button>
        )}
      </div>
    );
  }

  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:16,fontWeight:600}}>💎 Consignors</div>
        <button style={S.btn("primary")} onClick={()=>setView("new")}>+ New Consignor</button>
      </div>

      <div style={{fontSize:12,color:T.textMuted,marginBottom:12}}>
        {consignors.length} consignor{consignors.length!==1?"s":""} · Cards must be $500+ to qualify for consignment
      </div>

      {consignors.length === 0 ? (
        <div style={{textAlign:"center",padding:40,color:T.textMuted}}>
          <div style={{fontSize:32,marginBottom:8}}>💎</div>
          <div style={{fontSize:14,marginBottom:4}}>No consignors yet</div>
          <div style={{fontSize:12,color:T.textFaint}}>Add a consignor to start tracking consigned cards</div>
        </div>
      ) : consignors.map(c => {
        const cards = consignorCards[c.ID] || [];
        const totalValue = cards.reduce((s,card)=>s+(parseFloat(card.ListPrice)||0),0);
        return (
          <div key={c.ID} style={{...S.card,marginBottom:8,cursor:"pointer"}}
            onClick={()=>{setActiveConsignor(c);setView("detail");setEditForm(null);}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
              <div>
                <div style={{fontWeight:600,fontSize:15}}>{c.FullName}</div>
                <div style={{fontSize:12,color:T.textMuted,marginTop:2}}>
                  {[c.Email,c.Phone].filter(Boolean).join(" · ") || "No contact info"}
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:16,fontWeight:700,color:"#4ade80"}}>{fmt(totalValue)}</div>
                <div style={{fontSize:11,color:T.textFaint}}>{cards.length} card{cards.length!==1?"s":""}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── TRADES PAGE ─────────────────────────────────────────────────────────────
// ─── STORE REQUESTS (Store Mode Kiosk Orders queue) ──────────────────────────
function StoreRequestsTab({ vendor, onUpdateItem, onAddBuyer, orders, loading, onReload, S, T }) {
  const { toast, confirmAction } = useNotify();
  const [activeOrder, setActiveOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contactForm, setContactForm] = useState({ email:"", phone:"" });
  const [savingContact, setSavingContact] = useState(false);
  const [busyItemId, setBusyItemId] = useState(null);
  const [finishing, setFinishing] = useState(false);

  const load = onReload;

  const openDetail = (orderId) => {
    setDetailLoading(true);
    readKioskOrderDetail(orderId).then(o => {
      setActiveOrder(o);
      setContactForm({ email:o.Email||"", phone:o.Phone||"" });
      setDetailLoading(false);
    }).catch(()=>setDetailLoading(false));
  };

  const refreshDetail = () => { if (activeOrder) openDetail(activeOrder.ID); };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await updateKioskOrderContact(activeOrder.ID, contactForm);
      toast("Contact info saved", "success");
      refreshDetail();
    } catch(e) { toast("Could not save: " + e.message, "error"); }
    setSavingContact(false);
  };

  const toggleFulfill = async (item) => {
    setBusyItemId(item.ID);
    try {
      const nowFulfilled = !item.Fulfilled;
      await setKioskOrderItemFulfilled(item.ID, item.InventoryID, nowFulfilled);
      toast(nowFulfilled ? `✓ ${item.CardName} marked as pulled` : `${item.CardName} un-marked`, "success");
      refreshDetail();
    } catch(e) { toast("Could not update: " + e.message, "error"); }
    setBusyItemId(null);
  };

  const removeItem = async (item) => {
    setBusyItemId(item.ID);
    try {
      await removeKioskOrderItem(item.ID, item.InventoryID, item.Fulfilled);
      refreshDetail();
    } catch(e) { toast("Could not remove: " + e.message, "error"); }
    setBusyItemId(null);
  };

  const doComplete = async () => {
    const remaining = activeOrder.Items;
    if (remaining.length === 0) { toast("Nothing left in this order to complete", "alert"); return; }
    const ok = await confirmAction(
      "Completing this order will mark these cards as Sold.",
      { confirmLabel: "Complete Order" }
    );
    if (!ok) return;
    setFinishing(true);
    try {
      const result = await completeKioskOrder(activeOrder, remaining, vendor);
      if (result.buyerAdded) {
        toast(`Order #${activeOrder.OrderNumber} completed — added to Buyers`, "success");
      } else {
        toast(`Order #${activeOrder.OrderNumber} completed, but couldn't add to Buyers: ${result.buyerError}`, "alert");
      }
      setActiveOrder(null);
      load();
    } catch(e) { toast("Could not complete order: " + e.message, "error"); }
    setFinishing(false);
  };

  const doCancel = async () => {
    const ok = await confirmAction("Cancel this order? Any Fulfilled cards will be released back to In Stock.");
    if (!ok) return;
    setFinishing(true);
    try {
      await cancelKioskOrder(activeOrder, activeOrder.Items);
      toast("Order cancelled", "success");
      setActiveOrder(null);
      load();
    } catch(e) { toast("Could not cancel: " + e.message, "error"); }
    setFinishing(false);
  };

  return (
    <div>
      <div style={{fontSize:12, color:T.textMuted, marginBottom:12}}>
        Orders customers built at your Store kiosk, waiting to be pulled and picked up.
      </div>

      {loading ? (
        <div style={{textAlign:"center", padding:30, color:T.textMuted}}>Loading…</div>
      ) : orders.length === 0 ? (
        <div style={{textAlign:"center", padding:40}}>
          <div style={{fontSize:32, marginBottom:8}}>🧺</div>
          <div style={{fontSize:14, color:T.textMuted}}>No Store requests right now.</div>
        </div>
      ) : (
        <div style={{...S.card, padding:0}}>
          <div style={{display:"grid", gridTemplateColumns:"1.2fr 2fr 0.6fr 1fr 0.8fr", gap:8,
            padding:"10px 16px", fontSize:11, color:T.textFaint, textTransform:"uppercase",
            letterSpacing:"0.05em", borderBottom:`1px solid ${T.borderSoft||T.border}`}}>
            <span>Name</span><span>Cards</span><span>Order #</span><span>Time</span><span></span>
          </div>
          {orders.map((o,i) => (
            <div key={o.ID} style={{display:"grid", gridTemplateColumns:"1.2fr 2fr 0.6fr 1fr 0.8fr", gap:8,
              padding:"12px 16px", alignItems:"center", fontSize:13,
              borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
              <span style={{fontWeight:600}}>{o.FirstName} {o.LastName}</span>
              <span style={{color:T.textMuted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                {o.CardNames.join(", ")}
              </span>
              <span style={{fontFamily:"'DM Mono',monospace", color:T.primary||"#3C66EE"}}>#{o.OrderNumber}</span>
              <span style={{fontSize:11, color:T.textFaint}}>{new Date(o.CreatedAt).toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}</span>
              <button style={S.btn("sm")} onClick={()=>openDetail(o.ID)}>View details</button>
            </div>
          ))}
        </div>
      )}

      {(detailLoading || activeOrder) && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setActiveOrder(null); }}>
          <div style={S.drawer}>
            {detailLoading || !activeOrder ? (
              <div style={{textAlign:"center", padding:30}}>Loading…</div>
            ) : (
              <>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4}}>
                  <div>
                    <div style={{fontSize:18, fontWeight:800}}>{activeOrder.FirstName} {activeOrder.LastName}</div>
                    <div style={{fontSize:12, color:T.textMuted}}>Order #{activeOrder.OrderNumber}</div>
                  </div>
                  <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setActiveOrder(null)}>✕</button>
                </div>

                <div style={{...S.row, marginTop:16}}>
                  <div style={S.col}>
                    <label style={S.label}>Email (optional)</label>
                    <input style={S.input} value={contactForm.email}
                      onChange={e=>setContactForm(f=>({...f, email:e.target.value}))} placeholder="customer@email.com" />
                  </div>
                  <div style={S.col}>
                    <label style={S.label}>Phone (optional)</label>
                    <input style={S.input} value={contactForm.phone}
                      onChange={e=>setContactForm(f=>({...f, phone:e.target.value}))} placeholder="(555) 555-5555" />
                  </div>
                </div>
                <button style={{...S.btn("ghost"), fontSize:12, marginBottom:16}}
                  onClick={saveContact} disabled={savingContact}>
                  {savingContact ? "Saving…" : "Save Contact Info"}
                </button>

                <div style={S.divider}/>
                <div style={{fontSize:12, fontWeight:700, color:T.textMuted, marginBottom:10,
                  textTransform:"uppercase", letterSpacing:"0.05em"}}>
                  Cards Requested ({activeOrder.Items.length})
                </div>

                {activeOrder.Items.length === 0 ? (
                  <div style={{fontSize:13, color:T.textFaint, marginBottom:16}}>
                    No cards left in this order.
                  </div>
                ) : activeOrder.Items.map(item => {
                  const isSoldElsewhere = item.LiveStatus === "Sold";
                  const isBusy = busyItemId === item.ID;
                  return (
                    <div key={item.ID} style={{...S.card, marginBottom:8,
                      borderLeft: `4px solid ${item.Fulfilled ? "#4ade80" : isSoldElsewhere ? "#f87171" : (T.border||"transparent")}`,
                      background: item.Fulfilled ? "rgba(74,222,128,0.06)" : S.card.background}}>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10}}>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontWeight:600, fontSize:14}}>
                            {item.Fulfilled ? "✓ " : ""}{item.CardName}
                          </div>
                          <div style={{fontSize:12, color:T.textMuted, marginTop:2}}>
                            {item.LiveCondition || "—"} · {fmt(item.LivePrice)}
                          </div>
                          <span style={{display:"inline-block", marginTop:6, fontSize:10, fontWeight:700,
                            padding:"2px 8px", borderRadius:20,
                            background: isSoldElsewhere ? "rgba(248,113,113,0.12)"
                              : item.Fulfilled ? "rgba(74,222,128,0.12)" : "rgba(96,165,250,0.12)",
                            color: isSoldElsewhere ? "#f87171" : item.Fulfilled ? "#4ade80" : "#60a5fa"}}>
                            {isSoldElsewhere ? "Sold" : item.Fulfilled ? "Fulfilled — Pulled" : "In-Stock"}
                          </span>
                        </div>
                        <div style={{display:"flex", flexDirection:"column", gap:6, flexShrink:0}}>
                          <button style={{...S.btn("sm"), background: item.Fulfilled ? "#22c55e" : "#3C66EE", color:"#fff", fontWeight:700}}
                            onClick={()=>toggleFulfill(item)} disabled={isBusy || isSoldElsewhere}>
                            {isBusy ? "…" : item.Fulfilled ? "✓ Fulfilled" : "Fulfill"}
                          </button>
                          <button style={{...S.btn("sm"), background:"#dc2626", color:"#fff"}}
                            onClick={()=>removeItem(item)} disabled={isBusy}>
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div style={{display:"flex", gap:10, marginTop:16}}>
                  <button style={{...S.btn("ghost"), flex:1}} onClick={doCancel} disabled={finishing}>
                    Cancel Order
                  </button>
                  <button style={{...S.btn("primary"), flex:1}} onClick={doComplete} disabled={finishing}>
                    {finishing ? "Working…" : "Complete Order"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DEAL BOARD ───────────────────────────────────────────────────────────────
// ─── WANTED ITEMS ─────────────────────────────────────────────────────────────
function WantedItemsSection({ vendor, isPaid, S, T }) {
  const { toast, openDealBoardPlusModal, openUpgradeModal } = useNotify();
  const [view, setView] = useState("browse");
  const [feed, setFeed] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feedTotalCount, setFeedTotalCount] = useState(0);
  const PAGE_SIZE = 24;
  const [showPostForm, setShowPostForm] = useState(false);
  const [postCardName, setPostCardName] = useState("");
  const [postCondition, setPostCondition] = useState("Near Mint");
  const [postPrice, setPostPrice] = useState("");
  const [posting, setPosting] = useState(false);
  const [composeFor, setComposeFor] = useState(null);
  const [composeBody, setComposeBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      getDealBoardRequestsFeed({ limit: PAGE_SIZE, offset: 0 }).catch(() => ({ rows: [], totalCount: 0 })),
      vendor?.id ? getMyDealBoardRequests(vendor.id).catch(() => []) : Promise.resolve([]),
    ]).then(([f, m]) => {
      setFeed(f.rows);
      setFeedTotalCount(f.totalCount);
      setMine(m);
      setLoading(false);
    });
  };

  const loadMore = () => {
    setLoadingMore(true);
    getDealBoardRequestsFeed({ limit: PAGE_SIZE, offset: feed.length })
      .then(f => { setFeed(prev => [...prev, ...f.rows]); setFeedTotalCount(f.totalCount); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  useEffect(load, [vendor?.id]);

  const handleCardNameChange = (val) => {
    if (containsBlockedContent(val)) {
      const seenKey = "vs_content_filter_seen";
      if (!localStorage.getItem(seenKey)) {
        localStorage.setItem(seenKey, "1");
        toast("Inappropriate language isn't allowed on Deal Board and has been removed.", "alert");
      }
    }
    setPostCardName(stripBlockedContent(val));
  };

  const submitPost = async () => {
    if (!postCardName.trim() || !postCondition.trim() || !postPrice) {
      toast("All fields are required", "alert"); return;
    }
    setPosting(true);
    try {
      const cap = getDealBoardPostCap(vendor);
      if (cap !== Infinity) {
        const used = await getDealBoardPostCountThisMonth(vendor.id);
        if (used >= cap) {
          if (vendor?.plan === "deal_board_plus") {
            toast(`You've hit your ${cap} post limit for this month. Full Pro gives you unlimited posts.`, "alert");
            openUpgradeModal();
          } else {
            toast(`You've hit your ${cap} post limit for this month. Deal Board Plus gives you 30 a month.`, "alert");
            openDealBoardPlusModal();
          }
          setPosting(false);
          return;
        }
      }
      await postDealBoardRequest(vendor.id, {
        cardName: postCardName.trim(), condition: postCondition, price: postPrice,
      });
      toast("Request posted", "success");
      setShowPostForm(false); setPostCardName(""); setPostCondition("Near Mint"); setPostPrice("");
      load();
    } catch(e) { toast("Could not post: " + e.message, "error"); }
    setPosting(false);
  };

  const doStatus = async (requestId, status) => {
    try {
      await updateDealBoardRequestStatus(requestId, status);
      toast(status === "fulfilled" ? "Marked fulfilled" : "Request cancelled", "success");
      load();
    } catch(e) { toast("Could not update: " + e.message, "error"); }
  };

  const doSendCompose = async () => {
    if (!composeBody.trim() || !composeFor) return;
    setSending(true);
    try {
      await sendDealBoardMessage(null, composeFor.VendorID, composeBody.trim(), composeFor.ID);
      toast(`Message sent to ${composeFor.VendorName}`, "success");
      setComposeFor(null); setComposeBody("");
    } catch(e) { toast(e.message, "error"); }
    setSending(false);
  };

  return (
    <div>
      <div style={{fontSize:13, color:T.textMuted, marginBottom:16}}>
        Post items that you're looking to acquire.
      </div>

      <div style={{display:"flex", gap:8, marginBottom:16}}>
        {[["browse","Browse Requests"],["mine","My Requests"]].map(([key,label])=>(
          <button key={key} onClick={()=>setView(key)}
            style={{flex:1, padding:"9px 0", borderRadius:T.radius.md, cursor:"pointer",
              border:`1px solid ${view===key?T.primary:T.border}`,
              background:view===key?T.blueBg:"transparent",
              color:view===key?T.primary:T.textMuted, fontSize:13, fontWeight:700}}>
            {label}
          </button>
        ))}
      </div>

      <button style={{...S.btn("primary"), width:"100%", marginBottom:16}}
        onClick={()=>setShowPostForm(true)}>
        + Post a Request
      </button>

      {loading ? (
        <div style={{textAlign:"center", padding:30, color:T.textMuted}}>Loading…</div>
      ) : view === "browse" ? (
        feed.length === 0 ? (
          <div style={{textAlign:"center", padding:40}}>
            <div style={{fontSize:32, marginBottom:8}}>🔍</div>
            <div style={{fontSize:14, color:T.textMuted}}>No requests posted yet.</div>
          </div>
        ) : (
          <>
          <div style={{...S.card, padding:0}}>
            {feed.map((r,i) => (
              <div key={r.ID} style={{padding:"12px 16px",
                borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10}}>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontWeight:700, fontSize:14}}>{r.CardName}</div>
                    <div style={{fontSize:12, color:T.textMuted}}>{r.Condition} · {fmt(r.Price)}</div>
                    <div style={{fontSize:11, color:T.textFaint, marginTop:2}}>
                      👤 {r.VendorName} {r.IsPro && <span style={{color:T.primary, fontWeight:700}}>· Pro</span>}
                      {r.State && <> · {r.State}</>}
                    </div>
                  </div>
                  {r.VendorID !== vendor?.id && (
                    <button style={{...S.btn("sm"), background:T.primary, color:"#fff", flexShrink:0}}
                      onClick={()=>setComposeFor(r)}>
                      💬 Reply
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {feed.length < feedTotalCount && (
            <div style={{textAlign:"center", marginTop:16}}>
              <button style={S.btn("ghost")} disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading…" : `Load More (${feedTotalCount - feed.length} more)`}
              </button>
            </div>
          )}
          </>
        )
      ) : (
        mine.length === 0 ? (
          <div style={{textAlign:"center", padding:40}}>
            <div style={{fontSize:14, color:T.textMuted}}>You haven't posted any requests yet.</div>
          </div>
        ) : (
          <div style={{...S.card, padding:0}}>
            {mine.map((r,i) => (
              <div key={r.ID} style={{padding:"12px 16px",
                borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:10}}>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontWeight:700, fontSize:14}}>{r.CardName}</div>
                    <div style={{fontSize:12, color:T.textMuted}}>{r.Condition} · {fmt(r.Price)} · {r.Status}</div>
                  </div>
                  {r.Status === "active" && (
                    <div style={{display:"flex", gap:6, flexShrink:0}}>
                      <button style={{...S.btn("sm"), background:T.green||"#4ade80", color:"#fff"}}
                        onClick={()=>doStatus(r.ID, "fulfilled")}>Fulfilled</button>
                      <button style={{...S.btn("sm"), background:T.surface2, color:T.textMuted}}
                        onClick={()=>doStatus(r.ID, "cancelled")}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {showPostForm && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setShowPostForm(false); }}>
          <div style={S.drawer}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12}}>
              <div style={{fontSize:16, fontWeight:800}}>Post a Request</div>
              <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setShowPostForm(false)}>✕</button>
            </div>
            <label style={S.label}>Card or Sealed Product Name</label>
            <input style={{...S.input, marginBottom:12}} value={postCardName}
              onChange={e=>handleCardNameChange(e.target.value)}
              placeholder="e.g. Charizard from Delta Species" />
            <label style={S.label}>Condition</label>
            <select style={{...S.select, marginBottom:12}} value={postCondition}
              onChange={e=>setPostCondition(e.target.value)}>
              {["Mint","Near Mint","Lightly Played","Moderately Played","Heavily Played","Damaged","Sealed"].map(c=>
                <option key={c} value={c}>{c}</option>)}
            </select>
            <label style={S.label}>Price You're Willing to Pay</label>
            <input style={{...S.input, marginBottom:16}} type="number" min="0" step="0.01" value={postPrice}
              onChange={e=>setPostPrice(e.target.value)} placeholder="0.00" />
            <button style={{...S.btn("primary"), width:"100%"}} disabled={posting} onClick={submitPost}>
              {posting ? "Posting…" : "Post Request"}
            </button>
          </div>
        </div>
      )}

      {composeFor && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setComposeFor(null); }}>
          <div style={S.drawer}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12}}>
              <div>
                <div style={{fontSize:16, fontWeight:800}}>Reply about {composeFor.CardName}</div>
                <div style={{fontSize:12, color:T.textMuted}}>to {composeFor.VendorName}</div>
              </div>
              <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setComposeFor(null)}>✕</button>
            </div>
            <textarea style={{...S.input, minHeight:100, marginBottom:12}} value={composeBody}
              onChange={e=>setComposeBody(e.target.value)}
              placeholder="Hi, I have this card available…" />
            <button style={{...S.btn("primary"), width:"100%"}} disabled={sending || !composeBody.trim()}
              onClick={doSendCompose}>
              {sending ? "Sending…" : "Send Message"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddCustomItemModal({ vendor, consignors, buyers, isPaid, onClose, onSaved, S, T, toast, activeLocationId = null }) {
  const [itemName, setItemName] = useState("");
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [itemType, setItemType] = useState("");
  const [customItemType, setCustomItemType] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [description, setDescription] = useState("");
  const [condition, setCondition] = useState("New");
  const [sku, setSku] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState([]);
  const [productType, setProductType] = useState("Handmade");
  const [imageUrls, setImageUrls] = useState([]);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [isConsignment, setIsConsignment] = useState(false);
  const [consignorId, setConsignorId] = useState("");
  const [assignBuyerChecked, setAssignBuyerChecked] = useState(false);
  const [selectedBuyerId, setSelectedBuyerId] = useState("");
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [createdItem, setCreatedItem] = useState(null);
  const [notifying, setNotifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(prev => [...prev, t]);
    setTagInput("");
  };
  const addImageUrl = () => {
    const u = imageUrlInput.trim();
    if (u && imageUrls.length < 3) setImageUrls(prev => [...prev, u]);
    setImageUrlInput("");
  };
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || imageUrls.length >= 3) return;
    setUploading(true);
    try {
      const url = await uploadCardImage(vendor.id, file);
      setImageUrls(prev => [...prev, url]);
    } catch(err) {
      toast("Could not upload image: " + err.message, "error");
    }
    setUploading(false);
  };

  const finalCategory = category === "Other / Custom" ? customCategory.trim() : category;
  const finalItemType = itemType === "Other / Custom" ? customItemType.trim() : itemType;
  const canSubmit = itemName.trim() && finalCategory && finalItemType && price && parseFloat(price) > 0
    && quantity && parseInt(quantity, 10) >= 0 && description.trim() && condition && !saving && !uploading;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const saved = await addInventoryItem({
        CardName: itemName.trim(),
        Type: "Sealed Product",
        ImageURL: imageUrls[0] || null,
        ImageURLs: imageUrls,
        CustomCategory: finalCategory,
        ItemType: finalItemType,
        ListPrice: parseFloat(price),
        Quantity: parseInt(quantity, 10),
        Description: description.trim(),
        Condition: condition,
        Tags: tags,
        ProductType: productType,
        Status: "In Stock",
        IsOnline: isOnline,
        IsConsignment: isConsignment,
        ConsignorID: isConsignment ? (consignorId || null) : null,
        Source: "Custom Item",
        LocationID: activeLocationId,
        Category: "Accessories",
      });
      if (sku.trim()) await updateInventoryItem(saved.ID, { QRCode: sku.trim() });
      toast("Item added", "success");
      if (assignBuyerChecked && selectedBuyerId) {
        setCreatedItem(saved);
        setShowEmailConfirm(true);
        setSaving(false);
        return;
      }
      onSaved();
    } catch(e) {
      toast("Could not save: " + e.message, "error");
    }
    setSaving(false);
  };

  return (
    <>
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
          <div style={S.h2}>Add Custom Item</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={onClose}>✕</button>
        </div>
        <div style={{fontSize:12, color:T.textMuted, marginBottom:16}}>
          Create handmade goods and other non-card or sealed items.
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Item Name</label>
          <input style={S.input} value={itemName} onChange={e=>setItemName(e.target.value)}
            placeholder="e.g. Charizard Enamel Pin" />
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Category</label>
            <select style={S.select} value={category} onChange={e=>setCategory(e.target.value)}>
              <option value="">Select a category</option>
              {CUSTOM_ITEM_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
            {category === "Other / Custom" && (
              <input style={{...S.input, marginTop:6}} value={customCategory}
                onChange={e=>setCustomCategory(e.target.value)} placeholder="Enter custom category" />
            )}
          </div>
          <div style={S.col}>
            <label style={S.label}>Item Type</label>
            <select style={S.select} value={itemType} onChange={e=>setItemType(e.target.value)}>
              <option value="">Select an item type</option>
              {CUSTOM_ITEM_TYPES.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            {itemType === "Other / Custom" && (
              <input style={{...S.input, marginTop:6}} value={customItemType}
                onChange={e=>setCustomItemType(e.target.value)} placeholder="Enter custom item type" />
            )}
          </div>
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Price</label>
            <input style={S.input} type="number" min="0" step="0.01" value={price}
              onChange={e=>setPrice(e.target.value)} placeholder="0.00" />
          </div>
          <div style={S.col}>
            <label style={S.label}>Quantity on Hand</label>
            <input style={S.input} type="number" min="0" step="1" value={quantity}
              onChange={e=>setQuantity(e.target.value)} />
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Description</label>
          <textarea style={{...S.input, minHeight:70, resize:"vertical"}} maxLength={2000}
            value={description} onChange={e=>setDescription(e.target.value)}
            placeholder="Describe your item in detail (materials, size, care instructions, etc.)" />
          <div style={{fontSize:11, color:T.textFaint, marginTop:2, textAlign:"right"}}>
            {description.length} / 2000
          </div>
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Condition</label>
            <select style={S.select} value={condition} onChange={e=>setCondition(e.target.value)}>
              {CUSTOM_ITEM_CONDITIONS.map(c=><option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={S.col}>
            <label style={S.label}>SKU / Item Code (optional)</label>
            <input style={S.input} value={sku} onChange={e=>setSku(e.target.value)}
              placeholder="Auto-generated if left blank" />
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Tags / Keywords</label>
          <input style={S.input} value={tagInput}
            onChange={e=>setTagInput(e.target.value)}
            onKeyDown={e=>{ if (e.key==="Enter" || e.key===",") { e.preventDefault(); addTag(); } }}
            onBlur={addTag}
            placeholder="Add tags and press Enter" />
          <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
            Use commas or Enter to add multiple tags
          </div>
          {tags.length > 0 && (
            <div style={{display:"flex", flexWrap:"wrap", gap:6, marginTop:8}}>
              {tags.map(t => (
                <span key={t} style={{fontSize:12, padding:"3px 9px", borderRadius:12,
                  background:T.surface2, color:T.textMuted, display:"flex", alignItems:"center", gap:5}}>
                  {t}
                  <span style={{cursor:"pointer", fontWeight:700}}
                    onClick={()=>setTags(prev=>prev.filter(x=>x!==t))}>✕</span>
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Product Type</label>
          <div style={{display:"flex", gap:8}}>
            {["Handmade","Resale"].map(pt => (
              <button key={pt} type="button" onClick={()=>setProductType(pt)}
                style={{...S.btn(productType===pt ? "primary" : "ghost"), flex:1, padding:"10px"}}>
                {pt}
                <div style={{fontSize:10, fontWeight:400, opacity:0.8}}>
                  {pt==="Handmade" ? "Made by you" : "Purchased to resell"}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div style={{marginBottom:18}}>
          <label style={S.label}>Images (up to 3)</label>
          <div style={{display:"flex", gap:8, marginBottom:8}}>
            <input style={{...S.input, flex:1}} value={imageUrlInput}
              onChange={e=>setImageUrlInput(e.target.value)}
              onKeyDown={e=>{ if (e.key==="Enter") { e.preventDefault(); addImageUrl(); } }}
              placeholder="Paste an image URL" disabled={imageUrls.length>=3} />
            <button type="button" style={S.btn("ghost")} onClick={addImageUrl}
              disabled={imageUrls.length>=3 || !imageUrlInput.trim()}>Add</button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}}
            onChange={handleFileChange} />
          <button type="button" style={{...S.btn("ghost"), width:"100%"}}
            disabled={uploading || imageUrls.length>=3} onClick={()=>fileInputRef.current?.click()}>
            {uploading ? "Uploading…" : `Upload a photo (${imageUrls.length}/3)`}
          </button>
          {imageUrls.length > 0 && (
            <div style={{display:"flex", gap:8, marginTop:8}}>
              {imageUrls.map((url,i) => (
                <div key={i} style={{position:"relative"}}>
                  <img src={url} alt="" style={{width:60, height:60, objectFit:"cover",
                    background:T.surface2, borderRadius:6}} />
                  <span style={{position:"absolute", top:-6, right:-6, cursor:"pointer",
                    background:T.surface, borderRadius:"50%", width:18, height:18,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:11, border:`1px solid ${T.border}`}}
                    onClick={()=>setImageUrls(prev=>prev.filter((_,idx)=>idx!==i))}>✕</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
          marginBottom:18, padding:"10px 12px", background:T.surface2, borderRadius:8}}>
          <div>
            <div style={{fontSize:13, fontWeight:600}}>List Online</div>
            <div style={{fontSize:11, color:T.textFaint}}>Show this item in your public storefront.</div>
          </div>
          <button type="button" onClick={()=>setIsOnline(v=>!v)}
            style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
              background: isOnline ? "#4ade80" : T.surface, position:"relative",
              border:`0.5px solid ${isOnline ? "#4ade80" : T.border}`}}>
            <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
              position:"absolute", top:2, left: isOnline ? 21 : 3, transition:"left 0.15s"}} />
          </button>
        </div>

        {isPaid && (
        <div style={{marginBottom:18, padding:12, borderRadius:10,
          border:`0.5px solid ${isConsignment?T.chipBorder:T.border}`,
          background:isConsignment?T.blueBg:"transparent"}}>
          <div style={{display:"flex", justifyContent:"space-between", alignItems:"center",
            marginBottom:isConsignment?12:0}}>
            <span style={S.label}>Consignment Item</span>
            <button type="button" onClick={()=>setIsConsignment(v=>!v)}
              style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
                background: isConsignment ? "#4ade80" : T.surface, position:"relative",
                border:`0.5px solid ${isConsignment ? "#4ade80" : T.border}`}}>
              <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
                position:"absolute", top:2, left: isConsignment ? 21 : 3, transition:"left 0.15s"}} />
            </button>
          </div>

          {isConsignment && (<>
            {(() => {
              const minVal = parseFloat(vendor?.consign_settings?.min_card_value) || 0;
              const listP  = parseFloat(price) || 0;
              if (minVal > 0 && listP < minVal && listP > 0) {
                return (
                  <div style={{fontSize:11, color:T.red, marginBottom:10,
                    padding:"6px 8px", background:T.redBg,
                    borderRadius:6, border:`0.5px solid ${T.red}`}}>
                    List price is below your minimum consignment value of ${minVal}.
                  </div>
                );
              }
              return null;
            })()}
            <div>
              <label style={S.label}>Consignor *</label>
              <select style={S.select} value={consignorId} onChange={e=>setConsignorId(e.target.value)}>
                <option value="">Select consignor…</option>
                {(consignors||[]).map(c=>(
                  <option key={c.ID} value={c.ID}>{c.FullName}</option>
                ))}
              </select>
              {!consignors?.length && (
                <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
                  No consignors yet — add one from the CRM tab.
                </div>
              )}
            </div>
          </>)}
        </div>
        )}

        {isPaid && (buyers||[]).length > 0 && (
        <div style={{marginBottom:18}}>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
            <span style={S.label}>Buyers Request</span>
            <button type="button" onClick={()=>setAssignBuyerChecked(v=>!v)}
              style={{width:42, height:24, borderRadius:12, cursor:"pointer", flexShrink:0,
                background: assignBuyerChecked ? "#4ade80" : T.surface, position:"relative",
                border:`0.5px solid ${assignBuyerChecked ? "#4ade80" : T.border}`}}>
              <div style={{width:18, height:18, borderRadius:"50%", background:"#fff",
                position:"absolute", top:2, left: assignBuyerChecked ? 21 : 3, transition:"left 0.15s"}} />
            </button>
          </div>
          {assignBuyerChecked && (
            <select style={S.select} value={selectedBuyerId} onChange={e=>setSelectedBuyerId(e.target.value)}>
              <option value="">Select a buyer...</option>
              {buyers.map(b=>(
                <option key={b.ID} value={b.ID}>
                  {b.FullName}{(b.Phone||b.Email) ? ` (${[b.Phone,b.Email].filter(Boolean).join(", ")})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
        )}

        <div style={{display:"flex", gap:10}}>
          <button style={{...S.btn("ghost"), flex:1}} onClick={onClose}>Cancel</button>
          <button style={{...S.btn("primary"), flex:2}} disabled={!canSubmit} onClick={submit}>
            {saving ? "Saving…" : "Save Item"}
          </button>
        </div>
      </div>
    </div>

    {showEmailConfirm && (() => {
      const buyer = (buyers||[]).find(b => b.ID === selectedBuyerId);
      if (!buyer) return null;
      const finishAssignment = async (sendEmail) => {
        setNotifying(true);
        try {
          if (sendEmail) {
            await notifyBuyerCardFound({
              buyerEmail: buyer.Email,
              buyerName: buyer.FullName,
              cardName: createdItem?.CardName || "your requested item",
              businessName: vendor?.business_name || "",
            });
          }
          const notifiedAt = sendEmail ? new Date().toISOString() : "";
          await updateInventoryItem(createdItem.ID, { AssignedBuyerID: buyer.ID, BuyerNotifiedAt: notifiedAt });
          toast(sendEmail ? `Email sent to ${buyer.FullName}` : `Assigned to ${buyer.FullName}`, "success");
          setShowEmailConfirm(false);
          onSaved();
        } catch(e) {
          toast("Failed: " + e.message, "error");
        }
        setNotifying(false);
      };
      return (
        <div style={{...S.overlay, zIndex:10003}}>
          <div style={S.drawer}>
            <div style={S.h2}>Notify {buyer.FullName}?</div>
            <div style={{fontSize:13, color:T.textMuted, marginBottom:20, lineHeight:1.6}}>
              Send an email letting {buyer.FullName} know you've acquired their requested item
              {createdItem?.CardName ? ` (${createdItem.CardName})` : ""}, and to contact you at{" "}
              <strong>{vendor?.email || "your email"}</strong> for pickup instructions?
            </div>
            {!buyer.Email && (
              <div style={{...S.infoBox, marginBottom:16, borderColor:T.red}}>
                <div style={{fontSize:12, color:T.red}}>
                  This buyer has no email on file. An email can't be sent, but you can still assign this item to them.
                </div>
              </div>
            )}
            <button style={{...S.btn("primary"), width:"100%", padding:14, marginBottom:10}}
              disabled={notifying || !buyer.Email}
              onClick={()=>finishAssignment(true)}>
              {notifying ? "Sending…" : "Yes, Send Email"}
            </button>
            <button style={{...S.btn("ghost"), width:"100%", padding:12}}
              disabled={notifying}
              onClick={()=>finishAssignment(false)}>
              Do Not Send Email
            </button>
          </div>
        </div>
      );
    })()}
    </>
  );
}

function PostItemModal({ vendor, onClose, onPosted, S, T, toast, activeLocationId = null, onItemCreated }) {
  const [cardName, setCardName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageMode, setImageMode] = useState("url");
  const [uploading, setUploading] = useState(false);
  const [condition, setCondition] = useState("Near Mint");
  const [price, setPrice] = useState("");
  const [gameType, setGameType] = useState("Pokemon");
  const [state, setState] = useState(vendor?.state || "");
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadCardImage(vendor.id, file);
      setImageUrl(url);
    } catch(err) {
      toast("Could not upload image: " + err.message, "error");
    }
    setUploading(false);
  };

  const canSubmit = cardName.trim() && condition && price && parseFloat(price) > 0 && !posting && !uploading;

  const submit = async () => {
    if (!canSubmit) return;
    setPosting(true);
    try {
      const cap = getDealBoardPostCap(vendor);
      if (cap !== Infinity) {
        const used = await getDealBoardPostCountThisMonth(vendor.id);
        if (used >= cap) {
          toast(`You've hit your ${cap} post limit for this month.`, "alert");
          setPosting(false);
          return;
        }
      }
      if (DEAL_BOARD_INVENTORY_BACKED_GAMES.includes(gameType)) {
        const newItem = await addInventoryItem({
          CardName: cardName.trim(), ImageURL: imageUrl || null, Condition: condition,
          GameType: gameType, ListPrice: parseFloat(price), Quantity: 1,
          Status: "In Stock", Source: "Deal Board Post",
          LocationID: activeLocationId, Category: deriveCategory({ CardName: cardName.trim() }),
        });
        onItemCreated?.(newItem);
        await listOnDealBoard({ ...newItem, GameType: gameType, ListPrice: price }, vendor.id);
      } else {
        await postStandaloneDealBoardListing(vendor.id, {
          cardName: cardName.trim(), imageUrl, condition, price, gameType, state,
        });
      }
      toast("Posted to Deal Board", "success");
      onPosted();
    } catch(e) {
      toast("Could not post: " + e.message, "error");
    }
    setPosting(false);
  };

  return (
    <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) onClose(); }}>
      <div style={S.drawer}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <div style={S.h2}>Post an Item</div>
          <button style={{...S.btn("ghost"),padding:"4px 10px"}} onClick={onClose}>✕</button>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Item Name</label>
          <input style={S.input} value={cardName} onChange={e=>setCardName(e.target.value)}
            placeholder="e.g. Charizard VMAX" />
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Game</label>
          <select style={S.select} value={gameType} onChange={e=>setGameType(e.target.value)}>
            {DEAL_BOARD_GAME_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div style={{marginBottom:14}}>
          <label style={S.label}>Image</label>
          <div style={{display:"flex", gap:8, marginBottom:8}}>
            <button type="button" onClick={()=>setImageMode("url")}
              style={{...S.btn(imageMode==="url" ? "primary" : "ghost"), flex:1, padding:"7px"}}>
              Paste URL
            </button>
            <button type="button" onClick={()=>setImageMode("upload")}
              style={{...S.btn(imageMode==="upload" ? "primary" : "ghost"), flex:1, padding:"7px"}}>
              Upload
            </button>
          </div>
          {imageMode === "url" ? (
            <input style={S.input} value={imageUrl} onChange={e=>setImageUrl(e.target.value)}
              placeholder="https://…" />
          ) : (
            <>
              <input ref={fileInputRef} type="file" accept="image/*" style={{display:"none"}}
                onChange={handleFileChange} />
              <button type="button" style={{...S.btn("ghost"), width:"100%"}}
                disabled={uploading} onClick={()=>fileInputRef.current?.click()}>
                {uploading ? "Uploading…" : imageUrl ? "✓ Image uploaded — tap to replace" : "Choose a photo"}
              </button>
            </>
          )}
          <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
            Max 5MB. Copy an image URL, or upload a photo directly.
          </div>
          {imageUrl && (
            <img src={imageUrl} alt="" style={{width:60, height:84, objectFit:"contain",
              background:T.surface2, borderRadius:6, marginTop:8}} />
          )}
        </div>

        <div style={S.row}>
          <div style={S.col}>
            <label style={S.label}>Condition</label>
            <select style={S.select} value={condition} onChange={e=>setCondition(e.target.value)}>
              {CONDITIONS.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div style={S.col}>
            <label style={S.label}>Price</label>
            <input style={S.input} type="number" min="0" step="0.01" value={price}
              onChange={e=>setPrice(e.target.value)} placeholder="0.00" />
          </div>
        </div>

        <div style={{marginBottom:18}}>
          <label style={S.label}>Location (State)</label>
          <select style={S.select} value={state} onChange={e=>setState(e.target.value)}>
            <option value="">—</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <div style={{fontSize:11, color:T.textFaint, marginTop:4}}>
            Defaults to your profile, override it if this item's actually somewhere else.
          </div>
        </div>

        <div style={{display:"flex", gap:10}}>
          <button style={{...S.btn("ghost"), flex:1}} onClick={onClose}>Cancel</button>
          <button style={{...S.btn("primary"), flex:2}} disabled={!canSubmit} onClick={submit}>
            {posting ? "Posting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DealBoardGuidelinesModal({ onAgree, agreeing, S, T }) {
  return (
    <div style={{position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.75)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:20}}>
      <div style={{...S.card, maxWidth:440, width:"100%", maxHeight:"85vh", overflowY:"auto"}}>
        <div style={{fontSize:17, fontWeight:800, marginBottom:4}}>Deal Board Community Guidelines</div>
        <div style={{fontSize:13, color:T.textMuted, lineHeight:1.7, marginBottom:16}}>
          Deal Board works because vendors treat each other fairly. A few ground rules before you dive in:
        </div>

        {[
          ["Be honest about what you're posting.", "Accurately describe your cards' condition, and be upfront about what you're actually looking for or offering. Deal Board only works if people can trust what they're seeing."],
          ["No inappropriate language, of any kind.", "Profanity, sexual content, and abusive or discriminatory language are never allowed, in listings, in Wanted Items, or in messages to other vendors. This applies everywhere on Deal Board, no exceptions."],
          ["Treat other vendors the way you'd want to be treated.", "Disagreements happen. Handle them like professionals. Harassment, threats, or repeated bad-faith behavior toward another vendor won't be tolerated."],
          ["Violations have real consequences.", "Depending on what happened, your Deal Board access may be temporarily suspended or permanently removed, at our discretion. Repeated or serious violations can result in your entire VendorStax account being affected, not just Deal Board."],
        ].map(([title, body], i) => (
          <div key={i} style={{marginBottom:14}}>
            <div style={{fontSize:14, fontWeight:700, color:T.text, marginBottom:3}}>{title}</div>
            <div style={{fontSize:13, color:T.textMuted, lineHeight:1.6}}>{body}</div>
          </div>
        ))}

        <div style={{fontSize:12, color:T.textFaint, marginTop:8, marginBottom:18, lineHeight:1.6}}>
          By continuing, you agree to follow these guidelines and our full Terms & Conditions.
        </div>

        <button style={{...S.btn("primary"), width:"100%", padding:"14px"}}
          disabled={agreeing} onClick={onAgree}>
          {agreeing ? "…" : "I Agree and Want to Continue"}
        </button>
      </div>
    </div>
  );
}

function DealBoardPage({ vendor, isPaid, S, T, activeLocationId = null, onItemCreated }) {
  const { toast, confirmAction } = useNotify();

  if (!DEAL_BOARD_LIVE) {
    return (
      <div style={S.page}>
        <div style={{textAlign:"center", padding:"60px 24px"}}>
          <div style={{fontSize:48, marginBottom:16}}>🤝</div>
          <div style={{fontSize:20, fontWeight:800, color:T.text, marginBottom:8}}>Deal Board — Coming Soon</div>
          <div style={{fontSize:14, color:T.textMuted, maxWidth:420, margin:"0 auto", lineHeight:1.6}}>
            We're finishing up phone verification with our SMS provider before opening
            this up. Check back soon.
          </div>
        </div>
      </div>
    );
  }

  const [section, setSection] = useState("buysell");
  const [agreeingToGuidelines, setAgreeingToGuidelines] = useState(false);
  const [showPostItem, setShowPostItem] = useState(false);
  const [guidelinesAgreedThisSession, setGuidelinesAgreedThisSession] = useState(false);
  const [view, setView] = useState("browse");
  const [feed, setFeed] = useState([]);
  const [feedTotalCount, setFeedTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 24;
  const CAPPED_VIEW_LIMIT = 20;
  const [favoritesFeed, setFavoritesFeed] = useState([]);
  const [followingFeed, setFollowingFeed] = useState([]);
  const [capsuleLoading, setCapsuleLoading] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  const [priceMax, setPriceMax] = useState("");
  const [unlistingId, setUnlistingId] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set());
  const [followedIds, setFollowedIds] = useState(new Set());
  const [favoritingId, setFavoritingId] = useState(null);
  const [followingId, setFollowingId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connections, setConnections] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [messageDraft, setMessageDraft] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [composeFor, setComposeFor] = useState(null);
  const [composeBody, setComposeBody] = useState("");

  const dealBoardEligible = !!vendor?.id;

  const load = () => {
    setLoading(true);
    Promise.all([
      getDealBoardFeed({ limit: PAGE_SIZE, offset: 0, search, gameType: gameFilter, priceMax })
        .catch(() => ({ rows: [], totalCount: 0 })),
      vendor?.id ? getMyDealBoardListings(vendor.id).catch(() => []) : Promise.resolve([]),
      vendor?.id ? getMyFavoriteIds(vendor.id).catch(() => []) : Promise.resolve([]),
      vendor?.id ? getMyFollowedVendorIds(vendor.id).catch(() => []) : Promise.resolve([]),
      vendor?.id ? getDealBoardConnections(vendor.id).catch(() => []) : Promise.resolve([]),
      vendor?.id ? getDealBoardUnreadCount(vendor.id).catch(() => 0) : Promise.resolve(0),
    ]).then(([f, mine, favs, follows, conns, unread]) => {
      setFeed(f.rows); setFeedTotalCount(f.totalCount);
      setMyListings(mine);
      setFavoriteIds(new Set(favs)); setFollowedIds(new Set(follows));
      setConnections(conns); setUnreadCount(unread);
      setLoading(false);
    });
  };
  useEffect(load, [vendor?.id]);

  useEffect(() => {
    let debounceTimer = null;
    const sub = subscribeToDealBoardListings(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(load, 500);
    });
    return () => { clearTimeout(debounceTimer); sub?.unsubscribe?.(); };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      getDealBoardFeed({ limit: PAGE_SIZE, offset: 0, search, gameType: gameFilter, priceMax })
        .then(f => { setFeed(f.rows); setFeedTotalCount(f.totalCount); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search, gameFilter, priceMax]);

  const loadMoreFeed = () => {
    setLoadingMore(true);
    getDealBoardFeed({ limit: PAGE_SIZE, offset: feed.length, search, gameType: gameFilter, priceMax })
      .then(f => { setFeed(prev => [...prev, ...f.rows]); setFeedTotalCount(f.totalCount); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    if (view === "favorites") {
      if (favoriteIds.size === 0) { setFavoritesFeed([]); return; }
      setCapsuleLoading(true);
      getDealBoardFeed({ limit: CAPPED_VIEW_LIMIT, offset: 0, listingIds: [...favoriteIds] })
        .then(f => setFavoritesFeed(f.rows)).catch(() => setFavoritesFeed([])).finally(() => setCapsuleLoading(false));
    } else if (view === "following") {
      if (followedIds.size === 0) { setFollowingFeed([]); return; }
      setCapsuleLoading(true);
      getDealBoardFeed({ limit: CAPPED_VIEW_LIMIT, offset: 0, vendorIds: [...followedIds] })
        .then(f => setFollowingFeed(f.rows)).catch(() => setFollowingFeed([])).finally(() => setCapsuleLoading(false));
    }
  }, [view, favoriteIds.size, followedIds.size]);

  const openThread = async (otherVendorId, otherVendorName) => {
    setActiveThread({ OtherVendorID: otherVendorId, OtherVendorName: otherVendorName });
    setThreadLoading(true);
    try {
      const msgs = await getDealBoardThread(vendor.id, otherVendorId);
      setThreadMessages(msgs);
      await markDealBoardThreadRead(vendor.id, otherVendorId);
      load();
    } catch(e) { toast("Could not load messages: " + e.message, "error"); }
    setThreadLoading(false);
  };

  const doSendThreadMessage = async () => {
    if (!messageDraft.trim() || !activeThread) return;
    setSendingMessage(true);
    try {
      await sendDealBoardMessage(null, activeThread.OtherVendorID, messageDraft.trim());
      setMessageDraft("");
      const msgs = await getDealBoardThread(vendor.id, activeThread.OtherVendorID);
      setThreadMessages(msgs);
    } catch(e) { toast(e.message, "error"); }
    setSendingMessage(false);
  };

  const doRespond = async (otherVendorId, accept) => {
    try {
      await respondToDealBoardConnection(vendor.id, otherVendorId, accept);
      toast(accept ? "Request accepted" : "Request declined", "success");
      load();
    } catch(e) { toast("Could not respond: " + e.message, "error"); }
  };

  const doBlock = async (otherVendorId, otherVendorName) => {
    try {
      await blockDealBoardVendor(vendor.id, otherVendorId);
      toast(`Blocked ${otherVendorName}`, "success");
      setActiveThread(null);
      load();
    } catch(e) { toast("Could not block: " + e.message, "error"); }
  };

  const doDeleteMessage = async (messageId) => {
    if (!await confirmAction("Delete this message? This can't be undone.")) return;
    try {
      await deleteDealBoardMessage(messageId);
      setThreadMessages(msgs => msgs.filter(m => m.ID !== messageId));
      toast("Message deleted", "success");
    } catch(e) { toast("Could not delete: " + e.message, "error"); }
  };

  const doAdminRemoveListing = async (listingId, cardName) => {
    if (!await confirmAction(`Remove "${cardName}" from Deal Board? This can't be undone.`)) return;
    try {
      await adminRemoveDealBoardListing(listingId);
      setFeed(f => f.filter(l => l.ID !== listingId));
      toast("Listing removed", "success");
    } catch(e) { toast("Could not remove: " + e.message, "error"); }
  };

  const doSendComposeMessage = async () => {
    if (!composeBody.trim() || !composeFor) return;
    setSendingMessage(true);
    try {
      await sendDealBoardMessage(composeFor.ID, composeFor.VendorID, composeBody.trim());
      toast(`Message sent to ${composeFor.VendorName}`, "success");
      setComposeFor(null); setComposeBody("");
      load();
    } catch(e) { toast(e.message, "error"); }
    setSendingMessage(false);
  };


  const doToggleFavorite = async (listingId) => {
    const already = favoriteIds.has(listingId);
    setFavoritingId(listingId);
    try {
      await toggleFavorite(vendor.id, listingId, already);
      setFavoriteIds(prev => {
        const next = new Set(prev);
        already ? next.delete(listingId) : next.add(listingId);
        return next;
      });
    } catch(e) { toast("Could not update favorite: " + e.message, "error"); }
    setFavoritingId(null);
  };

  const doToggleFollow = async (followedVendorId) => {
    const already = followedIds.has(followedVendorId);
    setFollowingId(followedVendorId);
    try {
      await toggleFollow(vendor.id, followedVendorId, already);
      setFollowedIds(prev => {
        const next = new Set(prev);
        already ? next.delete(followedVendorId) : next.add(followedVendorId);
        return next;
      });
    } catch(e) { toast("Could not update follow: " + e.message, "error"); }
    setFollowingId(null);
  };

  const filtered = view === "favorites" ? favoritesFeed
    : view === "following" ? followingFeed
    : feed;

  if (vendor?.id && !vendor.deal_board_guidelines_accepted && !guidelinesAgreedThisSession) {
    return (
      <DealBoardGuidelinesModal S={S} T={T} agreeing={agreeingToGuidelines}
        onAgree={async () => {
          setAgreeingToGuidelines(true);
          try {
            await acceptDealBoardGuidelines(vendor.id);
            setGuidelinesAgreedThisSession(true);
          } catch(e) {
            toast("Could not save, please try again: " + e.message, "error");
          }
          setAgreeingToGuidelines(false);
        }} />
    );
  }

  return (
    <div style={S.page}>
      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12}}>
        <div>
          <div style={{fontSize:22, fontWeight:800, color:T.text, letterSpacing:"-0.02em"}}>🤝 Deal Board</div>
          <div style={{fontSize:13, color:T.textMuted, marginBottom:16}}>
            Buy, sell, and trade directly with other vendors. DealBoard makes the connection.
          </div>
        </div>
        {dealBoardEligible && (
          <button style={{...S.btn("primary"), flexShrink:0, whiteSpace:"nowrap"}}
            onClick={()=>setShowPostItem(true)}>
            + Post an Item
          </button>
        )}
      </div>

      {showPostItem && (
        <PostItemModal vendor={vendor} S={S} T={T} toast={toast} activeLocationId={activeLocationId}
          onItemCreated={onItemCreated}
          onClose={()=>setShowPostItem(false)}
          onPosted={()=>{ setShowPostItem(false); load(); }} />
      )}

      {!dealBoardEligible && section === "buysell" && (
        <div style={{...S.infoBox, marginBottom:16, borderColor:T.primary}}>
          <div style={{fontSize:13, color:T.text}}>
            Anyone logged in can browse the Deal Board. To message a vendor, favorite
            a card, follow a vendor, or list your own cards, you need Pro. Upgrade in
            <strong> Settings</strong>.
          </div>
        </div>
      )}

      <div style={{display:"flex", gap:8, marginBottom:16}}>
        {[["buysell","Buy/Sale"],["wanted","Wanted Items"]].map(([key,label])=>(
          <button key={key} onClick={()=>setSection(key)}
            style={{flex:1, padding:"12px 0", borderRadius:T.radius.md, cursor:"pointer",
              border:`1px solid ${section===key?T.primary:T.border}`,
              background:section===key?T.blueBg:"transparent",
              color:section===key?T.primary:T.textMuted, fontSize:15, fontWeight:800}}>
            {label}
          </button>
        ))}
      </div>

      {section === "wanted" ? (
        <WantedItemsSection vendor={vendor} isPaid={isPaid} S={S} T={T} />
      ) : (
      <>
      <div style={{display:"flex", gap:8, marginBottom:16}}>
        {[["browse","Browse"],["favorites","Favorites"],["following","Following"],["mine","My Listings"],["messages","Messages"]].map(([key,label])=>(
          <button key={key} onClick={()=>setView(key)}
            style={{flex:1, padding:"9px 0", borderRadius:T.radius.md, cursor:"pointer",
              border:`1px solid ${view===key?T.primary:T.border}`,
              background:view===key?T.blueBg:"transparent",
              color:view===key?T.primary:T.textMuted, fontSize:13, fontWeight:700}}>
            {label}
            {key==="messages" && unreadCount > 0 && (
              <span style={{marginLeft:6, background:T.red, color:"#fff",
                borderRadius:T.radius.pill, padding:"1px 6px", fontSize:10, fontWeight:700}}>
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {(view === "browse" || view === "favorites" || view === "following") && (<>
        <input style={{...S.input, marginBottom:12}} placeholder="Search by card name…"
          value={search} onChange={e=>setSearch(e.target.value)} />
        <div style={{display:"flex", gap:8, marginBottom:16}}>
          <select style={{...S.select, flex:1}} value={gameFilter} onChange={e=>setGameFilter(e.target.value)}>
            <option value="all">All Games</option>
            {DEAL_BOARD_GAME_TYPES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <input style={{...S.input, flex:1}} type="number" placeholder="Max price"
            value={priceMax} onChange={e=>setPriceMax(e.target.value)} />
        </div>

        {(view === "favorites" || view === "following" ? capsuleLoading : loading) ? (
          <div style={{textAlign:"center", padding:30, color:T.textMuted}}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{textAlign:"center", padding:40}}>
            <div style={{fontSize:32, marginBottom:8}}>🤝</div>
            <div style={{fontSize:14, color:T.textMuted}}>
              {view === "favorites" ? "No favorites yet — tap the heart on a card to save it here."
                : view === "following" ? "No listings from vendors you follow yet."
                : "No listings match right now."}
            </div>
          </div>
        ) : (
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(220px, 1fr))", gap:12}}>
            {filtered.map(l => {
              const isFav = favoriteIds.has(l.ID);
              const isFollowing = followedIds.has(l.VendorID);
              const isOwnListing = l.VendorID === vendor?.id;
              return (
              <div key={l.ID} style={{...S.card, padding:0, overflow:"hidden", position:"relative"}}>
                {vendor?.role === "admin" && (
                  <button onClick={()=>doAdminRemoveListing(l.ID, l.CardName)}
                    title="Remove listing (admin)"
                    style={{position:"absolute", top:8, left:8, zIndex:2,
                      width:32, height:32, borderRadius:"50%", border:"none", cursor:"pointer",
                      background:"rgba(0,0,0,0.5)", color:"#f87171", fontSize:14}}>
                    🗑️
                  </button>
                )}
                {dealBoardEligible && (
                  <button onClick={()=>doToggleFavorite(l.ID)} disabled={favoritingId===l.ID}
                    title={isFav ? "Remove favorite" : "Favorite this card"}
                    style={{position:"absolute", top:8, right:8, zIndex:2,
                      width:32, height:32, borderRadius:"50%", border:"none", cursor:"pointer",
                      background:"rgba(0,0,0,0.5)", color: isFav ? "#f87171" : "#fff", fontSize:16}}>
                    {isFav ? "♥" : "♡"}
                  </button>
                )}
                {l.ImageURL ? (
                  <img src={l.ImageURL} alt={l.CardName} style={{width:"100%", height:150, objectFit:"contain", background:T.surface2}} />
                ) : (
                  <div style={{width:"100%", height:150, background:T.surface2,
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:28}}>🃏</div>
                )}
                <div style={{padding:12}}>
                  <div style={{fontWeight:700, fontSize:14, marginBottom:2}}>{l.CardName}</div>
                  <div style={{fontSize:12, color:T.textMuted, marginBottom:8}}>{l.Condition || "—"}</div>
                  <div style={{fontSize:17, fontWeight:800, color:T.primary, marginBottom:8}}>{fmt(l.AskingPrice)}</div>
                  {l.VendorName && (
                    <>
                    <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", gap:6, marginBottom:8}}>
                      <div style={{fontSize:12, color:T.textMuted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                        👤 {l.VendorName} {l.IsPro && <span style={{color:T.primary, fontWeight:700}}>· Pro</span>}
                        {l.State && <> · {l.State}</>}
                      </div>
                      {!isOwnListing && dealBoardEligible && (
                        <button onClick={()=>doToggleFollow(l.VendorID)} disabled={followingId===l.VendorID}
                          style={{fontSize:11, fontWeight:700, flexShrink:0, padding:"3px 8px", borderRadius:20,
                            border:`1px solid ${isFollowing?T.border:T.primary}`,
                            background: isFollowing ? "transparent" : T.blueBg,
                            color: isFollowing ? T.textMuted : T.primary, cursor:"pointer"}}>
                          {isFollowing ? "Following" : "+ Follow"}
                        </button>
                      )}
                    </div>
                    {!isOwnListing && dealBoardEligible && (
                      <button onClick={()=>setComposeFor(l)}
                        style={{...S.btn("sm"), width:"100%", background:T.primary, color:"#fff"}}>
                        💬 Message
                      </button>
                    )}
                    </>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
        {view === "browse" && filtered.length < feedTotalCount && (
          <div style={{textAlign:"center", marginTop:16}}>
            <button style={S.btn("ghost")} disabled={loadingMore} onClick={loadMoreFeed}>
              {loadingMore ? "Loading…" : `Load More (${feedTotalCount - filtered.length} more)`}
            </button>
          </div>
        )}
      </>)}

      {view === "mine" && (
        loading ? (
          <div style={{textAlign:"center", padding:30, color:T.textMuted}}>Loading…</div>
        ) : myListings.length === 0 ? (
          <div style={{textAlign:"center", padding:40}}>
            <div style={{fontSize:32, marginBottom:8}}>🤝</div>
            <div style={{fontSize:14, color:T.textMuted}}>
              Nothing listed yet. Open a card in Inventory and toggle "List on Deal Board."
            </div>
          </div>
        ) : (
          <div style={{...S.card, padding:0}}>
            {myListings.map((l,i) => (
              <div key={l.ID} style={{display:"flex", alignItems:"center", gap:12, padding:"12px 16px",
                borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                {l.ImageURL && <img src={l.ImageURL} alt={l.CardName} style={{width:40, height:56, objectFit:"cover", borderRadius:6}} />}
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:600, fontSize:14}}>{l.CardName}</div>
                  <div style={{fontSize:12, color:T.textMuted}}>{l.Condition} · {fmt(l.AskingPrice)}</div>
                </div>
                <button style={{...S.btn("sm"), background:T.red, color:"#fff"}}
                  disabled={unlistingId===l.ID}
                  onClick={async()=>{
                    setUnlistingId(l.ID);
                    try {
                      await unlistDealBoardById(l.ID, vendor.id);
                      toast("Removed from Deal Board", "success");
                      load();
                    } catch(e) { toast("Could not remove: " + e.message, "error"); }
                    setUnlistingId(null);
                  }}>
                  {unlistingId===l.ID ? "…" : "Unlist"}
                </button>
              </div>
            ))}
          </div>
        )
      )}

      {view === "messages" && (
        loading ? (
          <div style={{textAlign:"center", padding:30, color:T.textMuted}}>Loading…</div>
        ) : connections.length === 0 ? (
          <div style={{textAlign:"center", padding:40}}>
            <div style={{fontSize:32, marginBottom:8}}>💬</div>
            <div style={{fontSize:14, color:T.textMuted}}>No conversations yet.</div>
          </div>
        ) : (
          <div style={{...S.card, padding:0}}>
            {connections.map((c,i) => (
              <div key={c.ID} style={{padding:"12px 16px",
                borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", gap:10}}>
                  <div style={{flex:1, minWidth:0, cursor: c.Status==="accepted" ? "pointer" : "default"}}
                    onClick={()=> c.Status==="accepted" && openThread(c.OtherVendorID, c.OtherVendorName)}>
                    <div style={{fontWeight:700, fontSize:14}}>{c.OtherVendorName}</div>
                    <div style={{fontSize:12, color:T.textMuted, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {c.Status==="pending" && c.IsIncoming ? "Wants to connect with you"
                        : c.Status==="pending" ? "Waiting for them to accept"
                        : c.LastMessage || "No messages yet"}
                    </div>
                  </div>
                  {c.Status==="pending" && c.IsIncoming && (
                    <div style={{display:"flex", gap:6, flexShrink:0}}>
                      <button style={{...S.btn("sm"), background:T.green||"#4ade80", color:"#fff"}}
                        onClick={()=>doRespond(c.OtherVendorID, true)}>Accept</button>
                      <button style={{...S.btn("sm"), background:T.surface2, color:T.textMuted}}
                        onClick={()=>doRespond(c.OtherVendorID, false)}>Decline</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {composeFor && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setComposeFor(null); }}>
          <div style={S.drawer}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12}}>
              <div>
                <div style={{fontSize:16, fontWeight:800}}>Message about {composeFor.CardName}</div>
                <div style={{fontSize:12, color:T.textMuted}}>to {composeFor.VendorName}</div>
              </div>
              <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setComposeFor(null)}>✕</button>
            </div>
            <textarea style={{...S.input, minHeight:100, marginBottom:12}} value={composeBody}
              onChange={e=>setComposeBody(e.target.value)}
              placeholder="Hi, is this still available?" />
            <button style={{...S.btn("primary"), width:"100%"}} disabled={sendingMessage || !composeBody.trim()}
              onClick={doSendComposeMessage}>
              {sendingMessage ? "Sending…" : "Send Message"}
            </button>
          </div>
        </div>
      )}

      {activeThread && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) setActiveThread(null); }}>
          <div style={S.drawer}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12}}>
              <div style={{fontSize:16, fontWeight:800}}>{activeThread.OtherVendorName}</div>
              <div style={{display:"flex", gap:8}}>
                <button style={{...S.btn("ghost"), fontSize:12, color:T.red}}
                  onClick={()=>doBlock(activeThread.OtherVendorID, activeThread.OtherVendorName)}>
                  Block
                </button>
                <button style={{...S.btn("ghost"), padding:"4px 10px"}} onClick={()=>setActiveThread(null)}>✕</button>
              </div>
            </div>

            <div style={{maxHeight:320, overflowY:"auto", marginBottom:12}}>
              {threadLoading ? (
                <div style={{textAlign:"center", padding:20, color:T.textMuted}}>Loading…</div>
              ) : threadMessages.map(m => (
                <div key={m.ID} style={{
                  display:"flex", flexDirection:"column",
                  alignItems: m.SenderID === vendor.id ? "flex-end" : "flex-start", marginBottom:10}}>
                  {m.CardName && (
                    <div style={{fontSize:10, color:T.textFaint, marginBottom:2}}>Re: {m.CardName}</div>
                  )}
                  <div style={{display:"flex", alignItems:"center", gap:6,
                    flexDirection: m.SenderID === vendor.id ? "row-reverse" : "row"}}>
                    <div style={{maxWidth:"75%", padding:"8px 12px", borderRadius:12, fontSize:13,
                      background: m.SenderID === vendor.id ? T.primary : T.surface2,
                      color: m.SenderID === vendor.id ? "#fff" : T.text}}>
                      {m.Body}
                    </div>
                    {vendor?.role === "admin" && (
                      <button title="Delete message (admin)"
                        onClick={()=>doDeleteMessage(m.ID)}
                        style={{background:"none", border:"none", cursor:"pointer",
                          color:T.textFaint, fontSize:13, padding:2, flexShrink:0}}>
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{display:"flex", gap:8}}>
              <input style={{...S.input, flex:1}} value={messageDraft}
                onChange={e=>setMessageDraft(e.target.value)}
                onKeyDown={e=>e.key==="Enter" && doSendThreadMessage()}
                placeholder="Type a message…" />
              <button style={S.btn("primary")} disabled={sendingMessage || !messageDraft.trim()}
                onClick={doSendThreadMessage}>
                {sendingMessage ? "…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function TradesPage({ trades, inventory, shows, onAddTrade, onUpdateItem, onAddInventoryItem, onEditTrade }) {
  const { S, T } = useTheme();
  const [view, setView]               = useState("list");
  const [activeTrade, setActiveTrade] = useState(null);
  const [editingTrade, setEditingTrade] = useState(false);
  const [tradeEditForm, setTradeEditForm] = useState({});
  const [creditRate, setCreditRate]   = useState(0.80);
  const [cardsOut, setCardsOut]       = useState([]);
  const [cardsIn, setCardsIn]         = useState([]);
  const [outSearch, setOutSearch]     = useState("");
  const [inSearch, setInSearch]       = useState("");
  const [saving, setSaving]           = useState(false);

  const RATES = [0.70, 0.80, 0.85, 0.90];

  const inStock = inventory.filter(i => i.Status === "In Stock" || i.Status === "Reserved");

  const outResults = useMemo(() => {
    if (!outSearch.trim()) return [];
    const q = outSearch.toLowerCase();
    return inStock.filter(i =>
      i.CardName?.toLowerCase().includes(q) || i.Set?.toLowerCase().includes(q)
    ).slice(0, 6);
  }, [inStock, outSearch]);

  const inResults = useMemo(() => {
    if (!inSearch.trim()) return [];
    const q = inSearch.toLowerCase();
    return inStock.filter(i =>
      i.CardName?.toLowerCase().includes(q) || i.Set?.toLowerCase().includes(q)
    ).filter(i => !cardsOut.find(c => c.ID === i.ID))
    .slice(0, 6);
  }, [inStock, inSearch, cardsOut]);

  const addCardOut = (item) => {
    if (cardsOut.find(c => c.ID === item.ID)) return;
    setCardsOut(prev => [...prev, item]);
    setOutSearch("");
  };

  const addCardIn = (item) => {
    const isDupe = inventory.some(i =>
      i.CardName?.toLowerCase() === item.CardName?.toLowerCase() &&
      i.Set?.toLowerCase() === item.Set?.toLowerCase() &&
      i.Status !== "Sold" && i.Status !== "Deleted"
    );
    setCardsIn(prev => [...prev, {
      ...item,
      _tradeId:  "ti_" + Date.now() + Math.random(),
      _isDupe:   isDupe,
      _mv:       "",
      _credit:   0,
      _isNew:    !item.ID,
    }]);
    setInSearch("");
  };

  const addNewCardIn = (name) => {
    if (!name.trim()) return;
    addCardIn({ CardName: name, Set: "", Condition: "Near Mint", Language: "English",
      Rarity: "Rare", Grade: "", Source: "Customer Trade" });
  };

  const updateCardInMV = (tradeId, mv) => {
    setCardsIn(prev => prev.map(c => {
      if (c._tradeId !== tradeId) return c;
      const credit = mv ? Math.round(parseFloat(mv) * creditRate) : 0;
      return { ...c, _mv: mv, _credit: credit };
    }));
  };

  const handleRateChange = (rate) => {
    setCreditRate(rate);
    setCardsIn(prev => prev.map(c => ({
      ...c,
      _credit: c._mv ? Math.round(parseFloat(c._mv) * rate) : 0,
    })));
  };

  const totalMVIn     = cardsIn.reduce((s, c) => s + (parseFloat(c._mv) || 0), 0);
  const totalCredit   = cardsIn.reduce((s, c) => s + (c._credit || 0), 0);
  const totalOutValue = cardsOut.reduce((s, c) => s + (c.ListPrice || 0), 0);
  const balance       = totalCredit - totalOutValue;

  const confirmTrade = async () => {
    if (cardsOut.length === 0 || cardsIn.length === 0) return;
    setSaving(true);
    const tradeDate = localDate();
    const tradeId   = "tr_" + Date.now();

    for (const card of cardsOut) {
      await onUpdateItem(card.ID, {
        Status:      "Sold",
        SoldDate:    tradeDate,
        SoldChannel: "Trade",
        SoldPrice:   card.ListPrice || 0,
        TradeCredit: totalCredit,
      });
    }

    for (const card of cardsIn) {
      const mv = parseFloat(card._mv) || 0;
      const ac = card._credit || 0;
      await onAddInventoryItem({
        Type:                     card.Type || "Single Card",
        CardName:                 card.CardName,
        Set:                      card.Set || "",
        Number:                   card.Number || "",
        Rarity:                   card.Rarity || "Rare",
        Language:                 card.Language || "English",
        Condition:                card.Condition || "Near Mint",
        Grade:                    card.Grade || "",
        Quantity:                 1,
        Source:                   "Customer Trade",
        AcquisitionCost:          Math.round(ac),
        ListPrice:                "",
        MinSellPrice:             ac > 0 ? minSell(Math.round(ac)) : 0,
        MarketValueAtAcquisition: mv,
        AcquisitionDate:          tradeDate,
        Status:                   "In Stock",
        LotID:"", LotName:"", LotItemNumber:"",
        ShowID:"", ShowName:"", ImageURL:"",
        Notes:                    `Trade-in · ${(creditRate*100).toFixed(0)}% credit`,
      });
    }

    await onAddTrade({
      Date:             tradeDate,
      CardsOut:         cardsOut.map(c => c.CardName).join(", "),
      CardsIn:          cardsIn.map(c => c.CardName).join(", "),
      TotalCreditValue: totalCredit,
      CreditRate:       creditRate,
      Type:             "Trade Credit",
      Notes:            "",
    });

    setSaving(false);
    setCardsOut([]);
    setCardsIn([]);
    setCreditRate(0.80);
    setView("list");
  };

  // ── List view ──────────────────────────────────────────────────────────────
  if (view === "list") return (
    <div style={S.page}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:12,color:T.textFaint,fontFamily:"'DM Mono',monospace"}}>{trades.length} trades</div>
        <button style={S.btn("primary")} onClick={()=>setView("new")}>+ New Trade</button>
      </div>

      {trades.length===0 && (
        <div style={{textAlign:"center",padding:"40px 0",color:T.textFaint,fontSize:13}}>No trades logged yet</div>
      )}

      {trades.slice().reverse().map(t=>(
        <div key={t.ID} style={{...S.card,cursor:"pointer"}} onClick={()=>setActiveTrade(t)}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <div style={{fontWeight:500,fontSize:14}}>Trade</div>
            <span style={S.badge("green")}>{fmtT(t.TotalCreditValue)} credit</span>
          </div>
          <div style={{fontSize:12,color:T.textMuted,marginBottom:3}}>
            <span style={{color:"#f87171"}}>Out: </span>{t.CardsOut||"—"}
          </div>
          <div style={{fontSize:12,color:T.textMuted}}>
            <span style={{color:"#4ade80"}}>In: </span>{t.CardsIn||"—"}
          </div>
          <div style={{fontSize:11,color:T.textFaint,marginTop:4}}>{t.Date} · {(parseFloat(t.CreditRate||0.8)*100).toFixed(0)}% credit rate · tap to view</div>
        </div>
      ))}

      {activeTrade && (
        <div style={S.overlay} onClick={e=>e.target===e.currentTarget&&setActiveTrade(null)}>
          <div style={S.drawer}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{fontSize:16,fontWeight:600}}>Trade Detail</div>
              <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>{setActiveTrade(null);setEditingTrade(false);}}>✕</button>
            </div>

            {editingTrade ? (
              <>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Date</label>
                  <input style={S.input} type="date" value={tradeEditForm.Date}
                    onChange={e=>setTradeEditForm(f=>({...f,Date:e.target.value}))} />
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Cards Out</label>
                  <input style={S.input} value={tradeEditForm.CardsOut}
                    onChange={e=>setTradeEditForm(f=>({...f,CardsOut:e.target.value}))} />
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Cards In</label>
                  <input style={S.input} value={tradeEditForm.CardsIn}
                    onChange={e=>setTradeEditForm(f=>({...f,CardsIn:e.target.value}))} />
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Total Credit Value ($)</label>
                  <input style={S.input} type="number" value={tradeEditForm.TotalCreditValue}
                    onChange={e=>setTradeEditForm(f=>({...f,TotalCreditValue:e.target.value}))} />
                </div>
                <div style={{marginBottom:20}}>
                  <label style={S.label}>Notes</label>
                  <input style={S.input} value={tradeEditForm.Notes||""}
                    onChange={e=>setTradeEditForm(f=>({...f,Notes:e.target.value}))} />
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button style={{...S.btn("primary"),flex:1}} onClick={async()=>{
                    await onEditTrade(activeTrade.ID, tradeEditForm);
                    setActiveTrade(null); setEditingTrade(false);
                  }}>Save Changes</button>
                  <button style={S.btn("ghost")} onClick={()=>setEditingTrade(false)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div style={{...S.infoBox,marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:13,color:T.textMuted}}>Date</span>
                    <span style={{fontSize:13,fontWeight:500}}>{activeTrade.Date}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:13,color:T.textMuted}}>Credit Rate</span>
                    <span style={{fontSize:13,fontWeight:500}}>{(parseFloat(activeTrade.CreditRate||0.8)*100).toFixed(0)}%</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:13,color:T.textMuted}}>Total Credit</span>
                    <span style={{fontSize:13,fontWeight:600,color:"#4ade80"}}>{fmt(activeTrade.TotalCreditValue)}</span>
                  </div>
                  {activeTrade.Notes && (
                    <div style={{display:"flex",justifyContent:"space-between"}}>
                      <span style={{fontSize:13,color:T.textMuted}}>Notes</span>
                      <span style={{fontSize:13}}>{activeTrade.Notes}</span>
                    </div>
                  )}
                </div>
                <div style={{marginBottom:12}}>
                  <label style={S.label}>Cards Out</label>
                  <div style={{...S.infoBox,marginBottom:0}}>
                    {(activeTrade.CardsOut||"—").split(",").map((c,i)=>(
                      <div key={i} style={{fontSize:13,color:"#f87171",padding:"3px 0"}}>{c.trim()}</div>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:20}}>
                  <label style={S.label}>Cards In</label>
                  <div style={{...S.infoBox,marginBottom:0}}>
                    {(activeTrade.CardsIn||"—").split(",").map((c,i)=>(
                      <div key={i} style={{fontSize:13,color:"#4ade80",padding:"3px 0"}}>{c.trim()}</div>
                    ))}
                  </div>
                </div>
                <button style={{...S.btn("primary"),width:"100%"}} onClick={()=>{
                  setTradeEditForm({
                    Date:activeTrade.Date,
                    CardsOut:activeTrade.CardsOut||"",
                    CardsIn:activeTrade.CardsIn||"",
                    TotalCreditValue:activeTrade.TotalCreditValue||0,
                    Notes:activeTrade.Notes||"",
                  });
                  setEditingTrade(true);
                }}>Edit Trade</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ── New trade view ─────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
        <button style={{...S.btn("ghost"),padding:"6px 10px"}} onClick={()=>setView("list")}>← Back</button>
        <div style={{fontSize:16,fontWeight:600}}>New Trade</div>
      </div>

      <div style={S.h2}>Your cards going out</div>
      <input style={{...S.input,marginBottom:8}} value={outSearch}
        onChange={e=>setOutSearch(e.target.value)}
        placeholder="Search my inventory..." />
      {outResults.map(item=>(
        <div key={item.ID} style={{...S.card,padding:"8px 12px",cursor:"pointer",marginBottom:6}}
          onClick={()=>addCardOut(item)}>
          <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
          <div style={{fontSize:11,color:T.textMuted}}>{item.Set} · {item.Condition} · {fmt(item.ListPrice)}</div>
        </div>
      ))}
      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:4}}>
        {cardsOut.map(c=>(
          <span key={c.ID} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"5px 10px",
            borderRadius:20,fontSize:12,background:"#fef3c7",border:"0.5px solid #fde68a",color:"#92400e"}}>
            {c.CardName} · {fmt(c.ListPrice)}
            <span style={{cursor:"pointer",fontSize:14,color:"#92400e"}}
              onClick={()=>setCardsOut(p=>p.filter(x=>x.ID!==c.ID))}>×</span>
          </span>
        ))}
      </div>

      <div style={S.divider}/>

      <div style={S.h2}>Trade credit rate</div>
      <div style={{display:"flex",gap:8,marginBottom:16}}>
        {RATES.map(r=>(
          <button key={r} style={{...S.chip(creditRate===r),flex:1,textAlign:"center",padding:"9px 0"}}
            onClick={()=>handleRateChange(r)}>
            {(r*100).toFixed(0)}%
          </button>
        ))}
      </div>

      <div style={S.divider}/>

      <div style={S.h2}>Cards coming in</div>
      <input style={{...S.input,marginBottom:8}} value={inSearch}
        onChange={e=>setInSearch(e.target.value)}
        placeholder="Search inventory or type a card name..."
        onKeyDown={e=>{
          if (e.key==="Enter" && inSearch.trim() && inResults.length===0) {
            addNewCardIn(inSearch); setInSearch("");
          }
        }} />
      {inResults.map(item=>(
        <div key={item.ID} style={{...S.card,padding:"8px 12px",cursor:"pointer",marginBottom:6}}
          onClick={()=>addCardIn(item)}>
          <div style={{fontSize:13,fontWeight:500}}>{item.CardName}</div>
          <div style={{fontSize:11,color:T.textMuted}}>{item.Set} · {item.Condition}</div>
        </div>
      ))}
      {inSearch.trim() && inResults.length===0 && (
        <div style={{...S.card,padding:"8px 12px",cursor:"pointer",marginBottom:6,borderColor:"#60a5fa"}}
          onClick={()=>{addNewCardIn(inSearch);setInSearch("");}}>
          <div style={{fontSize:13,color:"#88bdf2"}}>+ Add "{inSearch}" as new card</div>
          <div style={{fontSize:11,color:T.textMuted}}>Not in inventory — will be added after trade</div>
        </div>
      )}

      {cardsIn.map(c=>(
        <div key={c._tradeId} style={{...S.cardB(c._isDupe?"#3a2a00":undefined),marginBottom:8}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div>
              <div style={{fontSize:13,fontWeight:500}}>{c.CardName}
                {c._isDupe && <span style={{...S.badge("amber"),marginLeft:6,fontSize:10}}>Duplicate</span>}
                {c._isNew && <span style={{...S.badge("blue"),marginLeft:6,fontSize:10}}>New</span>}
              </div>
              <div style={{fontSize:11,color:T.textMuted}}>{c.Set||"No set"} · {c.Condition}</div>
            </div>
            <span style={{cursor:"pointer",fontSize:16,color:T.textMuted}}
              onClick={()=>setCardsIn(p=>p.filter(x=>x._tradeId!==c._tradeId))}>×</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
            <div>
              <div style={{fontSize:10,color:T.textMuted,marginBottom:3,fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.05em"}}>Market Value ($)</div>
              <input style={{...S.input,padding:"8px 10px",fontSize:13}}
                type="number" value={c._mv} placeholder="0.00"
                onChange={e=>updateCardInMV(c._tradeId, e.target.value)} />
            </div>
            <div>
              <div style={{fontSize:10,color:T.textMuted,marginBottom:3,fontFamily:"'DM Mono',monospace",textTransform:"uppercase",letterSpacing:"0.05em"}}>Trade Credit</div>
              <div style={{background:c._credit>0?"#0f2e1a":T.surface2,border:`0.5px solid ${c._credit>0?"#166534":T.border}`,
                borderRadius:8,padding:"8px 10px",fontSize:13,fontFamily:"'DM Mono',monospace",
                color:c._credit>0?"#4ade80":T.textFaint,fontWeight:c._credit>0?600:400}}>
                {c._credit>0?fmtT(c._credit):"—"}
              </div>
            </div>
          </div>
        </div>
      ))}

      {(cardsOut.length>0||cardsIn.length>0) && (
        <>
          <div style={S.divider}/>
          <div style={{...S.infoBox,marginBottom:16}}>
            <div style={{...S.row,marginBottom:6}}>
              <span style={{fontSize:13,color:T.textMuted,flex:1}}>Cards going out</span>
              <span style={{fontSize:13,color:"#f87171"}}>{fmtT(totalOutValue)}</span>
            </div>
            <div style={{...S.row,marginBottom:6}}>
              <span style={{fontSize:13,color:T.textMuted,flex:1}}>Total market value in</span>
              <span style={{fontSize:13}}>{fmtT(totalMVIn)}</span>
            </div>
            <div style={{...S.row,marginBottom:6}}>
              <span style={{fontSize:13,color:T.textMuted,flex:1}}>Total trade credit ({(creditRate*100).toFixed(0)}%)</span>
              <span style={{fontSize:13,color:"#4ade80",fontWeight:600}}>{fmtT(totalCredit)}</span>
            </div>
            <div style={{borderTop:`0.5px solid ${T.border}`,paddingTop:8,marginTop:4,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span style={{fontSize:13,color:T.textMuted}}>Balance</span>
              <div>
                <span style={{fontSize:14,fontWeight:600,color:balance>=0?"#4ade80":"#f87171"}}>
                  {balance>=0?`+${fmtT(balance)}`:`${fmtT(balance)}`}
                </span>
                <div style={{fontSize:11,color:balance>=0?"#4ade80":"#f87171",marginTop:2}}>
                  {balance>0?"You come out ahead":balance<0?`Customer owes you ${fmtT(Math.abs(balance))} cash or more cards`:"Even trade ✓"}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <button style={{...S.btn("primary"),width:"100%"}}
        onClick={confirmTrade}
        disabled={saving||cardsOut.length===0||cardsIn.length===0}>
        {saving?"Processing…":"Confirm Trade"}
      </button>
      <div style={{fontSize:11,color:T.textFaint,textAlign:"center",marginTop:8}}>
        Cards out will be marked traded · Cards in added to inventory · No images added (add later)
      </div>
    </div>
  );
}

// ─── COLLECTR IMPORT ─────────────────────────────────────────────────────────
const MAX_IMPORT_ROWS = 5000;
const MAX_IMPORT_FILE_BYTES = 5 * 1024 * 1024;

function sanitizeImportText(str, maxLen = 300) {
  if (!str) return '';
  return String(str)
    .replace(/<[^>]*>/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    .trim()
    .slice(0, maxLen);
}

function isValidDateString(str) {
  if (!str || typeof str !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(str.trim()) && !isNaN(new Date(str).getTime());
}

function parseCsv(text) {
  if (!text || typeof text !== 'string') throw new Error('File appears to be empty or unreadable.');
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) throw new Error('No data rows found — check the file has a header row plus at least one card.');
  if (lines.length - 1 > MAX_IMPORT_ROWS) {
    throw new Error(`This file has ${lines.length - 1} rows, which is over the ${MAX_IMPORT_ROWS}-row limit per import. Split it into smaller files.`);
  }
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g,''));
  if (headers.length < 2) throw new Error('Could not detect CSV columns — check the file is a valid, comma-separated CSV.');
  return lines.slice(1).map(line => {
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else { cur += line[i]; }
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/^"|"$/g,''); });
    return obj;
  });
}

function mapCollectrToVendorStax(row) {
  const grade = row['Grade'] === 'Ungraded' ? '' : sanitizeImportText(row['Grade']) || '';
  const marketPrice = parseFloat(
    row['Market Price (As of 2026-04-10)'] ||
    Object.entries(row).find(([k]) => k.startsWith('Market Price'))?.[1] || 0
  ) || 0;
  const cost = parseFloat(row['Average Cost Paid']) || 0;
  return {
    Type:                     'Single Card',
    CardName:                 sanitizeImportText(row['Product Name']) || '',
    Set:                      sanitizeImportText(row['Set']) || '',
    Number:                   sanitizeImportText(row['Card Number']) || '',
    Rarity:                   sanitizeImportText(row['Rarity']) || sanitizeImportText(row['Variance']) || 'Common',
    Language:                 'English',
    Condition:                sanitizeImportText(row['Card Condition']) || 'Near Mint',
    Grade:                    grade,
    Quantity:                 parseInt(row['Quantity']) || 1,
    Source:                   'Individual Buy',
    AcquisitionCost:          cost,
    ListPrice:                (() => { const v = parseFloat(row['ListPrice'] || row['List Price'] || row['list_price']); return !isNaN(v) && v >= 0 ? v : ''; })(),
    MinSellPrice:             cost > 0 ? minSell(cost) : '',
    MarketValueAtAcquisition: marketPrice,
    AcquisitionDate:          isValidDateString(row['Date Added']) ? row['Date Added'] : new Date().toISOString().split('T')[0],
    Notes:                    sanitizeImportText(row['Notes'], 1000) || '',
    Status:                   'In Stock',
    LotID:'', LotName:'', LotItemNumber:'',
    ShowID:'', ShowName:'', ImageURL:'',
    SoldPrice:'', SoldDate:'', SoldChannel:'', BuyerGender:'', TradeCredit:'',
  };
}

const CUSTOM_IMPORT_HEADERS = [
  'Portfolio Name','Category','Set','Product Name','Card Number','Rarity',
  'Variance','Grade','Card Condition','Acquisition Cost','Market At Buy',
  'List Price','Date Added','Notes','Acquisition Source','List Online',
];

const CUSTOM_IMPORT_SAMPLE_ROWS = [
  ['My Card Shop','Pokemon','Brilliant Stars','Mimikyu VMAX','069/172','Ultra Rare','Holofoil','Ungraded','Near Mint','8.50','12.29','15.00','2026-07-09','Light edge wear on back','Individual Buy','yes'],
  ['My Card Shop','Pokemon','Crown Zenith','Rayquaza VMAX','102/159','Ultra Rare','Holofoil','Ungraded','Near Mint','9.00','14.55','18.00','2026-07-09','','Binder Buy','yes'],
  ['My Card Shop','Pokemon','Silver Tempest','Lugia VSTAR','139/195','Ultra Rare','Holofoil','PSA 9','Near Mint','5.00','6.40','9.99','2026-07-09','Graded, minor centering','Pack Pull','no'],
];

function buildCustomImportSampleCsv() {
  const rows = [CUSTOM_IMPORT_HEADERS, ...CUSTOM_IMPORT_SAMPLE_ROWS];
  return rows.map(r => r.map(v => {
    const s = String(v ?? '');
    return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
  }).join(',')).join('\n');
}

function downloadCustomImportSample() {
  const blob = new Blob([buildCustomImportSampleCsv()], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'VendorStax_Custom_Import_Template.csv';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function mapCustomToVendorStax(row) {
  const cost        = parseFloat(row['Acquisition Cost']) || 0;
  const marketAtBuy = parseFloat(row['Market At Buy']) || 0;
  const listPriceVal = parseFloat(row['List Price']);
  const listOnlineRaw = (row['List Online'] || '').trim().toLowerCase();
  const sourceRaw = (row['Acquisition Source'] || '').trim();
  const matchedSource = ALL_SOURCES.find(s => s.toLowerCase() === sourceRaw.toLowerCase()) || 'Individual Buy';
  const gameRaw = (row['Category'] || '').trim();
  const matchedGame = ['Pokemon','Lorcana'].find(g => g.toLowerCase() === gameRaw.toLowerCase()) || 'Pokemon';
  const grade = row['Grade'] && row['Grade'].toLowerCase() !== 'ungraded' ? sanitizeImportText(row['Grade']) : '';
  const portfolioName = sanitizeImportText(row['Portfolio Name'], 100);
  const notes = sanitizeImportText(row['Notes'], 1000);
  return {
    Type:                     'Single Card',
    CardName:                 sanitizeImportText(row['Product Name']) || '',
    Set:                      sanitizeImportText(row['Set']) || '',
    Number:                   sanitizeImportText(row['Card Number']) || '',
    Rarity:                   sanitizeImportText(row['Rarity']) || sanitizeImportText(row['Variance']) || 'Common',
    Language:                 'English',
    Condition:                sanitizeImportText(row['Card Condition']) || 'Near Mint',
    Grade:                    grade,
    Quantity:                 1,
    Source:                   matchedSource,
    AcquisitionCost:          cost,
    ListPrice:                !isNaN(listPriceVal) && listPriceVal >= 0 ? listPriceVal : '',
    MinSellPrice:             cost > 0 ? minSell(cost) : '',
    MarketValueAtAcquisition: marketAtBuy,
    AcquisitionDate:          isValidDateString(row['Date Added']) ? row['Date Added'] : new Date().toISOString().split('T')[0],
    Notes:                    portfolioName ? `Portfolio: ${portfolioName}${notes ? ' · ' + notes : ''}` : notes,
    GameType:                 matchedGame,
    IsOnline:                 listOnlineRaw === 'yes',
    Status:                   'In Stock',
    LotID:'', LotName:'', LotItemNumber:'',
    ShowID:'', ShowName:'', ImageURL:'',
    SoldPrice:'', SoldDate:'', SoldChannel:'', BuyerGender:'', TradeCredit:'',
  };
}

function dupeKey(item) {
  return `${item.CardName}|${item.Set}|${item.Number}|${item.Condition}`.toLowerCase();
}

function ImportPage({ inventory, lots, onImport, onClose, vendor }) {
  const { S, T } = useTheme();
  const { toast, confirmAction } = useNotify();
  const [step, setStep]           = useState('upload');
  const [importFormat, setImportFormat] = useState('collectr');
  const [rows, setRows]           = useState([]);
  const [selected, setSelected]   = useState(new Set());
  const [search, setSearch]       = useState('');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [dupes, setDupes]         = useState(new Set());
  const [assignLotId, setAssignLotId]   = useState("");
  const [assignSource, setAssignSource] = useState("Individual Buy");
  const [overrideListPrice, setOverrideListPrice] = useState(false);
  const [fileError, setFileError] = useState("");
  const [batches, setBatches]     = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const fileRef = useRef(null);

  const existingKeys = useMemo(() => new Set(inventory.map(dupeKey)), [inventory]);
  const openLots     = useMemo(() => lots.filter(l => l.Status === "Open"), [lots]);

  const loadBatches = () => {
    if (!vendor?.id) return;
    setLoadingBatches(true);
    readImportBatches(vendor.id)
      .then(setBatches)
      .catch(e => toast("Couldn't load import history: " + e.message, "error"))
      .finally(() => setLoadingBatches(false));
  };

  useEffect(() => { if (step === 'upload') loadBatches(); }, [step, vendor?.id]);

  const handleUndoBatch = async (batch) => {
    const ok = await confirmAction(
      `Undo this import of ${batch.count} card${batch.count!==1?"s":""}? This removes them from your inventory — it won't affect anything else.`,
      { confirmLabel: "Undo Import", danger: true }
    );
    if (!ok) return;
    try {
      const count = await deleteImportBatch(vendor.id, batch.batchId);
      toast(`Removed ${count} card${count!==1?"s":""} from that import`, "success");
      loadBatches();
    } catch(e) {
      toast("Failed to undo import: " + e.message, "error");
    }
  };

  const handleExportBatch = async (batch) => {
    try {
      const rows = await readImportBatchCards(vendor.id, batch.batchId);
      if (!rows.length) { toast("No cards found for this import.", "alert"); return; }
      const headers = ["Card Name","Set","Card Number","Condition","Grading Company","Grade",
        "Status","List Price","Acquisition Cost","Sold Price","Sold Date","Sold Channel"];
      const csvEscape = v => {
        const s = (v ?? "").toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s;
      };
      const lines = [headers.join(",")];
      for (const r of rows) {
        lines.push([
          r.card_name, r.set_name, r.card_number, r.condition, r.grading_company, r.grade,
          r.status, r.list_price, r.acquisition_cost, r.sold_price, r.sold_date, r.sold_channel,
        ].map(csvEscape).join(","));
      }
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = `vendorstax-reconciliation-${batch.batchId}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast(`Exported ${rows.length} card${rows.length!==1?"s":""}.`, "success");
    } catch(e) {
      toast("Export failed: " + e.message, "error");
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    setFileError("");
    if (!/\.csv$/i.test(file.name)) {
      setFileError("Please upload a .csv file.");
      return;
    }
    if (file.size > MAX_IMPORT_FILE_BYTES) {
      setFileError(`File is too large (${(file.size/1024/1024).toFixed(1)}MB). Max size is ${MAX_IMPORT_FILE_BYTES/1024/1024}MB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      let parsed;
      try {
        parsed = parseCsv(e.target.result);
      } catch(err) {
        setFileError(err.message || "Couldn't read that file — check it's a valid CSV.");
        return;
      }
      const owned = importFormat === 'collectr'
        ? parsed.filter(r => r['Watchlist']?.toLowerCase() !== 'true' && r['Product Name'])
        : parsed.filter(r => r['Product Name']);
      if (owned.length === 0) {
        setFileError("No valid card rows found. Check the file has a 'Product Name' column with data.");
        return;
      }
      const imageMap = {};
      inventory.forEach(i => {
        if (i.ImageURL && i.Status !== 'Deleted' && i.CardName) {
          const key = i.CardName.toLowerCase();
          if (!imageMap[key]) imageMap[key] = i.ImageURL;
        }
      });
      const mapper = importFormat === 'collectr' ? mapCollectrToVendorStax : mapCustomToVendorStax;
      const mapped = owned.map(row => {
        const item = mapper(row);
        if (!item.ImageURL) {
          const img = imageMap[item.CardName?.toLowerCase()];
          if (img) item.ImageURL = img;
        }
        return item;
      });
      const dupeSet = new Set(mapped.filter(r => existingKeys.has(dupeKey(r))).map(dupeKey));
      setRows(mapped);
      setDupes(dupeSet);
      setSelected(new Set(mapped.filter(r => !dupeSet.has(dupeKey(r))).map((_,i) => i)));
      setStep('preview');
    };
    reader.onerror = () => setFileError("Couldn't read that file — please try again.");
    reader.readAsText(file);
  };

  const filtered = useMemo(() => {
    if (!search) return rows.map((r,i) => ({...r, _idx:i}));
    const q = search.toLowerCase();
    return rows.map((r,i) => ({...r,_idx:i}))
      .filter(r => r.CardName?.toLowerCase().includes(q) || r.Set?.toLowerCase().includes(q));
  }, [rows, search]);

  const toggleAll = () => {
    const filteredNonDupe = filtered.filter(r => !dupes.has(dupeKey(r))).map(r => r._idx);
    const allSelected = filteredNonDupe.every(i => selected.has(i));
    const next = new Set(selected);
    if (allSelected) { filteredNonDupe.forEach(i => next.delete(i)); }
    else { filteredNonDupe.forEach(i => next.add(i)); }
    setSelected(next);
  };

  const toggle = (idx) => {
    const next = new Set(selected);
    if (next.has(idx)) next.delete(idx); else next.add(idx);
    setSelected(next);
  };

  const doImport = async () => {
    setImporting(true);
    setStep('importing');
    let toImport = rows.filter((_,i) => selected.has(i));
    if (!toImport.length) { setStep('done'); setImporting(false); return; }

    const isFree = vendor?.role !== "admin" && !["monthly","annual","trial"].includes(vendor?.plan);
    if (isFree) {
      const activeCount = inventory.filter(i => i.Status !== "Sold" && i.Status !== "Deleted").length;
      const remaining = Math.max(0, FREE_INVENTORY_LIMIT - activeCount);
      if (remaining === 0) {
        setImporting(false); setStep('upload');
        toast(`You've reached the ${FREE_INVENTORY_LIMIT} card limit on the Free plan. Go to Settings to upgrade to Pro.`, "alert");
        return;
      }
      if (toImport.length > remaining) {
        toast(`Free plan limit: importing the first ${remaining} of ${toImport.length} selected cards. Upgrade to Pro in Settings to import the rest.`, "alert");
        toImport = toImport.slice(0, remaining);
      }
    }

    try {
      await supabase.auth.refreshSession();
    } catch(e) {
      console.warn("Session refresh failed:", e.message);
    }
    const batchId = `imp_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
    const lot = openLots.find(l => l.ID === assignLotId);
    let done = 0;
    for (const item of toImport) {
      let ac = item.AcquisitionCost;
      let ms = item.MinSellPrice;
      let lp = item.ListPrice;
      let lotFields = { LotID:"", LotName:"", LotItemNumber:"" };

      if (overrideListPrice) {
        const mv = parseFloat(item.MarketValueAtAcquisition) || 0;
        if (mv > 0) lp = mv;
      }

      if (lot) {
        if (lot.CostMethod === "proportional" && lot.AllocationRatio) {
          const mv = parseFloat(item.MarketValueAtAcquisition)||0;
          ac = mv>0 ? +(mv * lot.AllocationRatio).toFixed(2) : ac;
          ms = ac>0 ? minSell(ac) : ms;
        } else if (lot.CostMethod === "even" && lot.CostPerCard) {
          ac = lot.CostPerCard;
          ms = minSell(ac);
        }
        lotFields = { LotID: lot.ID, LotName: lot.Name, LotItemNumber: done+1 };
      }

      try {
        await onImport({
          ...item,
          Source:          importFormat === 'custom' && !assignLotId
            ? item.Source
            : (assignLotId ? (lot?.Source || assignSource) : assignSource),
          AcquisitionCost: ac,
          MinSellPrice:    ms,
          ListPrice:       lp,
          ImportBatchID:   batchId,
          ImportSource:    importFormat,
          ...lotFields,
        });
      } catch(e) {
        console.error("Import error on card:", item.CardName, e.message);
      }
      done++;
      setProgress(Math.round((done / toImport.length) * 100));
      if (done % 50 === 0) {
        try { await supabase.auth.refreshSession(); } catch(_) {}
      }
      if (done < toImport.length) await new Promise(r => setTimeout(r, 200));
    }
    setStep('done');
    setImporting(false);
  };

  if (step === 'upload') return (
    <div style={S.page}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <button style={{...S.btn('ghost'),padding:'6px 10px'}} onClick={onClose}>← Back</button>
        <div style={{fontSize:16,fontWeight:600}}>Import</div>
      </div>

      <div style={{display:'flex',gap:8,marginBottom:16}}>
        <button
          onClick={()=>{setImportFormat('collectr');setFileError('');}}
          style={{flex:1,padding:'12px',borderRadius:10,cursor:'pointer',
            fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:600,
            border:`1px solid ${importFormat==='collectr'?'#3b82f6':T.border}`,
            background:importFormat==='collectr'?'rgba(59,130,246,0.1)':'transparent',
            color:importFormat==='collectr'?'#3b82f6':T.textMuted}}>
          Collectr Import
        </button>
        <button
          onClick={()=>{setImportFormat('custom');setFileError('');}}
          style={{flex:1,padding:'12px',borderRadius:10,cursor:'pointer',
            fontFamily:"'Sora',sans-serif",fontSize:13,fontWeight:600,
            border:`1px solid ${importFormat==='custom'?'#4ade80':T.border}`,
            background:importFormat==='custom'?'rgba(74,222,128,0.1)':'transparent',
            color:importFormat==='custom'?'#4ade80':T.textMuted}}>
          Custom Import
        </button>
      </div>

      {fileError && (
        <div style={{...S.infoBox,marginBottom:12,borderColor:'#f87171',background:'rgba(248,113,113,0.06)'}}>
          <div style={{fontSize:12,color:'#f87171'}}>{fileError}</div>
        </div>
      )}

      {importFormat === 'collectr' ? (
        <>
          <div style={S.card}>
            <div style={{fontSize:14,color:T.textMuted,marginBottom:20,lineHeight:1.6}}>
              Export your collection from Collectr as a CSV file, then upload it here.
              Only owned cards will be imported — wishlist items are skipped automatically.
            </div>
            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}}
              onChange={e => handleFile(e.target.files[0])} />
            <button style={{...S.btn('primary'),width:'100%',padding:'14px'}}
              onClick={() => fileRef.current?.click()}>
              Upload Collectr CSV
            </button>
          </div>
          <div style={{...S.infoBox,marginTop:12}}>
            <div style={{fontSize:12,color:'#3b82f6',fontWeight:500,marginBottom:8}}>What gets imported</div>
            <div style={{fontSize:12,color:T.textMuted,lineHeight:2}}>
              <div>✓ Card name, set, number, rarity, condition, grade</div>
              <div>✓ Acquisition cost (if set in Collectr)</div>
              <div>✓ Market price → Market Value at Acquisition</div>
              <div>✓ Quantity, date added, notes</div>
              <div>✗ List price — left blank unless you choose to override it with Market Price on the next screen</div>
              <div>✗ Wishlist cards — skipped</div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={S.card}>
            <div style={{fontSize:14,color:T.textMuted,marginBottom:16,lineHeight:1.6}}>
              For collections tracked in TCGplayer, eBay, Shopify, Google Sheets, or anywhere
              else — fill in our template and upload it here.
            </div>

            <div
              onClick={downloadCustomImportSample}
              style={{display:'flex',alignItems:'center',gap:12,padding:'12px',
                borderRadius:10,border:`1px solid ${T.border}`,background:T.surface2,
                cursor:'pointer',marginBottom:16}}>
              <div style={{width:44,height:44,borderRadius:8,background:'rgba(74,222,128,0.12)',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,flexShrink:0}}>
                📄
              </div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600}}>VendorStax_Custom_Import_Template.csv</div>
                <div style={{fontSize:11,color:T.textMuted}}>{CUSTOM_IMPORT_HEADERS.length} columns · sample rows included</div>
              </div>
              <div style={{fontSize:12,color:'#4ade80',fontWeight:600,flexShrink:0}}>Download ⬇</div>
            </div>

            <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}}
              onChange={e => handleFile(e.target.files[0])} />
            <button style={{...S.btn('primary'),width:'100%',padding:'14px'}}
              onClick={() => fileRef.current?.click()}>
              Upload Custom CSV
            </button>
          </div>
          <div style={{...S.infoBox,marginTop:12}}>
            <div style={{fontSize:12,color:'#4ade80',fontWeight:500,marginBottom:8}}>Required columns</div>
            <div style={{fontSize:12,color:T.textMuted,lineHeight:1.9}}>
              {CUSTOM_IMPORT_HEADERS.map(h => <div key={h}>• {h}</div>)}
            </div>
            <div style={{fontSize:11,color:T.textFaint,marginTop:10,lineHeight:1.6}}>
              <strong>List Online</strong> accepts "yes" or "no". <strong>Acquisition Source</strong> should
              be one of: {ALL_SOURCES.join(", ")} — anything else defaults to "Individual Buy".
              <strong> Category</strong> should be "Pokemon" or "Lorcana."
            </div>
          </div>
        </>
      )}

      {batches.length > 0 && (
        <div style={{marginTop:20}}>
          <div style={{fontSize:12,fontWeight:700,color:T.textMuted,marginBottom:8,
            textTransform:'uppercase',letterSpacing:'.05em'}}>
            Recent Imports
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {batches.slice(0,10).map(b => (
              <div key={b.batchId} style={{...S.card,padding:'10px 12px',
                display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <div>
                  <div style={{fontSize:13,fontWeight:600}}>
                    {b.count} card{b.count!==1?'s':''} · {b.source==='custom'?'Custom Import':'Collectr Import'}
                  </div>
                  <div style={{fontSize:11,color:T.textFaint}}>
                    {new Date(b.createdAt).toLocaleString()}
                  </div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button
                    onClick={()=>handleExportBatch(b)}
                    style={{...S.btn('ghost'),fontSize:11,padding:'6px 10px'}}>
                    📄 Export
                  </button>
                  <button
                    onClick={()=>handleUndoBatch(b)}
                    style={{...S.btn('ghost'),fontSize:11,padding:'6px 10px',color:'#f87171',borderColor:'#f8717155'}}>
                    Undo
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (step === 'importing') return (
    <div style={{...S.page,textAlign:'center',paddingTop:60}}>
      <img src={T.text === "#e8effe" ? "/cv-logo_dark.png" : "/cv-logo.png"} alt="VendorStax"
        style={{height:32, objectFit:"contain", marginBottom:16}} />
      <div style={{fontSize:14,color:T.textMuted,marginBottom:20}}>Importing {selected.size} cards…</div>
      <div style={{height:8,borderRadius:4,background:T.border,overflow:'hidden',maxWidth:300,margin:'0 auto'}}>
        <div style={{height:'100%',borderRadius:4,background:'#3b82f6',width:`${progress}%`,transition:'width 0.2s'}} />
      </div>
      <div style={{fontSize:13,color:T.textMuted,marginTop:12}}>{progress}%</div>
    </div>
  );

  if (step === 'done') return (
    <div style={{...S.page,textAlign:'center',paddingTop:60}}>
      <div style={{fontSize:40,marginBottom:16}}>✓</div>
      <div style={{fontSize:18,fontWeight:600,marginBottom:8}}>Import Complete</div>
      <div style={{fontSize:14,color:T.textMuted,marginBottom:32}}>{selected.size} cards added to inventory</div>
      <button style={{...S.btn('primary'),padding:'12px 32px'}} onClick={onClose}>Go to Inventory</button>
    </div>
  );

  const nonDupeFiltered    = filtered.filter(r => !dupes.has(dupeKey(r)));
  const allNonDupeSelected = nonDupeFiltered.length>0 && nonDupeFiltered.every(r => selected.has(r._idx));

  return (
    <div style={S.page}>
      <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
        <button style={{...S.btn('ghost'),padding:'6px 10px'}} onClick={()=>setStep('upload')}>← Back</button>
        <div style={{flex:1}}>
          <div style={{fontSize:16,fontWeight:600}}>Preview Import</div>
          <div style={{fontSize:12,color:T.textMuted}}>{rows.length} owned cards · {dupes.size} already in inventory</div>
        </div>
      </div>

      {dupes.size>0 && (
        <div style={{...S.infoBox,marginBottom:12,borderColor:'#3a2a00'}}>
          <div style={{fontSize:12,color:'#60a5fa',fontWeight:500}}>
            {dupes.size} duplicate{dupes.size>1?'s':''} detected — unchecked by default
          </div>
        </div>
      )}

      <div style={{...S.infoBox,marginBottom:12}}>
        <div style={{fontSize:12,color:"#3b82f6",fontWeight:500,marginBottom:10}}>Assign to Lot or Source</div>
        {importFormat === 'custom' && (
          <div style={{fontSize:11,color:T.textFaint,marginBottom:10,lineHeight:1.5}}>
            Each card's own Acquisition Source from your file is used automatically.
            The dropdown below only applies if you assign these cards to a Lot.
          </div>
        )}
        <div style={{marginBottom:8}}>
          <label style={S.label}>Acquisition Source</label>
          <select style={S.select} value={assignSource} onChange={e=>{setAssignSource(e.target.value);if(e.target.value!=="Binder Buy"&&e.target.value!=="Bulk Lot"&&e.target.value!=="Pack Pull")setAssignLotId("");}}>
            {["Individual Buy","Binder Buy","Bulk Lot","Pack Pull"].map(s=>(
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        {(assignSource==="Binder Buy"||assignSource==="Bulk Lot"||assignSource==="Pack Pull") && openLots.length>0 && (
          <div>
            <label style={S.label}>Assign to Lot (optional)</label>
            <select style={S.select} value={assignLotId} onChange={e=>setAssignLotId(e.target.value)}>
              <option value="">No lot</option>
              {openLots.filter(l=>l.Source===assignSource||l.Source.includes(assignSource.split(" ")[0]))
                .map(l=>(
                  <option key={l.ID} value={l.ID}>
                    {l.Name} — {l.CostMethod==="proportional"?`${((l.AllocationRatio||0)*100).toFixed(1)}% of MV`:`${fmt(l.CostPerCard)}/card`}
                  </option>
              ))}
            </select>
            {assignLotId && (
              <div style={{fontSize:11,color:"#4ade80",marginTop:4}}>
                Cost basis will be calculated from each card's market value
              </div>
            )}
          </div>
        )}
      </div>

      {importFormat === 'collectr' && (
        <div style={{...S.infoBox,marginBottom:12,
          borderColor:overrideListPrice?"rgba(74,222,128,0.4)":undefined,
          background:overrideListPrice?"rgba(74,222,128,0.05)":undefined}}>
          <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer'}}>
            <input type="checkbox" checked={overrideListPrice}
              onChange={e=>setOverrideListPrice(e.target.checked)}
              style={{width:16,height:16,marginTop:2,accentColor:'#4ade80',flexShrink:0}} />
            <div>
              <div style={{fontSize:13,fontWeight:500,color:overrideListPrice?"#4ade80":T.text}}>
                Set List Price from Collectr Market Price
              </div>
              <div style={{fontSize:11,color:T.textMuted,marginTop:2,lineHeight:1.5}}>
                By default List Price is left blank for you to set manually. Check this to
                automatically set each card's List Price equal to its Collectr Market Price at import.
              </div>
            </div>
          </label>
        </div>
      )}

      <input style={{...S.input,marginBottom:10}} value={search}
        onChange={e=>setSearch(e.target.value)} placeholder="Filter by card name or set…" />

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <label style={{display:'flex',alignItems:'center',gap:8,fontSize:13,cursor:'pointer'}}>
          <input type="checkbox" checked={allNonDupeSelected} onChange={toggleAll}
            style={{width:16,height:16,accentColor:'#3b82f6'}} />
          <span style={{color:T.textMuted}}>Select all ({nonDupeFiltered.length})</span>
        </label>
        <div style={{fontSize:12,color:'#e8b84b',fontFamily:"'DM Mono',monospace"}}>{selected.size} selected</div>
      </div>

      <div style={{maxHeight:'45vh',overflowY:'auto',marginBottom:16}}>
        {filtered.map(row => {
          const isDupe    = dupes.has(dupeKey(row));
          const isChecked = selected.has(row._idx);
          return (
            <div key={row._idx}
              style={{...S.cardB(isDupe?'#3a2a00':isChecked?'#0f2e1a':undefined),
                padding:'10px 14px',marginBottom:8,cursor:'pointer'}}
              onClick={()=>toggle(row._idx)}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <input type="checkbox" checked={isChecked} onChange={()=>toggle(row._idx)}
                  onClick={e=>e.stopPropagation()}
                  style={{width:16,height:16,accentColor:'#3b82f6',flexShrink:0}} />
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,display:'flex',alignItems:'center',gap:6}}>
                    {row.CardName}
                    {isDupe && <span style={S.badge('amber')}>Duplicate</span>}
                  </div>
                  <div style={{fontSize:11,color:T.textMuted}}>
                    {[row.Set,row.Number,row.Condition,row.Quantity>1&&`×${row.Quantity}`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{textAlign:'right',flexShrink:0}}>
                  {row.MarketValueAtAcquisition>0 && (
                    <div style={{fontSize:12,color:'#3b82f6'}}>{fmt(row.MarketValueAtAcquisition)}</div>
                  )}
                  {row.AcquisitionCost>0 && (
                    <div style={{fontSize:11,color:T.textMuted}}>cost: {fmt(row.AcquisitionCost)}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button style={{...S.btn('primary'),width:'100%',maxWidth:560}} onClick={doImport} disabled={selected.size===0}>
        Import {selected.size} Cards
      </button>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard({ inventory, lots, shows, vendor, isEnterprise = false, activeLocationId = null }) {
  const { S, T } = useTheme();
  const [profitPeriod, setProfitPeriod] = useState("weekly");
  const [locationFilter, setLocationFilter] = useState(activeLocationId || "all");
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    if (!isEnterprise) return;
    readLocations().then(setLocations).catch(() => {});
  }, [isEnterprise]);
  const prevActiveLocationId = useRef(activeLocationId);
  useEffect(() => {
    if (activeLocationId !== prevActiveLocationId.current) {
      setLocationFilter(activeLocationId || "all");
      prevActiveLocationId.current = activeLocationId;
    }
  }, [activeLocationId]);
  const filteredInventory = locationFilter === "all"
    ? inventory
    : inventory.filter(i => i.LocationID === locationFilter);
  const inStock = filteredInventory.filter(i=>i.Status==="In Stock"||i.Status==="Reserved");
  const sold    = filteredInventory.filter(i=>i.Status==="Sold");

  const totalValue  = inStock.reduce((s,i)=>s+(i.ListPrice*(i.Quantity||1)),0);
  const totalCost   = inStock.reduce((s,i)=>s+(i.AcquisitionCost*(i.Quantity||1)),0);
  const now         = new Date();
  const weekAgo     = new Date(now-7*86400000).toISOString().split("T")[0];
  const monthAgo    = new Date(now-30*86400000).toISOString().split("T")[0];
  const todayStr    = now.toISOString().split("T")[0];
  const profitWeek  = sold.filter(i=>i.SoldDate>=weekAgo) .reduce((s,i)=>s+((i.SoldPrice||0)-(i.AcquisitionCost||0)),0);
  const profitMonth = sold.filter(i=>i.SoldDate>=monthAgo).reduce((s,i)=>s+((i.SoldPrice||0)-(i.AcquisitionCost||0)),0);
  const nonMovers   = inStock.filter(i=>i.Status==="In Stock"&&daysSince(i.AcquisitionDate)>=30);
  const openLots    = lots.filter(l=>l.Status!=="Deleted").slice(0,5);
  const activeShows = shows.filter(s=>(s.Status==="Active"||s.Status==="Upcoming")&&(!s.EndDate||s.EndDate>=todayStr));

  const weeklyProfit = Array.from({length:8},(_,i)=>{
    const wEnd  =new Date(now-(i)*7*86400000).toISOString().split("T")[0];
    const wStart=new Date(now-(i+1)*7*86400000).toISOString().split("T")[0];
    const p=sold.filter(s=>s.SoldDate>=wStart&&s.SoldDate<wEnd).reduce((sum,s)=>sum+((s.SoldPrice||0)-(s.AcquisitionCost||0)),0);
    return {week:`W${8-i}`,profit:+p.toFixed(2)};
  }).reverse();

  const thisWeekProfit = weeklyProfit[weeklyProfit.length-1]?.profit || 0;
  const lastWeekProfit = weeklyProfit[weeklyProfit.length-2]?.profit || 0;
  const profitTrendPct = lastWeekProfit !== 0 ? ((thisWeekProfit-lastWeekProfit)/Math.abs(lastWeekProfit))*100 : null;

  const setMap={};
  sold.forEach(i=>{ if(i.Set) setMap[i.Set]=(setMap[i.Set]||0)+1; });
  const bestSets=Object.entries(setMap).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([name,count])=>({name:name.length>14?name.slice(0,14)+"…":name,count}));

  const gMap={};
  sold.forEach(i=>{ if(i.BuyerGender) gMap[i.BuyerGender]=(gMap[i.BuyerGender]||0)+1; });
  const gData=Object.entries(gMap).map(([name,value])=>({name,value}));
  const GC=[T.primary,T.green,T.teal,T.red];

  const chMap={};
  sold.forEach(i=>{ if(i.SoldChannel) chMap[i.SoldChannel]=(chMap[i.SoldChannel]||0)+1; });
  const chData=Object.entries(chMap).map(([name,value])=>({name,value}));

  const pmMap={};
  sold.forEach(i=>{ if(i.PaymentMethod) pmMap[i.PaymentMethod]=(pmMap[i.PaymentMethod]||0)+1; });
  const pmData=Object.entries(pmMap).map(([name,value])=>({name,value}));

  const totalProfitAllTime = sold.reduce((s,i)=>s+((i.SoldPrice||0)-(i.AcquisitionCost||0)),0);
  const catMap = {};
  sold.forEach(i=>{
    const cat = i.Type || "Single Card";
    catMap[cat] = (catMap[cat]||0) + ((i.SoldPrice||0)-(i.AcquisitionCost||0));
  });
  const topCategory = Object.entries(catMap).sort((a,b)=>b[1]-a[1])[0];
  const topCategoryPct = topCategory && totalProfitAllTime > 0
    ? Math.max(0, Math.min(100, (topCategory[1]/totalProfitAllTime)*100))
    : 0;

  const greetName = vendor?.business_name || vendor?.first_name || vendor?.name || "Vendor";

  return (
    <div style={S.page}>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:20, fontWeight:700, color:T.text}}>Welcome back, {greetName}! 👋</div>
        <div style={{fontSize:13, color:T.textMuted, marginTop:2}}>Here's what's happening with your business today.</div>
      </div>

      {isEnterprise && locations.length > 1 && (
        <div style={{marginBottom:16}}>
          <select style={{...S.select, width:"auto", fontSize:13}}
            value={locationFilter} onChange={e=>setLocationFilter(e.target.value)}>
            <option value="all">All Locations</option>
            {locations.map(loc => (
              <option key={loc.ID} value={loc.ID}>{loc.StoreName} · #{loc.StoreNumber}</option>
            ))}
          </select>
        </div>
      )}

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(140px, 1fr))", gap:10, marginBottom:16}}>
        <div style={S.stat}>
          <div style={S.statLbl}>Inventory Value</div>
          <div style={S.statVal}>{fmt(totalValue)}</div>
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>Total Cost</div>
          <div style={S.statVal}>{fmt(totalCost)}</div>
        </div>
        <div style={S.stat}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
            <div style={S.statLbl}>Profit</div>
            <div style={{display:"flex",gap:4}}>
              <button onClick={()=>setProfitPeriod("weekly")}
                style={{fontSize:9,padding:"2px 6px",borderRadius:T.radius.sm,border:"none",cursor:"pointer",
                  background:profitPeriod==="weekly"?T.primary:"transparent",
                  color:profitPeriod==="weekly"?"#fff":T.textMuted,fontWeight:600}}>7d</button>
              <button onClick={()=>setProfitPeriod("monthly")}
                style={{fontSize:9,padding:"2px 6px",borderRadius:T.radius.sm,border:"none",cursor:"pointer",
                  background:profitPeriod==="monthly"?T.primary:"transparent",
                  color:profitPeriod==="monthly"?"#fff":T.textMuted,fontWeight:600}}>30d</button>
            </div>
          </div>
          <div style={{...S.statVal,color:(profitPeriod==="weekly"?profitWeek:profitMonth)>=0?T.green:T.red}}>
            {fmt(profitPeriod==="weekly"?profitWeek:profitMonth)}
          </div>
          {profitPeriod==="weekly" && profitTrendPct != null && (
            <div style={{fontSize:10, color: profitTrendPct>=0?T.green:T.red, marginTop:2, fontWeight:600}}>
              {profitTrendPct>=0?"▲":"▼"} {Math.abs(profitTrendPct).toFixed(1)}% vs previous week
            </div>
          )}
        </div>
        <div style={S.stat}>
          <div style={S.statLbl}>In Stock</div>
          <div style={S.statVal}>{inStock.length}</div>
        </div>
        <div style={S.stat}>
          <div style={{...S.statLbl,color:nonMovers.length>0?T.primary:T.textFaint}}>Non-Movers 30d+</div>
          <div style={{...S.statVal,color:nonMovers.length>0?T.primary:T.text}}>{nonMovers.length}</div>
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns: activeShows.length>0 && openLots.length>0 ? "1fr 1fr" : "1fr", gap:12, marginBottom:16}}>
        {activeShows.length>0 && (
          <div style={S.card}>
            <div style={S.h2}>Active Shows</div>
            {activeShows.map(show=>(
              <div key={show.ID} style={{
                display:"flex", justifyContent:"space-between", alignItems:"center",
                borderLeft:`3px solid ${show.Status==="Active"?T.teal:T.primary}`,
                background:T.surface2, borderRadius:T.radius.sm,
                padding:"10px 12px", marginBottom:8,
              }}>
                <div>
                  <div style={{fontWeight:600, fontSize:14, color:T.text}}>{show.Name}</div>
                  {show.Location && <div style={{fontSize:12, color:T.textMuted, marginTop:1}}>{show.Location}</div>}
                </div>
                <div style={{textAlign:"right"}}>
                  <span style={{
                    fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:T.radius.pill,
                    background: show.Status==="Active" ? T.tealBg : T.blueBg,
                    color: show.Status==="Active" ? T.teal : T.primary,
                  }}>
                    {show.Status==="Active" ? "ONGOING" : "UPCOMING"}
                  </span>
                  <div style={{fontSize:11, color:T.textFaint, marginTop:3}}>{show.StartDate}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {openLots.length>0 && (
          <div style={S.card}>
            <div style={S.h2}>Lot Acquisitions ({lots.filter(l=>l.Status!=="Deleted").length})</div>
            {openLots.map(lot=>{
              const lotSold=filteredInventory.filter(i=>i.LotID===lot.ID&&i.Status==="Sold");
              const revenue=lotSold.reduce((s,i)=>s+(i.SoldPrice||0),0);
              const pctRecov=lot.TotalPaid>0?Math.min(100,(revenue/lot.TotalPaid)*100):0;
              return (
                <div key={lot.ID} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:13,marginBottom:4}}>
                    <span style={{fontWeight:500, color:T.text}}>{lot.Name}</span>
                    <span style={{color:T.textFaint,fontFamily:"'DM Mono',monospace",fontSize:12}}>{fmt(revenue)} / {fmt(lot.TotalPaid)}</span>
                  </div>
                  <div style={{height:6,borderRadius:T.radius.sm,background:T.border,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:T.radius.sm,width:`${pctRecov}%`,background:pctRecov>=100?T.green:T.primary}} />
                  </div>
                  <div style={{fontSize:11,color:T.textFaint,marginTop:3}}>{pctRecov.toFixed(0)}% recovered</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{display:"grid", gridTemplateColumns: weeklyProfit.some(w=>w.profit!==0) ? "2fr 1fr" : "1fr", gap:12, marginBottom:20}}>
        {weeklyProfit.some(w=>w.profit!==0) && (
          <div style={S.card}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:2}}>
              <div style={S.h2}>Weekly Profit</div>
              <span style={{fontSize:11, color:T.textFaint}}>Last 7 Days</span>
            </div>
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={weeklyProfit} margin={{top:0,right:0,left:-22,bottom:0}}>
                <defs>
                  <linearGradient id="tealBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.teal} stopOpacity={0.95}/>
                    <stop offset="100%" stopColor={T.teal} stopOpacity={0.5}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="week" tick={{fill:T.textFaint, fontSize:11}} axisLine={false} tickLine={false}/>
                <YAxis tick={{fill:T.textFaint, fontSize:11}} axisLine={false} tickLine={false} tickFormatter={v=>`$${v}`}/>
                <Tooltip formatter={v=>[fmt(v),"Profit"]} contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:T.radius.sm,color:T.text}}/>
                <Bar dataKey="profit" radius={[4,4,0,0]}>
                  {weeklyProfit.map((e,i)=><Cell key={i} fill={e.profit>=0?"url(#tealBar)":T.red}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {topCategory && (
          <div style={{...S.card, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center"}}>
            <div style={{...S.h2, alignSelf:"flex-start"}}>Top Category</div>
            <div style={{fontSize:15, fontWeight:700, color:T.text, marginBottom:2}}>{topCategory[0]}</div>
            <div style={{fontSize:16, fontWeight:700, color:T.teal, marginBottom:8}}>{fmt(topCategory[1])}</div>
            <div style={{position:"relative", width:96, height:96}}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={[{v:topCategoryPct},{v:100-topCategoryPct}]} cx="50%" cy="50%"
                    innerRadius={34} outerRadius={44} dataKey="v" startAngle={90} endAngle={-270} stroke="none">
                    <Cell fill={T.teal} />
                    <Cell fill={T.border} />
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column"}}>
                <div style={{fontSize:18, fontWeight:700, color:T.text}}>{topCategoryPct.toFixed(0)}%</div>
              </div>
            </div>
            <div style={{fontSize:11, color:T.textFaint, marginTop:6}}>of total profit</div>
          </div>
        )}
      </div>

      {bestSets.length>0 && (
        <div style={{...S.card, marginBottom:20}}>
          <div style={S.h2}>Best Selling Sets</div>
          <ResponsiveContainer width="100%" height={Math.max(120,bestSets.length*40)}>
            <BarChart layout="vertical" data={bestSets} margin={{top:0,right:20,left:0,bottom:0}}>
              <XAxis type="number" tick={{fill:T.textFaint, fontSize:11}} axisLine={false} tickLine={false}/>
              <YAxis dataKey="name" type="category" tick={{fill:T.textMuted, fontSize:11}} axisLine={false} tickLine={false} width={100}/>
              <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:T.radius.sm,color:T.text}}/>
              <Bar dataKey="count" fill={T.primary} radius={[0,4,4,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {(gData.length>0||chData.length>0||pmData.length>0) && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
          {gData.length>0 && (
            <div style={S.card}>
              <div style={S.h2}>Buyers</div>
              <ResponsiveContainer width="100%" height={100}>
                <PieChart>
                  <Pie data={gData} cx="50%" cy="50%" innerRadius={25} outerRadius={40} dataKey="value" paddingAngle={2}>
                    {gData.map((_,i)=><Cell key={i} fill={GC[i%GC.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{background:T.surface,border:`1px solid ${T.border}`,borderRadius:T.radius.sm,color:T.text,fontSize:12}}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{marginTop:6}}>
                {gData.map((g,i)=>(
                  <div key={g.name} style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                    <div style={{width:8,height:8,borderRadius:2,background:GC[i],flexShrink:0}}/>
                    <span style={{fontSize:11,color:T.textFaint,flex:1}}>{g.name}</span>
                    <span style={{fontSize:11,fontFamily:"'DM Mono',monospace",color:T.text}}>{g.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {chData.length>0 && (
            <div style={S.card}>
              <div style={S.h2}>Channels</div>
              {chData.sort((a,b)=>b.value-a.value).map(c=>(
                <div key={c.name} style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                  <span style={{fontSize:12,color:T.textFaint}}>{c.name}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:T.primary}}>{c.value}</span>
                </div>
              ))}
            </div>
          )}
          {pmData.length>0 && (
            <div style={S.card}>
              <div style={S.h2}>Payment Methods</div>
              {pmData.sort((a,b)=>b.value-a.value).map(c=>(
                <div key={c.name} style={{display:"flex",justifyContent:"space-between",marginBottom:6,alignItems:"center"}}>
                  <span style={{fontSize:12,color:T.textFaint}}>{c.name}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:12,color:T.primary}}>{c.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {nonMovers.length>0 && (
        <div style={S.card}>
          <div style={S.h2}>Non-Movers (30+ days)</div>
          {nonMovers.sort((a,b)=>daysSince(b.AcquisitionDate)-daysSince(a.AcquisitionDate)).slice(0,6).map(item=>(
            <div key={item.ID} style={{
              display:"flex", gap:10, alignItems:"center",
              background: T===LIGHT ? "#FFF4E8" : "rgba(253,186,116,0.08)",
              border:`1px solid ${T===LIGHT ? "#FBD9A8" : "rgba(253,186,116,0.25)"}`,
              borderRadius:T.radius.sm, padding:"10px 12px", marginBottom:8,
            }}>
              {item.ImageURL && <img src={item.ImageURL} alt="" style={{width:32,height:44,objectFit:"cover",borderRadius:4,flexShrink:0}} />}
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13,fontWeight:500,color:T.text}}>{item.CardName}</div>
                <div style={{fontSize:11,color:T.textMuted}}>{item.Set||"No set"} · {item.Condition}{item.LotName&&` · ${item.LotName}`}</div>
              </div>
              <div style={{textAlign:"right", flexShrink:0}}>
                <div style={{fontSize:13,color:T.amber,fontWeight:600}}>{fmt(item.ListPrice)}</div>
                <div style={{fontSize:11,color:T.amber}}>{daysSince(item.AcquisitionDate)}d</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {inventory.length===0 && (
        <div style={{textAlign:"center",padding:"60px 0",color:"#333"}}>
          <div style={{fontSize:40,marginBottom:12}}>⬡</div>
          <div style={{fontSize:15,marginBottom:6}}>No inventory yet</div>
          <div style={{fontSize:13,color:"#444"}}>Tap + Add to get started</div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
// ─── QR SELL OVERLAY ─────────────────────────────────────────────────────────
function QRSellOverlay({ item: initialItem, inventory, activeShow, onSell, onClose, today }) {
  const { T } = useTheme();
  const [screen, setScreen]       = useState("sell");
  const [item, setItem]           = useState(initialItem);
  const [price, setPrice]         = useState(String(Math.round(initialItem.ListPrice||0)));
  const [soldCount, setSoldCount] = useState(0);
  const [lastSold, setLastSold]   = useState(null);
  const [scanError, setScanError] = useState("");
  const [saving, setSaving]       = useState(false);
  const priceRef = useRef(null);

  const cost     = parseFloat(item.AcquisitionCost)||0;
  const saleP    = parseFloat(price)||0;
  const profit   = saleP - cost;
  const belowMin = item.MinSellPrice>0 && saleP < item.MinSellPrice;
  const isSold   = item.Status === "Sold";

  const handleSell = async () => {
    if (!saleP || saving || isSold) return;
    setSaving(true);
    const { TaxRate, TaxAmount } = computeTax(saleP, vendor);
    await onSell(item.ID, {
      Status:"Sold", SoldPrice:saleP, SoldDate:today,
      SoldChannel:"Local Show", TaxRate, TaxAmount,
    });
    removeDealBoardListingIfListed(item.ID);
    setItem(i => ({...i, Status:"Sold"}));
    setLastSold({ name:item.CardName, price:saleP });
    setSoldCount(c => c+1);
    setSaving(false);
    setScreen("success");
  };

  const handleScan = (raw) => {
    if (!raw) return;
    let code = raw;
    try {
      const url = new URL(raw);
      code = url.searchParams.get("s") || url.searchParams.get("sell") || raw;
    } catch (_) {}
    const cleanId = code.replace(/^cv_/, "");
    const found =
      inventory.find(c => c.QRCode === code && c.Status !== "Sold") ||
      inventory.find(c => c.ID === cleanId && c.Status !== "Sold");
    if (found) {
      setItem(found);
      setPrice(String(Math.round(found.ListPrice||0)));
      setScanError("");
      setScreen("sell");
      setTimeout(() => priceRef.current?.select(), 80);
    } else {
      setScanError(`No card found for: ${code}`);
      setScreen("scan");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && screen === "sell") handleSell();
    if (e.key === "Escape") onClose();
  };

  // ── SCREEN 2: Success ──────────────────────────────────────────────────────
  if (screen === "success") return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"linear-gradient(135deg,#042b14,#0a1f0e)",
      display:"flex", flexDirection:"column",
      fontFamily:"'Sora',sans-serif",
    }}>
      <style>{`@keyframes qrPop{from{transform:scale(0.8);opacity:0}to{transform:scale(1);opacity:1}}`}</style>

      <div style={{display:"flex",justifyContent:"flex-end",padding:"16px 20px",flexShrink:0}}>
        <button onClick={onClose}
          style={{background:"none",border:"0.5px solid rgba(74,222,128,0.2)",
            borderRadius:8,color:"rgba(74,222,128,0.5)",fontSize:13,fontWeight:600,
            padding:"6px 14px",cursor:"pointer"}}>
          ✕ Exit
        </button>
      </div>

      <div style={{flex:1,display:"flex",flexDirection:"column",
        alignItems:"center",justifyContent:"center",padding:24,
        animation:"qrPop 0.2s ease"}}>
        <div style={{fontSize:80,marginBottom:20,lineHeight:1}}>✓</div>
        <div style={{fontSize:28,fontWeight:700,color:"#4ade80",marginBottom:8}}>
          Sold!
        </div>
        <div style={{fontSize:32,fontWeight:700,color:"#ffffff",marginBottom:12,
          fontFamily:"'DM Mono',monospace"}}>
          ${lastSold?.price}
        </div>
        <div style={{fontSize:16,color:"rgba(74,222,128,0.6)",marginBottom:4,
          textAlign:"center"}}>
          {lastSold?.name}
        </div>
        {soldCount > 1 && (
          <div style={{fontSize:12,color:"rgba(74,222,128,0.4)",marginTop:8}}>
            {soldCount} cards sold this session
          </div>
        )}
        {activeShow && (
          <div style={{fontSize:11,color:"rgba(74,222,128,0.35)",marginTop:6}}>
            Recorded to {activeShow.Name}
          </div>
        )}
      </div>

      <div style={{padding:"16px 20px 40px",flexShrink:0}}>
        <button onClick={()=>setScreen("scan")}
          style={{
            width:"100%",padding:"20px",borderRadius:14,border:"none",
            background:"#4ade80",color:"#042b14",
            fontSize:18,fontWeight:700,fontFamily:"'Sora',sans-serif",
            cursor:"pointer",
            boxShadow:"0 4px 24px rgba(74,222,128,0.35)",
          }}>
          📷 Scan Another Card
        </button>
      </div>
    </div>
  );

  // ── SCREEN 3: Ready to scan ────────────────────────────────────────────────
  if (screen === "scan") return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"linear-gradient(135deg,#080e1a,#0d1526)",
      display:"flex", flexDirection:"column",
      fontFamily:"'Sora',sans-serif",
    }}>
      <div style={{position:"absolute",inset:0,zIndex:20}}>
        <QRScanner
          onScan={raw => handleScan(raw)}
          onFail={() => setScreen("scan")}
          onClose={() => setScreen(lastSold ? "success" : "sell")}
        />
      </div>

      {scanError && (
        <div style={{position:"absolute",bottom:100,left:20,right:20,zIndex:30,
          background:"rgba(248,113,113,0.95)",borderRadius:12,
          padding:"12px 16px",fontSize:13,color:"#fff",fontWeight:600,
          textAlign:"center"}}>
          {scanError}
        </div>
      )}
    </div>
  );

  // ── SCREEN 1: Sell ─────────────────────────────────────────────────────────
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:500,
      background:"linear-gradient(135deg,#080e1a,#0d1526)",
      display:"flex", flexDirection:"column",
      fontFamily:"'Sora',sans-serif",
    }}
    onKeyDown={handleKeyDown} tabIndex={-1}>

      <style>{`@keyframes qrFadeIn{from{opacity:0}to{opacity:1}}`}</style>

      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        padding:"16px 20px",borderBottom:"0.5px solid rgba(203,226,254,0.1)",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{fontSize:11,fontWeight:600,color:"rgba(203,226,254,0.4)",
            textTransform:"uppercase",letterSpacing:"0.1em"}}>QR Quick Sell</div>
          {activeShow && (
            <div style={{background:"rgba(60,102,238,0.2)",border:"0.5px solid rgba(60,102,238,0.4)",
              borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:600,color:"#88bdf2"}}>
              📍 {activeShow.Name}
            </div>
          )}
          {!activeShow && (
            <div style={{background:"rgba(251,191,36,0.1)",border:"0.5px solid rgba(251,191,36,0.3)",
              borderRadius:20,padding:"2px 10px",fontSize:11,color:"#fbbf24"}}>
              ⚠ No active show
            </div>
          )}
          {soldCount > 0 && (
            <div style={{background:"rgba(74,222,128,0.15)",border:"0.5px solid #166534",
              borderRadius:20,padding:"2px 10px",fontSize:11,fontWeight:700,color:"#4ade80"}}>
              {soldCount} sold
            </div>
          )}
        </div>
        <button onClick={onClose}
          style={{background:"none",border:"0.5px solid rgba(203,226,254,0.15)",
            borderRadius:8,color:"rgba(203,226,254,0.5)",fontSize:13,fontWeight:600,
            padding:"6px 14px",cursor:"pointer"}}>
          ✕ Exit
        </button>
      </div>

      {isSold && (
        <div style={{margin:"16px 20px 0",background:"rgba(248,113,113,0.1)",
          border:"0.5px solid rgba(248,113,113,0.3)",borderRadius:12,
          padding:"12px 16px",fontSize:13,color:"#f87171",fontWeight:600,flexShrink:0}}>
          ⚠ This card is already marked as sold
        </div>
      )}

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",
        flexDirection:"column",gap:16,maxWidth:520,margin:"0 auto",width:"100%"}}>

        <div style={{display:"flex",gap:16,alignItems:"flex-start"}}>
          {item.ImageURL ? (
            <img src={item.ImageURL} alt={item.CardName}
              style={{width:90,height:126,objectFit:"cover",borderRadius:10,flexShrink:0,
                border:"0.5px solid rgba(203,226,254,0.15)",
                boxShadow:"0 4px 20px rgba(0,0,0,0.4)"}} />
          ) : (
            <div style={{width:90,height:126,borderRadius:10,flexShrink:0,
              background:"rgba(60,102,238,0.1)",border:"0.5px solid rgba(60,102,238,0.2)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:28,color:"rgba(60,102,238,0.4)"}}>⬡</div>
          )}
          <div style={{flex:1,minWidth:0}}>
            <div style={{fontSize:18,fontWeight:700,color:"#e8effe",
              lineHeight:1.3,marginBottom:6}}>{item.CardName}</div>
            <div style={{fontSize:12,color:"rgba(203,226,254,0.5)",marginBottom:4}}>
              {[item.Set,item.Number?`#${item.Number}`:null,item.Condition]
                .filter(Boolean).join(" · ")}
            </div>
            {item.Language && item.Language!=="English" && (
              <div style={{fontSize:11,color:"#3C66EE",fontWeight:600}}>{item.Language}</div>
            )}
          </div>
        </div>

        <div>
          <div style={{fontSize:10,fontWeight:600,color:"rgba(203,226,254,0.4)",
            textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>Sale Price</div>
          <div style={{position:"relative"}}>
            <span style={{position:"absolute",left:16,top:"50%",transform:"translateY(-50%)",
              fontSize:24,fontWeight:700,color:"rgba(203,226,254,0.4)"}}>$</span>
            <input ref={priceRef} type="number" value={price}
              onChange={e=>setPrice(e.target.value)}
              onFocus={e=>e.target.select()}
              style={{width:"100%",background:"rgba(60,102,238,0.1)",
                border:`2px solid ${belowMin?"#f87171":saleP>0?"#3C66EE":"rgba(60,102,238,0.3)"}`,
                borderRadius:14,color:"#e8effe",
                padding:"18px 16px 18px 44px",
                fontSize:36,fontWeight:700,fontFamily:"'DM Mono',monospace",
                outline:"none",boxSizing:"border-box",transition:"border-color 0.15s"}}
              autoFocus inputMode="decimal" />
          </div>
          <div style={{display:"flex",gap:6,marginTop:8,flexWrap:"wrap"}}>
            {[item.ListPrice,item.MinSellPrice].filter(p=>p&&p>0).map((p,i)=>(
              <button key={i} onClick={()=>setPrice(String(Math.round(p)))}
                style={{background:"rgba(60,102,238,0.15)",border:"0.5px solid rgba(60,102,238,0.3)",
                  borderRadius:8,padding:"5px 12px",fontSize:12,fontWeight:600,
                  color:"#88bdf2",cursor:"pointer"}}>
                {i===0?"List":"Min"} ${Math.round(p)}
              </button>
            ))}
            {belowMin&&<span style={{fontSize:11,color:"#f87171",padding:"5px 0",alignSelf:"center"}}>
              ⚠ Below min
            </span>}
          </div>
        </div>

        {saleP>0&&cost>0&&(
          <div style={{display:"flex",gap:8}}>
            <div style={{flex:1,background:"rgba(255,255,255,0.04)",borderRadius:10,
              padding:"10px 14px",border:"0.5px solid rgba(203,226,254,0.08)"}}>
              <div style={{fontSize:9,color:"rgba(203,226,254,0.4)",
                textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Cost</div>
              <div style={{fontSize:15,fontWeight:700,color:"#f87171"}}>${cost}</div>
            </div>
            <div style={{flex:1,
              background:profit>=0?"rgba(74,222,128,0.08)":"rgba(248,113,113,0.08)",
              borderRadius:10,padding:"10px 14px",
              border:`0.5px solid ${profit>=0?"rgba(74,222,128,0.2)":"rgba(248,113,113,0.2)"}`}}>
              <div style={{fontSize:9,color:"rgba(203,226,254,0.4)",
                textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>Profit</div>
              <div style={{fontSize:15,fontWeight:700,color:profit>=0?"#4ade80":"#f87171"}}>
                {profit>=0?"+":""}{fmt(profit)}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{padding:"16px 20px 32px",background:"rgba(8,14,26,0.95)",
        borderTop:"0.5px solid rgba(203,226,254,0.08)",flexShrink:0}}>
        <button onClick={handleSell}
          disabled={!saleP||saving||isSold}
          style={{
            width:"100%",padding:"20px",borderRadius:14,border:"none",
            background:!saleP||isSold?"rgba(60,102,238,0.2)":saving?"rgba(60,102,238,0.6)":"#3C66EE",
            color:!saleP||isSold?"rgba(203,226,254,0.3)":"#ffffff",
            fontSize:20,fontWeight:700,fontFamily:"'Sora',sans-serif",
            cursor:!saleP||isSold?"not-allowed":"pointer",
            transition:"all 0.15s",
            boxShadow:saleP&&!isSold?"0 4px 24px rgba(60,102,238,0.45)":"none",
          }}>
          {saving?"Confirming…":isSold?"Already Sold":`Sell  ·  $${price||"0"}`}
        </button>
      </div>
    </div>
  );
}


function upsertInventoryRow(list, saved) {
  const idx = list.findIndex(i => i.ID === saved.ID);
  if (idx === -1) return [...list, saved];
  const merged = { ...saved };
  for (const [k, v] of Object.entries(list[idx])) {
    if (v !== "" && v !== null && v !== undefined) merged[k] = v;
  }
  const next = list.slice();
  next[idx] = merged;
  return next;
}


// ─── APP ──────────────────────────────────────────────────────────────────────
function AppInner() {
  const { toast, confirmAction, showClaimBanner, openUpgradeModal, _showUpgradeModal, _closeUpgradeModal,
    openDealBoardPlusModal, _showDealBoardPlusModal, _closeDealBoardPlusModal } = useNotify();
  const [user, setUser]           = useState(null);
  const [vendor, setVendor]       = useState(null);
  const [priceUpdates, setPriceUpdates] = useState([]);
  const [showPriceUpdates, setShowPriceUpdates] = useState(false);

  useEffect(() => {
    if (!vendor?.id || IS_DEMO) return;
    getUnseenPriceUpdates(vendor).then(updates => {
      if (updates.length > 0) {
        setPriceUpdates(updates);
        setShowPriceUpdates(true);
      }
    });
  }, [vendor?.id]);
  const [needsAccountSetup, setNeedsAccountSetup] = useState(false);
  const [accountSuspended, setAccountSuspended] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userRole, setUserRole]   = useState(null);
  const [vendorUserId, setVendorUserId] = useState(null);
  const [locations, setLocations] = useState([]);
  const [myLocationIds, setMyLocationIds] = useState(null);
  const [activeLocationId, setActiveLocationId] = useState(() => localStorage.getItem("vs_active_location") || null);

  const isStaff = userRole === "staff";
  const isTeamAdmin = userRole === "admin" || userRole === null;

  // ── Plan / feature gating ─────────────────────────────────────────────────
  const planValue = useMemo(() => {
    const role = vendor?.role || "vendor";
    const plan = vendor?.plan || "free";
    const isAdmin = role === "admin";
    const isPaid  = isAdmin || plan === "monthly" || plan === "annual" || plan === "trial";
    const isFree  = !isPaid;
    const tier = resolveTier(vendor);
    const isEnterprise = isAdmin || tier === "enterprise";
    const canUse  = (feature) => canUseFeature(feature, { plan, tier, isAdmin });
    return { isPaid, isAdmin, isFree, canUse, vendor, plan, role, tier, isEnterprise,
      inventoryLimit: isFree ? FREE_INVENTORY_LIMIT : Infinity };
  }, [vendor]);

  useEffect(() => {
    if (!vendor?.id) return;
    readLocations().then(setLocations).catch(() => {});
  }, [vendor?.id]);

  useEffect(() => {
    if (!vendorUserId || userRole !== "staff") { setMyLocationIds(null); return; }
    getStaffLocationIds(vendorUserId).then(setMyLocationIds).catch(() => setMyLocationIds([]));
  }, [vendorUserId, userRole]);

  useEffect(() => {
    if (!locations.length) return;
    const allowed = myLocationIds && myLocationIds.length ? myLocationIds : locations.map(l => l.ID);
    const saved = localStorage.getItem("vs_active_location");
    const validSaved = saved && allowed.includes(saved) ? saved : null;
    const next = validSaved || allowed[0];
    if (next && next !== activeLocationId) setActiveLocationId(next);
  }, [locations, myLocationIds]); // eslint-disable-line

  const setActiveLocation = (id) => {
    setActiveLocationId(id);
    localStorage.setItem("vs_active_location", id);
  };
  const effectiveLocationId = activeLocationId || locations[0]?.ID || null;
  const [loading, setLoading]     = useState(true);
  const [loadMsg, setLoadMsg]     = useState("Loading…");
  const [inventory, setInventory] = useState([]);
  const [lots, setLots]           = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [buyers, setBuyers]         = useState([]);
  const [trades, setTrades]       = useState([]);
  const [shows, setShows]         = useState([]);
  const [trialOfferShow, setTrialOfferShow] = useState(null);
  const [showQuickScan, setShowQuickScan] = useState(false);
  const [showAddCustomItem, setShowAddCustomItem] = useState(false);
  const [trialChecked, setTrialChecked] = useState(false);
  const [showItems, setShowItems] = useState([]);
  const [tab, setTab]             = useState("dashboard");
  const [navOpen, setNavOpen]     = useState(false);
  const windowWidth               = useWindowWidth();
  const isWide                    = windowWidth >= 768;
  const [drawer, setDrawer]       = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [syncMsg, setSyncMsg]     = useState("");
  const [isDark, setIsDark]       = useState(() => localStorage.getItem("vs_theme") !== "light");
  const [showModeActive, setShowModeActive] = useState(false);
  const [showQRGenerator, setShowQRGenerator]   = useState(false);
  const [priceHistory, setPriceHistory]         = useState({});

  const pendingSellId = useRef(null);
  const [isQrVisit, setIsQrVisit] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sellParam = params.get("s") || params.get("sell");
    if (sellParam) {
      pendingSellId.current = sellParam;
      setIsQrVisit(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (IS_DEMO || !vendor || vendor.trial_used || planValue.isPaid) { setTrialOfferShow(null); return; }
    const active = shows.find(s => s.Status === "Active");
    if (active) setTrialOfferShow(active);
  }, [vendor, shows, planValue.isPaid]);

  const [showTrialExpiredModal, setShowTrialExpiredModal] = useState(false);
  useEffect(() => {
    if (IS_DEMO || !vendor?.id || trialChecked) return;
    setTrialChecked(true);
    if (vendor.plan !== "trial") return;
    checkTrialStatus().then(result => {
      if (result?.expired) setShowTrialExpiredModal(true);
    }).catch(() => {});
  }, [vendor?.id, vendor?.plan]);

  const T  = isDark ? DARK : LIGHT;
  const S  = makeStyles(T);

  // ── Vendor-aware business rules (fall back to config defaults) ─────────────
  const vendorRates = {
    tradeCreditRate: vendor?.trade_credit_rate ?? CONFIG.TRADE_CREDIT_RATE,
    cashBuyRate:     vendor?.cash_buy_rate     ?? CONFIG.CASH_BUY_RATE,
    markupRate:      vendor?.markup_rate       ?? MARKUP,
    breakevenBuffer: vendor?.breakeven_buffer  ?? CONFIG.BREAKEVEN_BUFFER,
  };
  const minSellDynamic = cost => Math.max(1, Math.ceil(cost * vendorRates.markupRate));

  const toggleTheme = () => {
    setIsDark(d => {
      const next = !d;
      localStorage.setItem("vs_theme", next ? "dark" : "light");
      return next;
    });
  };

  const handleLogin = useCallback(async () => {
    await signInWithGoogle();
  }, []);

  const loadData = useCallback(async (freshUser) => {
    setLoading(true); setLoadMsg("Loading…");
    try {
      // ── DEMO MODE: load demo data directly, skip auth ─────────────────────
      if (IS_DEMO) {
        const { vendor: demoVendor, inv, lots: demoLots, shows: demoShows,
                showItems: demoShowItems, consignors: demoCons,
                buyers: demoBuyers } = await loadDemoData();

        setVendor({ ...demoVendor, role:"vendor", plan:"monthly" });
        setInventory(inv);

        const lotWithCounts = demoLots.map(l => ({
          ...l, _itemCount: inv.filter(r => r.LotID === l.ID).length
        }));
        setLots(lotWithCounts);
        setTrades([]);
        setShows(demoShows);
        setShowItems(demoShowItems);
        setConsignors(demoCons);
        setBuyers(demoBuyers);
        setLoading(false); setLoadMsg("");
        return;
      }
      const [invRows, lotRows, tradeRows, showRows, showItemRows, consignorRows, buyerRows] = await Promise.all([
        readInventory().catch(e=>{console.error("readInventory failed:",e.message||e);return [];}),
        readLots().catch(e=>{console.error("readLots failed:",e.message||e);return [];}),
        readTrades().catch(e=>{console.error("readTrades failed:",e.message||e);return [];}),
        readShows().catch(e=>{console.error("readShows failed:",e.message||e);return [];}),
        readShowItems().catch(e=>{console.error("readShowItems failed:",e.message||e);return [];}),
        readConsignors().catch(e=>{console.error("readConsignors failed:",e.message||e);return [];}),
        readBuyers().catch(e=>{console.error("readBuyers failed:",e.message||e);return [];}),
      ]);

      const today = localDate();

      const updatedShows = await Promise.all(showRows.map(async show => {
        if (show.Status === "Upcoming" && show.StartDate && show.StartDate <= today) {
          await updateShow(show.ID, { Status: "Active" });
          return { ...show, Status: "Active" };
        }
        if (show.Status === "Active" && show.EndDate && show.EndDate < today) {
          await updateShow(show.ID, { Status: "Completed" });
          return { ...show, Status: "Completed" };
        }
        return show;
      }));

      const activeShowIds = new Set(
        updatedShows.filter(s => s.Status === "Active" || s.Status === "Upcoming").map(s => s.ID)
      );

      for (const card of invRows) {
        if (card.Status === "Reserved" && card.ShowID && !activeShowIds.has(card.ShowID)) {
          await updateInventoryItem(card.ID, { Status: "In Stock", ShowID: "", ShowName: "" });
          card.Status = "In Stock"; card.ShowID = ""; card.ShowName = "";
        }
      }
      const allShowItemIds = new Set(showItemRows.map(si => si.InventoryID));
      const nonActiveShowItemIds = new Set(
        showItemRows
          .filter(si => !activeShowIds.has(si.ShowID))
          .map(si => si.InventoryID)
      );
      for (const card of invRows) {
        if (card.Status === "Reserved" && nonActiveShowItemIds.has(card.ID) && !card.ShowID) {
          await updateInventoryItem(card.ID, { Status: "In Stock", ShowID: "", ShowName: "" });
          card.Status = "In Stock"; card.ShowID = ""; card.ShowName = "";
        }
      }

      const lotWithCounts = lotRows.map(l => ({
        ...l, _itemCount: invRows.filter(r => r.LotID === l.ID).length
      }));

      setInventory(invRows);
      setLots(lotWithCounts);
      setTrades(tradeRows);
      setShows(updatedShows);
      setShowItems(showItemRows);
      setConsignors(consignorRows);
      setBuyers(buyerRows);

      if (pendingSellId.current) {
        const raw = pendingSellId.current;
        let code = raw;
        try {
          const u = new URL(raw);
          code = u.searchParams.get("s") || u.searchParams.get("sell") || raw;
        } catch(_) {}
        code = code.replace(/^cv_/, "");

        const card = invRows.find(i => i.QRCode === code)
          || invRows.find(i => i.QRCode === raw)
          || invRows.find(i => i.ID === code)
          || invRows.find(i => i.ID === raw);

        if (card) {
          setActiveItem(card);
          setDrawer("qrsell");
          setIsQrVisit(true);
        } else {
          setIsQrVisit(false);
          console.warn("QR code not found in inventory:", code);
        }
        pendingSellId.current = null;
      }

      try {
        const vendorRow = await getVendor();
        if (vendorRow && !vendorRow.business_name) {
          setNeedsAccountSetup(true);
        } else if (vendorRow) {
          setVendor(vendorRow);
          if (vendorRow.onboarding_tour_seen === false) {
            setShowOnboarding(true);
          }
          if (vendorRow.sample_data_seeded === false) {
            seedSampleData(vendorRow.id).catch(e =>
              console.warn("Could not seed sample data:", e.message));
          }
          const currentUser = freshUser || user;
          if ((vendorRow.plan === "monthly" || vendorRow.plan === "annual" || vendorRow.plan === "trial") && currentUser?.email) {
            const roleRow = await getVendorRole(currentUser.email, vendorRow.id).catch(() => null);
            setUserRole(roleRow?.role || null);
            setVendorUserId(roleRow?.id || null);
            if (roleRow?.role === "staff") {
              setTab(t => t === "dashboard" ? "inventory" : t);
            }
          }
          const history = await getAllPriceHistory(vendorRow.id);
          const historyMap = {};
          history.forEach(h => {
            if (!historyMap[h.inventory_id]) historyMap[h.inventory_id] = [];
            historyMap[h.inventory_id].push(h);
          });
          setPriceHistory(historyMap);
        }
      } catch(e) {
        console.log("Vendor load:", e.message);
        if (e.code === "ACCOUNT_SUSPENDED") {
          setAccountSuspended(true);
        } else if (!isVendorAlias((freshUser || user)?.email)) {
          setNeedsAccountSetup(true);
        }
      }


    } catch(e) { console.error("loadData error:", e); }
    setLoading(false); setLoadMsg("");
  }, []);



  useEffect(() => {
    let realtimeChannel = null;
    let statusChannel = null;
    let cancelled = false;
    let starting = false;

    const startRealtime = async () => {
      if (starting) return;
      starting = true;
      const v = await getVendor().catch(() => null);
      if (!v || cancelled) { starting = false; return; }

      if (statusChannel) { supabase.removeChannel(statusChannel); statusChannel = null; }
      if (realtimeChannel) { supabase.removeChannel(realtimeChannel); realtimeChannel = null; }

      statusChannel = supabase
        .channel(`vendor-status-${v.id}`)
        .on("postgres_changes", {
          event: "UPDATE", schema: "public", table: "vendors", filter: `id=eq.${v.id}`,
        }, (payload) => {
          if (payload.new?.status === "suspended") setAccountSuspended(true);
          if (payload.new?.plan && payload.new.plan !== payload.old?.plan) {
            setVendor(prev => prev ? { ...prev, plan: payload.new.plan } : prev);
          }
        })
        .subscribe();

      realtimeChannel = subscribeToAll(v.id, {
        inventory: (payload) => {
          const { eventType, new: newRow, old: oldRow } = payload;
          if (eventType === "INSERT") {
            setInventory(inv => {
              if (inv.find(i => i.ID === newRow.id)) return inv;
              return [{
                ID:                        newRow.id,
                Type:                      newRow.type                      || "",
                CardName:                  newRow.card_name                 || "",
                Set:                       newRow.set_name                  || "",
                Number:                    newRow.card_number               || "",
                Rarity:                    newRow.rarity                    || "",
                Language:                  newRow.language                  || "English",
                Condition:                 newRow.condition                 || "",
                Grade:                     newRow.grade                     || "",
                Quantity:                  newRow.quantity                  ?? 1,
                Source:                    newRow.source                    || "",
                AcquisitionCost:           newRow.acquisition_cost          ? parseFloat(newRow.acquisition_cost) : "",
                ListPrice:                 newRow.list_price                ? parseFloat(newRow.list_price)       : "",
                MinSellPrice:              newRow.min_sell_price            ? parseFloat(newRow.min_sell_price)   : "",
                MarketValueAtAcquisition:  newRow.market_value_at_acquisition ? parseFloat(newRow.market_value_at_acquisition) : "",
                AcquisitionDate:           newRow.acquisition_date          || "",
                LotID:                     newRow.lot_id                    || "",
                LotName:                   newRow.lot_name                  || "",
                LotItemNumber:             newRow.lot_item_number           || "",
                ShowID:                    newRow.show_id                   || "",
                ShowName:                  newRow.show_name                 || "",
                AcquiredShowID:            newRow.acquired_show_id          || "",
                ImageURL:                  newRow.image_url                 || "",
                QRCode:                    newRow.qr_code                   || "",
                Status:                    newRow.status                    || "In Stock",
                SoldPrice:                 newRow.sold_price                ? parseFloat(newRow.sold_price) : "",
                SoldDate:                  newRow.sold_date                 || "",
                SoldChannel:               newRow.sold_channel              || "",
                BuyerGender:               newRow.buyer_gender              || "",
                TradeCredit:               newRow.trade_credit              ? parseFloat(newRow.trade_credit) : "",
                TradeGain:                 newRow.trade_gain                ? parseFloat(newRow.trade_gain)   : "",
                Notes:                     newRow.notes                     || "",
                GradingCompany:            newRow.grading_company           || "",
                GradingSentDate:           newRow.grading_sent_date         || "",
                GradingReturnDate:         newRow.grading_return_date       || "",
                GradingEstimatedValue:     newRow.grading_estimated_value   ? parseFloat(newRow.grading_estimated_value) : "",
                GradingCost:               newRow.grading_cost              ? parseFloat(newRow.grading_cost) : "",
                GameType:                  newRow.game_type                 || "Pokemon",
                LorcanaInkColor:           newRow.lorcana_ink_color         || "",
                LorcanaCharacterVersion:   newRow.lorcana_character_version || "",
                LorcanaProperty:           newRow.lorcana_property          || "",
                LorcanaFoilType:           newRow.lorcana_foil_type         || "Normal",
                IsConsignment:             newRow.is_consignment            || false,
                IsOnline:                  newRow.is_online                 || false,
                ConsignorID:               newRow.consignor_id              || "",
                MinAcceptPrice:            newRow.min_accept_price          ? parseFloat(newRow.min_accept_price) : "",
                TradesAccepted:            newRow.trades_accepted           || false,
                CreatedAt:                 newRow.created_at,
                MarketPrice:               newRow.market_price              ? parseFloat(newRow.market_price) : null,
                MarketPriceUpdatedAt:      newRow.market_price_updated_at   || null,
                MarketPriceSource:         newRow.market_price_source       || null,
                MarketPriceData:           newRow.market_price_data         || null,
                TcggoId:                   newRow.tcggo_id                  || null,
              }, ...inv];
            });
          } else if (eventType === "UPDATE") {
            setInventory(inv => inv.map(i => {
              if (i.ID !== newRow.id) return i;
              const n = newRow;
              return {
                ...i,
                Type:                     n.type                           || i.Type,
                CardName:                 n.card_name                      || i.CardName,
                Set:                      n.set_name                       || i.Set,
                Number:                   n.card_number                    ?? i.Number,
                Rarity:                   n.rarity                         || i.Rarity,
                Language:                 n.language                       || i.Language,
                Condition:                n.condition                      || i.Condition,
                Grade:                    n.grade                          ?? i.Grade,
                Quantity:                 n.quantity                       ?? i.Quantity,
                Source:                   n.source                         || i.Source,
                AcquisitionCost:          n.acquisition_cost != null       ? parseFloat(n.acquisition_cost)         : i.AcquisitionCost,
                ListPrice:                n.list_price != null             ? parseFloat(n.list_price)               : i.ListPrice,
                MinSellPrice:             n.min_sell_price != null         ? parseFloat(n.min_sell_price)           : i.MinSellPrice,
                MarketValueAtAcquisition: n.market_value_at_acquisition != null ? parseFloat(n.market_value_at_acquisition) : i.MarketValueAtAcquisition,
                AcquisitionDate:          n.acquisition_date               || i.AcquisitionDate,
                LotID:                    n.lot_id                         !== undefined ? (n.lot_id   || "") : i.LotID,
                LotName:                  n.lot_name                       !== undefined ? (n.lot_name || "") : i.LotName,
                LotItemNumber:            n.lot_item_number                !== undefined ? (n.lot_item_number || "") : i.LotItemNumber,
                ShowID:                   n.show_id                        !== undefined ? (n.show_id  || "") : i.ShowID,
                ShowName:                 n.show_name                      !== undefined ? (n.show_name|| "") : i.ShowName,
                AcquiredShowID:           n.acquired_show_id               !== undefined ? (n.acquired_show_id || "") : i.AcquiredShowID,
                ImageURL:                 n.image_url                      !== undefined ? (n.image_url|| "") : i.ImageURL,
                QRCode:                   n.qr_code                        !== undefined ? (n.qr_code  || "") : i.QRCode,
                Status:                   n.status                         || i.Status,
                SoldPrice:                n.sold_price != null             ? parseFloat(n.sold_price)               : i.SoldPrice,
                SoldDate:                 n.sold_date                      !== undefined ? (n.sold_date || "") : i.SoldDate,
                SoldChannel:              n.sold_channel                   !== undefined ? (n.sold_channel || "") : i.SoldChannel,
                BuyerGender:              n.buyer_gender                   !== undefined ? (n.buyer_gender || "") : i.BuyerGender,
                TradeCredit:              n.trade_credit != null           ? parseFloat(n.trade_credit)             : i.TradeCredit,
                TradeGain:                n.trade_gain != null             ? parseFloat(n.trade_gain)               : i.TradeGain,
                Notes:                    n.notes                          !== undefined ? (n.notes || "") : i.Notes,
                GradingCompany:           n.grading_company                !== undefined ? (n.grading_company || "") : i.GradingCompany,
                GradingSentDate:          n.grading_sent_date              !== undefined ? (n.grading_sent_date || "") : i.GradingSentDate,
                GradingReturnDate:        n.grading_return_date            !== undefined ? (n.grading_return_date || "") : i.GradingReturnDate,
                GradingEstimatedValue:    n.grading_estimated_value != null ? parseFloat(n.grading_estimated_value) : i.GradingEstimatedValue,
                GradingCost:              n.grading_cost != null           ? parseFloat(n.grading_cost)             : i.GradingCost,
                GameType:                 n.game_type                      !== undefined ? (n.game_type || "Pokemon") : i.GameType,
                LorcanaInkColor:          n.lorcana_ink_color              !== undefined ? (n.lorcana_ink_color || "") : i.LorcanaInkColor,
                LorcanaCharacterVersion:  n.lorcana_character_version      !== undefined ? (n.lorcana_character_version || "") : i.LorcanaCharacterVersion,
                LorcanaProperty:          n.lorcana_property               !== undefined ? (n.lorcana_property || "") : i.LorcanaProperty,
                LorcanaFoilType:          n.lorcana_foil_type               !== undefined ? (n.lorcana_foil_type || "Normal") : i.LorcanaFoilType,
                IsConsignment:            n.is_consignment                 !== undefined ? !!n.is_consignment : i.IsConsignment,
                IsOnline:                 n.is_online                      !== undefined ? !!n.is_online      : i.IsOnline,
                ConsignPayoutStatus:      n.consign_payout_status          !== undefined ? (n.consign_payout_status||null) : i.ConsignPayoutStatus,
                ConsignorID:              n.consignor_id                   !== undefined ? (n.consignor_id || "") : i.ConsignorID,
                MinAcceptPrice:           n.min_accept_price != null       ? parseFloat(n.min_accept_price)         : i.MinAcceptPrice,
                TradesAccepted:           n.trades_accepted                !== undefined ? !!n.trades_accepted : i.TradesAccepted,
                MarketPrice:              n.market_price != null           ? parseFloat(n.market_price)             : i.MarketPrice,
                MarketPriceUpdatedAt:     n.market_price_updated_at        !== undefined ? (n.market_price_updated_at || null) : i.MarketPriceUpdatedAt,
                MarketPriceSource:        n.market_price_source            !== undefined ? (n.market_price_source || null) : i.MarketPriceSource,
                MarketPriceData:          n.market_price_data              !== undefined ? (n.market_price_data || null) : i.MarketPriceData,
                TcggoId:                  n.tcggo_id                       !== undefined ? (n.tcggo_id || null) : i.TcggoId,
              };
            }));
          } else if (eventType === "DELETE") {
            setInventory(inv => inv.filter(i => i.ID !== oldRow.id));
          }
        },
        lots:   () => readLots().then(setLots),
        shows:  (payload) => {
          if (payload?.table === "show_items") {
            const { eventType, new: newRow, old: oldRow } = payload;
            if (eventType === "INSERT" && newRow?.inventory_id) {
              setShowItems(si => {
                const exists = si.find(s => s.ShowID === newRow.show_id && s.InventoryID === newRow.inventory_id);
                if (exists) return si;
                return [...si, { ShowID: newRow.show_id, InventoryID: newRow.inventory_id }];
              });
            } else if (eventType === "DELETE" && oldRow?.inventory_id) {
              setShowItems(si => si.filter(s =>
                !(s.ShowID === oldRow.show_id && s.InventoryID === oldRow.inventory_id)
              ));
            } else {
              readShowItems().then(setShowItems);
            }
          } else {
            readShows().then(setShows);
            readShowItems().then(setShowItems);
          }
        },
        trades: () => readTrades().then(setTrades),
        claims: (payload) => {
          if (payload.eventType === "INSERT") {
            const c = payload.new;
            const isCardRequest = c.card_name?.startsWith("[REQUEST]");
            const displayName = (c.card_name || "").replace("[REQUEST] ", "");
            const priceBit = c.offer_price
              ? ` · offer $${parseFloat(c.offer_price).toFixed(0)}`
              : c.list_price
                ? ` · $${parseFloat(c.list_price).toFixed(0)}`
                : "";
            showClaimBanner(
              isCardRequest
                ? `New card request — ${displayName} from ${c.customer_name}`
                : `New claim offer — ${displayName}${priceBit} from ${c.customer_name}`
            );
          }
        },
      });
      starting = false;
    };

    // ── DEMO MODE: skip auth, load demo data directly ──────────────────────
    if (IS_DEMO) {
      loadData();
      return;
    }

    getSession().then(session => {
      if (session?.user) {
        setUser(session.user);
        loadData(session.user).then(() => startRealtime());
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = onAuthStateChange((event, session) => {
      if (event === "TOKEN_REFRESHED" || event === "SIGNED_IN") {
        if (realtimeChannel) {
          supabase.removeChannel(realtimeChannel);
          realtimeChannel = null;
        }
        if (statusChannel) {
          supabase.removeChannel(statusChannel);
          statusChannel = null;
        }
        if (session?.user) startRealtime();
      } else if (!session?.user) {
        setUser(null);
        setNeedsAccountSetup(false);
        setAccountSuspended(false);
        clearVendorCache();
        if (realtimeChannel) {
          supabase.removeChannel(realtimeChannel);
          realtimeChannel = null;
        }
        if (statusChannel) {
          supabase.removeChannel(statusChannel);
          statusChannel = null;
        }
        setInventory([]); setLots([]); setTrades([]);
        setShows([]); setShowItems([]);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      if (realtimeChannel) supabase.removeChannel(realtimeChannel);
      if (statusChannel) supabase.removeChannel(statusChannel);
    };
  }, []);

  const saveItem = async (data) => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    setSyncMsg("Saving…");
    if (data.Set === "__new__") {
      toast("Please finish entering the new set name before saving.", "alert");
      setSyncMsg("");
      return;
    }
    if (!data.ID && planValue.isFree) {
      const activeCount = inventory.filter(i => i.Status !== "Sold" && i.Status !== "Deleted").length;
      if (activeCount >= FREE_INVENTORY_LIMIT) {
        setSyncMsg("");
        toast(
          `You've reached the ${FREE_INVENTORY_LIMIT} card limit on the Free plan. Go to Settings to upgrade to Pro.`,
          "alert"
        );
        return;
      }
    }
    if (data.ID) {
      const existing = inventory.find(i => i.ID === data.ID);
      if (existing && data.ListPrice && parseFloat(data.ListPrice) !== parseFloat(existing.ListPrice)) {
        logListPriceChange(existing, existing.ListPrice, data.ListPrice).catch(()=>{});
        if (vendor?.id && user?.email) {
          writeAuditLog(vendor.id, user.email, "card.price_changed",
            data.ID, data.CardName, { list_price: existing.ListPrice }, { list_price: data.ListPrice });
        }
      }
      await updateInventoryItem(data.ID, { ...data, Category: deriveCategory(data) });
      setInventory(inv => inv.map(i => i.ID === data.ID ? { ...i, ...data, Category: deriveCategory(data) } : i));
      toast("Card updated", "update");
      if (vendor?.id && user?.email) {
        writeAuditLog(vendor.id, user.email, "card.edited",
          data.ID, data.CardName, null, null);
      }
      const conditionChanged = existing && data.Condition && data.Condition !== existing.Condition;
      const gradingChanged = existing && (
        (data.GradingCompany || "") !== (existing.GradingCompany || "") ||
        (data.Grade || "") !== (existing.Grade || "")
      );
      if (conditionChanged || gradingChanged) {
        refreshSingleCardPrice(data.ID).catch(() => {});
      }
    } else {
      const dataWithCreator = { ...data, CreatedBy: user?.email || null,
        LocationID: data.LocationID || effectiveLocationId, Category: deriveCategory(data) };
      const saved = await addInventoryItem(dataWithCreator);
      setInventory(inv => upsertInventoryRow(inv, saved));
      if (saved.LotID) setLots(ls=>ls.map(l=>l.ID===saved.LotID?{...l,_itemCount:(l._itemCount||0)+1}:l));
      toast("Card created", "success");
      if (vendor?.id && user?.email) {
        writeAuditLog(vendor.id, user.email, "card.added",
          saved.ID, saved.CardName, null, { card_name: saved.CardName, set: saved.Set });
      }
    }
    setSyncMsg("");
  };

  const updateItem = async (id, updates) => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    setSyncMsg("Saving…");
    await updateInventoryItem(id, updates);
    setInventory(inv=>inv.map(i=>i.ID===id?{...i,...updates}:i));
    setSyncMsg("");
  };

  const deleteItem = async (id) => {
    if (isStaff) {
      const card = inventory.find(i => i.ID === id);
      if (card) {
        const createdByMe = card.CreatedBy === user?.email;
        const withinDay   = card.AcquisitionDate &&
          (Date.now() - new Date(card.CreatedAt || card.AcquisitionDate).getTime()) < 86400000;
        if (!createdByMe || !withinDay) {
          toast("Staff can only delete cards they added within the last 24 hours.", "alert");
          return;
        }
      }
    }
    if (!await confirmAction("Remove this item?", {confirmLabel:"Remove Item"})) return;
    const card = inventory.find(i => i.ID === id);
    setSyncMsg("Removing…");
    await deleteInventoryItem(id);
    setInventory(inv=>inv.filter(i=>i.ID!==id));
    setSyncMsg("");
    toast("Card deleted", "success");
    if (vendor?.id && user?.email) {
      writeAuditLog(vendor.id, user.email, "card.deleted",
        id, card?.CardName || "Unknown card", { card_name: card?.CardName, set: card?.Set }, null);
    }
  };

  const handleNewLot = async (lotData) => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    setSyncMsg("Saving…");
    const saved = await addLot(lotData);
    setLots(ls=>[...ls,{...saved,_itemCount:0}]);
    setSyncMsg("");
    return saved;
  };

  const handleUpdateLot = async (id, updates) => {
    setSyncMsg("Saving…");
    await updateLot(id, updates);
    setLots(ls=>ls.map(l=>l.ID===id?{...l,...updates}:l));
    setSyncMsg("");
  };

  const handleAddConsignor = async (data) => {
    if (IS_DEMO) { demoBlockToast(toast); return null; }
    setSyncMsg("Saving…");
    const saved = await addConsignor(data);
    setConsignors(cs=>[...cs, saved]);
    setSyncMsg("");
    return saved;
  };

  const handleUpdateConsignor = async (id, updates) => {
    setSyncMsg("Saving…");
    await updateConsignor(id, updates);
    setConsignors(cs=>cs.map(c=>c.ID===id?{...c,...updates}:c));
    setSyncMsg("");
  };

  const handleDeleteConsignor = async (id) => {
    setSyncMsg("Saving…");
    await deleteConsignor(id);
    setConsignors(cs=>cs.filter(c=>c.ID!==id));
    setSyncMsg("");
  };

  const handleImportItem = async (item) => {
    if (IS_DEMO) { demoBlockToast(toast); return null; }
    const saved = await addInventoryItem({ ...item,
      LocationID: item.LocationID || effectiveLocationId, Category: deriveCategory(item) });
    setInventory(inv => upsertInventoryRow(inv, saved));
    if (saved.LotID) setLots(ls => ls.map(l => l.ID===saved.LotID ? {...l, _itemCount:(l._itemCount||0)+1} : l));
    if (planValue.isFree) checkNearingCapEmail();
    return saved;
  };

  const handleAddTrade = async (trade) => {
    setSyncMsg("Saving…");
    const saved = await addTrade(trade);
    setTrades(t=>[...t,saved]);
    setSyncMsg("");
  };

  const handleAddShow = async (show) => {
    if (IS_DEMO) { demoBlockToast(toast); return; }
    const isFirstShow = shows.length === 0;
    setSyncMsg("Saving…");
    const saved = await addShow(show);
    setShows(s=>[...s,saved]);
    setSyncMsg("");
    if (isFirstShow && planValue.isFree) {
      setTimeout(() => openUpgradeModal(), 900);
    }
  };

  const handleUpdateShow = async (id, updates) => {
    setSyncMsg("Saving…");
    await updateShow(id, updates);
    setShows(s=>s.map(sh=>sh.ID===id?{...sh,...updates}:sh));
    setSyncMsg("");
  };

  const handleAddShowItem = async (showId, inventoryId) => {
    setSyncMsg("Saving…");
    const saved = await addShowItem(showId, inventoryId);
    const mapped = saved
      ? { ShowID: saved.show_id, InventoryID: saved.inventory_id, VendorID: saved.vendor_id }
      : { ShowID: showId, InventoryID: inventoryId };
    setShowItems(si => {
      if (si.some(x => x.ShowID === showId && x.InventoryID === inventoryId)) return si;
      return [...si, mapped];
    });
    const show = shows.find(s => s.ID === showId);
    await updateInventoryItem(inventoryId, {
      Status: "Reserved",
      ShowID: showId,
      ShowName: show?.Name || "",
    });
    setInventory(inv => inv.map(i =>
      i.ID === inventoryId
        ? { ...i, Status: "Reserved", ShowID: showId, ShowName: show?.Name || "" }
        : i
    ));
    setSyncMsg("");
  };

  const handleRemoveShowItem = async (showId, inventoryId) => {
    setSyncMsg("Saving…");
    await removeShowItem(showId, inventoryId);
    setShowItems(si=>si.filter(s=>!(s.ShowID===showId&&s.InventoryID===inventoryId)));
    await updateInventoryItem(inventoryId, { Status: "In Stock", ShowID: "", ShowName: "" });
    setInventory(inv => inv.map(i =>
      i.ID === inventoryId
        ? { ...i, Status: "In Stock", ShowID: "", ShowName: "" }
        : i
    ));
    setSyncMsg("");
  };

  const handleUnlinkShowItem = async (showId, inventoryId) => {
    try {
      await removeShowItem(showId, inventoryId);
      setShowItems(si=>si.filter(s=>!(s.ShowID===showId&&s.InventoryID===inventoryId)));
    } catch(e) {
      console.warn("Could not unlink show item:", e.message);
    }
  };

  const handleEditItem   = useCallback((item)=>{setActiveItem(item);setDrawer("edit");},[]);
  const handleSellItem   = useCallback((item)=>{setActiveItem(item);setDrawer("sell");},[]);
  const handleBulkEdit   = useCallback(()=>setDrawer("bulk"),[]);

  if (loading) return (
    <ThemeContext.Provider value={{S: makeStyles(T, isDark), T, rates: vendorRates}}>
      <LoadingScreen message={loadMsg}/>
    </ThemeContext.Provider>
  );

  if (!IS_DEMO) {
    if (!user) {
      if (isQrVisit && loading) return <ForbiddenPage />;
      return (
        <ThemeContext.Provider value={{S: makeStyles(T, isDark), T, rates: vendorRates}}>
          <LoginScreen onLogin={handleLogin}/>
        </ThemeContext.Provider>
      );
    }

    if (accountSuspended) {
      return (
        <SuspendedAccountScreen
          user={user}
          onSignOut={async()=>{
            await signOut();
            clearVendorCache();
            setUser(null);
            setAccountSuspended(false);
          }}
        />
      );
    }

    if (needsAccountSetup) {
      return (
        <ThemeContext.Provider value={{S: makeStyles(T, isDark), T, rates: vendorRates}}>
          <AccountSetupScreen user={user} onCreated={(created, intent)=>{
            setVendor(created);
            setNeedsAccountSetup(false);
            setShowOnboarding(true);
            if (intent === "deal") setTab("dealboard");
          }} />
        </ThemeContext.Provider>
      );
    }

    if (!vendor) {
      return (
        <PendingApprovalScreen
          user={user}
          onSignOut={async()=>{
            await signOut();
            clearVendorCache();
            setUser(null);
            setNeedsAccountSetup(false);
          }}
        />
      );
    }
  }

  const activeShow = shows.find(s => s.Status === "Active");
  if (showModeActive && activeShow) return (
    <DemoContext.Provider value={IS_DEMO}>
    <PlanContext.Provider value={planValue}>
    <ThemeContext.Provider value={{S: makeStyles(T, isDark), T, rates: vendorRates}}>
      {IS_DEMO && <DemoBanner />}
      {IS_DEMO && (
        <div style={{
          padding:"8px 16px", background:T.surface,
          borderBottom:`1px solid ${T.border}`,
          display:"flex", alignItems:"center",
        }}>
          <button
            onClick={()=>setShowModeActive(false)}
            style={{
              background:"none", border:`0.5px solid ${T.border}`,
              color:T.text, borderRadius:8, padding:"6px 14px",
              fontSize:13, cursor:"pointer", fontFamily:"'Sora',sans-serif",
              display:"flex", alignItems:"center", gap:6,
            }}>
            ← Back to Dashboard
          </button>
        </div>
      )}
      <ShowMode
        show={activeShow}
        inventory={inventory}
        showItems={showItems}
        buyers={buyers}
        isPaid={planValue.isPaid}
        vendorId={vendor?.id}
        vendor={vendor}
        userEmail={user?.email}
        onExit={()=>setShowModeActive(false)}
        onUpdateItem={IS_DEMO ? ()=>demoBlockToast(toast) : updateItem}
        onAddInventoryItem={IS_DEMO ? ()=>demoBlockToast(toast) : handleImportItem}
        onAddShowItem={IS_DEMO ? ()=>demoBlockToast(toast) : handleAddShowItem}
        onRemoveShowItem={IS_DEMO ? ()=>demoBlockToast(toast) : handleRemoveShowItem}
        onUnlinkShowItem={IS_DEMO ? ()=>demoBlockToast(toast) : handleUnlinkShowItem}
        onRefreshShowItems={IS_DEMO ? ()=>{} : ()=>readShowItems().then(setShowItems)}
        onAddTrade={IS_DEMO ? ()=>demoBlockToast(toast) : handleAddTrade}
      />
    </ThemeContext.Provider>
    </PlanContext.Provider>
    </DemoContext.Provider>
  );

  return (
    <DemoContext.Provider value={IS_DEMO}>
    <PlanContext.Provider value={planValue}>
    <ThemeContext.Provider value={{S: makeStyles(T, isDark), T, rates: vendorRates}}>
    <div style={{...S.app, display:"flex", flexDirection:"column", minHeight:"100vh"}}>

      {IS_DEMO && <DemoBanner />}

      {showTrialExpiredModal && (
        <div style={{...S.overlay, zIndex:10003}}>
          <div style={S.drawer}>
            <div style={S.h2}>Your Free Trial Has Ended</div>
            <div style={{fontSize:13, color:T.textMuted, marginBottom:22, lineHeight:1.6}}>
              Your 7 day Pro trial is over. Continue on Pro to keep everything you've been using,
              Dynamic Pricing included, or drop back to the Free plan.
            </div>
            <button style={{...S.btn("primary"), width:"100%", padding:14, marginBottom:10}}
              onClick={()=>{ setShowTrialExpiredModal(false); openUpgradeModal(); }}>
              Continue on Pro
            </button>
            <button style={{...S.btn("ghost"), width:"100%", padding:12}}
              onClick={async()=>{
                const result = await checkTrialStatus("downgrade");
                if (result?.plan) setVendor(v => v ? { ...v, plan: result.plan, dynamic_pricing_addon_status: "cancelled" } : v);
                setShowTrialExpiredModal(false);
                toast("You're back on the Free plan.", "alert");
              }}>
              Downgrade to Free
            </button>
          </div>
        </div>
      )}

      {!IS_DEMO && vendor?.plan === "trial" && vendor?.trial_ends_at && (() => {
        const daysLeft = Math.ceil((new Date(vendor.trial_ends_at) - new Date()) / 86400000);
        if (daysLeft > 5) return null;
        return (
          <div style={{
            width:"100%", boxSizing:"border-box", background:"#3C66EE", color:"#fff",
            fontFamily:"'Sora',sans-serif", textAlign:"center", padding:"10px 16px",
            fontSize:13, fontWeight:600,
          }}>
            {daysLeft > 0
              ? `${daysLeft} day${daysLeft!==1?"s":""} left in your free Pro trial — upgrade to keep everything.`
              : "Your free Pro trial ends today — upgrade to keep everything."}
            {" "}
            <button onClick={openUpgradeModal}
              style={{background:"none", border:"none", color:"#fff", textDecoration:"underline",
                fontWeight:700, cursor:"pointer", fontSize:13, padding:0}}>
              Upgrade now →
            </button>
          </div>
        );
      })()}

      {trialOfferShow && vendor && (
        <TrialOfferModal show={trialOfferShow} vendor={vendor}
          onClose={(started) => {
            setTrialOfferShow(null);
            setVendor(v => v ? { ...v, trial_used: true, ...(started ? { plan: "trial" } : {}) } : v);
          }} />
      )}

      {_showUpgradeModal && vendor && (
        <PayPalUpgradeModal vendor={vendor}
          onClose={(newPlan) => {
            _closeUpgradeModal();
            if (newPlan) setVendor(v => v ? { ...v, plan: newPlan } : v);
          }} />
      )}

      {_showDealBoardPlusModal && vendor && (
        <DealBoardPlusUpgradeModal vendor={vendor}
          onClose={(newPlan) => {
            _closeDealBoardPlusModal();
            if (newPlan) setVendor(v => v ? { ...v, plan: newPlan } : v);
          }} />
      )}

      {showQuickScan && (
        <QuickScanFlow
          S={S} T={T} toast={toast} confirmAction={confirmAction}
          vendorId={vendor?.id}
          defaultBuyPercentage={(vendor?.cash_buy_rate ?? CONFIG.CASH_BUY_RATE ?? 0.70) * 100}
          maxQueue={40}
          onAddInventoryItem={handleImportItem}
          onClose={async ()=>{
            setShowQuickScan(false);
            setSyncMsg("Refreshing…");
            await loadData();
            setSyncMsg("");
          }}
        />
      )}

      {showPriceUpdates && (
        <div style={S.overlay} onClick={e=>{ if (e.target===e.currentTarget) return; }}>
          <div style={{...S.drawer, maxWidth:480}}>
            <div style={{fontSize:18, fontWeight:800, color:T.text, marginBottom:4}}>
              💲 Last Night's Price Updates
            </div>
            <div style={{fontSize:13, color:T.textMuted, marginBottom:16}}>
              {priceUpdates.length} card{priceUpdates.length !== 1 ? "s" : ""} had their List Price
              updated by Dynamic Pricing.
            </div>
            <div style={{maxHeight:340, overflowY:"auto", marginBottom:16}}>
              {priceUpdates.map((u,i) => {
                const went_up = u.OldPrice != null && u.NewPrice > u.OldPrice;
                const went_down = u.OldPrice != null && u.NewPrice < u.OldPrice;
                return (
                  <div key={u.ID} style={{display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"10px 0", borderTop: i>0 ? `1px solid ${T.borderSoft||T.border}` : "none"}}>
                    <div style={{fontSize:13, fontWeight:600, flex:1, minWidth:0,
                      overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {u.CardName}
                    </div>
                    <div style={{fontSize:13, flexShrink:0, display:"flex", alignItems:"center", gap:6}}>
                      <span style={{color:T.textFaint}}>{u.OldPrice != null ? fmt(u.OldPrice) : "—"}</span>
                      <span style={{color:T.textFaint}}>→</span>
                      <span style={{fontWeight:700,
                        color: went_up ? "#4ade80" : went_down ? "#f87171" : T.text}}>
                        {fmt(u.NewPrice)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <button style={{...S.btn("primary"), width:"100%"}}
              onClick={async()=>{
                setShowPriceUpdates(false);
                try { await markPriceUpdatesSeen(vendor.id); } catch(e) {}
              }}>
              Got it
            </button>
          </div>
        </div>
      )}

      {/* ── TOP NAV ──────────────────────────────────────────────────────────── */}
      <div style={{...S.nav, display:"flex", alignItems:"center", gap:8, position:"sticky", top:0, zIndex:200}}>
        {!isWide && (
          <button onClick={()=>setNavOpen(v=>!v)}
            style={{background:"none",border:"none",cursor:"pointer",
              padding:"6px 8px",color:T.text,fontSize:20,lineHeight:1}}>
            {navOpen ? "✕" : "☰"}
          </button>
        )}

        <div style={S.logo}>
          <img src={isDark ? "/cv-logo_dark.png" : "/cv-logo.png"} alt="VendorStax"
            style={{
              height:28, maxWidth:160,
              objectFit:"contain", objectPosition:"left center",
              display:"block",
            }}
            onError={e=>{
              e.target.style.display="none";
              e.target.nextSibling.style.display="flex";
            }}
          />
          <span style={{display:"none", alignItems:"center", gap:2}}>
            <span style={{color:"#3C66EE"}}>Vendor</span>
            <span style={{background:"#3C66EE",color:"#ffffff",
              padding:"2px 8px 3px",borderRadius:7,
              marginLeft:2,fontSize:16,fontWeight:700}}>Stax</span>
          </span>
        </div>

        <div style={{flex:1}}/>
        {syncMsg && <div style={{fontSize:11,color:"#3b82f6",fontFamily:"'DM Mono',monospace"}}>{syncMsg}</div>}
        <button style={{...S.btn("ghost"),padding:"6px 10px",fontSize:16}}
          onClick={toggleTheme} title={isDark?"Switch to light mode":"Switch to dark mode"}>
          {isDark ? "☀️" : "🌙"}
        </button>
        {!IS_DEMO && (
          <button style={{...S.btn("ghost"),padding:"4px"}}
            onClick={async()=>{await signOut();clearVendorCache();setUser(null);setInventory([]);setLots([]);setTrades([]);setShows([]);setShowItems([]);setLoading(false);}}>
            <img src={user?.user_metadata?.avatar_url || user?.picture || ""} alt=""
              style={{width:28,height:28,borderRadius:"50%",display:"block",background:T.surface2}}/>
          </button>
        )}
      </div>

      {/* ── BODY: sidebar + content ───────────────────────────────────────────── */}
      <div style={{display:"flex", flex:1, position:"relative"}}>

        {!isWide && navOpen && (
          <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",zIndex:149}}
            onClick={()=>setNavOpen(false)} />
        )}

        {/* ── SIDEBAR NAV ──────────────────────────────────────────────────────── */}
        <nav style={{
          position: isWide ? "sticky" : "fixed",
          top: isWide ? 0 : 60,
          left: 0,
          height: isWide ? "100vh" : "calc(100vh - 60px)",
          width: 200,
          background: T.surface,
          borderRight: `0.5px solid ${T.border}`,
          zIndex: isWide ? 10 : 150,
          transform: isWide ? "none" : navOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.22s ease",
          display: "flex",
          flexDirection: "column",
          padding: "12px 0",
          overflowY: "auto",
          flexShrink: 0,
        }}>
          {[
            ...(!isStaff ? [["dashboard", "📊", "Dashboard", null]] : []),
            ["inventory",  "📦", "Inventory",    null],
            ["lots",       "📁", "Lots",          null],
            ...(!isStaff ? [["crm", "👥", "CRM", null]] : []),
            ["shows",      "🎪", "Shows",         null],
            ["reports",    "📈", "Reports",       null],
            ["import",     "⬆",  "Import",        "import_collectr"],
            ["settings",   "⚙", "Settings",       null],
            ["help",       "❓", "Help",           null],
            ...(planValue.isAdmin && !IS_DEMO ? [["admin", "🛡", "Admin", null]] : []),
          ].map(([key,fallbackEmoji,label,featureKey])=>(
            <button key={key}
              style={{
                background: tab===key ? "rgba(60,102,238,0.12)" : "transparent",
                border: "none",
                borderLeft: tab===key ? "3px solid #3C66EE" : "3px solid transparent",
                color: tab===key ? "#3C66EE" : T.text,
                fontFamily: "'Sora',sans-serif",
                fontSize: 14,
                fontWeight: tab===key ? 600 : 400,
                padding: "12px 20px",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s",
                whiteSpace: "nowrap",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
              onClick={()=>{ setTab(key); setNavOpen(false); }}
              data-tour-key={key}>
              <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:18,height:18,flexShrink:0}}>
                {CONFIG.NAV_ICONS?.[key] ? (
                  <span style={{
                    display:"inline-block",
                    width:18, height:18, flexShrink:0,
                    WebkitMaskImage:`url(${CONFIG.NAV_ICONS[key]})`,
                    maskImage:`url(${CONFIG.NAV_ICONS[key]})`,
                    WebkitMaskSize:"contain",
                    maskSize:"contain",
                    WebkitMaskRepeat:"no-repeat",
                    maskRepeat:"no-repeat",
                    WebkitMaskPosition:"center",
                    maskPosition:"center",
                    background: tab===key ? T.text : T.textMuted,
                  }}/>
                ) : (
                  <span style={{fontSize:15,lineHeight:1}}>{fallbackEmoji}</span>
                )}
              </span>
              {label}
              {featureKey && planValue.isFree && <PaidBadge />}
            </button>
          ))}

          {planValue.isFree && (
            <button onClick={()=>{ openUpgradeModal(); setNavOpen(false); }}
              style={{
                margin: "12px 16px 4px",
                background: "linear-gradient(135deg, #3C66EE, #1C4CE6)",
                border: "none",
                borderRadius: 10,
                color: "#fff",
                fontFamily: "'Sora',sans-serif",
                fontSize: 13,
                fontWeight: 700,
                padding: "11px 14px",
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                boxShadow: "0 4px 14px rgba(60,102,238,0.35)",
              }}>
              <span style={{fontSize:15}}>⭐</span>
              Upgrade to Pro
            </button>
          )}
        </nav>

        {showOnboarding && (
          <SpotlightTour
            vendor={vendor}
            ensureNavOpen={()=>setNavOpen(true)}
            onFinish={async ()=>{
              setShowOnboarding(false);
              setNavOpen(false);
              try { await markOnboardingTourSeen(vendor.id); }
              catch(e) { console.warn("Could not save onboarding tour status:", e.message); }
            }}
          />
        )}

        {/* ── MAIN CONTENT ─────────────────────────────────────────────────────── */}
        <main style={{
          flex: 1,
          minWidth: 0,
          overflowX: "hidden",
          paddingRight: isWide ? 8 : 0,
        }}>

          {planValue.isEnterprise && locations.length > 0 && (() => {
            const allowed = (myLocationIds && myLocationIds.length ? locations.filter(l => myLocationIds.includes(l.ID)) : locations);
            const current = locations.find(l => l.ID === activeLocationId) || allowed[0];
            if (!current) return null;
            return (
              <div style={{background:"#1a2332", padding:"8px 16px", display:"flex",
                justifyContent:"space-between", alignItems:"center", flexShrink:0,
                borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                <div style={{fontSize:12, color:"#88bdf2", fontWeight:600}}>
                  📍 {current.StoreName} · #{current.StoreNumber}
                </div>
                {allowed.length > 1 && (
                  <select value={current.ID} onChange={e=>setActiveLocation(e.target.value)}
                    style={{background:"transparent", color:"#88bdf2", border:"1px solid rgba(136,189,242,0.4)",
                      borderRadius:6, fontSize:11, padding:"3px 6px"}}>
                    {allowed.map(l => (
                      <option key={l.ID} value={l.ID} style={{color:"#000"}}>{l.StoreName} · #{l.StoreNumber}</option>
                    ))}
                  </select>
                )}
              </div>
            );
          })()}

      {(()=>{
        const today = new Date();
        const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate()+1);
        const todayStr    = today.toISOString().split("T")[0];
        const tomorrowStr = tomorrow.toISOString().split("T")[0];
        const incoming = inventory.filter(i =>
          i.GradingCompany &&
          i.GradingReturnDate &&
          (i.GradingReturnDate === todayStr || i.GradingReturnDate === tomorrowStr) &&
          i.Status !== "Sold"
        );
        if (incoming.length === 0) return null;
        return (
          <div style={{background:"linear-gradient(135deg,#3C66EE,#3b82f6)",
            padding:"10px 16px",display:"flex",justifyContent:"space-between",
            alignItems:"center",flexShrink:0}}>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>
              📬 {incoming.length} graded card{incoming.length!==1?"s":""} returning
              {incoming[0].GradingReturnDate===todayStr?" today":" tomorrow"}
            </div>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.8)"}}>
              {incoming.map(i=>`${i.CardName} (${i.GradingCompany})`).join(" · ")}
            </div>
          </div>
        );
      })()}

      {tab==="dashboard"  && <Dashboard inventory={inventory} lots={lots} shows={shows} vendor={vendor} isEnterprise={IS_DEMO ? true : planValue.isEnterprise} activeLocationId={activeLocationId}/>}
      {tab==="inventory"  && <InventoryList inventory={inventory} lots={lots} shows={shows}
        showItems={showItems} priceHistory={priceHistory}
        onEdit={handleEditItem} onSell={handleSellItem}
        onDelete={deleteItem} onBulkEdit={isStaff ? null : handleBulkEdit}
        onAddToShow={handleAddShowItem}
        showLocation={!!vendor?.kiosk_show_location}
        dynamicPricingEnabled={!!vendor?.dynamic_pricing_enabled}
        isPaid={planValue.isPaid}
        isEnterprise={IS_DEMO ? true : planValue.isEnterprise}
        locations={locations} activeLocationId={activeLocationId}
        locationOptions={vendor?.kiosk_location_options || []}
        onAddLocationOption={async(val)=>{
          const next = Array.from(new Set([...(vendor?.kiosk_location_options||[]), val]));
          const { data, error } = await supabase.from("vendors")
            .update({ kiosk_location_options: next }).eq("id", vendor.id).select().single();
          if (!error) setVendor(data);
        }}
        onRestock={async(item)=>{
          await updateItem(item.ID, { QRCode: "" });
          const newItem = {
            Type:      item.Type,
            CardName:  item.CardName,
            Set:       item.Set,
            Number:    item.Number,
            Rarity:    item.Rarity,
            Language:  item.Language,
            Condition: item.Condition,
            Grade:     item.Grade,
            Quantity:  1,
            Source:    item.Source || "Individual Buy",
            ImageURL:  item.ImageURL,
            Notes:     item.Notes,
            TcggoId:              item.TcggoId || null,
            MarketPrice:          item.MarketPrice || null,
            MarketPriceSource:    item.MarketPriceSource || null,
            MarketPriceUpdatedAt: item.MarketPriceUpdatedAt || null,
            MarketPriceData:      item.MarketPriceData || null,
            AcquisitionCost:"", ListPrice:"", MinSellPrice:"",
            MarketValueAtAcquisition:"",
            AcquisitionDate: localDate(),
            Status:"In Stock",
            LotID:"", LotName:"", LotItemNumber:"",
            ShowID:"", ShowName:"",
            SoldPrice:"", SoldDate:"", SoldChannel:"", BuyerGender:"", TradeCredit:"",
          };
          setActiveItem(newItem);
          setDrawer("add");
        }}
        onUpdateItem={updateItem}/>}
      {tab==="lots"       && <LotsPage lots={lots} inventory={inventory} onNewLot={handleNewLot} onUpdateLot={handleUpdateLot} onUpdateItem={updateItem} onAddItem={handleImportItem}/>}
      {tab==="crm" && !isStaff  && <CRMPage
          buyers={buyers} consignors={consignors} inventory={inventory}
          vendor={vendor} shows={shows}
          userEmail={user?.email}
          isEnterprise={IS_DEMO ? true : planValue.isEnterprise}
          onAddBuyer={async (form) => {
            const created = await addBuyer({ fullName: form.FullName, phone: form.Phone, email: form.Email, notes: form.Notes,
              address: form.Address, city: form.City, state: form.State, zip: form.Zip });
            setBuyers(b => [...b, created].sort((a,b)=>a.FullName.localeCompare(b.FullName)));
            return created;
          }}
          onUpdateBuyer={async (id, form) => {
            const updated = await updateBuyer(id, { fullName: form.FullName, phone: form.Phone, email: form.Email, notes: form.Notes,
              address: form.Address, city: form.City, state: form.State, zip: form.Zip });
            setBuyers(b => b.map(x => x.ID === id ? updated : x));
            return updated;
          }}
          onDeleteBuyer={async (id) => {
            await deleteBuyer(id);
            setBuyers(b => b.filter(x => x.ID !== id));
          }}
          onAddConsignor={handleAddConsignor}
          onUpdateConsignor={handleUpdateConsignor}
          onDeleteConsignor={handleDeleteConsignor}
          onUpdateItem={updateItem}
          canUseBuyers={planValue.canUse("buyers_list")}
          canUseConsignors={planValue.canUse("consignors")}
          S={S} T={T}
        />}
      {tab==="buyers"     && (planValue.canUse("buyers_list")
        ? <BuyersPage buyers={buyers} vendor={vendor} shows={shows}
            onAdd={async (form) => {
              const created = await addBuyer({ fullName: form.FullName, phone: form.Phone, email: form.Email, notes: form.Notes });
              setBuyers(b => [...b, created].sort((a,b)=>a.FullName.localeCompare(b.FullName)));
              return created;
            }}
            onUpdate={async (id, form) => {
              const updated = await updateBuyer(id, { fullName: form.FullName, phone: form.Phone, email: form.Email, notes: form.Notes });
              setBuyers(b => b.map(x => x.ID === id ? updated : x));
              return updated;
            }}
            onDelete={async (id) => {
              await deleteBuyer(id);
              setBuyers(b => b.filter(x => x.ID !== id));
            }}
          />
        : <div style={S.page}><PaidFeatureBanner feature="Buyers List" /></div>
      )}
      {tab==="consignors" && (planValue.canUse("consignors")
        ? <ConsignorsPage consignors={consignors} inventory={inventory}
            onAdd={handleAddConsignor} onUpdate={handleUpdateConsignor} onDelete={handleDeleteConsignor}
            onUpdateItem={updateItem}/>
        : <div style={S.page}><PaidFeatureBanner feature="Consignors" /></div>
      )}
      {tab==="shows"      && <ShowsPage shows={shows} inventory={inventory} showItems={showItems}
        trades={trades}
        onAddShow={handleAddShow} onUpdateShow={handleUpdateShow}
        onAddShowItem={handleAddShowItem} onRemoveShowItem={handleRemoveShowItem}
        onUpdateItem={updateItem}/>}
      {tab==="trades"     && <TradesPage trades={trades} inventory={inventory} shows={shows}
        onAddTrade={handleAddTrade} onUpdateItem={updateItem} onAddInventoryItem={handleImportItem}
        onEditTrade={async(id,updates)=>{
          setSyncMsg("Saving…");
          await updateTrade(id,updates);
          setTrades(ts=>ts.map(t=>t.ID===id?{...t,...updates}:t));
          setSyncMsg("");
        }}/>}
      {tab==="reports"    && <ReportsPage inventory={inventory} lots={lots} shows={shows} trades={trades}
        isPaid={planValue.isPaid} vendor={vendor} isStaff={isStaff} isEnterprise={IS_DEMO ? true : planValue.isEnterprise}
        activeLocationId={activeLocationId}
        buyers={buyers} onAddBuyer={async(form) => {
          const created = await addBuyer({ fullName: form.fullName, phone: form.phone, email: form.email, notes: form.notes, source: "kiosk_request", kioskRequestId: form.kioskRequestId });
          setBuyers(b => [...b, created].sort((a,b)=>a.FullName.localeCompare(b.FullName)));
          return created;
        }}/>}
      {tab==="import"     && (planValue.canUse("import_collectr")
        ? <ImportPage inventory={inventory} lots={lots} onImport={handleImportItem} vendor={vendor}
            onClose={()=>setTab("inventory")} />
        : <div style={S.page}><PaidFeatureBanner feature="Collectr Import" /></div>
      )}
      {tab==="dealboard"  && <DealBoardPage vendor={vendor} isPaid={planValue.isPaid} S={S} T={T} activeLocationId={effectiveLocationId} onItemCreated={saved => setInventory(inv => upsertInventoryRow(inv, saved))} />}
      {tab==="help"     && <HelpPage />}
      {tab==="settings" && <Settings vendor={vendor} isPaid={IS_DEMO ? true : planValue.isPaid} isAdmin={IS_DEMO ? true : planValue.isAdmin}
        isStaff={isStaff}
        isEnterprise={IS_DEMO ? true : planValue.isEnterprise}
        canUse={IS_DEMO ? (()=>true) : planValue.canUse}
        currentUserEmail={user?.email || ""}
        onUpgradeClick={openUpgradeModal}
        onVendorUpdate={(updated) => {
          if (IS_DEMO) return;
          setVendor(updated);
          clearVendorCache();
        }} />}
      {tab==="admin" && planValue.isAdmin && !IS_DEMO && <AdminPage
        onVendorCreated={()=>{}}
      />}

      {drawer==="addSearch" && (planValue.isPaid ? (
        <AddCardDrawer
          lots={lots} shows={shows} consignors={consignors} vendor={vendor}
          gameType="Pokemon"
          activeLocationId={effectiveLocationId}
          onClose={()=>{setDrawer(null);setActiveItem(null);}}
          onCreateLot={handleNewLot}
          onSave={async (cardData, opts) => {
            if (opts?.openFullForm) {
              setActiveItem({
                GameType: opts.gameType || "Pokemon",
                Type: opts.itemType || "Single Card",
                ...(opts.prefillName ? { CardName: opts.prefillName } : {}),
              });
              setDrawer("add");
              return;
            }
            await saveItem(cardData);
          }}
        />
      ) : (
        <ItemDrawer
          item={null}
          lots={lots} shows={shows} inventory={inventory} consignors={consignors}
          isPaid={false} buyers={buyers}
          onClose={()=>{setDrawer(null);setActiveItem(null);}} onSave={saveItem} onCreateLot={handleNewLot}
          onDuplicate={(clone)=>{setActiveItem(clone);setDrawer("add");}}/>
      ))}

      {(drawer==="add"||drawer==="edit") && (
        <ItemDrawer
          item={drawer==="edit" ? activeItem : (activeItem || null)}
          lots={lots} shows={shows} inventory={inventory} consignors={consignors}
          isPaid={planValue.isPaid} vendor={vendor} buyers={buyers}
          isEnterprise={IS_DEMO ? true : planValue.isEnterprise} locations={locations}
          onClose={()=>{setDrawer(null);setActiveItem(null);}} onSave={saveItem} onCreateLot={handleNewLot}
          onDuplicate={(clone)=>{setActiveItem(clone);setDrawer("add");}}/>
      )}
      {drawer==="qrsell" && activeItem && (
        <QRSellOverlay
          item={activeItem}
          inventory={inventory}
          activeShow={shows.find(s => s.Status === "Active") || null}
          onSell={async (id, updates) => {
            const currentShow = shows.find(s => s.Status === "Active");
            const finalUpdates = {
              ...updates,
              ...(currentShow ? {
                ShowID:   currentShow.ID,
                ShowName: currentShow.Name,
              } : {})
            };
            await updateItem(id, finalUpdates);
            if (currentShow) {
              const alreadyIn = showItems.some(
                si => si.ShowID === currentShow.ID && si.InventoryID === id
              );
              if (!alreadyIn) await handleAddShowItem(currentShow.ID, id);
            }
          }}
          onClose={()=>{setDrawer(null);setActiveItem(null);}}
          today={localDate()}
        />
      )}
      {drawer==="sell" && activeItem && (
        <SellDrawer item={activeItem} shows={shows} vendor={vendor} onClose={()=>setDrawer(null)} onSave={updateItem}/>
      )}
      {drawer==="bulk" && (
        <BulkEditDrawer inventory={inventory} lots={lots} isStaff={isStaff} onClose={()=>setDrawer(null)} onBulkSave={updateItem}/>
      )}
      <style>{`
        @media (min-width: 768px) {
          .vs-bottombar-btn { padding: 8px 8px !important; border-radius: 12px !important; font-size: 11px !important; }
          .vs-bottombar-icon { font-size: 15px !important; }
        }
      `}</style>
      <div style={{
        position:"fixed", bottom:0, left:0, right:0, zIndex:90,
        background:`linear-gradient(to top, ${T.bg} 85%, transparent)`,
        paddingTop:10,
        pointerEvents:"none",
      }}>
        <div className="vs-bottombar" style={{
          maxWidth:1200, margin:"0 auto",
          padding:"0 20px",
          paddingBottom:`calc(16px + env(safe-area-inset-bottom))`,
          display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10,
        }}>
          <button className="vs-bottombar-btn" style={{
            pointerEvents:"all",
            padding:"11px 8px", borderRadius:16, border:"none",
            background:"#384959", color:"#88bdf2",
            fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:700,
            cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.25)",
            display:"flex", flexDirection:"column", alignItems:"center", gap:4,
            opacity: shows.filter(s=>s.Status==="Active").length>0 ? 1 : 0.5,
          }}
          onClick={async()=>{
            if (shows.filter(s=>s.Status==="Active").length===0) {
              toast("Activate a show to enter Show Mode", "alert");
              return;
            }
            setSyncMsg("Refreshing…");
            await loadData();
            setSyncMsg("");
            setShowModeActive(true);
          }}>
            <span className="vs-bottombar-icon" style={{fontSize:18}}>👁</span>
            Show Mode
          </button>

          {planValue.canUse("quick_scan") ? (
            <button className="vs-bottombar-btn" style={{
              pointerEvents:"all",
              padding:"11px 8px", borderRadius:16, border:"none",
              background:"#384959", color:"#88bdf2",
              fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:700,
              cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.25)",
              display:"flex", flexDirection:"column", alignItems:"center", gap:4,
            }}
            onClick={()=>IS_DEMO ? demoBlockToast(toast) : setShowQuickScan(true)}>
              <span className="vs-bottombar-icon" style={{fontSize:18}}>⚡</span>
              Scan Card
            </button>
          ) : <div />}

          <button className="vs-bottombar-btn" style={{
            pointerEvents:"all",
            padding:"11px 8px", borderRadius:16, border:"none",
            background:"#384959", color:"#88bdf2",
            fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:700,
            cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.25)",
            display:"flex", flexDirection:"column", alignItems:"center", gap:4,
          }}
          onClick={()=>{setActiveItem(null);setDrawer("addSearch");}}>
            <span className="vs-bottombar-icon" style={{fontSize:18}}>+</span>
            Card/Sealed
          </button>

          <button className="vs-bottombar-btn" style={{
            pointerEvents:"all",
            padding:"11px 8px", borderRadius:16, border:"none",
            background:"#384959", color:"#88bdf2",
            fontFamily:"'Sora',sans-serif", fontSize:12, fontWeight:700,
            cursor:"pointer", boxShadow:"0 4px 20px rgba(0,0,0,0.25)",
            display:"flex", flexDirection:"column", alignItems:"center", gap:4,
          }}
          onClick={()=>IS_DEMO ? demoBlockToast(toast) : setShowAddCustomItem(true)}>
            <span className="vs-bottombar-icon" style={{fontSize:18}}>📦</span>
            Custom Item
          </button>
        </div>
      </div>

      {showAddCustomItem && (
        <AddCustomItemModal vendor={vendor} consignors={consignors} buyers={buyers} isPaid={planValue.isPaid} S={S} T={T} toast={toast} activeLocationId={effectiveLocationId}
          onClose={()=>setShowAddCustomItem(false)}
          onSaved={()=>{ setShowAddCustomItem(false); loadData(); }} />
      )}

      <div style={{height:120}} />

      {showQRGenerator && (
        <QRLabelGenerator
          inventory={inventory}
          onUpdateItem={updateItem}
          onClose={()=>setShowQRGenerator(false)}
        />
      )}

        </main>
      </div>
    </div>
    </ThemeContext.Provider>
    </PlanContext.Provider>
    </DemoContext.Provider>
  );
}

export default function App() {
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && !localStorage.getItem("vs_referred_by")) {
      localStorage.setItem("vs_referred_by", ref.trim().toLowerCase());
    }
  }, []);

  return (
    <NotifyProvider>
      <AppInner />
    </NotifyProvider>
  );
}
